import React, { useEffect, useState, useCallback, useRef } from "react";
import "./App.css";

// On the dev server (port 3000) the backend is on port 5000; when the app is
// served by Flask itself (one port, incl. via the ngrok tunnel) use same-origin.
const API = (typeof window !== "undefined" && window.location.port === "3000")
  ? "http://127.0.0.1:5000" : "";

// --- Stale-while-revalidate cache -----------------------------------------
// Screens unmount when you switch tabs, so on return they used to refetch from
// scratch and show a blank/loading state for a beat. This keeps the last
// response per key in memory: a remounting screen seeds its state from cache
// instantly (no lag), then refreshes in the background. `useState(() =>
// cacheGet(key, fallback))` for the initial value; call `cacheSet(key, data)`
// wherever you setState from a fetch.
const __swrCache = (typeof window !== "undefined")
  ? (window.__spoomCache || (window.__spoomCache = {})) : {};
function cacheGet(key, fallback) {
  return Object.prototype.hasOwnProperty.call(__swrCache, key) ? __swrCache[key] : fallback;
}
function cacheSet(key, data) { __swrCache[key] = data; return data; }

// Make sure the login cookie rides along on every backend request. Needed in
// dev mode (UI on :3000 talking to the API on :5000); harmless same-origin.
if (typeof window !== "undefined" && window.fetch && !window.__fetchPatched) {
  const _origFetch = window.fetch.bind(window);
  window.fetch = (input, init) =>
    _origFetch(input, { credentials: "include", ...(init || {}) }).then((res) => {
      try {
        const url = typeof input === "string" ? input : (input && input.url) || "";
        // A 401 on a normal data call means our session ended (e.g. idle logout).
        // Tell the app to show the login screen. (Auth calls handle their own 401s.)
        if (res.status === 401 && !url.includes("/auth/")) {
          window.dispatchEvent(new CustomEvent("spoom-unauthorized"));
        }
      } catch { /* ignore */ }
      return res;
    });
  window.__fetchPatched = true;
}

// True on phone-sized screens. Drives the mobile layout (bottom nav + cards).
function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return mobile;
}

// ----- Theme (light / dark / system) -----
function applyTheme(pref) {
  if (typeof document === "undefined") return;
  const sysDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
  const dark = pref === "dark" || (pref === "system" && sysDark);
  document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
}
function initTheme() {
  let pref = "light";
  try { pref = localStorage.getItem("spoom_theme") || "light"; } catch { /* ignore */ }
  applyTheme(pref);
  try {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", () => {
      let p = "light"; try { p = localStorage.getItem("spoom_theme") || "light"; } catch { /* ignore */ }
      if (p === "system") applyTheme("system");
    });
  } catch { /* ignore */ }
}
if (typeof window !== "undefined") initTheme();

// ----- Fingerprint / Face-ID sign-in (WebAuthn passkeys) -----
const passkeysSupported = typeof window !== "undefined" && !!window.PublicKeyCredential;

function _b64urlToBuf(s) {
  s = s.replace(/-/g, "+").replace(/_/g, "/");
  const pad = s.length % 4; if (pad) s += "=".repeat(4 - pad);
  const bin = atob(s); const b = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) b[i] = bin.charCodeAt(i);
  return b.buffer;
}
function _bufToB64url(buf) {
  const b = new Uint8Array(buf); let s = "";
  for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// Enroll this device's fingerprint/Face ID for the logged-in user.
async function registerPasskey() {
  const opts = await fetch(`${API}/auth/webauthn/register/begin`, { method: "POST" }).then((r) => r.json());
  if (opts.error) return { ok: false, error: opts.error };
  opts.challenge = _b64urlToBuf(opts.challenge);
  opts.user.id = _b64urlToBuf(opts.user.id);
  if (opts.excludeCredentials) opts.excludeCredentials = opts.excludeCredentials.map((c) => ({ ...c, id: _b64urlToBuf(c.id) }));
  const cred = await navigator.credentials.create({ publicKey: opts });
  const body = {
    id: cred.id, rawId: _bufToB64url(cred.rawId), type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment || undefined,
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
    response: {
      clientDataJSON: _bufToB64url(cred.response.clientDataJSON),
      attestationObject: _bufToB64url(cred.response.attestationObject),
    },
  };
  return fetch(`${API}/auth/webauthn/register/complete`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  }).then((r) => r.json());
}

// Sign in with a fingerprint/Face ID passkey.
async function loginWithPasskey() {
  const opts = await fetch(`${API}/auth/webauthn/login/begin`, { method: "POST" }).then((r) => r.json());
  opts.challenge = _b64urlToBuf(opts.challenge);
  if (opts.allowCredentials) opts.allowCredentials = opts.allowCredentials.map((c) => ({ ...c, id: _b64urlToBuf(c.id) }));
  const cred = await navigator.credentials.get({ publicKey: opts });
  const body = {
    id: cred.id, rawId: _bufToB64url(cred.rawId), type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment || undefined,
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
    response: {
      clientDataJSON: _bufToB64url(cred.response.clientDataJSON),
      authenticatorData: _bufToB64url(cred.response.authenticatorData),
      signature: _bufToB64url(cred.response.signature),
      userHandle: cred.response.userHandle ? _bufToB64url(cred.response.userHandle) : null,
    },
  };
  return fetch(`${API}/auth/webauthn/login/complete`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  }).then((r) => r.json());
}

const STATUS_LABELS = {
  not_started: "Not started",
  collect_info: "Collecting info",
  in_preparation: "In preparation",
  review: "In review",
  ready_to_file: "Ready to file",
  filed: "Filed",
  not_filing: "Not filing",
  completed: "Completed",
};
const STATUS_ORDER = [
  "not_started", "collect_info", "in_preparation",
  "review", "ready_to_file", "filed", "not_filing",
];
// Projects (sales tax / bookkeeping / 1099s) also offer a plain "Completed".
const PROJECT_STATUS_ORDER = [...STATUS_ORDER, "completed"];
// Small colored status chip palette for the Home "Due soon" rows.
const STATUS_CHIP = {
  not_started:    { bg: "#eef4f9", bd: "#cfe0ee", fg: "#0f4c75", dot: "#0f4c75" },
  collect_info:   { bg: "#eef4f9", bd: "#cfe0ee", fg: "#0f4c75", dot: "#0f4c75" },
  in_preparation: { bg: "#fff8ee", bd: "#f2dcae", fg: "#a15c00", dot: "#e5a53c" },
  review:         { bg: "#fff8ee", bd: "#f2dcae", fg: "#a15c00", dot: "#e5a53c" },
  ready_to_file:  { bg: "#e6f6f4", bd: "#b9e6dd", fg: "#0d8577", dot: "#0d8577" },
  filed:          { bg: "#eef2f5", bd: "#dbe2e9", fg: "#5b7186", dot: "#5b7186" },
  not_filing:     { bg: "#eef2f5", bd: "#dbe2e9", fg: "#5b7186", dot: "#5b7186" },
  completed:      { bg: "#e6f6f4", bd: "#b9e6dd", fg: "#0d8577", dot: "#0d8577" },
};
const CHIP_OVERDUE = { bg: "#fdecec", bd: "#f3c6c6", fg: "#c0392b", dot: "#c0392b" };
const ENTITY_TYPES = ["1040", "1065", "1120S"];
const DOC_TYPES = {
  w2: "W-2", "1099": "1099", "1098": "1098 — Mortgage", "1098e": "1098-E — Student loan",
  "1095a": "1095-A — Marketplace", "1095b": "1095-B/C — Coverage",
  closing: "Closing / HUD", k1: "K-1", receipt: "Receipt",
  prior_return: "Prior return", tax_return: "Tax return",
  organizer: "Client checklist", id: "ID / Driver's license", other: "Other",
};
const F1098E_FIELDS = [["student_loan_interest", "Box 1 — Student loan interest"]];
const CJE_REFS = [
  ["A", "Land"], ["B", "Building"], ["BA", "Building Costs (capitalized)"],
  ["C", "Escrow - Repairs"], ["D", "Escrow - Property Tax"], ["E", "Interest"],
  ["F", "Closing Costs (expensed)"], ["G", "Property Tax"], ["H", "Mortgage (loan)"],
  ["I", "Rental Income"], ["J", "Security Deposits"], ["K", "Insurance"],
  ["U", "Utilities"],
  ["P", "Payoff Existing Loan"], ["L", "Cash"],
];
const CJE_SIDE = { A: "D", B: "D", BA: "D", C: "D", D: "D", E: "D", F: "D", G: "D",
  H: "C", I: "C", J: "C", K: "D", U: "D", P: "D", L: "C" };
const cjeNum = (v) => Number(String(v == null ? "" : v).replace(/,/g, "")) || 0;
// Closing date -> mm/dd/yyyy (handles ISO YYYY-MM-DD; leaves other text as-is).
const toMDY = (d) => {
  if (!d) return "";
  const m = String(d).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[2].padStart(2, "0")}/${m[3].padStart(2, "0")}/${m[1]}`;
  return String(d);
};
// Show a number with thousands separators while keeping it editable (commas are
// stripped on the way back into state).
const fmtCommas = (v) => {
  if (v === "" || v == null) return "";
  const s = String(v).replace(/,/g, "");
  if (s === "-" || s === ".") return s;
  const neg = s.startsWith("-") ? "-" : "";
  const [intp, dec] = s.replace("-", "").split(".");
  const i = (intp || "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return neg + i + (dec !== undefined ? "." + dec : "");
};
const F1099_R_FIELDS = [
  ["r_gross", "Box 1 — Gross distribution"], ["r_taxable", "Box 2a — Taxable amount"],
  ["r_fed_wh", "Box 4 — Fed W/H"], ["r_code", "Box 7 — Distribution code"],
];
const F1099_NEC_FIELDS = [
  ["nec_comp", "Box 1 — Nonemployee comp"], ["nec_fed_wh", "Box 4 — Fed W/H"],
];
const F1099_MISC_FIELDS = [
  ["misc_rents", "Box 1 — Rents"], ["misc_royalties", "Box 2 — Royalties"],
  ["misc_other", "Box 3 — Other income"], ["misc_fed_wh", "Box 4 — Fed W/H"],
];
const F1099_B_FIELDS = [
  ["b_st_proceeds", "Short-term proceeds"], ["b_st_basis", "Short-term basis"],
  ["b_st_gain", "Short-term gain/(loss)"], ["b_lt_proceeds", "Long-term proceeds"],
  ["b_lt_basis", "Long-term basis"], ["b_lt_gain", "Long-term gain/(loss)"],
];
const F1095A_FIELDS = [
  ["annual_premium", "Line 33A — Annual premium"],
  ["annual_slcsp", "Line 33B — SLCSP premium"],
  ["annual_aptc", "Line 33C — Advance PTC"],
];
const ID_FIELDS = [
  ["full_name", "Full name"], ["dob", "Date of birth (YYYY-MM-DD)"],
  ["license_number", "License / ID number"], ["issue_date", "Issued (YYYY-MM-DD)"],
  ["expiration_date", "Expires (YYYY-MM-DD)"], ["address", "Address"],
  ["issuing_state", "Issuing state"],
];
const F1098_FIELDS = [
  ["mortgage_interest", "Box 1 — Mortgage interest"], ["points", "Box 6 — Points"],
  ["mortgage_ins_prem", "Box 5 — Mtg ins (PMI)"],
  ["hazard_insurance", "Hazard insurance (escrow)"],
  ["re_taxes", "Real estate taxes"],
  ["outstanding_principal", "Box 2 — Outstanding principal"],
  ["property_address", "Box 8 — Property address"],
];
const PROJECT_LABELS = {
  sales_tax: "Sales Tax", bookkeeping: "Bookkeeping", form_1099: "1099s",
  other: "Other",
};
const F1099_INT_FIELDS = [
  ["int_interest", "Box 1 — Interest"], ["int_us_gov", "Box 3 — US Gov interest"],
  ["int_tax_exempt", "Box 8 — Tax-exempt int"], ["int_fed_wh", "Box 4 — Fed W/H"],
];
const F1099_DIV_FIELDS = [
  ["div_ordinary", "Box 1a — Ordinary div"], ["div_qualified", "Box 1b — Qualified div"],
  ["div_cap_gain", "Box 2a — Cap gain distr"], ["div_nondiv", "Box 3 — Nondividend"],
  ["div_199a", "Box 5 — 199A div"], ["div_foreign_tax", "Box 7 — Foreign tax"],
  ["div_fed_wh", "Box 4 — Fed W/H"],
];
const K1_FIELDS = [
  ["box1", "Box 1 — Ordinary income"], ["box2", "Box 2 — Rental RE"],
  ["box3", "Box 3 — Other rental"], ["box4", "Box 4 — Guaranteed pmts"],
  ["box5", "Box 5 — Interest"], ["box6a", "Box 6a — Ord dividends"],
  ["box6b", "Box 6b — Qual dividends"], ["box7", "Box 7 — Royalties"],
  ["box8", "Box 8 — ST cap gain"], ["box9a", "Box 9a — LT cap gain"],
  ["box9c", "Box 9c — 1250 gain"], ["box10", "Box 10 — 1231 gain"],
  ["box19", "Box 19 — Distributions"], ["qbi", "199A — QBI"],
  ["qbi_1231", "199A — Section 1231 gain (loss)"],
  ["w2_wages", "199A — W-2 wages"], ["ubia", "199A — UBIA"],
  ["aj_gross_income", "Box 20 AJ — Agg. Business Gross Income"],
  ["aj_deductions", "Box 20 AJ — Agg. Business Total Deductions"],
];
const W2_FIELDS = [
  ["employee_name", "Employee"], ["employer_name", "Employer"],
  ["wages", "Box 1 — Wages"], ["fed_wh", "Box 2 — Fed W/H"],
  ["ss_wages", "Box 3 — SS wages"], ["ss_tax", "Box 4 — SS tax"],
  ["medicare_wages", "Box 5 — Medicare wages"], ["medicare_tax", "Box 6 — Medicare tax"],
  ["state", "Box 15 — State"], ["state_wages", "Box 16 — State wages"],
  ["state_wh", "Box 17 — State W/H"], ["local_wages", "Box 18 — Local wages"],
  ["local_wh", "Box 19 — Local tax"], ["locality", "Box 20 — Locality"],
];
const BK_FREQUENCIES = [
  ["monthly", "Monthly"], ["quarterly", "Quarterly"], ["annually", "Annually"],
];

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

function fmtDay(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}

// Convert an API date (RFC-1123 or ISO) to the YYYY-MM-DD an <input type=date> needs.
function toDateInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toISOString().slice(0, 10);
}

function isOverdue(iso, status) {
  if (!iso || status === "filed" || status === "completed") return false;
  const d = new Date(iso);
  if (isNaN(d)) return false;
  return d < new Date(new Date().toDateString());
}

function isDueThisMonth(iso, status) {
  if (!iso || status === "filed") return false;
  const d = new Date(iso);
  if (isNaN(d)) return false;
  const now = new Date();
  return d.getUTCFullYear() === now.getFullYear() && d.getUTCMonth() === now.getMonth();
}

function copyText(text) {
  if (!text) return;
  navigator.clipboard?.writeText(text);
}

function FilterDropdown({ options, selected, onToggle, allLabel }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const sel = Array.isArray(selected) ? selected : [];
  const summary = sel.length === 0 ? allLabel
    : sel.length === 1 ? ((options.find((o) => o.value === sel[0]) || {}).label || sel[0])
    : `${sel.length} selected`;
  return (
    <div className="frf-dd" ref={ref}>
      <button type="button" className="frf-dropdown-btn" onClick={() => setOpen((o) => !o)}>
        <span>{summary}</span><span className="frf-caret">{open ? "\u25b2" : "\u25bc"}</span>
      </button>
      {open && (
        <div className="frf-pop">
          {options.map((o) => (
            <label key={o.value} className="frf-check">
              <input type="checkbox" checked={sel.includes(o.value)} onChange={() => onToggle(o.value)} />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function readSavedFilters() {
  try { return JSON.parse(localStorage.getItem("spoom_filters") || "{}"); }
  catch (e) { return {}; }
}

// Opens a new message in the default mail app (Outlook desktop) to this address.
function mailtoLink(email) {
  return `mailto:${encodeURIComponent(email)}`;
}

// WhatsApp wants digits only; assume US (+1) for a bare 10-digit number.
function waDigits(phone) {
  const d = (phone || "").replace(/\D/g, "");
  return d.length === 10 ? "1" + d : d;
}

function fmtDuration(total) {
  total = Math.max(0, total | 0);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// --- Tax-form sanity validation (instant arithmetic, no AI cost) ---
const SS_RATE = 0.062;
const MEDICARE_RATE = 0.0145;
const SS_WAGE_BASE = {
  2021: 142800, 2022: 147000, 2023: 160200, 2024: 168600, 2025: 176100,
};
function vnum(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = parseFloat(String(v).replace(/[$,]/g, ""));
  return isNaN(n) ? null : n;
}
function money(n) { return "$" + Math.round(n).toLocaleString(); }

function validateW2(f, year) {
  const w = [];
  const ssw = vnum(f.ss_wages), sst = vnum(f.ss_tax);
  const mw = vnum(f.medicare_wages), mt = vnum(f.medicare_tax);
  const sw = vnum(f.state_wages), swh = vnum(f.state_wh), wages = vnum(f.wages);
  if (ssw != null && sst != null) {
    const exp = Math.round(ssw * SS_RATE);
    if (Math.abs(sst - exp) > 2)
      w.push(`Box 4 SS tax (${money(sst)}) ≠ 6.2% of Box 3 SS wages (expected ~${money(exp)})`);
  }
  if (mw != null && mt != null) {
    const exp = Math.round(mw * MEDICARE_RATE);
    if (Math.abs(mt - exp) > 2)
      w.push(`Box 6 Medicare tax (${money(mt)}) ≠ 1.45% of Box 5 (expected ~${money(exp)})`);
  }
  const base = SS_WAGE_BASE[year];
  if (ssw != null && base && ssw > base + 1)
    w.push(`Box 3 SS wages (${money(ssw)}) exceed the ${year} SS wage base (${money(base)})`);
  if (sw != null && swh != null && swh > sw + 1)
    w.push(`State W/H (${money(swh)}) is more than state wages (${money(sw)})`);
  [["Box 1 wages", wages], ["Box 3 SS wages", ssw], ["Box 5 Medicare wages", mw]]
    .forEach(([lbl, v]) => { if (v != null && v < 0) w.push(`${lbl} is negative`); });
  return w;
}

function validate1099(f) {
  const w = [];
  const ft = (f.form_type || "").toUpperCase();
  if (ft.includes("DIV")) {
    const ord = vnum(f.div_ordinary), qual = vnum(f.div_qualified);
    if (ord != null && qual != null && qual > ord + 1)
      w.push(`Qualified dividends (${money(qual)}) exceed ordinary dividends (${money(ord)})`);
  }
  const fields = ft.includes("INT")
    ? ["int_interest", "int_us_gov", "int_tax_exempt", "int_fed_wh"]
    : ["div_ordinary", "div_qualified", "div_cap_gain", "div_nondiv", "div_199a", "div_foreign_tax", "div_fed_wh"];
  fields.forEach((k) => { const v = vnum(f[k]); if (v != null && v < 0) w.push(`${k} is negative`); });
  return w;
}

// Start/stop work timer with a live-ticking display.
function Timer({ seconds, startedAt, onStart, onStop }) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!startedAt) return;
    const t = setInterval(() => force((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [startedAt]);
  const running = !!startedAt;
  const extra = running
    ? Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000))
    : 0;
  const total = (seconds || 0) + extra;
  return (
    <span className="timer">
      <button className={`btn small ${running ? "danger" : "ghost"}`}
        onClick={running ? onStop : onStart}>{running ? "Stop" : "Start"}</button>
      <span className={`timer-val${running ? " running" : ""}`}>{fmtDuration(total)}</span>
    </span>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`badge status-${status}`}>
      {STATUS_LABELS[status] || status || "—"}
    </span>
  );
}

function DueList({ scope, onOpenClient, refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API}/due?scope=${scope}`)
      .then((r) => r.json()).then(setItems).catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [scope]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (refreshKey) load(); }, [refreshKey]); // eslint-disable-line

  return (
    <section className="table-wrap">
      {loading ? <div className="empty">Loading…</div>
        : items.length === 0 ? <div className="empty">Nothing due here. 🎉</div>
          : (
            <table className="data-table">
              <thead><tr><th>Due</th><th>Client</th><th>Type</th><th>Item</th></tr></thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i}>
                    <td className={scope === "overdue" ? "overdue" : ""}>{fmtDay(it.due_date)}</td>
                    <td className="name-cell">
                      <button className="link-btn" onClick={() => it.client_id && onOpenClient(it.client_id)}>
                        {it.client_name}
                      </button>
                    </td>
                    <td><span className="badge entity">{it.kind}</span></td>
                    <td>{it.label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
    </section>
  );
}

function ArcGauge({ pct }) {
  const r = 46, cx = 60, cy = 56;
  const a = Math.PI * (1 - (pct || 0) / 100);
  const x = cx + r * Math.cos(a);
  const y = cy - r * Math.sin(a);
  const bg = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
  const fg = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${x} ${y}`;
  return (
    <svg className="arc-gauge" viewBox="0 0 120 64" width="120" height="64">
      <path d={bg} fill="none" stroke="#e4e8ee" strokeWidth="11" strokeLinecap="round" />
      <path d={fg} fill="none" stroke="#159aab" strokeWidth="11" strokeLinecap="round" />
      <text x={cx} y="50" textAnchor="middle" fontSize="18" fontWeight="700" fill="#16a34a">{pct || 0}%</text>
    </svg>
  );
}

function CompletedCard({ done, total, pct, active, onClick, label }) {
  return (
    <button type="button" className={`metric-card completed-card${active ? " active" : ""}`} onClick={onClick}>
      <ArcGauge pct={pct} />
      <div className="completed-info">
        <div className="completed-form">{label || "Completed"}</div>
        <div className="metric-value green completed-count">{done} / {total}</div>
      </div>
    </button>
  );
}

function MetricCard({ label, value, accent, onClick, active }) {
  return (
    <button
      type="button"
      className={`metric-card${active ? " active" : ""}`}
      onClick={onClick}
    >
      <div className={`metric-value ${accent}`}>{value}</div>
      <div className="metric-label">{label}</div>
    </button>
  );
}

function AddClientModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", entity_type: "1040", email: "", phone: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Name is required."); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/clients`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Failed");
      onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Add client</h2>
        <form onSubmit={submit}>
          <label>Name
            <input autoFocus value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <label>Entity type
            <select value={form.entity_type}
              onChange={(e) => setForm({ ...form, entity_type: e.target.value })}>
              {ENTITY_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </label>
          <label>Email
            <input value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </label>
          <label>Phone
            <input value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </label>
          {error && <div className="form-error">{error}</div>}
          <div className="modal-actions">
            <button type="button" className="btn ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={saving}>
              {saving ? "Saving…" : "Add client"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------------- Dashboard (client list) ---------------- */

function Dashboard({ onOpenClient, refreshKey }) {
  const [clients, setClients] = useState(() => cacheGet("dash_clients", []));
  const [metrics, setMetrics] = useState(() => cacheGet("dash_metrics", null));
  const [loading, setLoading] = useState(() => cacheGet("dash_clients", null) === null);
  const [error, setError] = useState("");

  const saved = readSavedFilters();
  const [groups, setGroups] = useState([]);
  const [activeFilter, setActiveFilter] = useState(saved.activeFilter || "active");
  const VALID_BOXES = ["all", "overdue", "this_month", "next_month",
    "completed_1040", "completed_1065", "completed_1120S"];
  const [box, setBox] = useState(
    VALID_BOXES.includes(saved.box) ? saved.box : "all");
  // When a "Form 1040/1065/1120S" gauge is selected, toggle between the filed
  // (completed) returns and the ones still in progress for that entity.
  const [completedView, setCompletedView] = useState(saved.completedView === "unfiled" ? "unfiled" : "filed");
  const [colFilters, setColFilters] = useState(() => {
    const base = { name: "", entity: [], group: [], year: [], status: [], due: [] };
    const c = saved.colFilters || {};
    const arr = (v) => (Array.isArray(v) ? v : (v ? [String(v)] : []));
    return { ...base, ...c, entity: arr(c.entity), group: arr(c.group),
      year: arr(c.year), status: arr(c.status), due: arr(c.due) };
  });
  const [sortKey, setSortKey] = useState(saved.sortKey || "client_name");
  const [sortDir, setSortDir] = useState(saved.sortDir || "asc");

  const setCol = (key, val) => setColFilters((f) => ({ ...f, [key]: val }));
  const toggleCol = (key, val) => setColFilters((f) => {
    const cur = Array.isArray(f[key]) ? f[key] : [];
    return { ...f, [key]: cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val] };
  });

  // Remember the filters/sort between sessions.
  useEffect(() => {
    try {
      localStorage.setItem("spoom_filters", JSON.stringify(
        { activeFilter, box, colFilters, sortKey, sortDir, completedView }));
    } catch (e) { /* ignore */ }
  }, [activeFilter, box, colFilters, sortKey, sortDir, completedView]);

  const [showAdd, setShowAdd] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const fileInput = useRef(null);

  const loadMetrics = useCallback(() => {
    fetch(`${API}/metrics`).then((r) => r.json())
      .then((d) => setMetrics(cacheSet("dash_metrics", d))).catch(() => {});
  }, []);

  const loadGroups = useCallback(() => {
    fetch(`${API}/groups`).then((r) => r.json()).then(setGroups).catch(() => {});
  }, []);

  const loadClients = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    const p = new URLSearchParams();
    if (activeFilter) p.set("active", activeFilter);
    if (box.startsWith("completed") && completedView === "filed") p.set("completed", "only");
    // All column filters + sorting are done server-side (authoritative) so the
    // table always refetches and re-renders on any change.
    if (colFilters.name) p.set("search", colFilters.name);
    // Entity/group/year/status/due are all multi-select, filtered client-side.
    p.set("sort", sortKey);
    p.set("dir", sortDir);
    fetch(`${API}/clients?${p.toString()}`)
      .then((r) => { if (!r.ok) throw new Error("Could not reach the server"); return r.json(); })
      .then((data) => { setClients(cacheSet("dash_clients", data)); setError(""); })
      .catch((err) => setError(err.message))
      .finally(() => { if (!silent) setLoading(false); });
  }, [activeFilter, box, completedView, colFilters.name, sortKey, sortDir]);

  useEffect(() => { loadMetrics(); loadGroups(); }, [loadMetrics, loadGroups]);
  // Refresh when a client popup closes (so edits are reflected).
  useEffect(() => {
    if (refreshKey) { loadClients(true); loadMetrics(); loadGroups(); }
  }, [refreshKey]); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(loadClients, 200);
    return () => clearTimeout(t);
  }, [loadClients]);

  const changeStatus = async (taxYearId, status) => {
    if (!taxYearId) return;
    await fetch(`${API}/tax_years/${taxYearId}/status`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    loadClients(true); // silent: keep scroll position
    loadMetrics();
  };

  const changeEntity = async (clientId, entity_type) => {
    await fetch(`${API}/clients/${clientId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entity_type }),
    });
    loadClients(true); // silent: keep scroll position
  };

  const completeReturn = async (taxYearId) => {
    await fetch(`${API}/tax_years/${taxYearId}/complete`, { method: "POST" });
    loadClients(true);
    loadMetrics();
  };

  const reopenReturn = async (taxYearId) => {
    await fetch(`${API}/tax_years/${taxYearId}/reopen`, { method: "POST" });
    loadClients(true);
    loadMetrics();
  };

  const onImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${API}/import`, { method: "POST", body: fd });
    const result = await res.json();
    alert(`Imported ${result.added} client(s), skipped ${result.skipped}.`);
    e.target.value = "";
    loadClients();
    loadMetrics();
  };

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  // Filtering and sorting are done server-side now (loadClients sends the
  // params and refetches on change). The only client-side step is the completed
  // gauges, which narrow the already-loaded list to one entity type.
  const cf = colFilters;
  const boxEntity = box.startsWith("completed_") ? box.split("_")[1] : "";
  // The "Filed" gauge counts the current tax season (e.g. 2025 returns worked
  // during 2026). Scope "In progress" to that same season so next year's
  // (2026) returns don't leak in.
  const seasonYear = new Date().getFullYear() - 1;
  const baseList = boxEntity
    ? clients.filter((c) => c.entity_type === boxEntity
        && (completedView === "filed"
            ? c.completed
            : !c.completed && Number(c.latest_year) <= seasonYear))
    : clients;
  const dueMatch = (cond, c) => {
    if (cond === "overdue") return isOverdue(c.due_date, c.status);
    if (cond === "has") return !!c.due_date;
    if (cond === "none") return !c.due_date;
    if (cond === "due_month") {
      if (!c.due_date) return false;
      const d = new Date(c.due_date), n = new Date();
      return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth();
    }
    return true;
  };
  const sorted = baseList.filter((c) =>
    (cf.entity.length === 0 || cf.entity.includes(c.entity_type)) &&
    (cf.group.length === 0 || cf.group.includes(c.group_name)
      || (cf.group.includes("__none__") && !c.group_name)) &&
    (cf.year.length === 0 || cf.year.includes(String(c.latest_year))) &&
    (cf.status.length === 0 || cf.status.includes(c.status)
      || (cf.status.includes("__amend__") && c.amendment)) &&
    (cf.due.length === 0 || cf.due.some((cond) => dueMatch(cond, c))));

  const yearOptions = [...new Set(clients.map((c) => c.latest_year).filter(Boolean))]
    .sort((a, b) => b - a);

  const arrow = (key) => (sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : "");
  const isMobile = useIsMobile();

  return (
    <div className="returns-view">
      <input type="file" accept=".csv" ref={fileInput}
        style={{ display: "none" }} onChange={onImport} />
      {isMobile ? (
        <>
          <header className="m-home-head">
            <div>
              <div className="m-home-hi">Tax Returns</div>
              <div className="m-home-sub">
                {metrics ? `${metrics.active_clients} active · ${metrics.overdue} overdue` : "—"}
              </div>
            </div>
          </header>
          <div className="m-quick">
            <button className="m-quick-tile primary" onClick={() => setShowAdd(true)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6M22 11h-6" /></svg>
              Add client
            </button>
            <button className="m-quick-tile" onClick={() => fileInput.current.click()}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
              Import
            </button>
            <a className="m-quick-tile" href={`${API}/export.csv`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 9l5-5 5 5M12 4v12" /></svg>
              Export
            </a>
          </div>
        </>
      ) : (
        <header className="topbar">
          <div>
            <h1>Tax Returns</h1>
          </div>
          <div className="topbar-actions">
            <button className="btn primary" onClick={() => setShowAdd(true)}>
              + Add client
            </button>
          </div>
        </header>
      )}

      <section className="metrics">
        <MetricCard label="Active clients" accent="blue"
          value={metrics ? metrics.active_clients : "—"}
          active={box === "all"}
          onClick={() => setBox("all")} />
        <MetricCard label="Overdue" accent="red"
          value={metrics ? metrics.overdue : "—"}
          active={box === "overdue"}
          onClick={() => setBox("overdue")} />
        <MetricCard label="Due this month" accent="amber"
          value={metrics ? metrics.due_this_month : "—"}
          active={box === "this_month"}
          onClick={() => setBox("this_month")} />
        <MetricCard label="Due next month" accent="amber"
          value={metrics ? metrics.due_next_month : "—"}
          active={box === "next_month"}
          onClick={() => setBox("next_month")} />
      </section>

      <section className="gauges-frame">
        <h3 className="gauges-frame-title">Return Progress</h3>
        <div className="metrics gauges">
          {["1040", "1065", "1120S"].map((et) => {
            const d = (metrics && metrics.by_entity && metrics.by_entity[et]) || { done: 0, total: 0, pct: 0 };
            return (
              <CompletedCard key={et} label={`Form ${et}`}
                done={d.done} total={d.total} pct={d.pct}
                active={box === `completed_${et}`}
                onClick={() => setBox(`completed_${et}`)} />
            );
          })}
        </div>
      </section>

      {["overdue", "this_month", "next_month"].includes(box) ? (
        <DueList scope={box} onOpenClient={onOpenClient} refreshKey={refreshKey} />
      ) : (
      <div className="returns-main">
      <aside className="fixed-right-filter-sidebar no-print">
        <div className="frf-title">Filters</div>
        {box.startsWith("completed_") && (
          <div className="frf-field">
            <span className="frf-label">Form {box.split("_")[1]} view</span>
            <div className="fin-toggle frf-toggle">
              <button className={completedView === "filed" ? "active" : ""}
                onClick={() => setCompletedView("filed")}>Filed</button>
              <button className={completedView === "unfiled" ? "active" : ""}
                onClick={() => setCompletedView("unfiled")}>In progress</button>
            </div>
          </div>
        )}
        <label className="frf-field">
          <span className="frf-label">Search</span>
          <input className="frf-search" placeholder="Client name…" value={cf.name}
            onChange={(e) => setCol("name", e.target.value)} />
        </label>
        <div className="frf-field">
          <span className="frf-label">Entity</span>
          <FilterDropdown allLabel="All entities" selected={cf.entity}
            onToggle={(v) => toggleCol("entity", v)}
            options={ENTITY_TYPES.map((t) => ({ value: t, label: t }))} />
        </div>
        <div className="frf-field">
          <span className="frf-label">Group</span>
          <FilterDropdown allLabel="All groups" selected={cf.group}
            onToggle={(v) => toggleCol("group", v)}
            options={[{ value: "__none__", label: "No group" },
              ...groups.map((g) => ({ value: g.name, label: g.name }))]} />
        </div>
        <div className="frf-field">
          <span className="frf-label">Year</span>
          <FilterDropdown allLabel="All years" selected={cf.year}
            onToggle={(v) => toggleCol("year", v)}
            options={yearOptions.map((y) => ({ value: String(y), label: String(y) }))} />
        </div>
        <div className="frf-field">
          <span className="frf-label">Status</span>
          <FilterDropdown allLabel="All statuses" selected={cf.status}
            onToggle={(v) => toggleCol("status", v)}
            options={[...STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABELS[s] })),
              { value: "__amend__", label: "⚠ Needs amendment" }]} />
        </div>
        <div className="frf-field">
          <span className="frf-label">Due</span>
          <FilterDropdown allLabel="Any due date" selected={cf.due}
            onToggle={(v) => toggleCol("due", v)}
            options={[{ value: "overdue", label: "Overdue" },
              { value: "due_month", label: "Due this month" },
              { value: "has", label: "Has date" },
              { value: "none", label: "No date" }]} />
        </div>
        <label className="frf-field">
          <span className="frf-label">Active only</span>
          <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
            <option value="all">Active + inactive</option>
          </select>
        </label>
        <button className="btn ghost frf-clear" onClick={() => { setColFilters({
          name: "", entity: [], group: [], year: [], status: [], due: [] }); setBox("all"); }}>
          Clear filters
        </button>
      </aside>

      <section className="table-wrap">
        {error && <div className="banner error">{error}</div>}
        {loading ? (
          <div className="empty">Loading…</div>
        ) : sorted.length === 0 ? (
          <div className="empty">No clients match your filters.</div>
        ) : isMobile ? (
          <>
            <div className="m-filter-bar no-print">
              <input className="m-search" placeholder="Search name…" value={cf.name}
                onChange={(e) => setCol("name", e.target.value)} />
              <select value={cf.status[0] || ""}
                onChange={(e) => setCol("status", e.target.value ? [e.target.value] : [])}>
                <option value="">All statuses</option>
                {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                <option value="__amend__">⚠ Needs amendment</option>
              </select>
              <select value={cf.due[0] || ""} onChange={(e) => setCol("due", e.target.value ? [e.target.value] : [])}>
                <option value="">Any due</option>
                <option value="overdue">Overdue</option>
                <option value="due_month">Due this month</option>
              </select>
              {box.startsWith("completed_") && (
                <div className="fin-toggle frf-toggle m-view-toggle">
                  <button className={completedView === "filed" ? "active" : ""}
                    onClick={() => setCompletedView("filed")}>Filed</button>
                  <button className={completedView === "unfiled" ? "active" : ""}
                    onClick={() => setCompletedView("unfiled")}>In progress</button>
                </div>
              )}
            </div>
            <div className="client-cards">
              {sorted.map((c) => (
                <div key={c.tax_year_id || c.client_id}
                  className={`client-card${c.active ? "" : " row-inactive"}`}>
                  <button className="cc-main" onClick={() => onOpenClient(c.client_id)}>
                    <span className="cc-name">{c.client_name}
                      {!c.active && <span className="badge inactive">Inactive</span>}</span>
                    <span className="cc-meta">
                      <span className="badge entity">{c.entity_type}</span>
                      {c.group_name && <span className="badge group">{c.group_name}</span>}
                      {c.latest_year && <span className="cc-year">{c.latest_year}</span>}
                    </span>
                  </button>
                  <div className="cc-row">
                    {c.tax_year_id ? (
                      <select className="status-select" value={c.status}
                        onChange={(e) => changeStatus(c.tax_year_id, e.target.value)}>
                        {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                      </select>
                    ) : <StatusBadge status={null} />}
                    <span className={`cc-due${isOverdue(c.due_date, c.status) ? " overdue" : ""}`}>
                      {c.due_date ? fmtDay(c.due_date) : "—"}
                      {c.extended && <span className="badge ext">Ext</span>}
                    </span>
                  </div>
                  {(c.phone || c.email) && (
                    <div className="cc-actions">
                      {c.phone && <a className="cc-act" href={`tel:${c.phone}`}>📞 Call</a>}
                      {c.phone && <a className="cc-act" href={`sms:${c.phone}`}>💬 Text</a>}
                      {c.phone && <a className="cc-act wa" target="_blank" rel="noreferrer"
                        href={`https://wa.me/${waDigits(c.phone)}`}>WhatsApp</a>}
                      {c.email && <a className="cc-act" href={mailtoLink(c.email)}>✉ Email</a>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => toggleSort("client_name")}>
                  Client <span className="count-badge">{sorted.length}</span>{arrow("client_name")}</th>
                <th className="sortable" onClick={() => toggleSort("entity_type")}>Entity{arrow("entity_type")}</th>
                <th className="sortable" onClick={() => toggleSort("group_name")}>Group{arrow("group_name")}</th>
                <th className="sortable" onClick={() => toggleSort("latest_year")}>Year{arrow("latest_year")}</th>
                <th className="sortable" onClick={() => toggleSort("status")}>Status{arrow("status")}</th>
                <th className="sortable" onClick={() => toggleSort("due_date")}>Due date{arrow("due_date")}</th>
                <th>Contact</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => (
                <tr key={c.tax_year_id || c.client_id} className={c.active ? "" : "row-inactive"}>
                  <td className="name-cell">
                    <button className="link-btn" onClick={() => onOpenClient(c.client_id)}>
                      {c.client_name}
                    </button>
                    {!c.active && <span className="badge inactive">Inactive</span>}
                  </td>
                  <td>
                    <select className="status-select" value={c.entity_type}
                      onChange={(e) => changeEntity(c.client_id, e.target.value)}>
                      {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </td>
                  <td>{c.group_name
                    ? <span className="badge group">{c.group_name}</span>
                    : <span className="muted">—</span>}</td>
                  <td>{c.latest_year || "—"}</td>
                  <td>
                    {c.tax_year_id ? (
                      <>
                        <select className="status-select" value={c.status}
                          onChange={(e) => changeStatus(c.tax_year_id, e.target.value)}>
                          {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                        </select>
                        {!c.completed && (c.status === "filed" || c.status === "not_filing") &&
                          <button className="btn primary small"
                            onClick={() => completeReturn(c.tax_year_id)}>Complete</button>}
                        {c.completed &&
                          <button className="btn ghost small"
                            onClick={() => reopenReturn(c.tax_year_id)}>Reopen</button>}
                        {c.amendment === true &&
                          <span className="badge amend">Amended</span>}
                        {c.completed && c.completed_at &&
                          <div className="muted tiny">Completed {fmtDay(c.completed_at)}</div>}
                        {!c.completed && c.status_changed_at &&
                          <div className="muted tiny">{fmtDate(c.status_changed_at)}</div>}
                      </>
                    ) : <StatusBadge status={null} />}
                  </td>
                  <td className={isOverdue(c.due_date, c.status) ? "overdue" : ""}>
                    {c.due_date ? fmtDay(c.due_date) : <span className="muted">—</span>}
                    {c.extended && <span className="badge ext">Extended</span>}
                    {isOverdue(c.due_date, c.status) && <div className="tiny">overdue</div>}
                  </td>
                  <td className="contact-cell">
                    <div className="contact-item">
                      <span className="contact-val">{c.email || <span className="muted">no email</span>}</span>
                      {c.email && (
                        <div className="contact-btns">
                          <button className="copy-btn" title="Copy email" onClick={() => copyText(c.email)}>⧉</button>
                          <a className="copy-btn" title="New email in Outlook" href={mailtoLink(c.email)}>✉</a>
                        </div>
                      )}
                    </div>
                    <div className="contact-item">
                      <span className="contact-val">{c.phone || <span className="muted">no phone</span>}</span>
                      {c.phone && (
                        <div className="contact-btns">
                          <button className="copy-btn" title="Copy phone" onClick={() => copyText(c.phone)}>⧉</button>
                          <a className="copy-btn" title="Call" href={`tel:${c.phone}`}>📞</a>
                          <a className="copy-btn" title="Text" href={`sms:${c.phone}`}>💬</a>
                          <a className="copy-btn wa" title="WhatsApp" target="_blank" rel="noreferrer"
                            href={`https://wa.me/${waDigits(c.phone)}`}>WA</a>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      </div>
      )}

      {showAdd && (
        <AddClientModal onClose={() => setShowAdd(false)}
          onSaved={() => { loadClients(); loadMetrics(); }} />
      )}
    </div>
  );
}

/* ---------------- Documents panel ---------------- */

function DocumentsPanel({ taxYearId, year, onClientUpdated, onChecklistChanged }) {
  const [docs, setDocs] = useState([]);
  // New uploads default to "other"; the type is then set by auto-detect
  // ("Identify") or the per-row dropdown — no separate upload-type selector.
  const docType = "other";
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    fetch(`${API}/tax_years/${taxYearId}/documents`)
      .then((r) => r.json()).then(setDocs).catch(() => {});
  }, [taxYearId]);
  useEffect(() => { load(); }, [load]);

  const [drag, setDrag] = useState(false);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      await fetch(`${API}/tax_years/${taxYearId}/documents`, { method: "POST", body: fd });
    }
    setBusy(false);
    load();
  };
  const onPick = (e) => { uploadFiles(e.target.files); e.target.value = ""; };
  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    uploadFiles(e.dataTransfer.files);
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this document?")) return;
    await fetch(`${API}/documents/${id}`, { method: "DELETE" });
    load();
  };

  const changeDocType = async (id, doc_type) => {
    await fetch(`${API}/documents/${id}/type`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ doc_type }),
    });
    load();
  };

  // --- W-2 field verification (Phase 3.3) ---
  const [w2Doc, setW2Doc] = useState(null);
  const [w2Fields, setW2Fields] = useState(null);
  const [w2Loading, setW2Loading] = useState(false);
  const [w2Err, setW2Err] = useState("");
  const openW2 = async (d) => {
    setW2Doc(d); setW2Fields(null); setW2Err(""); setW2Loading(true);
    try {
      const res = await fetch(`${API}/ai/extract_w2`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ document_id: d.id }),
      });
      const r = await res.json();
      if (r.ok) setW2Fields({ ...r.fields, person: r.fields.person || "", box14: r.fields.box14 || [], box12: r.fields.box12 || [] });
      else setW2Err(r.error);
    } catch (e) { setW2Err("Could not reach the server."); }
    finally { setW2Loading(false); }
  };
  const setW2 = (k, v) => setW2Fields((f) => ({ ...f, [k]: v }));
  const setBox14 = (i, key, v) => setW2Fields((f) => {
    const list = [...(f.box14 || [])];
    list[i] = { ...list[i], [key]: v };
    return { ...f, box14: list };
  });
  const addBox14 = () => setW2Fields((f) => ({ ...f, box14: [...(f.box14 || []), { label: "", amount: "" }] }));
  const removeBox14 = (i) => setW2Fields((f) => ({ ...f, box14: (f.box14 || []).filter((_, j) => j !== i) }));
  const setBox12 = (i, key, v) => setW2Fields((f) => {
    const list = [...(f.box12 || [])]; list[i] = { ...list[i], [key]: v }; return { ...f, box12: list };
  });
  const addBox12 = () => setW2Fields((f) => ({ ...f, box12: [...(f.box12 || []), { code: "", amount: "" }] }));
  const removeBox12 = (i) => setW2Fields((f) => ({ ...f, box12: (f.box12 || []).filter((_, j) => j !== i) }));
  const reExtractW2 = async () => {
    setW2Loading(true); setW2Err("");
    try {
      const res = await fetch(`${API}/ai/extract_w2`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ document_id: w2Doc.id, force: true }),
      });
      const r = await res.json();
      if (r.ok) setW2Fields({ ...r.fields, person: r.fields.person || "", box14: r.fields.box14 || [], box12: r.fields.box12 || [] });
      else setW2Err(r.error);
    } finally { setW2Loading(false); }
  };
  // After confirming a doc: tick its checklist item, or ask to link a near-match,
  // or add it as a new received item.
  const reconcileAfterConfirm = async (docId) => {
    try {
      const res = await fetch(`${API}/documents/${docId}/checklist_reconcile`,
        { method: "POST" });
      const r = await res.json();
      if (r.status === "suggest") {
        const link = window.confirm(
          `This document didn't exactly match a checklist item.\n\n` +
          `Is it the same as your existing item:\n"${r.item}"?\n\n` +
          `OK = link it (check that item off).\n` +
          `Cancel = add "${r.label}" as a new item.`);
        if (link) {
          await fetch(`${API}/checklist/${r.item_id}`, {
            method: "PUT", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ completed: true }),
          });
        } else if (r.label) {
          const cr = await fetch(`${API}/tax_years/${taxYearId}/checklist`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ description: r.label }),
          });
          const ni = await cr.json();
          if (ni && ni.id) {
            await fetch(`${API}/checklist/${ni.id}`, {
              method: "PUT", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ completed: true }),
            });
          }
        }
      }
    } catch (e) { /* ignore */ }
    onChecklistChanged && onChecklistChanged();
  };

  const confirmW2 = async () => {
    const docId = w2Doc.id;
    await fetch(`${API}/documents/${docId}/fields`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fields: w2Fields, confirmed: true }),
    });
    // Auto-name the document and tick the checklist (best effort, stay quiet).
    try {
      await fetch(`${API}/documents/${docId}/auto_name`, { method: "POST" });
    } catch (e) { /* ignore */ }
    setW2Doc(null); setW2Fields(null);
    load();
    reconcileAfterConfirm(docId);
  };

  // --- 1099 verification (1099-INT / 1099-DIV) ---
  const [f99Doc, setF99Doc] = useState(null);
  const [f99Fields, setF99Fields] = useState(null);
  const [f99Loading, setF99Loading] = useState(false);
  const [f99Err, setF99Err] = useState("");
  const openF99 = async (d, force) => {
    setF99Doc(d); if (!force) setF99Fields(null); setF99Err(""); setF99Loading(true);
    try {
      const res = await fetch(`${API}/ai/extract_1099`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ document_id: d.id, force: !!force }),
      });
      const r = await res.json();
      if (r.ok) setF99Fields({ ...r.fields, person: r.fields.person || "", form_type: r.fields.form_type || "" });
      else setF99Err(r.error);
    } catch (e) { setF99Err("Could not reach the server."); }
    finally { setF99Loading(false); }
  };
  const setF99 = (k, v) => setF99Fields((f) => ({ ...f, [k]: v }));
  const confirmF99 = async () => {
    await fetch(`${API}/documents/${f99Doc.id}/fields`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fields: f99Fields, confirmed: true }),
    });
    const docId = f99Doc.id;
    setF99Doc(null); setF99Fields(null);
    load();
    reconcileAfterConfirm(docId);
  };

  // --- 1098 verification (mortgage interest) ---
  const [f98Doc, setF98Doc] = useState(null);
  const [f98Fields, setF98Fields] = useState(null);
  const [f98Loading, setF98Loading] = useState(false);
  const [f98Err, setF98Err] = useState("");
  const openF98 = async (d, force) => {
    setF98Doc(d); if (!force) setF98Fields(null); setF98Err(""); setF98Loading(true);
    try {
      const res = await fetch(`${API}/ai/extract_1098`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ document_id: d.id, force: !!force }),
      });
      const r = await res.json();
      if (r.ok) setF98Fields({ ...r.fields, person: r.fields.person || "" });
      else setF98Err(r.error);
    } catch (e) { setF98Err("Could not reach the server."); }
    finally { setF98Loading(false); }
  };
  const setF98 = (k, v) => setF98Fields((f) => ({ ...f, [k]: v }));
  const confirmF98 = async () => {
    await fetch(`${API}/documents/${f98Doc.id}/fields`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fields: f98Fields, confirmed: true }),
    });
    const docId = f98Doc.id;
    setF98Doc(null); setF98Fields(null);
    load();
    reconcileAfterConfirm(docId);
  };

  // --- 1098-E verification (student loan interest) ---
  const [e98Doc, setE98Doc] = useState(null);
  const [e98Fields, setE98Fields] = useState(null);
  const [e98Loading, setE98Loading] = useState(false);
  const [e98Err, setE98Err] = useState("");
  const openE98 = async (d, force) => {
    setE98Doc(d); if (!force) setE98Fields(null); setE98Err(""); setE98Loading(true);
    try {
      const res = await fetch(`${API}/ai/extract_1098e`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ document_id: d.id, force: !!force }),
      });
      const r = await res.json();
      if (r.ok) setE98Fields({ ...r.fields, person: r.fields.person || "" });
      else setE98Err(r.error);
    } catch (e) { setE98Err("Could not reach the server."); }
    finally { setE98Loading(false); }
  };
  const setE98 = (k, v) => setE98Fields((f) => ({ ...f, [k]: v }));
  const confirmE98 = async () => {
    await fetch(`${API}/documents/${e98Doc.id}/fields`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fields: e98Fields, confirmed: true }),
    });
    const docId = e98Doc.id;
    setE98Doc(null); setE98Fields(null);
    load();
    reconcileAfterConfirm(docId);
  };

  // --- Closing / HUD -> journal entry (extract -> review -> build Excel) ---
  const [cje, setCje] = useState(null);           // review modal state
  const [cjePage, setCjePage] = useState(1);      // HUD page shown in the viewer
  const [cjeBusy, setCjeBusy] = useState(null);   // doc id being read
  const [cjeBuilding, setCjeBuilding] = useState(false);
  const [cjeFocus, setCjeFocus] = useState(null); // review line index focused from the PDF
  const cjeRowRefs = useRef({});                   // line index -> <tr> element
  useEffect(() => {                                // scroll a clicked reference into view
    if (cjeFocus == null) return;
    const el = cjeRowRefs.current[cjeFocus];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "center", behavior: "smooth" });
    const t = setTimeout(() => setCjeFocus(null), 2000);  // clear the highlight after a moment
    return () => clearTimeout(t);
  }, [cjeFocus]);
  const openClosingJE = async (d, force) => {
    setCjeBusy(d.id);
    try {
      const res = await fetch(`${API}/documents/${d.id}/closing_je/extract`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ force: !!force }),
      });
      const r = await res.json();
      if (!r.ok) { window.alert(r.error || "Couldn’t read the closing statement."); return; }
      const s = r.spec || {};
      const lines = [];
      (s.sections || []).forEach((sec) => (sec.lines || []).forEach((ln) =>
        lines.push({ num: lines.length + 1, section: sec.header || "", desc: ln.desc || "",
          amount: ln.amount == null ? "" : ln.amount, ref: ln.ref || "",
          conf: ln.conf, note: ln.note, page: ln.page })));
      setCje({ docId: d.id, name: d.file_name, annoT: Date.now(), type: s.type || "purchase",
        property: s.property || "", date: toMDY(s.date || ""),
        purchase_price: s.purchase_price == null ? "" : s.purchase_price,
        cash: s.cash_to_from_borrower, landPct: "15", lines });
    } catch (e) { window.alert("Could not reach the server."); }
    finally { setCjeBusy(null); }
  };
  const setCjeLine = (i, key, val) => setCje((c) => {
    const lines = c.lines.slice(); lines[i] = { ...lines[i], [key]: val };
    // Changing the code resets to that code's natural side (drop any manual flip).
    if (key === "ref") lines[i].side = null;
    const touched = key === "ref" ? { ...(c.touched || {}), [i]: true } : c.touched;
    return { ...c, lines, touched };
  });
  const cjeConfirm = (i) => setCje((c) => {
    const lines = c.lines.slice(); lines[i] = { ...lines[i], conf: 1 };
    return { ...c, lines, touched: { ...(c.touched || {}), [i]: true } };
  });
  const cjeRenumber = (lines) => lines.map((l, i) => ({ ...l, num: i + 1 }));
  const cjeAddLine = () => setCje((c) => ({
    ...c, lines: cjeRenumber([...c.lines, { section: "", desc: "", amount: "", ref: "F" }]),
  }));
  const cjeDelLine = (i) => setCje((c) => ({
    ...c, lines: cjeRenumber(c.lines.filter((_, j) => j !== i)),
  }));
  // Effective debit/credit side of a line: explicit override wins, else the code's natural side.
  const cjeSideOf = (l) => l.side || CJE_SIDE[l.ref] || "D";
  const cjeFlipSide = (i) => setCje((c) => {
    const lines = c.lines.slice();
    const cur = lines[i].side || CJE_SIDE[lines[i].ref] || "D";
    lines[i] = { ...lines[i], side: cur === "D" ? "C" : "D" };
    return { ...c, lines, touched: { ...(c.touched || {}), [i]: true } };
  });
  // Place (or, with page=null, clear) a manual reference marker on a line by
  // dragging it onto the page image. mx,my are fractions (0..1) from top-left.
  const cjePlace = (i, page, fx, fy) => setCje((c) => {
    if (i == null || i < 0 || !c.lines[i]) return c;
    const lines = c.lines.slice();
    // Placing also un-hides the line (mhide cleared); page=null clears a manual drop.
    if (page == null) lines[i] = { ...lines[i], mx: null, my: null, mpage: null };
    else lines[i] = { ...lines[i], mx: fx, my: fy, mpage: page, mhide: false };
    return { ...c, lines };
  });
  // Remove a reference from the page entirely (won't be stamped on the saved PDF).
  const cjeHide = (i) => setCje((c) => {
    if (i == null || i < 0 || !c.lines[i]) return c;
    const lines = c.lines.slice();
    lines[i] = { ...lines[i], mx: null, my: null, mpage: null, mhide: true };
    return { ...c, lines };
  });
  const buildClosingJE = async () => {
    const isRefi = (cje.type || "").startsWith("refi");
    const totDr = cje.lines.reduce((s, l) => cjeSideOf(l) === "D" ? s + cjeNum(l.amount) : s, 0);
    const totCr = cje.lines.reduce((s, l) => cjeSideOf(l) === "C" ? s + cjeNum(l.amount) : s, 0);
    const plug = Math.abs(Math.round((totDr - totCr) * 100) / 100);
    const hudCash = cje.cash != null && cje.cash !== "" ? cjeNum(cje.cash) : null;
    if (hudCash != null && Math.abs(plug - hudCash) > 1) {
      if (!window.confirm(
        `Heads up — the computed Cash (${plug.toLocaleString()}) doesn't match the `
        + `statement's cash to/from borrower (${hudCash.toLocaleString()}). A line may be `
        + `missing or miscoded.\n\nBuild anyway?`)) return;
    }
    const spec = {
      type: cje.type, property: cje.property, date: cje.date,
      purchase_price: isRefi ? null : (cje.purchase_price === "" ? null : cjeNum(cje.purchase_price)),
      lines: cje.lines.map((l) => ({ section: l.section, desc: l.desc,
        amount: l.amount === "" ? null : cjeNum(l.amount), ref: l.ref, side: cjeSideOf(l),
        page: l.page, mx: l.mx, my: l.my, mpage: l.mpage, mhide: l.mhide })),
    };
    setCjeBuilding(true);
    try {
      const res = await fetch(`${API}/documents/${cje.docId}/closing_je`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ spec, land_pct: cje.landPct.trim() === "" ? null : Number(cje.landPct) }),
      });
      const r = await res.json().catch(() => ({}));
      if (!res.ok || !r.ok) { window.alert(r.error || "Couldn’t build the entry."); return; }
      // It's saved into this client's Documents — refresh the list and open it.
      const a = document.createElement("a");
      a.href = `${API}/documents/${r.document_id}/download`;
      a.click();
      // Learn from the lines I confirmed or recoded, for next time.
      const learn = cje.lines.filter((_, i) => cje.touched && cje.touched[i]).map((l) => ({ desc: l.desc, ref: l.ref }));
      if (learn.length) {
        fetch(`${API}/documents/${cje.docId}/closing_je/learn`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ lines: learn }),
        }).catch(() => {});
      }
      if (typeof load === "function") load();   // refresh the documents list
      const pdfNote = r.annotated_file_name
        ? `\n\nThe numbered HUD “${r.annotated_file_name}” (with the red reference numbers) was also saved to Documents.`
        : "";
      window.alert(`Saved “${r.file_name}” to this client's Documents. To edit it, download it from Documents, change it in Excel, then use Replace to re-save it in Spoom.${pdfNote}`);
      setCje(null);
    } catch (e) { window.alert("Could not reach the server."); }
    finally { setCjeBuilding(false); }
  };

  // --- 1095-A verification (marketplace) ---
  const [a95Doc, setA95Doc] = useState(null);
  const [a95Fields, setA95Fields] = useState(null);
  const [a95Loading, setA95Loading] = useState(false);
  const [a95Err, setA95Err] = useState("");
  const openA95 = async (d, force) => {
    setA95Doc(d); if (!force) setA95Fields(null); setA95Err(""); setA95Loading(true);
    try {
      const res = await fetch(`${API}/ai/extract_1095a`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ document_id: d.id, force: !!force }),
      });
      const r = await res.json();
      if (r.ok) setA95Fields({ ...r.fields, person: r.fields.person || "" });
      else setA95Err(r.error);
    } catch (e) { setA95Err("Could not reach the server."); }
    finally { setA95Loading(false); }
  };
  const setA95 = (k, v) => setA95Fields((f) => ({ ...f, [k]: v }));
  const confirmA95 = async () => {
    await fetch(`${API}/documents/${a95Doc.id}/fields`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fields: a95Fields, confirmed: true }),
    });
    const docId = a95Doc.id;
    setA95Doc(null); setA95Fields(null);
    load();
    reconcileAfterConfirm(docId);
  };

  // --- Driver's license / ID verification ---
  const [idDoc, setIdDoc] = useState(null);
  const [idFields, setIdFields] = useState(null);
  const [idLoading, setIdLoading] = useState(false);
  const [idErr, setIdErr] = useState("");
  const openId = async (d, force) => {
    setIdDoc(d); if (!force) setIdFields(null); setIdErr(""); setIdLoading(true);
    try {
      const res = await fetch(`${API}/ai/extract_id`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ document_id: d.id, force: !!force }),
      });
      const r = await res.json();
      if (r.ok) setIdFields({ ...r.fields, person: r.fields.person || "" });
      else setIdErr(r.error);
    } catch (e) { setIdErr("Could not reach the server."); }
    finally { setIdLoading(false); }
  };
  const setIdF = (k, v) => setIdFields((f) => ({ ...f, [k]: v }));
  const confirmId = async () => {
    await fetch(`${API}/documents/${idDoc.id}/fields`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fields: idFields, confirmed: true }),
    });
    const docId = idDoc.id;
    setIdDoc(null); setIdFields(null);
    load();
    reconcileAfterConfirm(docId);
  };

  // --- K-1 verification ---
  const [k1Doc, setK1Doc] = useState(null);
  const [k1Fields, setK1Fields] = useState(null);
  const [k1Loading, setK1Loading] = useState(false);
  const [k1Err, setK1Err] = useState("");
  const [k1Draft, setK1Draft] = useState(false);
  const openK1 = async (d, force) => {
    setK1Doc(d); if (!force) { setK1Fields(null); setK1Draft(!!d.is_draft); }
    setK1Err(""); setK1Loading(true);
    try {
      const res = await fetch(`${API}/ai/extract_k1`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ document_id: d.id, force: !!force }),
      });
      const r = await res.json();
      if (r.ok) setK1Fields({ ...r.fields, person: r.fields.person || "", form: r.fields.form || "", box20: r.fields.box20 || [], states: r.fields.states || [] });
      else setK1Err(r.error);
    } catch (e) { setK1Err("Could not reach the server."); }
    finally { setK1Loading(false); }
  };
  const setK1 = (k, v) => setK1Fields((f) => ({ ...f, [k]: v }));
  const setK1List = (listKey, i, key, v) => setK1Fields((f) => {
    const list = [...(f[listKey] || [])]; list[i] = { ...list[i], [key]: v };
    return { ...f, [listKey]: list };
  });
  const addK1List = (listKey, blank) => setK1Fields((f) => ({ ...f, [listKey]: [...(f[listKey] || []), blank] }));
  const removeK1List = (listKey, i) => setK1Fields((f) => ({ ...f, [listKey]: (f[listKey] || []).filter((_, j) => j !== i) }));
  const confirmK1 = async () => {
    const docId = k1Doc.id;
    await fetch(`${API}/documents/${docId}/fields`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fields: k1Fields, confirmed: true }),
    });
    // Auto-name the document to "K-1 - <Entity>" (best effort, stay quiet).
    try {
      await fetch(`${API}/documents/${docId}/auto_name`, { method: "POST" });
    } catch (e) { /* ignore */ }
    // Draft flag → adds/removes a "Final (non-draft) needed" checklist item.
    try {
      await fetch(`${API}/documents/${docId}/draft`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_draft: k1Draft }),
      });
    } catch (e) { /* ignore */ }
    setK1Doc(null); setK1Fields(null);
    load();
    reconcileAfterConfirm(docId);
  };

  const [fillingId, setFillingId] = useState(null);
  const fillClientInfo = async (d) => {
    setFillingId(d.id);
    try {
      const res = await fetch(`${API}/ai/extract_client_info`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ document_id: d.id }),
      });
      const r = await res.json();
      if (r.ok) { onClientUpdated && onClientUpdated(); window.alert("Client info filled from the return. Review it on the Client tab."); }
      else window.alert(`Could not read it: ${r.error}`);
    } catch (e) { window.alert("Could not reach the server."); }
    finally { setFillingId(null); }
  };

  const [showVerified, setShowVerified] = useState(false);
  const replaceRef = useRef(null);
  const [replaceId, setReplaceId] = useState(null);
  const onReplacePick = async (e) => {
    const file = e.target.files[0]; e.target.value = "";
    if (!file || !replaceId) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await fetch(`${API}/documents/${replaceId}/replace`, { method: "POST", body: fd });
    } catch (err) { window.alert("Could not replace the file."); }
    setReplaceId(null); load();
  };
  const [buildingId, setBuildingId] = useState(null);
  const buildChecklist = async (d) => {
    setBuildingId(d.id);
    try {
      const res = await fetch(`${API}/tax_years/${taxYearId}/checklist/from_organizer`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ document_id: d.id }),
      });
      const r = await res.json();
      if (r.ok) {
        onChecklistChanged && onChecklistChanged();
        const tick = r.ticked ? ` ${r.ticked} already received and checked off.` : "";
        window.alert(r.added
          ? `Added ${r.added} item(s) to the checklist.${tick}`
          : `No new items — the checklist already has everything from this document.${tick}`);
      } else { window.alert(`Could not read it: ${r.error}`); }
    } catch (e) { window.alert("Could not reach the server."); }
    finally { setBuildingId(null); }
  };

  const [readingId, setReadingId] = useState(null);
  const [aiText, setAiText] = useState(null);   // { name, text } | { name, error }
  const [batching, setBatching] = useState(false);
  const [batchProg, setBatchProg] = useState(null);     // { i, total } during "Read all"
  const [readingNowId, setReadingNowId] = useState(null); // doc currently being read
  const [aiBatch, setAiBatch] = useState(null); // [{name, detected, updated} | {name, error}]
  const readAll = async (force) => {
    const todo = force ? docs : docs.filter((d) => !d.read_text);  // skip already-read unless forced
    if (!todo.length) {
      window.alert("All documents here have already been read.");
      return;
    }
    if (force && !window.confirm(
      `Re-read all ${todo.length} document(s)? This re-runs the AI on each and uses API credits.`)) {
      return;
    }
    setBatching(true); setAiText(null); setAiBatch(null);
    const results = [];
    let i = 0;
    for (const d of todo) {
      i += 1;
      setBatchProg({ i, total: todo.length });
      setReadingNowId(d.id);
      try {
        const res = await fetch(`${API}/ai/extract_text`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ document_id: d.id }),
        });
        const r = await res.json();
        results.push(r.ok
          ? { id: d.id, name: d.file_name, detected: r.detected_type, updated: r.updated, text: r.text }
          : { name: d.file_name, error: r.error });
      } catch (e) {
        results.push({ name: d.file_name, error: "server error" });
      }
    }
    setReadingNowId(null); setBatchProg(null);
    setBatching(false);
    load();
    setAiBatch(results);
  };
  const readDoc = async (d) => {
    setReadingId(d.id); setAiText(null);
    try {
      const res = await fetch(`${API}/ai/extract_text`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ document_id: d.id }),
      });
      const r = await res.json();
      if (r.ok) {
        setAiText({ id: d.id, name: d.file_name, text: r.text,
          detected: r.detected_type, updated: r.updated });
        if (r.updated) load();   // refresh the type badge
      } else {
        setAiText({ name: d.file_name, error: r.error });
      }
    } catch (e) {
      setAiText({ name: d.file_name, error: "Could not reach the server." });
    } finally { setReadingId(null); }
  };

  return (
    <div className="sub-panel">
      <div className="sub-head">
        <h4>Documents</h4>
        <div className="upload-row">
          {docs.some((d) => d.doc_type === "other") && (
            <button className="btn primary small" disabled={batching}
              title="Figure out the type of any unidentified documents"
              onClick={() => readAll(false)}>
              {batching
                ? `Identifying ${batchProg ? `${batchProg.i} of ${batchProg.total}` : ""}…`
                : "Identify all"}
            </button>
          )}
        </div>
      </div>
      {aiBatch && (
        <div className="ai-read">
          <div className="sub-head">
            <h4>Read all — results</h4>
            <button className="icon-btn" onClick={() => setAiBatch(null)}>✕</button>
          </div>
          <ul className="doc-list">
            {aiBatch.map((r, i) => (
              <li key={i}>
                {r.error
                  ? <><span className="doc-name">{r.name}</span>
                      <span className="muted small" style={{ color: "var(--red)" }}>{r.error}</span></>
                  : <>
                      <button className="link-btn doc-name"
                        onClick={() => setAiText({ id: r.id, name: r.name, text: r.text, detected: r.detected, updated: r.updated })}>
                        {r.name}
                      </button>
                      <span className="badge entity">{DOC_TYPES[r.detected] || r.detected}{r.updated ? " ✓" : ""}</span>
                      <span className="muted small">view text</span>
                    </>}
              </li>
            ))}
          </ul>
        </div>
      )}
      <input type="file" multiple ref={fileRef} style={{ display: "none" }} onChange={onPick} />
      <input type="file" ref={replaceRef} style={{ display: "none" }} onChange={onReplacePick} />
      <div className={`dropzone${drag ? " over" : ""}`}
        onClick={() => fileRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}>
        {busy ? "Uploading…" : "Drag files here, or click to choose (you can pick several)"}
      </div>
      {docs.length === 0 ? (
        <div className="muted small pad">No documents yet.</div>
      ) : (() => {
        const renderDoc = (d) => (
          <li key={d.id}>
            <select className="status-select" value={d.doc_type}
              onChange={(e) => changeDocType(d.id, e.target.value)}>
              {Object.entries(DOC_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <a href={`${API}/documents/${d.id}/download`} className="doc-name">{d.file_name}</a>
            {d.is_draft && <span className="badge draft">DRAFT</span>}
            {(readingNowId === d.id || readingId === d.id
              || (w2Loading && w2Doc?.id === d.id)
              || (f99Loading && f99Doc?.id === d.id)
              || (f98Loading && f98Doc?.id === d.id)
              || (e98Loading && e98Doc?.id === d.id)
              || (a95Loading && a95Doc?.id === d.id)
              || (idLoading && idDoc?.id === d.id)
              || (k1Loading && k1Doc?.id === d.id)) && (
              <span className="reading-pill"><span className="reading-dot" />Reading…</span>
            )}
            {d.doc_type === "w2" && (
              <button className="btn primary small" disabled={w2Loading && w2Doc?.id === d.id}
                onClick={() => openW2(d)}>
                {(w2Loading && w2Doc?.id === d.id) ? "Reading…" : (d.confirmed ? "✓ W-2 fields" : "Verify W-2")}
              </button>
            )}
            {d.doc_type === "1099" && (
              <button className="btn primary small" disabled={f99Loading && f99Doc?.id === d.id}
                onClick={() => openF99(d)}>
                {(f99Loading && f99Doc?.id === d.id) ? "Reading…" : (d.confirmed ? "✓ 1099 fields" : "Verify 1099")}
              </button>
            )}
            {d.doc_type === "1098" && (
              <button className="btn primary small" disabled={f98Loading && f98Doc?.id === d.id}
                onClick={() => openF98(d)}>
                {(f98Loading && f98Doc?.id === d.id) ? "Reading…" : (d.confirmed ? "✓ 1098 fields" : "Verify 1098")}
              </button>
            )}
            {d.doc_type === "1098e" && (
              <button className="btn primary small" disabled={e98Loading && e98Doc?.id === d.id}
                onClick={() => openE98(d)}>
                {(e98Loading && e98Doc?.id === d.id) ? "Reading…" : (d.confirmed ? "✓ 1098-E fields" : "Verify 1098-E")}
              </button>
            )}
            {d.doc_type === "closing" && (
              <button className="btn primary small" disabled={cjeBusy === d.id}
                title="Read this HUD/Closing Disclosure, review the lines, then build a balanced journal entry (Excel)"
                onClick={() => openClosingJE(d)}>
                {cjeBusy === d.id ? "Reading…" : "Generate AJE"}
              </button>
            )}
            {d.doc_type === "1095a" && (
              <button className="btn primary small" disabled={a95Loading && a95Doc?.id === d.id}
                onClick={() => openA95(d)}>
                {(a95Loading && a95Doc?.id === d.id) ? "Reading…" : (d.confirmed ? "✓ 1095-A fields" : "Verify 1095-A")}
              </button>
            )}
            {d.doc_type === "id" && (
              <button className="btn primary small" disabled={idLoading && idDoc?.id === d.id}
                onClick={() => openId(d)}>
                {(idLoading && idDoc?.id === d.id) ? "Reading…" : (d.confirmed ? "✓ ID fields" : "Read license")}
              </button>
            )}
            {d.doc_type === "k1" && (
              <button className="btn primary small" disabled={k1Loading && k1Doc?.id === d.id}
                onClick={() => openK1(d)}>
                {(k1Loading && k1Doc?.id === d.id) ? "Reading…" : (d.confirmed ? "✓ K-1 fields" : "Verify K-1")}
              </button>
            )}
            {(d.doc_type === "tax_return" || d.doc_type === "prior_return") && (
              <button className="btn ghost small" disabled={fillingId === d.id}
                onClick={() => fillClientInfo(d)}>
                {fillingId === d.id ? "Reading…" : "Fill client info"}
              </button>
            )}
            {d.doc_type === "organizer" && (
              <button className="btn primary small" disabled={buildingId === d.id}
                onClick={() => buildChecklist(d)}>
                {buildingId === d.id ? "Reading…" : "Build checklist"}
              </button>
            )}
            {d.doc_type === "other" && (
              <button className="btn ghost small" disabled={readingId === d.id}
                title="Figure out what kind of document this is"
                onClick={() => readDoc(d)}>
                {readingId === d.id ? "Identifying…" : "Identify"}
              </button>
            )}
            <button className="btn ghost small"
              title="Replace this file with an updated version"
              onClick={() => { setReplaceId(d.id); replaceRef.current.click(); }}>Replace</button>
            <button className="icon-btn" title="Remove" onClick={() => remove(d.id)}>✕</button>
          </li>
        );
        // Verified forms drop to the bottom under a collapsible section.
        const pending = docs.filter((d) => !d.confirmed);
        const verified = docs.filter((d) => d.confirmed);
        return (
          <>
            {pending.length > 0
              ? <ul className="doc-list">{pending.map(renderDoc)}</ul>
              : <div className="muted small pad">Everything here is verified. 🎉</div>}
            {verified.length > 0 && (
              <div className="cl-done">
                <button type="button" className="cl-done-toggle"
                  onClick={() => setShowVerified((v) => !v)}>
                  {showVerified ? "▾" : "▸"} Verified ({verified.length})
                </button>
                {showVerified && <ul className="doc-list">{verified.map(renderDoc)}</ul>}
              </div>
            )}
          </>
        );
      })()}
      {k1Doc && (
        <div className="modal-overlay" onClick={() => { setK1Doc(null); setK1Fields(null); }}>
          <div className="w2-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sub-head">
              <h3>Verify K-1 — {k1Doc.file_name}</h3>
              <button className="btn ghost" onClick={() => { setK1Doc(null); setK1Fields(null); }}>Close</button>
            </div>
            {k1Loading ? <div className="empty">Reading the K-1…</div>
              : k1Err ? <div className="banner error">{k1Err}</div>
                : k1Fields && (
                  <div className="w2-compare">
                    <div className="w2-doc">
                      <DocViewer docId={k1Doc.id} name={k1Doc.file_name} />
                    </div>
                    <div className="w2-form">
                      <p className="muted small">Amber fields are ones the AI was less than 95% sure about.</p>
                      <label className="w2-row">
                        <span>K-1 form</span>
                        <select value={k1Fields.form || ""} onChange={(e) => setK1("form", e.target.value)}>
                          <option value="">— select —</option>
                          <option value="1065">1065 (Partnership)</option>
                          <option value="1120S">1120S (S-corp)</option>
                          <option value="1041">1041 (Trust)</option>
                        </select>
                      </label>
                      <label className="w2-row">
                        <span>Whose?</span>
                        <select value={k1Fields.person || ""} onChange={(e) => setK1("person", e.target.value)}>
                          <option value="">— select —</option>
                          <option value="T">Taxpayer</option>
                          <option value="S">Spouse</option>
                        </select>
                      </label>
                      <label className="w2-row">
                        <span>Entity</span>
                        <input value={k1Fields.entity_name || ""} onChange={(e) => setK1("entity_name", e.target.value)} />
                      </label>
                      {K1_FIELDS.map(([k, label]) => {
                        const low = k1Fields.confidence && k1Fields.confidence[k] != null && k1Fields.confidence[k] < 0.95;
                        return (
                          <label key={k} className={`w2-row${low ? " low" : ""}`}>
                            <span>{label}</span>
                            <input value={k1Fields[k] == null ? "" : k1Fields[k]}
                              onChange={(e) => setK1(k, e.target.value)} />
                          </label>
                        );
                      })}
                      <div className="w2-row box14-head"><span>Box 20 codes</span><span></span></div>
                      {(k1Fields.box20 || []).map((it, i) => (
                        <div key={i} className="box14-item">
                          <input placeholder="code (e.g. N)" value={it.code || ""}
                            onChange={(e) => setK1List("box20", i, "code", e.target.value)} />
                          <input placeholder="amount" value={it.amount == null ? "" : it.amount}
                            onChange={(e) => setK1List("box20", i, "amount", e.target.value)} />
                          <button className="icon-btn" onClick={() => removeK1List("box20", i)}>✕</button>
                        </div>
                      ))}
                      <button className="btn ghost small" onClick={() => addK1List("box20", { code: "", amount: "" })}>+ Add Box 20 code</button>
                      <div className="w2-row box14-head"><span>State info</span><span></span></div>
                      {(k1Fields.states || []).map((it, i) => (
                        <div key={i} className="box14-item">
                          <input placeholder="state (e.g. NJ)" value={it.state || ""}
                            onChange={(e) => setK1List("states", i, "state", e.target.value)} />
                          <input placeholder="amount" value={it.amount == null ? "" : it.amount}
                            onChange={(e) => setK1List("states", i, "amount", e.target.value)} />
                          <button className="icon-btn" onClick={() => removeK1List("states", i)}>✕</button>
                        </div>
                      ))}
                      <button className="btn ghost small" onClick={() => addK1List("states", { state: "", amount: "" })}>+ Add state</button>
                      <label className="w2-check" title="Adds a 'Final (non-draft) needed' checklist item">
                        <input type="checkbox" checked={k1Draft}
                          onChange={(e) => setK1Draft(e.target.checked)} />
                        This is a DRAFT — final still needed
                      </label>
                      <div className="modal-actions">
                        <button className="btn ghost" onClick={() => openK1(k1Doc, true)} disabled={k1Loading}>Read again</button>
                        <button className="btn primary" onClick={confirmK1}>Confirm</button>
                      </div>
                    </div>
                  </div>
                )}
          </div>
        </div>
      )}

      {f99Doc && (
        <div className="modal-overlay" onClick={() => { setF99Doc(null); setF99Fields(null); }}>
          <div className="w2-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sub-head">
              <h3>Verify 1099 — {f99Doc.file_name}</h3>
              <button className="btn ghost" onClick={() => { setF99Doc(null); setF99Fields(null); }}>Close</button>
            </div>
            {f99Loading ? <div className="empty">Reading the 1099…</div>
              : f99Err ? <div className="banner error">{f99Err}</div>
                : f99Fields && (
                  <div className="w2-compare">
                    <div className="w2-doc">
                      <DocViewer docId={f99Doc.id} name={f99Doc.file_name} />
                    </div>
                    <div className="w2-form">
                      <p className="muted small">Amber fields are ones the AI was less than 95% sure about.</p>
                      <label className="w2-row">
                        <span>Form type</span>
                        <select value={f99Fields.form_type || ""} onChange={(e) => setF99("form_type", e.target.value)}>
                          <option value="">— select —</option>
                          <option value="1099-INT">1099-INT</option>
                          <option value="1099-DIV">1099-DIV</option>
                          <option value="1099-R">1099-R</option>
                          <option value="1099-NEC">1099-NEC</option>
                          <option value="1099-MISC">1099-MISC</option>
                          <option value="1099-B">1099-B</option>
                          <option value="consolidated">Consolidated brokerage</option>
                          <option value="other">Other</option>
                        </select>
                      </label>
                      <label className="w2-row">
                        <span>Whose?</span>
                        <select value={f99Fields.person || ""} onChange={(e) => setF99("person", e.target.value)}>
                          <option value="">— select —</option>
                          <option value="T">Taxpayer</option>
                          <option value="S">Spouse</option>
                        </select>
                      </label>
                      <label className="w2-row">
                        <span>Payer</span>
                        <input value={f99Fields.payer_name || ""} onChange={(e) => setF99("payer_name", e.target.value)} />
                      </label>
                      {(() => {
                        // Show every field-group that matches the form type OR
                        // that already has a value (consolidated statements).
                        const ft = (f99Fields.form_type || "").toUpperCase();
                        const has = (list) => list.some(([k]) => f99Fields[k] != null && f99Fields[k] !== "");
                        const groups = [
                          ["INT", F1099_INT_FIELDS], ["DIV", F1099_DIV_FIELDS],
                          ["R", F1099_R_FIELDS], ["NEC", F1099_NEC_FIELDS],
                          ["MISC", F1099_MISC_FIELDS], ["B", F1099_B_FIELDS],
                        ];
                        let shown = [];
                        groups.forEach(([tag, list]) => {
                          if (ft.includes(tag) || ft === "CONSOLIDATED" || has(list)) shown = shown.concat(list);
                        });
                        return shown;
                      })().map(([k, label]) => {
                        const low = f99Fields.confidence && f99Fields.confidence[k] != null && f99Fields.confidence[k] < 0.95;
                        return (
                          <label key={k} className={`w2-row${low ? " low" : ""}`}>
                            <span>{label}</span>
                            <input value={f99Fields[k] == null ? "" : f99Fields[k]}
                              onChange={(e) => setF99(k, e.target.value)} />
                          </label>
                        );
                      })}
                      {validate1099(f99Fields).length > 0 && (
                        <div className="validation-box">
                          <strong>⚠ Check these:</strong>
                          <ul>{validate1099(f99Fields).map((m, i) => <li key={i}>{m}</li>)}</ul>
                        </div>
                      )}
                      <div className="modal-actions">
                        <button className="btn ghost" onClick={() => openF99(f99Doc, true)} disabled={f99Loading}
                          title="Re-read with the stronger AI model (better on messy scans)">Read again</button>
                        <button className="btn primary" onClick={confirmF99}>Confirm</button>
                      </div>
                    </div>
                  </div>
                )}
          </div>
        </div>
      )}

      {a95Doc && (
        <div className="modal-overlay" onClick={() => { setA95Doc(null); setA95Fields(null); }}>
          <div className="w2-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sub-head">
              <h3>Verify 1095-A — {a95Doc.file_name}</h3>
              <button className="btn ghost" onClick={() => { setA95Doc(null); setA95Fields(null); }}>Close</button>
            </div>
            {a95Loading ? <div className="empty">Reading the 1095-A…</div>
              : a95Err ? <div className="banner error">{a95Err}</div>
                : a95Fields && (
                  <div className="w2-compare">
                    <div className="w2-doc"><DocViewer docId={a95Doc.id} name={a95Doc.file_name} /></div>
                    <div className="w2-form">
                      <p className="muted small">Amber fields are ones the AI was less than 95% sure about.</p>
                      <label className="w2-row">
                        <span>Whose?</span>
                        <select value={a95Fields.person || ""} onChange={(e) => setA95("person", e.target.value)}>
                          <option value="">— select —</option>
                          <option value="T">Taxpayer</option>
                          <option value="S">Spouse</option>
                        </select>
                      </label>
                      <label className="w2-row">
                        <span>Issuer</span>
                        <input value={a95Fields.issuer_name || ""} onChange={(e) => setA95("issuer_name", e.target.value)} />
                      </label>
                      {F1095A_FIELDS.map(([k, label]) => {
                        const low = a95Fields.confidence && a95Fields.confidence[k] != null && a95Fields.confidence[k] < 0.95;
                        return (
                          <label key={k} className={`w2-row${low ? " low" : ""}`}>
                            <span>{label}</span>
                            <input value={a95Fields[k] == null ? "" : a95Fields[k]}
                              onChange={(e) => setA95(k, e.target.value)} />
                          </label>
                        );
                      })}
                      <div className="modal-actions">
                        <button className="btn ghost" onClick={() => openA95(a95Doc, true)} disabled={a95Loading}>Read again</button>
                        <button className="btn primary" onClick={confirmA95}>Confirm</button>
                      </div>
                    </div>
                  </div>
                )}
          </div>
        </div>
      )}

      {idDoc && (
        <div className="modal-overlay" onClick={() => { setIdDoc(null); setIdFields(null); }}>
          <div className="w2-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sub-head">
              <h3>Driver's license / ID — {idDoc.file_name}</h3>
              <button className="btn ghost" onClick={() => { setIdDoc(null); setIdFields(null); }}>Close</button>
            </div>
            {idLoading ? <div className="empty">Reading the ID…</div>
              : idErr ? <div className="banner error">{idErr}</div>
                : idFields && (
                  <div className="w2-compare">
                    <div className="w2-doc"><DocViewer docId={idDoc.id} name={idDoc.file_name} /></div>
                    <div className="w2-form">
                      <p className="muted small">Amber fields are ones the AI was less than 95% sure about.</p>
                      <label className="w2-row">
                        <span>Whose?</span>
                        <select value={idFields.person || ""} onChange={(e) => setIdF("person", e.target.value)}>
                          <option value="">— select —</option>
                          <option value="T">Taxpayer</option>
                          <option value="S">Spouse</option>
                        </select>
                      </label>
                      {ID_FIELDS.map(([k, label]) => {
                        const low = idFields.confidence && idFields.confidence[k] != null && idFields.confidence[k] < 0.95;
                        return (
                          <label key={k} className={`w2-row${low ? " low" : ""}`}>
                            <span>{label}</span>
                            <input value={idFields[k] == null ? "" : idFields[k]}
                              onChange={(e) => setIdF(k, e.target.value)} />
                          </label>
                        );
                      })}
                      <div className="modal-actions">
                        <button className="btn ghost" onClick={() => openId(idDoc, true)} disabled={idLoading}>Read again</button>
                        <button className="btn primary" onClick={confirmId}>Confirm</button>
                      </div>
                    </div>
                  </div>
                )}
          </div>
        </div>
      )}

      {cje && (() => {
        const isPurchase = !(cje.type || "").startsWith("refi");
        const price = cjeNum(cje.purchase_price);
        const lp = Math.max(0, Math.min(100, Number(cje.landPct) || 0)) / 100;
        const land = isPurchase ? Math.round(price * lp * 100) / 100 : 0;
        const bldg = isPurchase ? Math.round((price - land) * 100) / 100 : 0;
        const totDr = cje.lines.reduce((s, l) => cjeSideOf(l) === "D" ? s + cjeNum(l.amount) : s, land + bldg);
        const totCr = cje.lines.reduce((s, l) => cjeSideOf(l) === "C" ? s + cjeNum(l.amount) : s, 0);
        const plug = Math.abs(Math.round((totDr - totCr) * 100) / 100);
        const cashSide = (totDr - totCr) > 0 ? "credit" : "debit";
        const hud = cje.cash != null && cje.cash !== "" ? cjeNum(cje.cash) : null;
        const off = hud != null && Math.abs(plug - hud) > 1;
        const f2 = (n) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return (
        <div className="modal-overlay" onClick={() => !cjeBuilding && setCje(null)}>
          <div className="w2-modal cje-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sub-head">
              <h3>Closing journal entry — {cje.name}</h3>
              <button className="btn ghost" onClick={() => setCje(null)}>Close</button>
            </div>
            <div className="w2-compare">
              <div className="w2-doc"><CjePlacer docId={cje.docId} page={cjePage || 1}
                setPage={setCjePage} lines={cje.lines} onPlace={cjePlace} onRemove={cjeHide}
                onPick={(i) => setCjeFocus(i)} annoT={cje.annoT} /></div>
              <div className="w2-form cje-form">
                <p className="muted small">Amber rows are below 95% confidence — compare to the HUD on the left, fix the amount or code, then click ✓ to confirm (Spoom remembers your coding for next time). Cash is the balancing plug.</p>
                {cjeBusy === cje.docId && (
                  <div className="cje-reading">
                    <div className="cje-reading-bar"><span /></div>
                    <div className="muted small">Reading the statement with AI… this usually takes 20–40 seconds.</div>
                  </div>
                )}
                <div className="cje-head">
                  <label>Type
                    <select value={cje.type} onChange={(e) => setCje({ ...cje, type: e.target.value })}>
                      <option value="purchase">Purchase</option>
                      <option value="refinance">Refinance</option>
                    </select>
                  </label>
                  <label>Property <input value={cje.property} onChange={(e) => setCje({ ...cje, property: e.target.value })} /></label>
                  <label>Date <input value={cje.date} onChange={(e) => setCje({ ...cje, date: e.target.value })} /></label>
                  {!cje.type.startsWith("refi") && (
                    <>
                      <label>Purchase price <input type="text" inputMode="decimal" value={fmtCommas(cje.purchase_price)}
                        onChange={(e) => setCje({ ...cje, purchase_price: e.target.value.replace(/,/g, "") })} /></label>
                      <label>Land % <input type="number" style={{ width: 60 }} value={cje.landPct}
                        onChange={(e) => setCje({ ...cje, landPct: e.target.value })} /></label>
                    </>
                  )}
                </div>
                <div className="cje-lines">
                  <table className="data-table compact">
                    <thead><tr><th>Section</th><th>Description</th><th>Debit</th><th>Credit</th><th>Code</th><th></th></tr></thead>
                    <tbody>
                      {isPurchase && price > 0 && (
                        <>
                          <tr className="cje-fixed">
                            <td>Purchase Price</td>
                            <td>Land ({Math.round(lp * 100)}%) — set by purchase price + land %</td>
                            <td className="cje-amtcell">{f2(land)}</td><td></td>
                            <td>A — Land</td><td></td>
                          </tr>
                          <tr className="cje-fixed">
                            <td>Purchase Price</td>
                            <td>Building ({Math.round((1 - lp) * 100)}%)</td>
                            <td className="cje-amtcell">{f2(bldg)}</td><td></td>
                            <td>B — Building</td><td></td>
                          </tr>
                        </>
                      )}
                      {cje.lines.map((l, i) => {
                        const low = l.conf != null && l.conf < 0.95;
                        const side = cjeSideOf(l);
                        const flip = (
                          <button type="button" className="cje-flip" title="Move to the other column (debit ⇄ credit)"
                            onClick={() => cjeFlipSide(i)}>⇄</button>);
                        const amtInput = (
                          <div className="cje-amtwrap">
                            <input className="cje-amt" inputMode="decimal" value={fmtCommas(l.amount)}
                              onChange={(e) => setCjeLine(i, "amount", e.target.value.replace(/,/g, ""))} />
                            {flip}
                          </div>);
                        return (
                          <tr key={i} ref={(el) => { cjeRowRefs.current[i] = el; }}
                            className={(low ? "cje-low" : "") + (cjeFocus === i ? " cje-focus" : "")}>
                            <td><input value={l.section} onChange={(e) => setCjeLine(i, "section", e.target.value)} /></td>
                            <td>
                              <div className="cje-desc-row">
                                <button type="button"
                                  className={"cje-refnum" + (l.mx != null ? " placed" : "")}
                                  draggable
                                  onDragStart={(e) => { e.dataTransfer.setData("text/plain", String(i)); e.dataTransfer.effectAllowed = "copy"; }}
                                  title={`#${l.num} — drag onto the statement to place a reference, or click to jump to its page`}
                                  onClick={() => { const p = l.mpage != null ? l.mpage + 1 : l.page; if (p) setCjePage(Number(p)); }}>{l.num}</button>
                                <input style={{ width: "100%" }} value={l.desc} onChange={(e) => setCjeLine(i, "desc", e.target.value)} />
                              </div>
                              {low && <div className="cje-note">⚠ {l.note || "double-check this line"}</div>}
                            </td>
                            <td>{side === "D" ? amtInput : null}</td>
                            <td>{side === "C" ? amtInput : null}</td>
                            <td>
                              <select value={l.ref} onChange={(e) => setCjeLine(i, "ref", e.target.value)}>
                                {CJE_REFS.map(([c, lab]) => <option key={c} value={c}>{c} — {lab}</option>)}
                              </select>
                            </td>
                            <td className="cje-rowbtns">
                              {low && <button className="btn small primary" title="Confirm this line is correct" onClick={() => cjeConfirm(i)}>✓</button>}
                              <button className="icon-btn" title="Remove line" onClick={() => cjeDelLine(i)}>✕</button>
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="cje-fixed">
                        <td></td><td>Cash (balancing plug)</td>
                        <td className="cje-amtcell">{cashSide === "debit" ? f2(plug) : ""}</td>
                        <td className="cje-amtcell">{cashSide === "credit" ? f2(plug) : ""}</td>
                        <td>L — Cash</td><td></td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr className="cje-totrow">
                        <td></td><td>Total</td>
                        <td className="cje-amtcell">{f2(totDr + (cashSide === "debit" ? plug : 0))}</td>
                        <td className="cje-amtcell">{f2(totCr + (cashSide === "credit" ? plug : 0))}</td>
                        <td></td><td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="cje-totals">
                  <span>Debits <strong>{f2(totDr)}</strong></span>
                  <span>Credits <strong>{f2(totCr)}</strong></span>
                  <span>Cash plug <strong>{f2(plug)}</strong> ({cashSide})</span>
                  {hud != null && <span className={off ? "cje-warn" : "muted"}>{off ? "⚠ " : "✓ "}Statement cash {f2(hud)}</span>}
                </div>
                <div className="modal-actions">
                  <button className="btn ghost" onClick={cjeAddLine}>+ Add line</button>
                  <button className="btn ghost" disabled={cjeBusy === cje.docId}
                    onClick={() => openClosingJE({ id: cje.docId, file_name: cje.name }, true)}>
                    {cjeBusy === cje.docId ? "Reading…" : "Re-read"}
                  </button>
                  <button className="btn primary" onClick={buildClosingJE} disabled={cjeBuilding || cjeBusy === cje.docId}>
                    {cjeBuilding ? "Building…" : "Build Excel ↓"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {e98Doc && (
        <div className="modal-overlay" onClick={() => { setE98Doc(null); setE98Fields(null); }}>
          <div className="w2-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sub-head">
              <h3>Verify 1098-E — {e98Doc.file_name}</h3>
              <button className="btn ghost" onClick={() => { setE98Doc(null); setE98Fields(null); }}>Close</button>
            </div>
            {e98Loading ? <div className="empty">Reading the 1098-E…</div>
              : e98Err ? <div className="banner error">{e98Err}</div>
                : e98Fields && (
                  <div className="w2-compare">
                    <div className="w2-doc"><DocViewer docId={e98Doc.id} name={e98Doc.file_name} /></div>
                    <div className="w2-form">
                      <label className="w2-row">
                        <span>Whose?</span>
                        <select value={e98Fields.person || ""} onChange={(e) => setE98("person", e.target.value)}>
                          <option value="">— select —</option>
                          <option value="T">Taxpayer</option>
                          <option value="S">Spouse</option>
                        </select>
                      </label>
                      <label className="w2-row">
                        <span>Lender</span>
                        <input value={e98Fields.lender_name || ""} onChange={(e) => setE98("lender_name", e.target.value)} />
                      </label>
                      {F1098E_FIELDS.map(([k, label]) => (
                        <label key={k} className="w2-row">
                          <span>{label}</span>
                          <input value={e98Fields[k] == null ? "" : e98Fields[k]}
                            onChange={(e) => setE98(k, e.target.value)} />
                        </label>
                      ))}
                      <div className="modal-actions">
                        <button className="btn ghost" onClick={() => openE98(e98Doc, true)} disabled={e98Loading}>Read again</button>
                        <button className="btn primary" onClick={confirmE98}>Confirm</button>
                      </div>
                    </div>
                  </div>
                )}
          </div>
        </div>
      )}

      {f98Doc && (
        <div className="modal-overlay" onClick={() => { setF98Doc(null); setF98Fields(null); }}>
          <div className="w2-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sub-head">
              <h3>Verify 1098 — {f98Doc.file_name}</h3>
              <button className="btn ghost" onClick={() => { setF98Doc(null); setF98Fields(null); }}>Close</button>
            </div>
            {f98Loading ? <div className="empty">Reading the 1098…</div>
              : f98Err ? <div className="banner error">{f98Err}</div>
                : f98Fields && (
                  <div className="w2-compare">
                    <div className="w2-doc">
                      <DocViewer docId={f98Doc.id} name={f98Doc.file_name} />
                    </div>
                    <div className="w2-form">
                      <p className="muted small">Amber fields are ones the AI was less than 95% sure about.</p>
                      <label className="w2-row">
                        <span>Whose?</span>
                        <select value={f98Fields.person || ""} onChange={(e) => setF98("person", e.target.value)}>
                          <option value="">— select —</option>
                          <option value="T">Taxpayer</option>
                          <option value="S">Spouse</option>
                        </select>
                      </label>
                      <label className="w2-row">
                        <span>Lender</span>
                        <input value={f98Fields.lender_name || ""} onChange={(e) => setF98("lender_name", e.target.value)} />
                      </label>
                      {F1098_FIELDS.map(([k, label]) => {
                        const low = f98Fields.confidence && f98Fields.confidence[k] != null && f98Fields.confidence[k] < 0.95;
                        return (
                          <label key={k} className={`w2-row${low ? " low" : ""}`}>
                            <span>{label}</span>
                            <input value={f98Fields[k] == null ? "" : f98Fields[k]}
                              onChange={(e) => setF98(k, e.target.value)} />
                          </label>
                        );
                      })}
                      <div className="modal-actions">
                        <button className="btn ghost" onClick={() => openF98(f98Doc, true)} disabled={f98Loading}
                          title="Re-read with the stronger AI model (better on messy scans)">Read again</button>
                        <button className="btn primary" onClick={confirmF98}>Confirm</button>
                      </div>
                    </div>
                  </div>
                )}
          </div>
        </div>
      )}

      {w2Doc && (
        <div className="modal-overlay" onClick={() => { setW2Doc(null); setW2Fields(null); }}>
          <div className="w2-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sub-head">
              <h3>Verify W-2 — {w2Doc.file_name}</h3>
              <button className="btn ghost" onClick={() => { setW2Doc(null); setW2Fields(null); }}>Close</button>
            </div>
            {w2Loading ? <div className="empty">Reading the W-2…</div>
              : w2Err ? <div className="banner error">{w2Err}</div>
                : w2Fields && (
                  <div className="w2-compare">
                    <div className="w2-doc">
                      <DocViewer docId={w2Doc.id} name={w2Doc.file_name} />
                    </div>
                    <div className="w2-form">
                      <p className="muted small">Amber fields are ones the AI was less than 95% sure about — please double-check.</p>
                      <label className="w2-row">
                        <span>Whose W-2?</span>
                        <select value={w2Fields.person || ""} onChange={(e) => setW2("person", e.target.value)}>
                          <option value="">— select —</option>
                          <option value="T">Taxpayer</option>
                          <option value="S">Spouse</option>
                        </select>
                      </label>
                      {W2_FIELDS.map(([k, label]) => {
                        const low = w2Fields.confidence && w2Fields.confidence[k] != null && w2Fields.confidence[k] < 0.95;
                        return (
                          <label key={k} className={`w2-row${low ? " low" : ""}`}>
                            <span>{label}</span>
                            <input value={w2Fields[k] == null ? "" : w2Fields[k]}
                              onChange={(e) => setW2(k, e.target.value)} />
                          </label>
                        );
                      })}
                      <div className="w2-row box14-head"><span>Box 12 — Codes</span><span></span></div>
                      {(w2Fields.box12 || []).map((it, i) => (
                        <div key={i} className="box14-item">
                          <input placeholder="code (e.g. D)" value={it.code || ""}
                            onChange={(e) => setBox12(i, "code", e.target.value)} />
                          <input placeholder="amount" value={it.amount == null ? "" : it.amount}
                            onChange={(e) => setBox12(i, "amount", e.target.value)} />
                          <button className="icon-btn" onClick={() => removeBox12(i)}>✕</button>
                        </div>
                      ))}
                      <button className="btn ghost small" onClick={addBox12}>+ Add Box 12 code</button>
                      <div className="w2-row box14-head"><span>Box 14 — Other</span><span></span></div>
                      {(w2Fields.box14 || []).map((it, i) => (
                        <div key={i} className="box14-item">
                          <input placeholder="label" value={it.label || ""}
                            onChange={(e) => setBox14(i, "label", e.target.value)} />
                          <input placeholder="amount" value={it.amount == null ? "" : it.amount}
                            onChange={(e) => setBox14(i, "amount", e.target.value)} />
                          <button className="icon-btn" onClick={() => removeBox14(i)}>✕</button>
                        </div>
                      ))}
                      <button className="btn ghost small" onClick={addBox14}>+ Add Box 14 item</button>
                      {validateW2(w2Fields, year).length > 0 && (
                        <div className="validation-box">
                          <strong>⚠ Check these:</strong>
                          <ul>{validateW2(w2Fields, year).map((m, i) => <li key={i}>{m}</li>)}</ul>
                        </div>
                      )}
                      <div className="w2-row box14-head"><span>Box 13</span><span></span></div>
                      <label className="w2-check">
                        <input type="checkbox" checked={!!w2Fields.box13_statutory}
                          onChange={(e) => setW2("box13_statutory", e.target.checked)} />
                        Statutory employee
                      </label>
                      <label className="w2-check">
                        <input type="checkbox" checked={!!w2Fields.box13_retirement}
                          onChange={(e) => setW2("box13_retirement", e.target.checked)} />
                        Retirement plan
                      </label>
                      <label className="w2-check">
                        <input type="checkbox" checked={!!w2Fields.box13_sick_pay}
                          onChange={(e) => setW2("box13_sick_pay", e.target.checked)} />
                        Third-party sick pay
                      </label>
                      <div className="modal-actions">
                        <button className="btn ghost" onClick={reExtractW2} disabled={w2Loading}
                          title="Re-read with the stronger AI model (better on messy scans)">Read again</button>
                        <button className="btn primary" onClick={confirmW2}>Confirm</button>
                      </div>
                    </div>
                  </div>
                )}
          </div>
        </div>
      )}

      {aiText && (
        <div className="ai-read">
          <div className="sub-head">
            <h4>AI read — {aiText.name}</h4>
            <button className="icon-btn" onClick={() => setAiText(null)}>✕</button>
          </div>
          {aiText.error ? <div className="banner error">{aiText.error}</div> : (
            <>
              {aiText.detected && (
                <div className="muted small pad">
                  Detected type: <span className="badge entity">{DOC_TYPES[aiText.detected] || aiText.detected}</span>
                  {aiText.updated && " — document type updated."}
                </div>
              )}
              <div className="ai-compare">
                {aiText.id && (
                  <div className="ai-doc">
                    <DocViewer docId={aiText.id} name={aiText.name} />
                  </div>
                )}
                <pre className="ai-text">{aiText.text}</pre>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- Document viewer (zoom + rotate) ---------------- */

function DocViewer({ docId, name, page, srcUrl }) {
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  const isPdf = (/\.pdf$/i).test(name || "");
  const src = srcUrl || `${API}/documents/${docId}/view`;
  const pdfSrc = page ? `${src}#page=${page}` : src;   // hash after any ?query is valid
  return (
    <div className="doc-viewer">
      <div className="doc-viewer-bar">
        <button className="btn ghost small" onClick={() => setZoom((z) => Math.max(0.25, +(z - 0.25).toFixed(2)))}>−</button>
        <span className="muted small">{Math.round(zoom * 100)}%</span>
        <button className="btn ghost small" onClick={() => setZoom((z) => +(z + 0.25).toFixed(2))}>+</button>
        <button className="btn ghost small" onClick={() => setRot((r) => (r + 90) % 360)}>⟳ Rotate</button>
        <button className="btn ghost small" onClick={() => { setZoom(1); setRot(0); }}>Reset</button>
      </div>
      <div className="doc-viewer-body">
        {isPdf
          ? <iframe key={page || 0} title="doc" src={pdfSrc} style={{ transform: `rotate(${rot}deg)` }} />
          : <img alt={name} src={src} className="doc-viewer-img"
              style={{ transform: `scale(${zoom}) rotate(${rot}deg)` }} />}
      </div>
    </div>
  );
}

/* ---------------- Closing-entry PDF placer (drag a line onto the page) ------ */
// Renders the closing statement as a page image you can drop review lines onto.
// A drop records a manual (mx,my) fraction + page on that line, which both shows
// a marker here and gets stamped onto the saved numbered HUD at build time.
function CjePlacer({ docId, page, setPage, lines, onPlace, onPick, onRemove, annoT }) {
  const [pages, setPages] = useState(1);
  const [auto, setAuto] = useState({});   // num -> {page, xf, yf} resolved by the server
  const [zoom, setZoom] = useState(1);
  const [drag, setDrag] = useState(false);
  const imgRef = useRef(null);
  const markDrag = useRef(null);
  // Pointer-drag an already-placed marker to reposition it; a click (no drag)
  // jumps to that line in the review; right-click removes the placement.
  const onMarkDown = (e, idx) => {
    if (e.button !== 0) return;                 // left button only
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX, sy = e.clientY;
    markDrag.current = { idx, moved: false };
    const move = (ev) => {
      // ignore tiny jitter so a click stays a click (jump), not a move
      if (!markDrag.current.moved
          && Math.abs(ev.clientX - sx) < 4 && Math.abs(ev.clientY - sy) < 4) return;
      const img = imgRef.current; if (!img) return;
      const r = img.getBoundingClientRect();
      const fx = Math.min(Math.max((ev.clientX - r.left) / r.width, 0), 1);
      const fy = Math.min(Math.max((ev.clientY - r.top) / r.height, 0), 1);
      markDrag.current.moved = true;
      onPlace(idx, (page || 1) - 1, fx, fy);
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      if (markDrag.current && !markDrag.current.moved && onPick) onPick(idx);
      markDrag.current = null;
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };
  useEffect(() => {
    let alive = true;
    fetch(`${API}/documents/${docId}/closing_je/pageinfo`)
      .then((r) => r.json()).then((r) => { if (alive && r.ok) setPages(r.pages || 1); })
      .catch(() => {});
    fetch(`${API}/documents/${docId}/closing_je/markers`)
      .then((r) => r.json()).then((r) => {
        if (!alive || !r.ok) return;
        const m = {};
        (r.markers || []).forEach((p) => { m[p.num] = p; });
        setAuto(m);
      }).catch(() => {});
    return () => { alive = false; };
  }, [docId, annoT]);
  const pg0 = (page || 1) - 1;
  // Every reference is a live marker: a line's manual drop wins, otherwise the
  // server-resolved (OCR/AI) position. Hidden lines are skipped.
  const marks = (lines || []).map((l, i) => {
    if (l.mhide) return null;
    let p0; let xf; let yf; let manual = false;
    if (l.mx != null && l.my != null) { p0 = l.mpage || 0; xf = l.mx; yf = l.my; manual = true; }
    else { const a = auto[l.num]; if (!a) return null; p0 = a.page; xf = a.xf; yf = a.yf; }
    return { i, num: l.num, desc: l.desc, xf, yf, p0, manual };
  }).filter((m) => m && m.p0 === pg0);
  const drop = (e) => {
    e.preventDefault(); setDrag(false);
    const idx = parseInt(e.dataTransfer.getData("text/plain"), 10);
    const img = imgRef.current;
    if (Number.isNaN(idx) || !img) return;
    const r = img.getBoundingClientRect();
    const fx = Math.min(Math.max((e.clientX - r.left) / r.width, 0), 1);
    const fy = Math.min(Math.max((e.clientY - r.top) / r.height, 0), 1);
    onPlace(idx, pg0, fx, fy);
  };
  return (
    <div className="doc-viewer cje-placer">
      <div className="doc-viewer-bar">
        <button className="btn ghost small" disabled={pg0 <= 0} onClick={() => setPage(Math.max(1, (page || 1) - 1))}>‹ Prev</button>
        <span className="muted small">Page {page || 1} / {pages}</span>
        <button className="btn ghost small" disabled={pg0 >= pages - 1} onClick={() => setPage(Math.min(pages, (page || 1) + 1))}>Next ›</button>
        <span className="cje-placer-hint">Drag a line's number onto the page to place a reference</span>
        <button className="btn ghost small" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}>−</button>
        <span className="muted small">{Math.round(zoom * 100)}%</span>
        <button className="btn ghost small" onClick={() => setZoom((z) => +(z + 0.25).toFixed(2))}>+</button>
      </div>
      <div className="doc-viewer-body cje-placer-body">
        <div className={"cje-placer-page" + (drag ? " over" : "")}
          onDragOver={(e) => { e.preventDefault(); if (!drag) setDrag(true); }}
          onDragLeave={() => setDrag(false)} onDrop={drop}
          style={{ width: `${zoom * 100}%` }}>
          <img ref={imgRef} alt="closing statement"
            src={`${API}/documents/${docId}/closing_je/page/${page || 1}.png?t=${annoT || 0}`}
            style={{ width: "100%", display: "block" }} draggable={false} />
          {marks.map((m) => (
            <div key={m.num} className={"cje-placed" + (m.manual ? " manual" : "")}
              title={`#${m.num} ${m.desc || ""} — drag to move • click to find it in the review`}
              style={{ left: `${m.xf * 100}%`, top: `${m.yf * 100}%` }}
              onMouseDown={(e) => onMarkDown(e, m.i)}
              onContextMenu={(e) => { e.preventDefault(); onRemove(m.i); }}>
              {m.num}
              <span className="cje-placed-x" title="Remove this reference"
                onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                onClick={(e) => { e.stopPropagation(); onRemove(m.i); }}>×</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Checklist panel ---------------- */

// Reasons an item might be "not applicable this year", tailored to the item
// type. First matching rule wins; a tailored match replaces the generic list
// (so you don't get redundant catch-alls like "No activity" on a dependents row).
const NA_RULES = [
  [/\bw-?2\b/, ["Didn't work there this year", "Left the employer last year"]],
  [/refinanc/, ["Did not refinance this year"]],
  [/1098|mortgage/, ["Loan paid off", "Refinanced / different lender", "Property sold"]],
  [/\b7203\b|stock.*basis|basis.*statement/, ["Doesn't have a K-1 from an S-Corp"]],
  [/\bk-?1\b/, ["Sold/disposed of the interest", "Entity dissolved", "No K-1 issued this year"]],
  [/new brokerage/, ["No new brokerage accounts this year"]],
  [/1099-?b|brokerage|capital gain/, ["Account closed/sold", "No sales this year"]],
  [/1099-?int|interest/, ["Account closed/sold", "Interest under $10 (no 1099)"]],
  [/1099-?div|dividend/, ["Account closed/sold", "No distributions this year"]],
  [/1099-?g|unemployment|state refund/, ["No unemployment this year", "No state refund this year"]],
  [/rental|schedule e/, ["Property sold", "No longer rented"]],
  [/dependent/, ["No change in dependents", "No dependents"]],
  [/childcare|day camp|dependent care/, ["No childcare expenses", "No qualifying children"]],
  [/medical|dental/, ["No medical expenses to itemize", "Taking the standard deduction"]],
  [/charit/, ["No charitable contributions", "Taking the standard deduction"]],
  [/virtual currency|crypto/, ["No crypto activity this year"]],
  [/foreign asset|foreign bank|fbar/, ["No foreign assets or accounts"]],
  [/healthcare|health coverage|1095/, ["Covered all year", "No marketplace coverage"]],
  [/\bira\b|\bsep\b|retirement contribution/, ["No retirement contributions this year"]],
  [/estimate|overpayment|schedule of payments/, ["No estimates or extension", "No prior-year overpayment"]],
  [/licen[sc]e/, ["Doesn't file in NY", "No change since last year"]],
  [/bank account|direct deposit/, ["No change since last year", "Paper check (no direct deposit)"]],
  [/confirm address|^address/, ["No change since last year"]],
];

function naReasons(desc) {
  const d = (desc || "").toLowerCase();
  let specific = null;
  for (const [re, reasons] of NA_RULES) {
    if (re.test(d)) { specific = reasons; break; }
  }
  const base = specific || ["No activity this year", "Client confirmed won't receive it"];
  return [...base, "Other…"];
}

function SchedASummary({ clientId, refreshKey }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    if (!clientId) return;
    let live = true;
    fetch(`${API}/clients/${clientId}/leadsheet/summary`, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (live && d && d.ok) setS(d); })
      .catch(() => {});
    return () => { live = false; };
  }, [clientId, refreshKey]);
  if (!s || !s.rows || !s.rows.length) return null;
  const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
  return (
    <div className="scheda-card">
      <div className="scheda-head">
        <strong>Schedule A — Itemized deductions</strong>
        {s.year && <span className="scheda-year">{s.year} · from your file</span>}
      </div>
      <p className="muted small" style={{ margin: "0 0 10px" }}>
        Nothing to type here — these come from the client's answers and your verified documents.
        {s.agi ? ` AGI estimated at ${money(s.agi)} from the verified income documents, so the medical floor is ${money(s.med_floor)}.` : ""}
      </p>
      {s.rows.map((r, i) => (
        <div className="scheda-row" key={i}>
          <span className="scheda-label">{r.label}</span>
          <span className="scheda-source muted small">{r.source}</span>
          <span className="scheda-amt">{money(r.amount)}</span>
        </div>
      ))}
      <div className="scheda-row scheda-total">
        <span className="scheda-label">Total → lead sheet</span>
        <span className="scheda-amt">{money(s.total)}</span>
      </div>
    </div>
  );
}

function ChecklistPanel({ taxYearId, refreshKey, clientId, clientEmail, clientName, clientContact, year }) {
  const [items, setItems] = useState([]);
  const [schedAKey, setSchedAKey] = useState(0);
  const [text, setText] = useState("");
  const [showDone, setShowDone] = useState(false);
  const [showNa, setShowNa] = useState(false);
  const [showReview, setShowReview] = useState(true);
  const [naFor, setNaFor] = useState(null);   // item id whose reason picker is open
  const [compose, setCompose] = useState(null);  // request-list email
  const [sending, setSending] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState(null);

  const load = useCallback(() => {
    fetch(`${API}/tax_years/${taxYearId}/checklist`)
      .then((r) => r.json()).then(setItems).catch(() => {});
  }, [taxYearId]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    fetch(`${API}/email_templates`).then((r) => r.json()).then(setEmailTemplates).catch(() => {});
  }, []);

  // Email the client a request list built from the outstanding checklist items.
  const openRequestList = async () => {
    const outstanding = items.filter(
      (i) => !i.completed && !i.na && (i.section || "") !== "review");
    if (!outstanding.length) {
      window.alert("Nothing outstanding — every checklist item is received or marked not applicable.");
      return;
    }
    const list = outstanding.map((i) => `• ${i.description}`).join("\n");
    let greet = (clientContact || "").trim().split(/\s+/)[0].replace(/[,;.]+$/, "");
    if (!greet && clientId) {
      try {
        const c = await fetch(`${API}/clients/${clientId}`).then((r) => r.json());
        const fn = (c.first_name || "").trim();
        const sfn = (c.spouse_first_name || "").trim();
        if (fn && sfn && (c.filing_status || "").toUpperCase() === "MFJ") greet = `${fn} and ${sfn}`;
        else if (fn) greet = fn;
      } catch (e) { /* ignore */ }
    }
    if (!greet) greet = (clientName || "Client").trim().split(/\s+/)[0].replace(/[,;.]+$/, "");
    const tpl = (emailTemplates && emailTemplates.template_request) || FALLBACK_TEMPLATE_REQUEST;
    const body = fillTemplate(tpl, { greeting: greet, year: String(year || ""), checklist: list });
    setCompose({ to: clientEmail || "", cc: "", subject: `Documents needed for your ${year} tax return`, body });
  };
  const sendRequest = async () => {
    setSending(true);
    const res = await fetch(`${API}/clients/${clientId}/email_return_outlook`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ to: compose.to, cc: compose.cc, subject: compose.subject, body: compose.body, file_ids: [] }),
    });
    const d = await res.json().catch(() => ({}));
    setSending(false);
    if (!res.ok || !d.ok) { window.alert(d.message || "Couldn’t send the request."); return; }
    setCompose(null);
    window.alert("Request list sent to the client.");
  };

  const add = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    await fetch(`${API}/tax_years/${taxYearId}/checklist`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ description: text }),
    });
    setText("");
    load();
  };

  const put = async (id, body) => {
    await fetch(`${API}/checklist/${id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    load();
  };
  const toggle = (item) => put(item.id, { completed: !item.completed });
  const clearNa = (item) => put(item.id, { na: false });
  // Show a $ amount box on deduction-type lines (charity, medical, donations).
  const wantsAmount = (desc) => /charit|medical|donat|contribut|rent|estimat/i.test(desc || "");
  const saveAmount = (item, v) => put(item.id, { amount: v === "" ? "" : Number(v) });
  // Typed text -> number (commas, $ and spaces tolerated); null if not a number.
  const parseAmt = (v) => {
    const s = String(v == null ? "" : v).replace(/[$,\s]/g, "");
    if (s === "") return "";
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };
  // Number -> "11,000" / "11,000.50" for display.
  const fmtAmt = (n) => (n == null || n === "" ? "" :
    Number(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 }));
  const amountBox = (it) => ((wantsAmount(it.description) || it.amount != null) ? (
    <input className="cl-amt" type="text" inputMode="decimal" placeholder="$ amount"
      defaultValue={fmtAmt(it.amount)}
      title="Amount the client reported (flows to the lead sheet). Press Enter or click away to save."
      onFocus={(e) => { e.target.value = e.target.value.replace(/,/g, ""); }}
      onBlur={(e) => {
        if (e.target.dataset.posted === "1") { delete e.target.dataset.posted; return; }
        const parsed = parseAmt(e.target.value);
        if (parsed === null) { e.target.value = fmtAmt(it.amount); return; }
        e.target.value = fmtAmt(parsed);
        if (parsed !== (it.amount == null ? "" : Number(it.amount))) saveAmount(it, parsed);
      }}
      onKeyDown={(e) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        const parsed = parseAmt(e.target.value);
        if (parsed === null) { window.alert("That amount doesn't look like a number."); return; }
        if (parsed === "") { window.alert("Type an amount first."); return; }
        // Enter posts it: save the figure and tick the item off the list.
        e.target.value = fmtAmt(parsed);
        e.target.dataset.posted = "1"; // stop onBlur from saving it a second time
        e.target.blur();
        Promise.resolve(put(it.id, { amount: parsed, completed: true }))
          .then(() => setSchedAKey((k) => k + 1));
      }} />
  ) : null);
  const pickReason = (item, value) => {
    setNaFor(null);
    if (!value) return;
    if (value === "Other…") {
      const r = window.prompt("Why is this not applicable this year?");
      if (r && r.trim()) put(item.id, { na: true, na_reason: r.trim() });
    } else {
      put(item.id, { na: true, na_reason: value });
    }
  };

  const remove = async (id) => {
    await fetch(`${API}/checklist/${id}`, { method: "DELETE" });
    load();
  };

  const dedupe = async () => {
    if (!window.confirm(
      "Tidy up this checklist?\n\nThis merges duplicates and re-derives which " +
      "items are checked off from your verified documents. Any check-offs you " +
      "made by hand will be recomputed too.")) return;
    try {
      const res = await fetch(`${API}/tax_years/${taxYearId}/checklist/dedupe`,
        { method: "POST" });
      const r = await res.json();
      load();
      if (r.error) { window.alert(`Couldn't clean up: ${r.error}`); return; }
      if (r.total === undefined) {
        window.alert("Old server code is still running — restart Flask, then try again.");
        return;
      }
      const parts = [];
      if (r.removed_auto) parts.push(`removed ${r.removed_auto} auto item(s)`);
      if (r.removed) parts.push(`merged ${r.removed} duplicate(s)`);
      parts.push(`${r.ticked} item(s) checked from verified documents`);
      window.alert(`Done — ${parts.join("; ")}.`);
    } catch (e) { window.alert("Could not reach the server."); }
  };

  const addStandard = async () => {
    const res = await fetch(`${API}/tax_years/${taxYearId}/checklist/standard`,
      { method: "POST" });
    const r = await res.json();
    load();
    window.alert(r.added ? `Added ${r.added} standard item(s).`
      : "Standard items are already on the list.");
  };

  // Review (QC) items are kept separate from the client/prep items.
  const reviewItems = items.filter((i) => i.section === "review");
  const main = items.filter((i) => i.section !== "review");
  const done = main.filter((i) => i.completed);
  const naItems = main.filter((i) => i.na && !i.completed);
  const todo = main.filter((i) => !i.completed && !i.na);
  const resolved = done.length + naItems.length;
  const pct = main.length ? Math.round((resolved / main.length) * 100) : 0;
  const reviewDone = reviewItems.filter((i) => i.completed).length;

  const renderTodo = (it) => (
    <li key={it.id}>
      <label>
        <input type="checkbox" checked={false} onChange={() => toggle(it)} />
        <span>{it.description}</span>
      </label>
      {amountBox(it)}
      {naFor === it.id ? (
        <select className="na-select" defaultValue="" autoFocus
          onChange={(e) => pickReason(it, e.target.value)}
          onBlur={() => setNaFor(null)}>
          <option value="" disabled>Reason…</option>
          {naReasons(it.description).map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      ) : (
        <button className="na-btn" title="Mark not applicable this year"
          onClick={() => setNaFor(it.id)}>N/A</button>
      )}
      <button className="icon-btn" title="Remove" onClick={() => remove(it.id)}>✕</button>
    </li>
  );

  const renderDone = (it) => (
    <li key={it.id} className="done">
      <label>
        <input type="checkbox" checked onChange={() => toggle(it)} />
        <span>{it.description}</span>
      </label>
      {amountBox(it)}
      <button className="icon-btn" title="Remove" onClick={() => remove(it.id)}>✕</button>
    </li>
  );

  const renderNa = (it) => (
    <li key={it.id} className="na">
      <label><span>{it.description}</span></label>
      <span className="na-tag">N/A — {it.na_reason || "no reason"}</span>
      <button className="na-btn" title="Undo" onClick={() => clearNa(it)}>Undo</button>
      <button className="icon-btn" title="Remove" onClick={() => remove(it.id)}>✕</button>
    </li>
  );

  return (
    <div className="sub-panel">
      <div className="sub-head">
        <h4>Checklist</h4>
        <div className="cl-head-actions">
          <button className="btn small primary" onClick={openRequestList}
            title="Email the client a request list of the documents still needed (the outstanding checklist items)">✉ Request docs</button>
          <button className="btn ghost small" onClick={addStandard}
            title="Add the standard prep questions and reviewer items">Add standard items</button>
          {items.length > 1 && (
            <button className="btn ghost small" onClick={dedupe}
              title="Remove auto-generated document items and merge duplicates">Tidy up</button>
          )}
        </div>
      </div>
      {items.length > 0 && (
        <div className="cl-progress">
          <div className="cl-progress-track">
            <div className="cl-progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="cl-progress-label">
            {pct}% · {todo.length} {todo.length === 1 ? "item" : "items"} left
          </span>
        </div>
      )}
      <SchedASummary clientId={clientId} refreshKey={schedAKey} />
      {items.length === 0 ? (
        <div className="muted small pad">No items yet.</div>
      ) : (
        <>
          {todo.length === 0 ? (
            <div className="muted small pad">Nothing outstanding. 🎉</div>
          ) : (
            <ul className="check-list">{todo.map(renderTodo)}</ul>
          )}
          {naItems.length > 0 && (
            <div className="cl-done">
              <button type="button" className="cl-done-toggle"
                onClick={() => setShowNa((v) => !v)}>
                {showNa ? "▾" : "▸"} Not applicable ({naItems.length})
              </button>
              {showNa && <ul className="check-list">{naItems.map(renderNa)}</ul>}
            </div>
          )}
          {done.length > 0 && (
            <div className="cl-done">
              <button type="button" className="cl-done-toggle"
                onClick={() => setShowDone((v) => !v)}>
                {showDone ? "▾" : "▸"} Completed ({done.length})
              </button>
              {showDone && <ul className="check-list">{done.map(renderDone)}</ul>}
            </div>
          )}
        </>
      )}
      {reviewItems.length > 0 && (
        <div className="cl-review">
          <button type="button" className="cl-done-toggle"
            onClick={() => setShowReview((v) => !v)}>
            {showReview ? "▾" : "▸"} Review — QC ({reviewDone}/{reviewItems.length})
          </button>
          {showReview && (
            <ul className="check-list">
              {reviewItems.map((it) => (
                <li key={it.id} className={it.completed ? "done" : ""}>
                  <label>
                    <input type="checkbox" checked={!!it.completed}
                      onChange={() => toggle(it)} />
                    <span>{it.description}</span>
                  </label>
                  <button className="icon-btn" title="Remove"
                    onClick={() => remove(it.id)}>✕</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <form className="add-item-row" onSubmit={add}>
        <input placeholder="Add checklist item…" value={text}
          onChange={(e) => setText(e.target.value)} />
        <button className="btn ghost small" type="submit">Add</button>
      </form>

      {compose && (
        <div className="modal-overlay" onClick={() => !sending && setCompose(null)}>
          <div className="exp-modal email-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sub-head"><h3>Send document request</h3>
              <button className="icon-btn" title="Close" onClick={() => setCompose(null)}>✕</button>
            </div>
            <label className="email-row">To<input value={compose.to} onChange={(e) => setCompose({ ...compose, to: e.target.value })} /></label>
            <label className="email-row">Cc<input value={compose.cc} placeholder="optional" onChange={(e) => setCompose({ ...compose, cc: e.target.value })} /></label>
            <label className="email-row">Subject<input value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} /></label>
            <label className="email-row col">Message
              <textarea rows={12} value={compose.body} onChange={(e) => setCompose({ ...compose, body: e.target.value })} />
            </label>
            <div className="exp-modal-actions">
              <button className="btn ghost small" onClick={() => setCompose(null)} disabled={sending}>Cancel</button>
              <button className="btn primary small" onClick={sendRequest} disabled={sending}>{sending ? "Sending…" : "Send request"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Group contacts (shared) ---------------- */

function GroupContactsPanel({ groupId, groupName, onContactsChange }) {
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState({ name: "", title: "", email: "", phone: "" });
  const [displayName, setDisplayName] = useState(groupName || "");
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(groupName || "");
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", title: "", email: "", phone: "" });

  useEffect(() => { setDisplayName(groupName || ""); setNameVal(groupName || ""); }, [groupName]);

  const load = useCallback(() => {
    fetch(`${API}/groups/${groupId}/contacts`)
      .then((r) => r.json()).then(setContacts).catch(() => {});
  }, [groupId]);
  useEffect(() => { load(); }, [load]);

  const saveName = async () => {
    const name = nameVal.trim();
    if (!name) return;
    const res = await fetch(`${API}/groups/${groupId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    });
    if (res.ok) { setDisplayName(name); setEditingName(false); }
    else { window.alert((await res.json()).error || "Could not rename"); }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    await fetch(`${API}/groups/${groupId}/contacts`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    setForm({ name: "", title: "", email: "", phone: "" });
    load();
    onContactsChange && onContactsChange();
  };

  const startEdit = (ct) => {
    setEditId(ct.id);
    setEditForm({ name: ct.name || "", title: ct.title || "",
      email: ct.email || "", phone: ct.phone || "" });
  };

  const saveEdit = async () => {
    await fetch(`${API}/contacts/${editId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(editForm),
    });
    setEditId(null);
    load();
    onContactsChange && onContactsChange();
  };

  const remove = async (id) => {
    await fetch(`${API}/contacts/${id}`, { method: "DELETE" });
    load();
    onContactsChange && onContactsChange();
  };

  return (
    <section className="detail-card">
      <div className="sub-head">
        {editingName ? (
          <span className="rename-row">
            <input value={nameVal} onChange={(e) => setNameVal(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") saveName(); }} autoFocus />
            <button className="btn primary small" onClick={saveName}>Save</button>
            <button className="btn ghost small" onClick={() => { setEditingName(false); setNameVal(displayName); }}>Cancel</button>
          </span>
        ) : (
          <h3>Group contacts — {displayName}{" "}
            <button className="btn ghost small" onClick={() => setEditingName(true)}>Rename group</button>
            <span className="muted small"> (shared by everyone in this group)</span>
          </h3>
        )}
      </div>

      {contacts.length === 0 ? (
        <div className="muted small pad">No group contacts yet.</div>
      ) : (
        <table className="data-table compact">
          <thead>
            <tr><th>Name</th><th>Title</th><th>Email</th><th>Phone</th><th></th></tr>
          </thead>
          <tbody>
            {contacts.map((ct) => (editId === ct.id ? (
              <tr key={ct.id}>
                <td><input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></td>
                <td><input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} /></td>
                <td><input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></td>
                <td><input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></td>
                <td className="row-actions">
                  <button className="btn primary small" onClick={saveEdit}>Save</button>
                  <button className="btn ghost small" onClick={() => setEditId(null)}>Cancel</button>
                </td>
              </tr>
            ) : (
              <tr key={ct.id}>
                <td>{ct.name}</td>
                <td>{ct.title || <span className="muted">—</span>}</td>
                <td>
                  {ct.email ? (
                    <span className="contact-line">
                      <span className="contact-val">{ct.email}</span>
                      <button className="copy-btn" title="Copy email" onClick={() => copyText(ct.email)}>⧉</button>
                      <a className="copy-btn" title="New email in Outlook" href={mailtoLink(ct.email)}>✉</a>
                    </span>
                  ) : <span className="muted">—</span>}
                </td>
                <td>
                  {ct.phone ? (
                    <span className="contact-line">
                      <span className="contact-val">{ct.phone}</span>
                      <button className="copy-btn" title="Copy phone" onClick={() => copyText(ct.phone)}>⧉</button>
                      <a className="copy-btn" title="Call" href={`tel:${ct.phone}`}>📞</a>
                      <a className="copy-btn" title="Text" href={`sms:${ct.phone}`}>💬</a>
                      <a className="copy-btn wa" title="WhatsApp" target="_blank" rel="noreferrer"
                        href={`https://wa.me/${waDigits(ct.phone)}`}>WA</a>
                    </span>
                  ) : <span className="muted">—</span>}
                </td>
                <td className="row-actions">
                  <button className="btn ghost small" onClick={() => startEdit(ct)}>Edit</button>
                  <button className="icon-btn" title="Remove" onClick={() => remove(ct.id)}>✕</button>
                </td>
              </tr>
            )))}
          </tbody>
        </table>
      )}
      <form className="contact-add" onSubmit={add}>
        <input placeholder="Name" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input placeholder="Title (e.g. Owner)" value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <input placeholder="Email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input placeholder="Phone" value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <button className="btn ghost small" type="submit">+ Add contact</button>
      </form>
    </section>
  );
}

/* ---------------- Client detail ---------------- */

// 15-minute increments up to 8 hours, labeled "1 hr 15 min" etc.
const QUARTER_HOUR_OPTIONS = Array.from({ length: 32 }, (_, i) => {
  const min = (i + 1) * 15;
  const h = Math.floor(min / 60), m = min % 60;
  const label = [h ? `${h} hr` : "", m ? `${m} min` : ""].filter(Boolean).join(" ");
  return { min, label };
});

// Fill {placeholders} in an email template; unknown tags are left untouched.
function fillTemplate(tpl, vars) {
  return (tpl || "").replace(/\{(\w+)\}/g, (m, k) => (k in vars ? (vars[k] ?? "") : m));
}

// Used only if the server's templates can't be fetched (defaults live server-side).
const FALLBACK_TEMPLATE_BUSINESS =
`Dear {greeting},

Attached please find the following:
{attachments}

Please review the return carefully to confirm there are no omissions or misstatements. I'll send the signature form separately for e-signature; once you've reviewed it, please sign and pay any remaining balance so I can e-file it.

My fee for this return is {fee}. The easiest way to pay is by Zelle to my phone number, 848-482-1709; credit-card payments add a 3.5% processing fee.

Please let me know if you have any questions.

Thank you,`;

const FALLBACK_TEMPLATE_1040 =
`Dear {greeting},

Attached please find the following documents for your review:
{attachments}

I have completed your {year} tax return, and the results are as follows:
{results}
{estimates}
Please review the attached returns carefully to confirm everything looks correct. I will send a separate form for your e-signature; once you have reviewed the documents, please sign and pay the remaining balance so I can file them for you.
{trump}
My fee for preparing this return is {fee}. The easiest way to pay is by Zelle to my phone number, 848-482-1709. Please note that credit-card payments will incur a 3.5% processing fee.

Please let me know if you have any questions.

Thank you,`;

const FALLBACK_TEMPLATE_REQUEST =
`Dear {greeting},

To prepare your {year} tax return, please send us the following:
{checklist}

You can reply to this email with the documents attached, or send them whichever way is easiest. If any item doesn't apply to you this year, just let me know.

Thank you,`;

function ReturnFilesPanel({ clientId, year, taxYearId, refreshKey, onStatus, clientEmail, clientName, clientContact, entityType }) {
  const markReadyToEfile = async () => {
    if (!taxYearId) return;
    if (!window.confirm("Mark the signed signature forms as received and set this return to “Ready to e-file”?")) return;
    await fetch(`${API}/tax_years/${taxYearId}/status`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: "ready_to_file" }),
    });
    if (onStatus) onStatus();
    window.alert("Marked Ready to e-file.");
  };
  const [files, setFiles] = useState([]);
  const [sel, setSel] = useState([]);
  const [compose, setCompose] = useState(null);
  const [sending, setSending] = useState(false);
  const [verify, setVerify] = useState(null);   // 1040 results verify modal
  const [nudging, setNudging] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState(null);  // editable templates
  useEffect(() => {
    fetch(`${API}/email_templates`).then((r) => r.json()).then(setEmailTemplates).catch(() => {});
  }, []);
  const load = useCallback(() => {
    fetch(`${API}/clients/${clientId}/return_files`).then((r) => r.json())
      .then((d) => setFiles(Array.isArray(d) ? d.filter((f) => !year || f.tax_year === year) : []))
      .catch(() => setFiles([]));
  }, [clientId, year]);
  useEffect(() => { load(); setSel([]); }, [load, refreshKey]);
  const LABEL = { letter: "Client Letter", federal: "Federal", state: "State", signature: "Signature", k1: "K-1" };
  const toggle = (id) => setSel((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const allSel = files.length > 0 && sel.length === files.length;
  const del = async (id) => {
    if (!window.confirm("Remove this file from Spoom? (also deletes it from disk)")) return;
    await fetch(`${API}/return_files/${id}`, { method: "DELETE" });
    load();
  };
  const delSelected = async () => {
    if (!sel.length) return;
    if (!window.confirm(`Delete the ${sel.length} selected file(s) from Spoom? (also deletes them from disk)`)) return;
    await Promise.all(sel.map((id) =>
      fetch(`${API}/return_files/${id}`, { method: "DELETE" })));
    setSel([]);
    load();
  };
  // Shared context for either email template: attachment list, greeting, fee.
  const gatherContext = async () => {
    const chosen = files.filter((f) => sel.includes(f.id));
    const list = chosen.map((f) => `• ${(f.name || "").replace(/\.pdf$/i, "")}`).join("\n");
    const partner = (chosen.find((f) => f.partner) || {}).partner;
    // Greeting: an explicit contact/partner wins; otherwise use the taxpayer's
    // first name, and for Married-Filing-Jointly include the spouse too.
    let greet = (clientContact || partner || "").trim().split(/\s+/)[0].replace(/[,;.]+$/, "");
    if (!greet) {
      try {
        const c = await fetch(`${API}/clients/${clientId}`).then((r) => r.json());
        const fn = (c.first_name || "").trim();
        const sfn = (c.spouse_first_name || "").trim();
        if (fn && sfn && (c.filing_status || "").toUpperCase() === "MFJ") greet = `${fn} and ${sfn}`;
        else if (fn) greet = fn;
      } catch (e) { /* fall through to the name-based guess */ }
    }
    if (!greet) {
      greet = (clientName || "Client").trim().split(/\s+/)[0].replace(/[,;.]+$/, "");
    }
    let feeStr = "$[____]";
    try {
      const bd = await fetch(`${API}/clients/${clientId}/bills`).then((r) => r.json());
      const owed = (Array.isArray(bd) ? bd : [])
        .filter((b) => b.status !== "paid")
        .reduce((s, b) => s + (Number(b.amount) - Number(b.paid_amount || 0)), 0);
      if (owed > 0) feeStr = `$${owed.toFixed(2)}`;
    } catch (e) { /* leave blank placeholder */ }
    return { list, greet, feeStr };
  };

  const money = (n) => `$${Math.abs(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  // 1065 (and any non-1040) template — from the user's editable template.
  const openComposeGeneric = async () => {
    const { list, greet, feeStr } = await gatherContext();
    const tpl = (emailTemplates && emailTemplates.template_business) || FALLBACK_TEMPLATE_BUSINESS;
    const body = fillTemplate(tpl, {
      greeting: greet, attachments: list, fee: feeStr, year: String(year),
    });
    setCompose({ to: clientEmail || "", cc: "", subject: `Your ${year} Tax Return — for your review`, body });
  };

  // 1040 template — built from the confirmed per-jurisdiction results.
  const buildBody1040 = ({ list, greet, feeStr }, res) => {
    const results = Array.isArray(res.results) ? res.results : [];
    const estimates = Array.isArray(res.estimates) ? res.estimates : [];
    const resultLines = results.map((r) => {
      const refund = Number(r.refund) || 0;
      const owed = Number(r.balance_due) || 0;
      if (refund > 0) return `• ${r.jurisdiction}: **Refund of ${money(refund)}**`;
      if (owed > 0) return `• ${r.jurisdiction}: **Balance due of ${money(owed)}**`;
      return `• ${r.jurisdiction}: no refund or balance due`;
    }).join("\n");
    let estBlock = "";
    if (estimates.length) {
      const estLines = estimates.map((e) => {
        const q = Number(e.quarters) || 4;
        return `• ${e.jurisdiction}: ${money(e.amount)} per quarter (${q} payments)`;
      }).join("\n");
      estBlock =
`
I have also set up estimated tax payments to roll forward for next year:
${estLines}
`;
    }
    let trumpBlock = "";
    if (res.has_dependent_child) {
      trumpBlock =
`
Also, since you have a dependent child, you might want to look into a "Trump Account." Even though your children don't qualify for the current government seed money, opening one now could still be useful later if the rules change. You can find more info and set one up at [TrumpAccounts.gov](https://www.trumpaccounts.gov/).
`;
    }
    const tpl = (emailTemplates && emailTemplates.template_1040) || FALLBACK_TEMPLATE_1040;
    const body = fillTemplate(tpl, {
      greeting: greet, attachments: list, fee: feeStr, year: String(year),
      results: resultLines, estimates: estBlock, trump: trumpBlock,
    });
    setCompose({ to: clientEmail || "", cc: "", subject: `Your ${year} Tax Return — for your review`, body });
  };

  const openCompose = async () => {
    if ((entityType || "").trim() === "1040") {
      // Extract per-jurisdiction results first, then show the verify modal.
      setVerify({ loading: true });
      try {
        const res = await fetch(`${API}/clients/${clientId}/extract_1040_results`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ year }),
        }).then((r) => r.json());
        if (!res.ok) { window.alert(res.error || "Couldn't read the return results."); setVerify(null); return; }
        const f = res.fields || {};
        setVerify({
          loading: false,
          results: Array.isArray(f.results) ? f.results : [],
          estimates: Array.isArray(f.estimates) ? f.estimates : [],
          has_dependent_child: !!f.has_dependent_child,
        });
      } catch (e) { window.alert("Couldn't read the return results."); setVerify(null); }
      return;
    }
    await openComposeGeneric();
  };

  const confirmVerify = async () => {
    const fields = {
      results: verify.results, estimates: verify.estimates,
      has_dependent_child: verify.has_dependent_child,
    };
    // Persist the confirmed numbers (best-effort), then build the email.
    try {
      await fetch(`${API}/clients/${clientId}/save_1040_results`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ year, fields }),
      });
    } catch (e) { /* non-fatal */ }
    const ctx = await gatherContext();
    setVerify(null);
    buildBody1040(ctx, fields);
  };

  const nudge = async () => {
    setNudging(true);
    try {
      const res = await fetch(`${API}/clients/${clientId}/nudge_return`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: clientEmail || "", year }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.ok) { window.alert(d.message || d.error || "Couldn't open a nudge draft."); }
    } catch (e) { window.alert("Couldn't open a nudge draft."); }
    setNudging(false);
  };
  const send = async () => {
    setSending(true);
    const res = await fetch(`${API}/clients/${clientId}/email_return_outlook`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ to: compose.to, cc: compose.cc, subject: compose.subject, body: compose.body, file_ids: sel }),
    });
    const d = await res.json().catch(() => ({}));
    setSending(false);
    if (!res.ok || !d.ok) { window.alert(d.message || "Couldn’t send the email."); return; }
    setCompose(null); setSel([]);
    window.alert("Email sent.");
  };
  const openInOutlook = async () => {
    setSending(true);
    const res = await fetch(`${API}/clients/${clientId}/email_return_draft`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ to: compose.to, cc: compose.cc, subject: compose.subject, body: compose.body, file_ids: sel }),
    });
    const d = await res.json().catch(() => ({}));
    setSending(false);
    if (!res.ok || !d.ok || !d.webLink) {
      window.alert(d.message || "Couldn’t create the Outlook draft."); return;
    }
    window.open(d.webLink, "_blank", "noopener");   // opens the draft in Outlook on the web
    setCompose(null); setSel([]);
  };

  if (!files.length) {
    return <div className="muted small pad">No split return yet for {year}. Use the <strong>Split return</strong> button at the top to upload and split a completed return.</div>;
  }
  return (
    <>
      <div className="rf-toolbar">
        <label className="muted small">
          <input type="checkbox" checked={allSel}
            onChange={() => setSel(allSel ? [] : files.map((f) => f.id))} /> Select all
        </label>
        <button className="btn small primary" disabled={!sel.length} onClick={openCompose}>
          Send {sel.length || ""} to client →
        </button>
        {sel.length > 0 && (
          <button className="btn small ghost" onClick={delSelected}
            title="Delete the selected files from Spoom and disk">
            🗑 Delete {sel.length} selected
          </button>
        )}
        <button className="btn small ghost" onClick={markReadyToEfile}
          title="Mark the signed 8879 / signature forms received and set the return to Ready to e-file">
          ✔ Signed forms received → Ready to e-file
        </button>
        <button className="btn small ghost" onClick={nudge} disabled={nudging}
          title="Open an Outlook draft that replies to / forwards your last email to this client as a friendly follow-up">
          {nudging ? "Opening…" : "🔔 Nudge"}
        </button>
      </div>
      <ul className="return-files">
        {files.map((f) => (
          <li key={f.id}>
            <input type="checkbox" checked={sel.includes(f.id)} onChange={() => toggle(f.id)}
              title="Select to send to client" />
            <span className={`badge entity rf-${f.kind || "other"}`}>{LABEL[f.kind] || "File"}</span>
            <a className="link-btn rf-name" href={`${API}/return_files/${f.id}/download`} target="_blank" rel="noreferrer">{f.name}</a>
            <button className="icon-btn" title="Remove" onClick={() => del(f.id)}>✕</button>
          </li>
        ))}
      </ul>

      {verify && (
        <div className="modal-overlay" onClick={() => !verify.loading && setVerify(null)}>
          <div className="exp-modal email-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sub-head"><h3>Confirm {year} return results</h3>
              {!verify.loading && <button className="icon-btn" title="Close" onClick={() => setVerify(null)}>✕</button>}
            </div>
            {verify.loading ? (
              <div className="muted small pad">Reading the return…</div>
            ) : (
              <>
                <div className="muted small">Refund or balance due per jurisdiction (edit if the AI got it wrong):</div>
                <table className="mini-table"><tbody>
                  {(verify.results || []).map((r, i) => (
                    <tr key={i}>
                      <td><input value={r.jurisdiction || ""}
                        onChange={(e) => setVerify((v) => { const results = [...v.results]; results[i] = { ...results[i], jurisdiction: e.target.value }; return { ...v, results }; })} /></td>
                      <td>Refund <input type="number" style={{ width: 90 }} value={r.refund ?? 0}
                        onChange={(e) => setVerify((v) => { const results = [...v.results]; results[i] = { ...results[i], refund: e.target.value, balance_due: 0 }; return { ...v, results }; })} /></td>
                      <td>Owe <input type="number" style={{ width: 90 }} value={r.balance_due ?? 0}
                        onChange={(e) => setVerify((v) => { const results = [...v.results]; results[i] = { ...results[i], balance_due: e.target.value, refund: 0 }; return { ...v, results }; })} /></td>
                      <td><button className="icon-btn" title="Remove" onClick={() => setVerify((v) => ({ ...v, results: v.results.filter((_, j) => j !== i) }))}>✕</button></td>
                    </tr>
                  ))}
                </tbody></table>
                <button className="btn small ghost" onClick={() => setVerify((v) => ({ ...v, results: [...(v.results || []), { jurisdiction: "", refund: 0, balance_due: 0 }] }))}>+ Add jurisdiction</button>

                <div className="muted small" style={{ marginTop: 8 }}>Next-year estimated payments (per quarter):</div>
                <table className="mini-table"><tbody>
                  {(verify.estimates || []).map((e0, i) => (
                    <tr key={i}>
                      <td><input value={e0.jurisdiction || ""}
                        onChange={(e) => setVerify((v) => { const estimates = [...v.estimates]; estimates[i] = { ...estimates[i], jurisdiction: e.target.value }; return { ...v, estimates }; })} /></td>
                      <td>Amt <input type="number" style={{ width: 90 }} value={e0.amount ?? 0}
                        onChange={(e) => setVerify((v) => { const estimates = [...v.estimates]; estimates[i] = { ...estimates[i], amount: e.target.value }; return { ...v, estimates }; })} /></td>
                      <td># <input type="number" style={{ width: 50 }} value={e0.quarters ?? 4}
                        onChange={(e) => setVerify((v) => { const estimates = [...v.estimates]; estimates[i] = { ...estimates[i], quarters: e.target.value }; return { ...v, estimates }; })} /></td>
                      <td><button className="icon-btn" title="Remove" onClick={() => setVerify((v) => ({ ...v, estimates: v.estimates.filter((_, j) => j !== i) }))}>✕</button></td>
                    </tr>
                  ))}
                </tbody></table>
                <button className="btn small ghost" onClick={() => setVerify((v) => ({ ...v, estimates: [...(v.estimates || []), { jurisdiction: "", amount: 0, quarters: 4 }] }))}>+ Add estimate</button>

                <label className="muted small" style={{ display: "block", marginTop: 8 }}>
                  <input type="checkbox" checked={!!verify.has_dependent_child}
                    onChange={(e) => setVerify((v) => ({ ...v, has_dependent_child: e.target.checked }))} />
                  {" "}Has a dependent child (include Trump Account paragraph)
                </label>
                <div className="exp-modal-actions">
                  <button className="btn ghost small" onClick={() => setVerify(null)}>Cancel</button>
                  <button className="btn primary small" onClick={confirmVerify}>Confirm → compose email</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {compose && (
        <div className="modal-overlay" onClick={() => !sending && setCompose(null)}>
          <div className="exp-modal email-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sub-head"><h3>Send to client</h3>
              <button className="icon-btn" title="Close" onClick={() => setCompose(null)}>✕</button>
            </div>
            <label className="email-row">To<input value={compose.to} onChange={(e) => setCompose({ ...compose, to: e.target.value })} /></label>
            <label className="email-row">Cc<input value={compose.cc} placeholder="optional" onChange={(e) => setCompose({ ...compose, cc: e.target.value })} /></label>
            <label className="email-row">Subject<input value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} /></label>
            <label className="email-row col">Message
              <textarea rows={12} value={compose.body} onChange={(e) => setCompose({ ...compose, body: e.target.value })} />
            </label>
            <div className="muted small">Attachments: {files.filter((f) => sel.includes(f.id)).map((f) => f.name).join(", ")}</div>
            <div className="exp-modal-actions">
              <button className="btn ghost small" onClick={() => setCompose(null)} disabled={sending}>Cancel</button>
              <button className="btn ghost small" onClick={send} disabled={sending}>{sending ? "Sending…" : "Send now"}</button>
              <button className="btn primary small" onClick={openInOutlook} disabled={sending}>{sending ? "Working…" : "Open in Outlook"}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ---- Multi-entity switcher: shared engine (internal app now, portal later) ---- */
// Maps a tax-year status to one of the 4 client-facing milestones.
const ENTITY_STEPS = ["Document Gathering", "In Prep", "Tax Review", "Ready to Sign"];
function entityStepIndex(status) {
  if (["in_preparation"].includes(status)) return 1;
  if (["review"].includes(status)) return 2;
  if (["ready_to_file", "filed", "not_filing", "completed"].includes(status)) return 3;
  return 0; // not_started, collect_info, or unknown
}

// Reusable switcher. Pass `clientId` (the active record) and `onSwitch(id)`.
// `variant` is reserved so the client portal can restyle the same engine later.
function EntitySwitcher({ clientId, onSwitch, variant = "internal" }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/entity_group/${clientId}`).then((r) => r.json())
      .then((d) => { if (!cancelled) setData(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, [clientId]);

  if (!data || !data.group || (data.entities || []).length < 2) return null; // solo → no switcher
  const active = data.entities.find((e) => e.client_id === clientId) || data.entities[0];
  const step = entityStepIndex(active.status);

  const badge = (e) => {
    const overdue = isOverdue(e.due_date, e.status);
    const chip = overdue ? CHIP_OVERDUE : (STATUS_CHIP[e.status] || STATUS_CHIP.not_started);
    return { bg: chip.bg, bd: chip.bd, fg: chip.fg,
      label: overdue ? "Overdue" : (STATUS_LABELS[e.status] || "—") };
  };

  return (
    <div className={`ent-switch ${variant}`}>
      <button className="ent-switch-head" onClick={() => setOpen(true)}>
        <span className="ent-switch-group">Group: {data.group.name}</span>
        <span className="ent-switch-active">{active.name}
          <span className="ent-switch-caret">▾</span></span>
      </button>

      {/* 4-step milestone timeline for the active entity */}
      <div className="ent-timeline">
        {ENTITY_STEPS.map((label, i) => (
          <div key={label} className={`ent-step${i <= step ? " done" : ""}${i === step ? " current" : ""}`}>
            <span className="ent-step-dot">{i < step ? "✓" : i + 1}</span>
            <span className="ent-step-label">{label}</span>
          </div>
        ))}
      </div>

      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <div className="ent-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="ent-sheet-head">
              <div>
                <div className="ent-sheet-group">{data.group.name}</div>
                <div className="ent-sheet-sub">{data.entities.length} entities</div>
              </div>
              <button className="icon-btn" onClick={() => setOpen(false)}>✕</button>
            </div>
            {data.entities.map((e) => {
              const b = badge(e);
              return (
                <button key={e.client_id}
                  className={`ent-row${e.client_id === clientId ? " active" : ""}`}
                  onClick={() => { setOpen(false); if (e.client_id !== clientId) onSwitch(e.client_id); }}>
                  <span className="ent-row-main">
                    <span className="ent-row-name">{e.name}</span>
                    <span className="ent-row-meta">{e.entity_type}{e.year ? ` · ${e.year}` : ""}</span>
                  </span>
                  <span className="ent-row-badge" style={{ background: b.bg, borderColor: b.bd, color: b.fg }}>{b.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Opens this client's portal exactly as they see it. The URL is their thread's
// private token, so there's nothing to log into.
function PortalLink({ clientId }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let live = true;
    fetch(`${API}/threads`, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((rows) => {
        if (!live || !Array.isArray(rows)) return;
        const t = rows.find((x) => String(x.client_id) === String(clientId) && x.token && !x.revoked);
        if (t) setUrl(`${window.location.origin}/m/${t.token}`);
      })
      .catch(() => {});
    return () => { live = false; };
  }, [clientId]);
  if (!url) return null;
  return (
    <a className="btn ghost" href={url} target="_blank" rel="noopener noreferrer"
      title="Opens this client's portal exactly as they see it">Open portal ↗</a>
  );
}

function ClientDetail({ clientId, onBack, onDataChange, onMessageClient, onSwitchClient }) {
  const notifyChange = () => { if (onDataChange) onDataChange(); };
  const [client, setClient] = useState(null);
  const [edit, setEdit] = useState(null);
  const [newYear, setNewYear] = useState(new Date().getFullYear() - 1);
  const [openYear, setOpenYear] = useState(null);
  const [clBump, setClBump] = useState(0);   // bump to refresh the checklist panel
  const [yearTab, setYearTab] = useState("documents");  // Documents | Checklist | Filed Return
  const [returnsBump, setReturnsBump] = useState(0);    // refresh the Filed Return panel
  const [detailTab, setDetailTab] = useState("client");
  const [moreOpen, setMoreOpen] = useState(false);   // mobile ⋯ action sheet
  const snapRef = useRef(null);                       // camera capture (mobile hero)
  // Reset the modal's scroll to the top whenever the tab changes (or a different
  // client opens), so every tab opens from the top. Runs immediately, on the next
  // frame, and again shortly after — to win against late content (data loading,
  // images) that would otherwise leave it scrolled down.
  useEffect(() => {
    const toTop = () => {
      const m = document.querySelector(".detail-modal");
      if (m) m.scrollTop = 0;
    };
    toTop();
    const raf = requestAnimationFrame(toTop);
    const t = setTimeout(toTop, 80);
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [detailTab, clientId]);
  const [groups, setGroups] = useState([]);
  const [newGroup, setNewGroup] = useState("");
  const [groupContacts, setGroupContacts] = useState([]);

  const loadGroups = useCallback(() => {
    fetch(`${API}/groups`).then((r) => r.json()).then(setGroups).catch(() => {});
  }, []);
  useEffect(() => { loadGroups(); }, [loadGroups]);

  const [timeEntries, setTimeEntries] = useState([]);
  const [timerLabel, setTimerLabel] = useState("");
  const [showReport, setShowReport] = useState(false);
  const [manualLabel, setManualLabel] = useState("");
  const [manualSub, setManualSub] = useState("");
  const [manualNote, setManualNote] = useState("");
  const [manualMin, setManualMin] = useState("");
  const [manualDate, setManualDate] = useState("");
  const loadTimeEntries = useCallback(() => {
    fetch(`${API}/clients/${clientId}/time_entries`)
      .then((r) => r.json()).then(setTimeEntries).catch(() => {});
  }, [clientId]);
  useEffect(() => { loadTimeEntries(); }, [loadTimeEntries]);

  // --- Billing ---
  const [bills, setBills] = useState([]);
  const [incomeAccts, setIncomeAccts] = useState([]);   // income account names
  const [billAcct, setBillAcct] = useState("");          // default for new bills
  const loadBills = useCallback(() => {
    fetch(`${API}/clients/${clientId}/bills`)
      .then((r) => r.json()).then((d) => setBills(Array.isArray(d) ? d : [])).catch(() => {});
  }, [clientId]);
  useEffect(() => { loadBills(); }, [loadBills]);
  useEffect(() => {
    fetch(`${API}/finance/accounts?kind=income`).then((r) => r.json())
      .then((d) => {
        const names = (Array.isArray(d) ? d : []).map((a) => a.name);
        setIncomeAccts(names);
        const preferred = names.find((n) => /service income/i.test(n)) || names[0] || "";
        setBillAcct((cur) => cur || preferred);
      }).catch(() => {});
  }, []);
  const addIncomeAccount = async () => {
    const name = window.prompt("New income account name?");
    if (!name || !name.trim()) return;
    await fetch(`${API}/finance/accounts`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: name.trim(), kind: "income" }),
    });
    setIncomeAccts((p) => (p.includes(name.trim()) ? p : [...p, name.trim()].sort()));
    setBillAcct(name.trim());
  };

  const saveRate = async (v) => {
    setClient((p) => (p ? { ...p, bill_rate: v } : p));
    setEdit((p) => ({ ...p, bill_rate: v }));
    await fetch(`${API}/clients/${clientId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ bill_rate: Number(v) || 0 }),
    });
  };
  const billFromEntry = async (e) => {
    const hours = +(e.seconds / 3600).toFixed(2);
    const rate = Number((edit.bill_rate ?? client.bill_rate) || 0);
    // Choose how to bill this work: a set price (flat) or by the hour.
    const flat = window.confirm(
      "How do you want to bill this?\n\nOK = Flat fee (a set price)\nCancel = Hourly (qty = hours × rate)");
    const billType = flat ? "flat" : "hourly";
    const def = flat ? "" : (hours * rate).toFixed(2);
    const promptMsg = flat
      ? "Flat fee — set price to charge?"
      : `Bill for ${hours}h${rate ? ` @ $${rate}/hr` : ""} — amount to charge?`;
    const amt = window.prompt(promptMsg, def);
    if (amt === null || isNaN(Number(amt)) || amt === "") return;
    const res = await fetch(`${API}/clients/${clientId}/bills`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ description: e.label || "Time", hours,
                             amount: Number(amt), entry_id: e.id,
                             bill_type: billType, sublabel: e.subcategory || null,
                             account: billAcct || null }),
    });
    if (res.status === 409) {
      const d = await res.json().catch(() => ({}));
      window.alert(d.message || "This time can’t be billed because it overlaps billed time.");
      return;
    }
    loadBills(); loadTimeEntries(); notifyChange();
  };
  const togglePaid = async (b) => {
    const paying = b.status !== "paid";
    let body;
    if (paying) {
      let m = window.prompt("How were these funds received?  (Zelle, ACH, CC, Cash, Check, Other)", "Zelle");
      if (m === null) return;               // cancelled
      m = (m || "").trim();
      body = { status: "paid", payment_method: m || null };
    } else {
      body = { status: "unpaid" };
    }
    await fetch(`${API}/bills/${b.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    loadBills(); notifyChange();
  };
  const recordPayment = async (b) => {
    const paid = Number(b.paid_amount || 0);
    const bal = (Number(b.amount || 0) - paid).toFixed(2);
    const amt = window.prompt(
      `Record a payment for this bill.\nAmount: $${Number(b.amount).toFixed(2)} · Already paid: $${paid.toFixed(2)} · Balance: $${bal}\n\nPayment amount?`, bal);
    if (amt === null || isNaN(Number(amt)) || Number(amt) <= 0) return;
    let m = window.prompt("How were these funds received?  (Zelle, ACH, CC, Cash, Check, Other)", "Zelle");
    if (m === null) return;
    await fetch(`${API}/bills/${b.id}/payments`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ amount: Number(amt), method: (m || "").trim() || null }),
    });
    loadBills(); notifyChange();
  };
  const delBill = async (id) => {
    await fetch(`${API}/bills/${id}`, { method: "DELETE" });
    loadBills(); notifyChange();
  };
  const splitReturnFile = async (file) => {
    if (!file) return;
    // Ask which tax year this return is for (source of truth for the filenames).
    const guess = String(new Date().getFullYear() - 1);
    const year = window.prompt("What tax year is this return for? (e.g. 2024)", guess);
    if (year === null) return; // cancelled
    // Let the user choose where to save (native folder dialog on the PC).
    let dest = null;
    try {
      const pf = await fetch(`${API}/pick_folder`, { method: "POST" }).then((r) => r.json());
      dest = pf && pf.path ? pf.path : null;
    } catch (e) { /* fall back to the default Split Returns folder */ }
    const fd = new FormData(); fd.append("file", file);
    if ((year || "").trim()) fd.append("year", year.trim());
    if (dest) fd.append("dest", dest);
    const res = await fetch(`${API}/clients/${clientId}/split_return`, { method: "POST", body: fd });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || !d.ok) {
      window.alert(d.message || "Couldn’t split the return. Make sure it’s the bookmarked PDF from your tax software.");
      return;
    }
    setReturnsBump((b) => b + 1);
    setYearTab("return");
    window.alert(`Split into ${d.files.length} files. They're saved on the "Filed Return" tab (and on disk at:\n${d.folder}).`);
  };
  const sendBillToQb = async (b) => {
    const res = await fetch(`${API}/bills/${b.id}/qbo`, { method: "POST" });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { window.alert(d.message || d.error || "Couldn’t send to QuickBooks."); return; }
    loadBills();
  };
  const unlinkBillFromQb = async (b) => {
    if (!window.confirm("Un-sync this bill from QuickBooks?\n\n(This only removes the link in Spoom so you can re-send it — it does NOT delete the invoice in QuickBooks. Delete that there if you want it gone.)")) return;
    await fetch(`${API}/bills/${b.id}/qbo/unlink`, { method: "POST" });
    loadBills();
  };
  const [selBills, setSelBills] = useState([]);   // bill ids selected to combine
  const toggleSel = (id) => setSelBills((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  // --- New combined bill / write-off basket ---
  const [billModal, setBillModal] = useState(false);
  const [billRows, setBillRows] = useState({});   // entry_id -> {mode:'bill'|'writeoff', amount, bill_type}
  const [billFlats, setBillFlats] = useState([]); // extra flat-fee lines [{description, amount}]
  const [billCombine, setBillCombine] = useState(true);
  const [billBusy, setBillBusy] = useState(false);
  const [billCcMode, setBillCcMode] = useState("none");   // none | surcharge | gross_up
  const [invoiceView, setInvoiceView] = useState(null);   // { billId } | { billIds:[] }
  const rateNow = Number((edit?.bill_rate ?? client?.bill_rate) || 0);
  const unbilledEntries = (timeEntries || []).filter(
    (e) => e.ended_at && !e.bill_id && !e.written_off && (e.seconds || 0) > 0);
  const openBillModal = () => {
    const init = {};
    unbilledEntries.forEach((e) => {
      const hrs = +(e.seconds / 3600).toFixed(2);
      init[e.id] = { mode: "none", bill_type: "hourly",
        amount: (hrs * rateNow ? (hrs * rateNow).toFixed(2) : "") };
    });
    setBillRows(init); setBillFlats(unbilledEntries.length ? [] : [{ description: "", amount: "" }]);
    setBillCombine(true); setBillModal(true);
  };
  const setRow = (id, patch) => setBillRows((p) => ({ ...p, [id]: { ...p[id], ...patch } }));
  const billSelTotal = Object.entries(billRows)
    .filter(([, v]) => v.mode === "bill")
    .reduce((s, [, v]) => s + (Number(v.amount) || 0), 0)
    + billFlats.reduce((s, f) => s + (Number(f.amount) || 0), 0);
  const billItems = () => {
    const items = [];
    unbilledEntries.forEach((e) => {
      const v = billRows[e.id];
      if (v && v.mode === "bill") {
        items.push({ entry_id: e.id, description: e.label || "Time",
          hours: +(e.seconds / 3600).toFixed(2), amount: Number(v.amount) || 0,
          bill_type: v.bill_type, sublabel: e.subcategory || null });
      }
    });
    billFlats.forEach((f) => {
      if ((f.description || "").trim() || Number(f.amount))
        items.push({ description: (f.description || "Fee").trim(), hours: 0,
          amount: Number(f.amount) || 0, bill_type: "flat" });
    });
    return items;
  };
  const submitBill = async () => {
    const items = billItems();
    const writeoff_entry_ids = Object.entries(billRows)
      .filter(([, v]) => v.mode === "writeoff").map(([id]) => Number(id));
    if (!items.length && !writeoff_entry_ids.length) {
      window.alert("Pick at least one entry to bill or write off."); return;
    }
    setBillBusy(true);
    const res = await fetch(`${API}/clients/${clientId}/bills/batch`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ combine: billCombine, items, writeoff_entry_ids,
        account: billAcct || null }),
    });
    setBillBusy(false);
    if (!res.ok) { const d = await res.json().catch(() => ({}));
      window.alert(d.message || d.error || "Couldn’t create the bill."); return; }
    // Apply the chosen card-fee handling to the bill(s) just created.
    if (billCcMode !== "none") {
      const d = await res.json().catch(() => ({}));
      const created = (d && d.bills) || [];
      await Promise.all(created.map((bl) =>
        fetch(`${API}/bills/${bl.id}/cc_mode`, {
          method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cc_mode: billCcMode }),
        }).catch(() => {})));
    }
    setBillModal(false);
    loadBills(); loadTimeEntries(); notifyChange();
  };
  const combineToQb = async () => {
    if (selBills.length < 2) return;
    const res = await fetch(`${API}/qbo/invoice`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ bill_ids: selBills }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { window.alert(d.message || d.error || "Couldn’t send to QuickBooks."); return; }
    setSelBills([]); loadBills();
  };

  const clientTimer = async (action) => {
    await fetch(`${API}/clients/${clientId}/timer`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(action === "start" ? { action, label: timerLabel } : { action }),
    });
    loadTimeEntries();
  };
  const deleteEntry = async (id) => {
    await fetch(`${API}/time_entries/${id}`, { method: "DELETE" });
    loadTimeEntries();
  };

  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState({ title: "", due_date: "", roll_over: false });
  const loadTasks = useCallback(() => {
    fetch(`${API}/clients/${clientId}/tasks`)
      .then((r) => r.json()).then(setTasks).catch(() => {});
  }, [clientId]);
  useEffect(() => { loadTasks(); }, [loadTasks]);
  const addTask = async () => {
    if (!newTask.title.trim()) return;
    await fetch(`${API}/clients/${clientId}/tasks`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newTask),
    });
    setNewTask({ title: "", due_date: "", roll_over: false });
    loadTasks();
  };
  const setRoll = async (t) => {
    await fetch(`${API}/tasks/${t.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ roll_over: !t.roll_over }),
    });
    loadTasks();
  };
  const toggleTask = async (t) => {
    await fetch(`${API}/tasks/${t.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ done: !t.done }),
    });
    loadTasks();
  };
  const setTaskDue = async (t, due_date) => {
    await fetch(`${API}/tasks/${t.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ due_date: due_date || null }),
    });
    loadTasks();
  };
  const delTask = async (id) => {
    await fetch(`${API}/tasks/${id}`, { method: "DELETE" });
    loadTasks();
  };

  // Auto-file: upload to the client, AI routes it to the right tax year.
  const autoRef = useRef(null);
  const [autoBusy, setAutoBusy] = useState(false);
  const [autoDrag, setAutoDrag] = useState(false);
  const [autoMsg, setAutoMsg] = useState(null);
  const autoFile = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setAutoBusy(true); setAutoMsg(null);
    const results = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      const isPdf = /\.pdf$/i.test(file.name);
      const url = isPdf
        ? `${API}/clients/${clientId}/documents/split`
        : `${API}/clients/${clientId}/documents/auto`;
      try {
        const res = await fetch(url, { method: "POST", body: fd });
        const r = await res.json();
        if (!r.ok) { results.push(`${file.name} — ${r.error}`); continue; }
        if (r.results) {
          r.results.forEach((d) => results.push(
            `${d.file} → ${d.year}${d.guessed ? " (year guessed — check it)" : ""}${d.note || ""}`));
        } else {
          results.push(`${file.name} → filed to ${r.year}${r.guessed_year ? " (year guessed — check it)" : ""}`);
        }
      } catch (e) { results.push(`${file.name} — server error`); }
    }
    setAutoBusy(false); setAutoMsg(results); load();
  };
  const postManualTime = async (force) => {
    const res = await fetch(`${API}/clients/${clientId}/time_entries`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ label: manualLabel, minutes: Number(manualMin),
                             subcategory: manualSub || null, entry_note: manualNote || null,
                             date: manualDate, force }),
    });
    if (res.status === 409) {
      const d = await res.json().catch(() => ({}));
      if (d.error === "overlap_billed") {
        window.alert(d.message || "Overlapping billed time is not allowed.");
        return;
      }
      const lines = (d.conflicts || [])
        .map((c) => `• ${c.client_name}: ${Math.round((c.seconds || 0) / 60)} min${c.bill_id ? " (billed)" : ""}`)
        .join("\n");
      if (window.confirm(`${d.message || "This overlaps existing time."}\n\n${lines}\n\nSave anyway?`)) {
        await postManualTime(true);
      }
      return;
    }
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      window.alert(d.error || "Couldn’t add time.");
      return;
    }
    setManualMin(""); setManualDate(""); setManualSub(""); setManualNote("");
    loadTimeEntries(); notifyChange();
  };
  const addManualTime = async () => {
    if (!manualMin) { window.alert("Choose a duration."); return; }
    if (!manualDate) { window.alert("Pick a start date/time so overlaps can be checked."); return; }
    await postManualTime(false);
  };

  const gid = client && client.group_id;
  const loadGroupContacts = useCallback(() => {
    if (!gid) { setGroupContacts([]); return; }
    fetch(`${API}/groups/${gid}/contacts`)
      .then((r) => r.json()).then(setGroupContacts).catch(() => {});
  }, [gid]);
  useEffect(() => { loadGroupContacts(); }, [loadGroupContacts]);

  const load = useCallback(() => {
    fetch(`${API}/clients/${clientId}`)
      .then((r) => r.json())
      .then((data) => {
        if (!data || data.error) { onBack(); return; } // gone/deleted → close
        data.tax_years = data.tax_years || [];
        data.projects = data.projects || [];
        setClient(data);
        setEdit({
          name: data.name || "", entity_type: data.entity_type || "1040",
          first_name: data.first_name || "", last_name: data.last_name || "",
          title: data.title || "",
          spouse_first_name: data.spouse_first_name || "",
          spouse_last_name: data.spouse_last_name || "",
          email: data.email || "", phone: data.phone || "", address: data.address || "",
          city: data.city || "", state: data.state || "", zip: data.zip || "",
          filing_status: data.filing_status || "",
          group_id: data.group_id || "",
          primary_contact_id: data.primary_contact_id || "",
          notes: data.notes || "",
          importance: data.importance || 3,
        });
        if (data.tax_years.length && openYear === null) {
          setOpenYear(data.tax_years[0].id);
        }
      })
      .catch(() => {}); // ignore transient fetch errors (e.g. backend starting)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, openYear]);
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line

  const lookupZip = async (zip) => {
    const z = (zip || "").trim();
    if (!/^\d{5}$/.test(z)) return;
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${z}`);
      if (!res.ok) return;
      const d = await res.json();
      const place = d.places && d.places[0];
      if (place) {
        setEdit((prev) => ({ ...prev,
          city: prev.city || place["place name"],
          state: prev.state || place["state abbreviation"] }));
      }
    } catch (e) { /* ignore lookup failures */ }
  };

  const saveClient = async () => {
    const payload = { ...edit };
    if (edit.entity_type === "1040") {
      delete payload.name;              // derived from first/last on the server
    } else {
      delete payload.first_name;        // businesses use the single name field
      delete payload.last_name;
    }
    // Don't send a blank contact id (let the server keep/auto-pick the contact).
    if (!payload.primary_contact_id) delete payload.primary_contact_id;
    await fetch(`${API}/clients/${clientId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    load();
  };

  const createGroup = async () => {
    const name = newGroup.trim();
    if (!name) return;
    const res = await fetch(`${API}/groups`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    });
    const g = await res.json();
    setNewGroup("");
    loadGroups();
    setEdit((prev) => ({ ...prev, group_id: g.id })); // select the new group
  };

  const addYear = async () => {
    await fetch(`${API}/clients/${clientId}/tax_years`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ year: Number(newYear) }),
    });
    load();
  };

  const deleteYear = async (taxYearId) => {
    if (!window.confirm("Delete this tax year and its documents/checklist?")) return;
    await fetch(`${API}/tax_years/${taxYearId}`, { method: "DELETE" });
    if (openYear === taxYearId) setOpenYear(null);
    load();
  };

  const toggleActive = async () => {
    await fetch(`${API}/clients/${clientId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ active: !client.active }),
    });
    load();
  };

  // Per-client importance (1=low .. 5=critical) — drives the AI Planner.
  // Saved immediately so it's a one-click change near the header.
  const setImportance = async (value) => {
    const imp = Number(value);
    setEdit((prev) => ({ ...prev, importance: imp }));
    setClient((prev) => (prev ? { ...prev, importance: imp } : prev));
    await fetch(`${API}/clients/${clientId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ importance: imp }),
    });
  };

  const buildLeadSheet = async () => {
    try {
      const res = await fetch(`${API}/clients/${clientId}/leadsheet/save`, { method: "POST" });
      const r = await res.json();
      if (r.ok) window.alert(`Lead sheet saved/updated at:\n\n${r.path}\n\nOpen it from that folder — re-clicking updates the same file.`);
      else window.alert(r.error || "Could not build the lead sheet.");
    } catch (e) { window.alert("Could not reach the server."); }
  };

  const deleteClient = async () => {
    const typed = window.prompt(
      "This permanently deletes the client and all their data and cannot be " +
      "undone.\n\nTip: use \"Mark inactive\" instead to archive (reversible).\n\n" +
      `To delete, type the client's name exactly:\n${client.name}`);
    if (typed === null) return;            // cancelled
    if (typed.trim() !== (client.name || "").trim()) {
      window.alert("Name didn't match — nothing was deleted.");
      return;
    }
    await fetch(`${API}/clients/${clientId}`, { method: "DELETE" });
    onBack(); // close the popup and refresh the dashboard
  };

  const setYearStatus = async (taxYearId, status) => {
    await fetch(`${API}/tax_years/${taxYearId}/status`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    load();
  };

  const setYearDue = async (taxYearId, due_date) => {
    await fetch(`${API}/tax_years/${taxYearId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ due_date }),
    });
    load();
  };

  const setYearImportance = async (taxYearId, importance) => {
    await fetch(`${API}/tax_years/${taxYearId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ importance: Number(importance) }),
    });
    load();
  };

  const toggleExtend = async (taxYearId, extended) => {
    await fetch(`${API}/tax_years/${taxYearId}/extend`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ extended }),
    });
    load();
  };

  const amendYear = async (taxYearId) => {
    if (!window.confirm("Send this filed return back for amendment?")) return;
    await fetch(`${API}/tax_years/${taxYearId}/amend`, { method: "POST" });
    load();
  };

  const completeYear = async (taxYearId) => {
    await fetch(`${API}/tax_years/${taxYearId}/complete`, { method: "POST" });
    load();
  };

  const reopenYear = async (taxYearId) => {
    await fetch(`${API}/tax_years/${taxYearId}/reopen`, { method: "POST" });
    load();
  };

  // Other projects (sales tax / bookkeeping / 1099s)
  const [projType, setProjType] = useState("sales_tax");
  const [projYear, setProjYear] = useState(new Date().getFullYear() - 1);
  const [projFreq, setProjFreq] = useState("monthly");
  const [projName, setProjName] = useState("");
  const [showDoneProj, setShowDoneProj] = useState({}); // per-type: show completed

  const addProject = async () => {
    if (projType === "other" && !projName.trim()) {
      window.alert("Enter a name for the Other project."); return;
    }
    await fetch(`${API}/clients/${clientId}/projects`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        project_type: projType, year: Number(projYear),
        frequency: projFreq, name: projName.trim(),
      }),
    });
    setProjName("");
    load();
  };

  const setProjectStatus = async (id, status) => {
    await fetch(`${API}/projects/${id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    load();
  };

  const setProjectDue = async (id, due_date) => {
    await fetch(`${API}/projects/${id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ due_date }),
    });
    load();
  };

  const deleteProject = async (id) => {
    await fetch(`${API}/projects/${id}`, { method: "DELETE" });
    load();
  };

  const completeProject = async (id) => {
    await fetch(`${API}/projects/${id}/complete`, { method: "POST" });
    load();
  };

  const reopenProject = async (id) => {
    await fetch(`${API}/projects/${id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: "in_preparation" }),
    });
    load();
  };

  if (!client || !edit) return <div className="empty">Loading…</div>;

  const projectsByType = (client.projects || []).reduce((acc, p) => {
    (acc[p.project_type] = acc[p.project_type] || []).push(p);
    return acc;
  }, {});

  const runningEntry = timeEntries.find((e) => !e.ended_at);
  const bankedSeconds = timeEntries.reduce(
    (s, e) => s + (e.ended_at ? (e.seconds || 0) : 0), 0);

  const timerOptions = (() => {
    const cutoff = new Date().getFullYear() - 6;   // recent tax years only
    const opts = [
      ...(client.tax_years || [])
        .filter((ty) => Number(ty.year) >= cutoff)
        .map((ty) => `${ty.year} Tax Return`),
      ...(client.projects || []).map(
        (p) => `${PROJECT_LABELS[p.project_type] || p.project_type} — ${p.period_label}`),
    ];
    // General work categories — always available even without a dated project.
    ["Bookkeeping", "Sales Tax", "Payroll", "1099s", "Advisory"]
      .forEach((g) => { if (!opts.includes(g)) opts.push(g); });
    return opts;
  })();

  return (
    <>
      <header className="topbar cd-head">
        <div>
          <button className="link-btn back" onClick={onBack}>✕ Close</button>
          <h1>
            {client.name}
            {!client.active && <span className="badge inactive">Inactive</span>}
          </h1>
        </div>
        <button className="cd-more" title="More actions" onClick={() => setMoreOpen(true)}>⋯</button>
        {moreOpen && <div className="cd-sheet-scrim" onClick={() => setMoreOpen(false)} />}
        <div className={`topbar-actions cd-actions${moreOpen ? " open" : ""}`}
          onClick={(e) => { if (e.target.tagName === "BUTTON" || e.target.tagName === "A") setMoreOpen(false); }}>
          <div className="cd-sheet-grip" />
          <button className={`btn ${client.active ? "ghost" : "primary"}`} onClick={toggleActive}>
            {client.active ? "Mark inactive" : "Mark active"}
          </button>
          <button className="btn ghost" onClick={() => setShowReport(true)}>Year-end report</button>
          <PortalLink clientId={client.id} />
          <button className="btn ghost" onClick={buildLeadSheet}>Update lead sheet</button>
          <a className="btn ghost" href={`${API}/clients/${client.id}/leadsheet.xlsx`}>Download</a>
          <label className="btn ghost" style={{ cursor: "pointer" }} title="Upload a completed return PDF (bookmarked) to split into named files">
            Split return
            <input type="file" accept="application/pdf" style={{ display: "none" }}
              onChange={(e) => { splitReturnFile(e.target.files[0]); e.target.value = ""; }} />
          </label>
          <button className="btn danger" onClick={deleteClient}>Delete client</button>
        </div>
      </header>

      {onSwitchClient && <EntitySwitcher clientId={clientId} onSwitch={onSwitchClient} />}

      {(() => {
        const ini = (client.name || "?").split(/[\s,]+/).filter(Boolean)
          .slice(0, 2).map((w) => w[0]).join("").toUpperCase();
        const entLabel = client.entity_type === "1040" ? "Individual · 1040" : client.entity_type;
        return (
          <section className="cd-hero">
            <div className="cd-hero-top">
              <span className="cd-av">{ini}</span>
              <div style={{ minWidth: 0 }}>
                <div className="cd-name">{client.name}</div>
                <div className="cd-chips">
                  <span className="chip entity">{entLabel}</span>
                  {!client.active && <span className="badge inactive">Inactive</span>}
                </div>
              </div>
            </div>
            <div className="cd-actrow">
              <a className={`cd-act${client.phone ? "" : " off"}`} href={client.phone ? `tel:${client.phone}` : undefined}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" /></svg>Call</a>
              <a className={`cd-act${client.email ? "" : " off"}`} href={client.email ? `mailto:${client.email}` : undefined}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 5L2 7" /></svg>Email</a>
              <button className={`cd-act${onMessageClient ? "" : " off"}`}
                onClick={() => onMessageClient && onMessageClient(client.id)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>Message</button>
              <button className="cd-act" onClick={() => snapRef.current && snapRef.current.click()}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>Snap</button>
              <input ref={snapRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
                onChange={(e) => { autoFile(e.target.files); e.target.value = ""; }} />
            </div>
          </section>
        );
      })()}

      <section className="timer-bar">
        <span className="timer-bar-label">Time on this client</span>
        {runningEntry ? (
          <span className="muted">Working on: {runningEntry.label || "General"}</span>
        ) : (
          <select value={timerLabel} onChange={(e) => setTimerLabel(e.target.value)}>
            <option value="">General</option>
            {timerOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        )}
        <Timer seconds={bankedSeconds} startedAt={runningEntry && runningEntry.started_at}
          onStart={() => clientTimer("start")} onStop={() => clientTimer("stop")} />
      </section>

      <nav className="tabs detail-tabs">
        {[["client", "Client"], ["years", "Tax Years & Docs"],
          ["projects", "Projects"], ["time", "Time"], ["history", "History"]].map(([k, label]) => (
          <button key={k} className={`tab${detailTab === k ? " active" : ""}`}
            onClick={() => setDetailTab(k)}>{label}</button>
        ))}
      </nav>

      {detailTab === "client" && (<>
      <section className="detail-card">
        <h3>Auto-file documents</h3>
        <p className="muted small">Drop documents here — Spoom reads each one and files it under the correct tax year automatically (creating the year if needed).</p>
        <input type="file" multiple ref={autoRef} style={{ display: "none" }}
          onChange={(e) => { autoFile(e.target.files); e.target.value = ""; }} />
        <div className={`dropzone${autoDrag ? " over" : ""}`}
          onClick={() => autoRef.current.click()}
          onDragOver={(e) => { e.preventDefault(); setAutoDrag(true); }}
          onDragLeave={() => setAutoDrag(false)}
          onDrop={(e) => { e.preventDefault(); setAutoDrag(false); autoFile(e.dataTransfer.files); }}>
          {autoBusy ? "Reading & filing…" : "Drag files here, or click to choose (several at once is fine)"}
        </div>
        {autoMsg && (
          <ul className="doc-list">
            {autoMsg.map((m, i) => <li key={i} className="small">{m}</li>)}
          </ul>
        )}
      </section>

      <section className="detail-card">
        <h3>Client info</h3>
        <div className="form-grid">
          {edit.entity_type === "1040" ? (
            <>
              <label>First name
                <input value={edit.first_name}
                  onChange={(e) => setEdit({ ...edit, first_name: e.target.value })} />
              </label>
              <label>Last name
                <input value={edit.last_name}
                  onChange={(e) => setEdit({ ...edit, last_name: e.target.value })} />
              </label>
            </>
          ) : (
            <label>Business name
              <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
            </label>
          )}
          <label>Entity type
            <select value={edit.entity_type}
              onChange={(e) => setEdit({ ...edit, entity_type: e.target.value })}>
              {ENTITY_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </label>
          <label>Title
            <input placeholder="Mr / Mrs / Dr" value={edit.title}
              onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
          </label>
          {edit.entity_type === "1040" && (
            <>
              <label>Spouse first name
                <input value={edit.spouse_first_name}
                  onChange={(e) => setEdit({ ...edit, spouse_first_name: e.target.value })} />
              </label>
              <label>Spouse last name
                <input placeholder="only if different" value={edit.spouse_last_name}
                  onChange={(e) => setEdit({ ...edit, spouse_last_name: e.target.value })} />
              </label>
              <label>Filing status
                <select value={edit.filing_status || ""}
                  onChange={(e) => setEdit({ ...edit, filing_status: e.target.value })}>
                  <option value="">— select —</option>
                  <option value="Single">Single</option>
                  <option value="MFJ">Married filing jointly</option>
                  <option value="MFS">Married filing separately</option>
                  <option value="HoH">Head of household</option>
                  <option value="QW">Qualifying surviving spouse</option>
                </select>
              </label>
            </>
          )}
          <label>Email
            <input value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} />
          </label>
          <label>Phone
            <input value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} />
          </label>
          <label className="full">Street address
            <input value={edit.address} onChange={(e) => setEdit({ ...edit, address: e.target.value })} />
          </label>
          <label>City
            <input value={edit.city} onChange={(e) => setEdit({ ...edit, city: e.target.value })} />
          </label>
          <label>State
            <input value={edit.state} onChange={(e) => setEdit({ ...edit, state: e.target.value })} />
          </label>
          <label>Zip
            <input value={edit.zip}
              onChange={(e) => setEdit({ ...edit, zip: e.target.value })}
              onBlur={(e) => lookupZip(e.target.value)} />
          </label>
          <label className="full">Notes
            <textarea className="notes-area" rows="3" value={edit.notes}
              placeholder="Reminders, e.g. waiting on K-1, call about extension…"
              onChange={(e) => setEdit({ ...edit, notes: e.target.value })} />
          </label>
          <label>Group
            <select value={edit.group_id}
              onChange={(e) => setEdit({ ...edit, group_id: e.target.value, primary_contact_id: "" })}>
              <option value="">— No group —</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </label>
          {client.group_id && groupContacts.length > 0 && (
            <label>Contact shown on dashboard
              <select value={edit.primary_contact_id}
                onChange={(e) => setEdit({ ...edit, primary_contact_id: e.target.value })}>
                {groupContacts.map((ct) => (
                  <option key={ct.id} value={ct.id}>
                    {ct.name}{ct.title ? ` (${ct.title})` : ""}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label>New group
            <span className="new-group-row">
              <input placeholder="Type a name…" value={newGroup}
                onChange={(e) => setNewGroup(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createGroup(); } }} />
              <button type="button" className="btn ghost small" onClick={createGroup}>Create</button>
            </span>
          </label>
        </div>
        <div className="modal-actions">
          <button className="btn primary" onClick={saveClient}>Save changes</button>
        </div>
      </section>

      {client.group_id &&
        <GroupContactsPanel groupId={client.group_id} groupName={client.group_name}
          onContactsChange={loadGroupContacts} />}
      </>)}

      {detailTab === "years" && (
      <section className="detail-card">
        <div className="sub-head">
          <h3>Tax years</h3>
          <div className="upload-row">
            <input type="number" className="year-input" value={newYear}
              onChange={(e) => setNewYear(e.target.value)} />
            <button className="btn ghost small" onClick={addYear}>+ Add year</button>
          </div>
        </div>

        {client.tax_years.length === 0 ? (
          <div className="muted small pad">No tax years yet. Add one above.</div>
        ) : (
          client.tax_years.map((ty) => (
            <div key={ty.id} className="year-block">
              <div className="year-row" onClick={() => setOpenYear(openYear === ty.id ? null : ty.id)}>
                <span className="year-label">
                  {openYear === ty.id ? "▾" : "▸"} {ty.year}
                  {ty.amendment && <span className="badge amend">Amended</span>}
                </span>
                <span className="year-status" onClick={(e) => e.stopPropagation()}>
                  <select className="status-select" value={ty.status}
                    onChange={(e) => setYearStatus(ty.id, e.target.value)}>
                    {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                  </select>
                  <span className="due-wrap">
                    <span className="due-label">Due</span>
                    <input type="date" className={isOverdue(ty.due_date, ty.status) ? "overdue-input" : ""}
                      value={toDateInput(ty.due_date)}
                      onChange={(e) => setYearDue(ty.id, e.target.value)} />
                  </span>
                  <label className="ext-toggle">
                    <input type="checkbox" checked={!!ty.extended}
                      onChange={(e) => toggleExtend(ty.id, e.target.checked)} />
                    Extended
                  </label>
                  <span className="due-wrap" title="Priority for THIS year — used by the AI Planner (resets each year)">
                    <span className="due-label">Priority</span>
                    <select className="status-select" value={ty.importance || 3}
                      onChange={(e) => setYearImportance(ty.id, e.target.value)}>
                      <option value={1}>1 — Low</option>
                      <option value={2}>2 — Below</option>
                      <option value={3}>3 — Normal</option>
                      <option value={4}>4 — High</option>
                      <option value={5}>5 — Critical</option>
                    </select>
                  </span>
                  {!ty.completed && (ty.status === "filed" || ty.status === "not_filing") &&
                    <button className="btn primary small" onClick={() => completeYear(ty.id)}>
                      Complete
                    </button>}
                  {ty.completed &&
                    <button className="btn ghost small" onClick={() => reopenYear(ty.id)}>
                      Reopen
                    </button>}
                  {ty.completed && <span className="badge group">Completed</span>}
                  {ty.status === "filed" && !ty.completed &&
                    <button className="btn ghost small" onClick={() => amendYear(ty.id)}>
                      Needs amendment
                    </button>}
                  {ty.completed && ty.completed_at &&
                    <span className="muted tiny">Completed {fmtDay(ty.completed_at)}</span>}
                  {!ty.completed && ty.status_changed_at &&
                    <span className="muted tiny">Updated {fmtDate(ty.status_changed_at)}</span>}
                  <button className="icon-btn" title="Delete year"
                    onClick={() => deleteYear(ty.id)}>✕</button>
                </span>
              </div>
              {openYear === ty.id && (
                <div className="year-detail">
                  <div className="year-subtabs">
                    <button type="button"
                      className={`year-subtab${yearTab === "documents" ? " active" : ""}`}
                      onClick={() => setYearTab("documents")}>Documents</button>
                    <button type="button"
                      className={`year-subtab${yearTab === "checklist" ? " active" : ""}`}
                      onClick={() => setYearTab("checklist")}>Checklist</button>
                    <button type="button"
                      className={`year-subtab${yearTab === "return" ? " active" : ""}`}
                      onClick={() => setYearTab("return")}>Filed Return</button>
                  </div>
                  <div style={{ display: yearTab === "documents" ? "block" : "none" }}>
                    <DocumentsPanel taxYearId={ty.id} year={ty.year} onClientUpdated={load}
                      onChecklistChanged={() => setClBump((b) => b + 1)} />
                  </div>
                  <div style={{ display: yearTab === "checklist" ? "block" : "none" }}>
                    <ChecklistPanel taxYearId={ty.id} refreshKey={clBump}
                      clientId={clientId} year={ty.year}
                      clientEmail={client.email || client.contact_email}
                      clientName={client.name} clientContact={client.contact_name} />
                  </div>
                  <div style={{ display: yearTab === "return" ? "block" : "none" }}>
                    <ReturnFilesPanel clientId={clientId} year={ty.year} taxYearId={ty.id}
                      refreshKey={returnsBump} onStatus={notifyChange}
                      clientEmail={client.email || client.contact_email}
                      clientName={client.name} clientContact={client.contact_name}
                      entityType={client.entity_type} />
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </section>
      )}

      {detailTab === "projects" && (
      <section className="detail-card">
        <div className="sub-head">
          <h3>Other projects</h3>
          <div className="upload-row">
            <select value={projType} onChange={(e) => setProjType(e.target.value)}>
              {Object.entries(PROJECT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {projType === "bookkeeping" && (
              <select value={projFreq} onChange={(e) => setProjFreq(e.target.value)}>
                {BK_FREQUENCIES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            )}
            {projType === "other" && (
              <input placeholder="Name (e.g. Insurance Audit)" value={projName}
                onChange={(e) => setProjName(e.target.value)} />
            )}
            <input type="number" className="year-input" value={projYear}
              onChange={(e) => setProjYear(e.target.value)} />
            <button className="btn ghost small" onClick={addProject}>+ Add</button>
          </div>
        </div>

        {Object.keys(PROJECT_LABELS).map((type) => {
          const rows = projectsByType[type] || [];
          if (rows.length === 0) return null;
          const active = rows.filter((p) => p.status !== "completed");
          const done = rows.filter((p) => p.status === "completed");
          const open = !!showDoneProj[type];
          const projRow = (p) => (
            <tr key={p.id} className={p.status === "completed" ? "row-done" : ""}>
              <td>{p.period_label}</td>
              <td>
                <select className="status-select" value={p.status}
                  onChange={(e) => setProjectStatus(p.id, e.target.value)}>
                  {PROJECT_STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
                {p.status === "completed" && p.completed_at &&
                  <div className="muted tiny">Completed {fmtDay(p.completed_at)}</div>}
              </td>
              <td className={isOverdue(p.due_date, p.status) ? "overdue" : ""}>
                <input type="date" value={toDateInput(p.due_date)}
                  onChange={(e) => setProjectDue(p.id, e.target.value)} />
              </td>
              <td>
                {p.status === "completed" ? (
                  <button className="btn ghost small" onClick={() => reopenProject(p.id)}>Reopen</button>
                ) : (
                  <button className="btn primary small" onClick={() => completeProject(p.id)}>Complete</button>
                )}
              </td>
              <td><button className="icon-btn" title="Remove"
                onClick={() => deleteProject(p.id)}>✕</button></td>
            </tr>
          );
          return (
            <div key={type} className="proj-group">
              <h4>{PROJECT_LABELS[type]}</h4>
              {active.length > 0 && (
                <table className="data-table compact">
                  <thead>
                    <tr><th>Period</th><th>Status</th><th>Due date</th><th></th><th></th></tr>
                  </thead>
                  <tbody>{active.map(projRow)}</tbody>
                </table>
              )}
              {done.length > 0 && (
                <div className="done-projects">
                  <button className="link-btn done-toggle"
                    onClick={() => setShowDoneProj((s) => ({ ...s, [type]: !s[type] }))}>
                    {open ? "▾" : "▸"} {done.length} completed
                  </button>
                  {open && (
                    <table className="data-table compact">
                      <tbody>{done.map(projRow)}</tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {(client.projects || []).length === 0 &&
          <div className="muted small pad">No other projects yet. Add one above.</div>}
      </section>
      )}

      {detailTab === "client" && (
      <section className="detail-card">
        <h3>My deadlines</h3>
        <p className="muted small">Personal deadlines you set — these show in the This Week tab, the Calendar, and the Due/Overdue boxes.</p>
        {tasks.length > 0 && (
          <ul className="check-list">
            {tasks.map((t) => (
              <li key={t.id} className={t.done ? "done" : ""}>
                <label>
                  <input type="checkbox" checked={t.done} onChange={() => toggleTask(t)} />
                  <span>{t.title}</span>
                </label>
                <button className={`na-btn${t.roll_over ? " roll-on" : ""}`}
                  title={t.roll_over ? "Rolls forward to today until done" : "Roll forward until done"}
                  onClick={() => setRoll(t)}>↻</button>
                <input type="date" className={"task-date-input " + (isOverdue(t.due_date, t.done ? "filed" : "") ? "overdue" : "")}
                  value={toDateInput(t.due_date)} title="Change deadline date"
                  onChange={(e) => setTaskDue(t, e.target.value)} />
                <button className="icon-btn" title="Delete" onClick={() => delTask(t.id)}>✕</button>
              </li>
            ))}
          </ul>
        )}
        <div className="manual-time">
          <input placeholder="New deadline (e.g. finish bookkeeping)" value={newTask.title}
            onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") addTask(); }} style={{ flex: 1 }} />
          <input type="date" value={newTask.due_date}
            onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })} />
          <label className="cal-hidedone" title="Keep moving to today until completed">
            <input type="checkbox" checked={!!newTask.roll_over}
              onChange={(e) => setNewTask({ ...newTask, roll_over: e.target.checked })} />
            ↻ Roll over
          </label>
          <button className="btn ghost small" onClick={addTask}>+ Add deadline</button>
        </div>
      </section>
      )}

      {detailTab === "time" && (
      <section className="detail-card">
        <div className="sub-head">
          <h3>Time log</h3>
          <a className="btn ghost small" href={`${API}/clients/${clientId}/time_entries/export.xlsx`}>
            Export to Excel
          </a>
        </div>
        {timeEntries.length === 0 ? (
          <div className="muted small pad">No time recorded yet. Use the timer at the top.</div>
        ) : (
          <table className="data-table compact">
            <thead>
              <tr><th>Date</th><th>Project</th><th>Start</th><th>End</th><th>Duration</th><th></th></tr>
            </thead>
            <tbody>
              {timeEntries.map((e) => (
                <tr key={e.id}>
                  <td>{fmtDay(e.started_at)}</td>
                  <td>{e.label || <span className="muted">General</span>}
                    {e.subcategory ? <span className="muted small"> · {e.subcategory}</span> : null}
                    {e.entry_note ? <div className="muted small" title={e.entry_note}>📝 {e.entry_note}</div> : null}
                  </td>
                  <td>{new Date(e.started_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</td>
                  <td>{e.ended_at
                    ? new Date(e.ended_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
                    : <span className="timer-val running">running</span>}</td>
                  <td>{e.ended_at ? fmtDuration(e.seconds) : "—"}</td>
                  <td>
                    {e.ended_at && <button className="btn ghost small" title="Create a bill from this time"
                      onClick={() => billFromEntry(e)}>Bill</button>}
                    <button className="icon-btn" title="Delete entry"
                      onClick={() => deleteEntry(e.id)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="manual-time time-log-add">
          <span className="muted small">Add time manually:</span>
          <select value={manualLabel} onChange={(e) => setManualLabel(e.target.value)}>
            <option value="">General</option>
            {timerOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <select value={manualSub} onChange={(e) => setManualSub(e.target.value)}
            title="Type of work">
            <option value="">Type…</option>
            {["Consultation", "Preparation", "Review", "Meeting", "Bookkeeping", "Correspondence", "Other"]
              .map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <input type="text" placeholder="Note (optional)" value={manualNote}
            onChange={(e) => setManualNote(e.target.value)} style={{ minWidth: 140 }} />
          <select className="year-input" value={manualMin}
            onChange={(e) => setManualMin(e.target.value)}>
            <option value="">Duration…</option>
            {QUARTER_HOUR_OPTIONS.map((o) => (
              <option key={o.min} value={o.min}>{o.label}</option>
            ))}
          </select>
          <input type="datetime-local" title="Start time (used to detect overlaps)"
            value={manualDate} onChange={(e) => setManualDate(e.target.value)} />
          <button className="btn ghost small" onClick={addManualTime}>+ Add time</button>
        </div>
      </section>
      )}

      {detailTab === "time" && (
      <section className="detail-card">
        <div className="sub-head">
          <h3>Billing</h3>
          <span className="sub-head-right">
            <button className="btn small primary" onClick={openBillModal}
              title="Create a bill — from logged time, a flat fee, or both">
              ＋ New bill
            </button>
            <label className="muted small">Income account
              <select value={billAcct} onChange={(e) => {
                if (e.target.value === "__new__") { addIncomeAccount(); return; }
                setBillAcct(e.target.value);
              }} style={{ marginLeft: 6 }}>
                {incomeAccts.length === 0 && <option value="">Service Income</option>}
                {incomeAccts.map((n) => <option key={n} value={n}>{n}</option>)}
                <option value="__new__">+ New account…</option>
              </select>
            </label>
            <label className="muted small">
              Rate&nbsp;$
              <input type="number" min="0" step="5" className="year-input"
                style={{ width: 70 }} value={(edit.bill_rate ?? client.bill_rate) || 0}
                onChange={(e) => saveRate(e.target.value)} /> /hr
            </label>
          </span>
        </div>
        {selBills.length >= 2 && (
          <div className="muted small pad">
            <button className="btn small primary" onClick={combineToQb}>
              Send {selBills.length} selected as one QuickBooks invoice
            </button>
            <button className="btn small ghost" onClick={() => setSelBills([])} style={{ marginLeft: 6 }}>Clear</button>
          </div>
        )}
        {bills.length === 0 ? (
          <div className="muted small pad">No bills yet. Click “Bill” on a time entry above.</div>
        ) : (
          <table className="data-table compact">
            <thead>
              <tr><th></th><th>Date</th><th>Description</th><th>Hours</th><th>Amount</th><th>Status</th><th>QuickBooks</th></tr>
            </thead>
            <tbody>
              {bills.map((b) => (
                <tr key={b.id} className={b.status === "paid" ? "row-inactive" : ""}>
                  <td>{!b.qb_invoice_id && (
                    <input type="checkbox" checked={selBills.includes(b.id)}
                      onChange={() => toggleSel(b.id)} title="Select to combine into one invoice" />
                  )}</td>
                  <td>{fmtDay(b.created_at)}</td>
                  <td>{b.description || <span className="muted">—</span>}{b.sublabel ? <span className="muted small"> — {b.sublabel}</span> : null}</td>
                  <td>{b.bill_type === "flat" ? <span className="muted">Flat</span> : (Number(b.hours) ? `${Number(b.hours)}h` : "—")}</td>
                  <td>${Number(b.amount).toFixed(2)}</td>
                  <td>
                    {b.status === "paid" ? (
                      <button className="btn small ghost" onClick={() => togglePaid(b)}>✓ Paid</button>
                    ) : (
                      <>
                        <button className="btn small primary" onClick={() => togglePaid(b)}>Mark paid</button>
                        <button className="btn small ghost" style={{ marginLeft: 4 }}
                          title="Record a partial / installment payment"
                          onClick={() => recordPayment(b)}>+ Payment</button>
                        {Number(b.paid_amount) > 0 && (
                          <div className="muted small">
                            Paid ${Number(b.paid_amount).toFixed(2)} · Bal $
                            {(Number(b.amount) - Number(b.paid_amount)).toFixed(2)}
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td className="bill-actions">
                    <button className="btn small ghost" title="View the client-facing invoice"
                      onClick={() => setInvoiceView({ billId: b.id })}>Invoice</button>
                    {b.qb_invoice_id
                      ? <button className="qbo-badge linkish" title="In QuickBooks — click to un-sync"
                          onClick={() => unlinkBillFromQb(b)}>✓ QB</button>
                      : <button className="btn small ghost" title="Send this invoice to QuickBooks"
                          onClick={() => sendBillToQb(b)}>Send to QB</button>}
                    <button className="icon-btn" title="Delete bill"
                      onClick={() => delBill(b.id)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {bills.some((b) => b.status !== "paid") && (
          <div className="muted small pad">
            Outstanding: <strong>${bills.filter((b) => b.status !== "paid")
              .reduce((s, b) => s + (Number(b.amount) - Number(b.paid_amount || 0)), 0).toFixed(2)}</strong>
          </div>
        )}
      </section>
      )}

      {detailTab === "history" && <HistoryTab clientId={clientId} />}

      {billModal && (
        <div className="modal-overlay" onClick={() => !billBusy && setBillModal(false)}>
          <div className="exp-modal" onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 640, width: "94%" }}>
            <div className="sub-head"><h3>New bill — {client.name}</h3>
              <button className="icon-btn" title="Close" onClick={() => setBillModal(false)}>✕</button>
            </div>
            <p className="muted small" style={{ margin: "2px 0 12px" }}>
              Pick the logged time to bill or write off. Bill several projects together,
              or close out entries you won’t charge.
            </p>

            <div className="bill-basket">
              {unbilledEntries.length === 0 && (
                <div className="muted small">No unbilled time — add a flat-fee line below to invoice.</div>
              )}
              {unbilledEntries.map((e) => {
                const v = billRows[e.id] || {};
                const hrs = +(e.seconds / 3600).toFixed(2);
                return (
                  <div key={e.id} className={`bill-line${v.mode === "writeoff" ? " off" : ""}`}>
                    <div className="bill-line-main">
                      <div className="bill-line-desc">{e.label || "Time"}
                        {e.subcategory ? <span className="muted small"> — {e.subcategory}</span> : null}</div>
                      <div className="muted small">{fmtDay(e.started_at)} · {hrs}h{rateNow ? ` @ $${rateNow}/hr` : ""}</div>
                    </div>
                    <div className="bill-line-actions">
                      <div className="fin-toggle bill-mode">
                        {[["bill", "Bill"], ["writeoff", "Write off"], ["none", "Skip"]].map(([m, lbl]) => (
                          <button key={m} className={v.mode === m ? "active" : ""}
                            onClick={() => setRow(e.id, { mode: m })}>{lbl}</button>
                        ))}
                      </div>
                      {v.mode === "bill" && (
                        <span className="bill-amt">$<input type="number" min="0" step="0.01"
                          value={v.amount} onChange={(ev) => setRow(e.id, { amount: ev.target.value })} /></span>
                      )}
                    </div>
                  </div>
                );
              })}

              {billFlats.map((f, i) => (
                <div key={`flat${i}`} className="bill-line">
                  <div className="bill-line-main">
                    <input className="bill-flat-desc" placeholder="Flat fee description (e.g. 2025 return prep)"
                      value={f.description}
                      onChange={(ev) => setBillFlats((p) => p.map((x, j) => j === i ? { ...x, description: ev.target.value } : x))} />
                  </div>
                  <div className="bill-line-actions">
                    <span className="chip" style={{ background: "var(--field-bg)" }}>Flat fee</span>
                    <span className="bill-amt">$<input type="number" min="0" step="0.01" value={f.amount}
                      onChange={(ev) => setBillFlats((p) => p.map((x, j) => j === i ? { ...x, amount: ev.target.value } : x))} /></span>
                    <button className="icon-btn" title="Remove"
                      onClick={() => setBillFlats((p) => p.filter((_, j) => j !== i))}>✕</button>
                  </div>
                </div>
              ))}
            </div>

            <button className="btn ghost small" style={{ marginTop: 10 }}
              onClick={() => setBillFlats((p) => [...p, { description: "", amount: "" }])}>
              ＋ Add flat-fee line
            </button>

            <div className="bill-foot">
              <label className="bill-combine">
                <input type="checkbox" checked={billCombine}
                  onChange={(e) => setBillCombine(e.target.checked)} />
                Combine into one bill
              </label>
              <span className="bill-total">Total to bill: <strong>${billSelTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
            </div>
            <div className="bill-cc-row">
              <span className="filed-toggle-label">If paid by card:</span>
              <div className="fin-toggle bill-mode">
                <button className={billCcMode === "none" ? "active" : ""}
                  onClick={() => setBillCcMode("none")}>No fee</button>
                <button className={billCcMode === "surcharge" ? "active" : ""}
                  onClick={() => setBillCcMode("surcharge")}>Client pays 3.5%</button>
                <button className={billCcMode === "gross_up" ? "active" : ""}
                  onClick={() => setBillCcMode("gross_up")}>Add 3.5% to total</button>
              </div>
            </div>
            <div className="exp-modal-actions">
              <button className="btn ghost small" onClick={() => setBillModal(false)} disabled={billBusy}>Cancel</button>
              <button className="btn primary small" onClick={submitBill} disabled={billBusy}>
                {billBusy ? "Saving…" : "Create bill"}</button>
            </div>
          </div>
        </div>
      )}

      {showReport &&
        <ReportModal clientId={clientId} onClose={() => setShowReport(false)} />}

      {invoiceView &&
        <InvoiceModal view={invoiceView} onClose={() => setInvoiceView(null)}
          onChanged={loadBills} />}
    </>
  );
}

/* ---------------- Year-end report popup ---------------- */

function ReportModal({ clientId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API}/clients/${clientId}/report`)
      .then((r) => r.json()).then(setData).catch(() => {});
  }, [clientId]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
        {!data ? <div className="empty">Loading…</div> : (
          <>
            <header className="topbar">
              <div>
                <h1>Year-end report</h1>
                <p className="subtitle">{data.client_name}</p>
              </div>
              <div className="topbar-actions">
                <a className="btn ghost" href={`${API}/clients/${clientId}/report.xlsx`}>Export to Excel</a>
                <button className="btn ghost" onClick={onClose}>Close</button>
              </div>
            </header>

            <section className="detail-card">
              <h3>Work — total time {fmtDuration(data.total_seconds)}</h3>
              <table className="data-table compact">
                <thead>
                  <tr><th>Type</th><th>Item</th><th>Status</th><th>Completed</th><th>Time</th></tr>
                </thead>
                <tbody>
                  {data.work.map((w, i) => (
                    <tr key={i}>
                      <td>{w.type}</td>
                      <td>{w.item}</td>
                      <td>{STATUS_LABELS[w.status] || w.status}</td>
                      <td>{w.completed_at ? fmtDay(w.completed_at) : <span className="muted">—</span>}</td>
                      <td>{w.seconds ? fmtDuration(w.seconds) : <span className="muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section className="detail-card">
              <h3>Time log</h3>
              {data.entries.length === 0 ? (
                <div className="muted small pad">No time recorded.</div>
              ) : (
                <table className="data-table compact">
                  <thead>
                    <tr><th>Date</th><th>Project</th><th>Duration</th></tr>
                  </thead>
                  <tbody>
                    {data.entries.map((e) => (
                      <tr key={e.id}>
                        <td>{fmtDay(e.started_at)}</td>
                        <td>{e.label || "General"}</td>
                        <td>{e.ended_at ? fmtDuration(e.seconds) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- Projects tab (one type across all clients) ---------------- */

function InvoiceModal({ view, onClose, onChanged }) {
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const url = view.billIds
    ? `${API}/invoice?bill_ids=${view.billIds.join(",")}`
    : `${API}/bills/${view.billId}/invoice`;
  const load = useCallback(() => {
    setInv(null); setErr("");
    fetch(url).then((r) => r.json()).then((d) => {
      if (d.error) setErr(d.message || d.error); else setInv(d);
    }).catch(() => setErr("Couldn’t load the invoice."));
  }, [url]);
  useEffect(() => { load(); }, [load]);

  const money = (n) => "$" + Number(n || 0).toLocaleString(undefined,
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtD = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString(undefined,
    { month: "short", day: "numeric", year: "numeric" }) : "");

  const sendToQb = async () => {
    const res = view.billIds
      ? await fetch(`${API}/qbo/invoice`, { method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bill_ids: view.billIds }) })
      : await fetch(`${API}/bills/${view.billId}/qbo`, { method: "POST" });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { window.alert(d.message || d.error || "Couldn’t send to QuickBooks."); return; }
    load(); if (onChanged) onChanged();
  };
  const refreshLink = async () => {
    const res = await fetch(`${API}/bills/${inv.bill_ids[0]}/qbo/refresh_link`, { method: "POST" });
    const d = await res.json().catch(() => ({}));
    if (d.qb_invoice_link) { load(); }
    else window.alert(d.message || "No pay link available yet.");
  };
  const printInvoice = () => {
    const node = document.getElementById("inv-paper");
    if (!node) { window.print(); return; }
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Invoice ${inv.number}</title>
      <style>body{margin:0;font-family:'IBM Plex Sans',system-ui,sans-serif;background:#fff;padding:24px;}</style>
      </head><body>${node.outerHTML}</body></html>`);
    w.document.close(); w.focus(); setTimeout(() => { w.print(); }, 250);
  };

  const NAVY = "#0f4c75", MUTED = "#6b7482", LINE = "#e6eaf0";
  const [logoOk, setLogoOk] = useState(true);
  const firmInit = inv && (inv.firm.name || "S").trim()[0].toUpperCase();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="exp-modal inv-modal" onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560, width: "94%" }}>
        <div className="sub-head no-print">
          <h3>Invoice</h3>
          <button className="icon-btn" title="Close" onClick={onClose}>✕</button>
        </div>

        {err && <div className="muted small" style={{ padding: "14px 4px", color: "#c0392b" }}>{err}</div>}
        {!inv && !err && <div className="muted small" style={{ padding: "16px 4px" }}>Loading…</div>}

        {inv && (
          <>
            <div id="inv-paper" style={{ background: "#fff", border: `1px solid ${LINE}`,
              borderRadius: 10, padding: "26px 24px", fontVariantNumeric: "tabular-nums",
              boxShadow: "0 6px 20px -10px rgba(15,23,42,0.2)" }}>
              {/* firm header */}
              <div style={{ display: "flex", justifyContent: "space-between",
                alignItems: "flex-start", borderBottom: `2px solid ${NAVY}`, paddingBottom: 14 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  {logoOk
                    ? <img src={`${API}/firm-logo`} alt={inv.firm.name}
                        onError={() => setLogoOk(false)}
                        style={{ height: 42, width: "auto", maxWidth: 210, objectFit: "contain" }} />
                    : <>
                        <div style={{ width: 34, height: 34, borderRadius: 8, background: NAVY,
                          color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                          fontWeight: 700, fontSize: 17 }}>{firmInit}</div>
                        <div>
                          <div style={{ fontSize: 16, fontWeight: 700, color: NAVY }}>{inv.firm.name}</div>
                          {inv.firm.tagline && <div style={{ fontSize: 11, color: MUTED }}>{inv.firm.tagline}</div>}
                        </div>
                      </>}
                </div>
                <div style={{ textAlign: "right", fontSize: 11, color: MUTED, lineHeight: 1.5,
                  whiteSpace: "pre-line" }}>{inv.firm.address}{inv.firm.ein ? `\nEIN ${inv.firm.ein}` : ""}</div>
              </div>

              {/* meta */}
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
                <div>
                  <div style={{ fontSize: 9.5, fontWeight: 700, color: "#98a2b3",
                    textTransform: "uppercase", letterSpacing: "0.06em" }}>Bill to</div>
                  <div style={{ fontSize: 13, fontWeight: 700, marginTop: 4 }}>{inv.bill_to.name}</div>
                  <div style={{ fontSize: 11, color: MUTED }}>{inv.bill_to.entity_type}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: NAVY }}>INVOICE</div>
                  <div style={{ fontSize: 11, color: MUTED, marginTop: 4, lineHeight: 1.7 }}>
                    No. <strong style={{ color: "#1f2733" }}>{inv.number}</strong><br />
                    {inv.issued && <>Issued {fmtD(inv.issued)}<br /></>}
                    {inv.due && <>Due <strong style={{ color: "#1f2733" }}>{fmtD(inv.due)}</strong></>}
                  </div>
                </div>
              </div>

              {/* lines */}
              <div style={{ marginTop: 18 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 40px 70px", gap: 6,
                  paddingBottom: 7, borderBottom: `1px solid ${LINE}`, fontSize: 9.5,
                  fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  <span>Description</span><span style={{ textAlign: "right" }}>Hrs</span>
                  <span style={{ textAlign: "right" }}>Amount</span>
                </div>
                {inv.lines.map((l, i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 40px 70px",
                    gap: 6, padding: "9px 0", borderBottom: "1px solid #f0f3f7", fontSize: 12 }}>
                    <span>{l.description}</span>
                    <span style={{ textAlign: "right", color: MUTED }}>{l.hours != null ? l.hours : "—"}</span>
                    <span style={{ textAlign: "right", fontWeight: 600 }}>{money(l.amount)}</span>
                  </div>
                ))}
                {inv.cc_line && inv.cc_line.in_total && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 40px 70px", gap: 6,
                    padding: "9px 0", borderBottom: "1px solid #f0f3f7", fontSize: 12 }}>
                    <span>{inv.cc_line.description}</span><span />
                    <span style={{ textAlign: "right", fontWeight: 600 }}>{money(inv.cc_line.amount)}</span>
                  </div>
                )}
              </div>

              {/* totals */}
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
                <div style={{ width: "62%" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5,
                    color: MUTED, marginBottom: 6 }}><span>Subtotal</span><span>{money(inv.subtotal)}</span></div>
                  {inv.cc_mode === "surcharge" && (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5,
                      color: MUTED, marginBottom: 6 }}>
                      <span>{inv.cc_line.description}</span><span>{money(inv.cc_line.amount)}</span></div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline",
                    paddingTop: 9, borderTop: `2px solid ${NAVY}` }}>
                    <span style={{ fontSize: 12, fontWeight: 700 }}>
                      {inv.cc_mode === "surcharge" ? "Total (Zelle / check)" : "Total due"}</span>
                    <span style={{ fontSize: 18, fontWeight: 800, color: NAVY }}>{money(inv.total_due)}</span>
                  </div>
                  {inv.cc_mode === "surcharge" && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline",
                      marginTop: 6 }}>
                      <span style={{ fontSize: 11.5, color: MUTED }}>Total if paying by card</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{money(inv.card_total)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* pay note */}
              {inv.firm.pay_note && (
                <div style={{ marginTop: 18, background: "#f3f6f9", borderRadius: 8, padding: 13,
                  fontSize: 10.5, color: MUTED, lineHeight: 1.6 }}>
                  <strong style={{ color: "#1f2733", fontSize: 11 }}>Payment</strong><br />{inv.firm.pay_note}</div>
              )}
              {inv.synced && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center",
                  gap: 6, marginTop: 12, fontSize: 10, color: "#98a2b3" }}>
                  <span style={{ width: 15, height: 15, borderRadius: 4, background: "#2ca01c",
                    color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 8, fontWeight: 800 }}>qb</span>Synced with QuickBooks Online</div>
              )}
            </div>

            <div className="exp-modal-actions no-print" style={{ flexWrap: "wrap" }}>
              <button className="btn ghost small" onClick={printInvoice}>Print / PDF</button>
              {inv.synced ? (
                inv.qb_invoice_link
                  ? <a className="btn primary small" href={inv.qb_invoice_link} target="_blank"
                      rel="noreferrer">Pay {money(inv.card_total)} online</a>
                  : <button className="btn primary small" onClick={refreshLink}>Get pay link</button>
              ) : (
                <button className="btn primary small" onClick={sendToQb}>Send to QuickBooks</button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MessagesTab() {
  const [threads, setThreads] = useState(() => cacheGet("msg_threads_all", []));
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [rec, setRec] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [nt, setNt] = useState({ client_id: "", subject: "", body: "" });
  const [copied, setCopied] = useState(false);
  const [filePick, setFilePick] = useState(false);
  const [attachMenu, setAttachMenu] = useState(false);
  const [clientFiles, setClientFiles] = useState(null); // null = loading
  const [pickSel, setPickSel] = useState([]);
  const [attaching, setAttaching] = useState(false);
  const [linkMgr, setLinkMgr] = useState(false);

  const loadThreads = useCallback(() => {
    fetch(`${API}/threads?filter=${filter}`).then((r) => r.json())
      .then((d) => setThreads(cacheSet(`msg_threads_${filter}`, Array.isArray(d) ? d : []))).catch(() => {});
  }, [filter]);
  const loadThread = useCallback((id) => {
    fetch(`${API}/threads/${id}`).then((r) => r.json()).then(setThread).catch(() => {});
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => {
    const iv = setInterval(() => { loadThreads(); if (openId) loadThread(openId); }, 10000);
    return () => clearInterval(iv);
  }, [loadThreads, loadThread, openId]);
  useEffect(() => { if (openId) loadThread(openId); }, [openId, loadThread]);

  const back = () => { setOpenId(null); setThread(null); loadThreads(); };
  const send = async () => {
    const b = text.trim(); if (!b) return;
    setText("");
    await fetch(`${API}/threads/${openId}/messages`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ body: b }),
    });
    loadThread(openId);
  };
  const [recSecs, setRecSecs] = useState(0);
  const recExtras = useRef({ stream: null, sr: null, transcript: "", timer: null });
  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      const started = Date.now();
      recExtras.current = { stream, sr: null, transcript: "", timer: null };
      // Live transcription via the browser's built-in speech recognition, when
      // available (Chrome / Android). Runs alongside the recording; the text is
      // saved with the note. No external service or API key needed.
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SR) {
        try {
          const sr = new SR();
          sr.continuous = true; sr.interimResults = false; sr.lang = "en-US";
          sr.onresult = (ev) => {
            let t = "";
            for (let i = 0; i < ev.results.length; i++) t += ev.results[i][0].transcript;
            recExtras.current.transcript = t.trim();
          };
          sr.onerror = () => {};
          sr.start();
          recExtras.current.sr = sr;
        } catch { /* speech recognition unavailable */ }
      }
      setRecSecs(0);
      recExtras.current.timer = setInterval(
        () => setRecSecs(Math.round((Date.now() - started) / 1000)), 1000);
      mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      mr.onstop = async () => {
        clearInterval(recExtras.current.timer);
        try { if (recExtras.current.sr) recExtras.current.sr.stop(); } catch { /* ignore */ }
        if (recExtras.current.cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        const blob = new Blob(chunks, { type: "audio/webm" });
        const fd = new FormData();
        fd.append("file", blob, "voice.webm");
        fd.append("duration", String(Math.round((Date.now() - started) / 1000)));
        if (recExtras.current.transcript) fd.append("transcript", recExtras.current.transcript);
        await fetch(`${API}/threads/${openId}/voice`, { method: "POST", body: fd });
        stream.getTracks().forEach((t) => t.stop());
        loadThread(openId);
      };
      mr.start(); setRec(mr);
    } catch (e) { window.alert("Couldn't access the microphone."); }
  };
  const stopRec = () => { if (rec) { recExtras.current.cancelled = false; rec.stop(); setRec(null); } };
  const cancelRec = () => { if (rec) { recExtras.current.cancelled = true; rec.stop(); setRec(null); } };
  const del = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await fetch(`${API}/messages/${id}`, { method: "DELETE" });
    loadThread(openId);
  };
  const attach = async (e) => {
    const fs = Array.from(e.target.files || []);
    e.target.value = "";
    if (!fs.length || !openId) return;
    const fd = new FormData();
    fs.forEach((f) => fd.append("files", f));
    await fetch(`${API}/threads/${openId}/attach`, { method: "POST", body: fd });
    loadThread(openId);
  };
  const openFilePicker = () => {
    if (!thread) return;
    setFilePick(true); setPickSel([]); setClientFiles(null);
    fetch(`${API}/files`).then((r) => r.json())
      .then((d) => setClientFiles((Array.isArray(d) ? d : []).filter((f) => f.client_id === thread.client_id)))
      .catch(() => setClientFiles([]));
  };
  const keyOf = (f) => `${f.category}:${f.download.match(/\/(\d+)\//)?.[1] || f.download}`;
  const togglePick = (f) => {
    const k = keyOf(f);
    setPickSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  };
  const attachExisting = async () => {
    const chosen = (clientFiles || []).filter((f) => pickSel.includes(keyOf(f)));
    if (!chosen.length || !openId) return;
    setAttaching(true);
    const items = chosen.map((f) => ({ category: f.category, id: Number(f.download.match(/\/(\d+)\//)?.[1]) }));
    await fetch(`${API}/threads/${openId}/attach_existing`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    });
    setAttaching(false); setFilePick(false); setPickSel([]);
    loadThread(openId);
  };
  const openNew = () => {
    if (!clients.length) {
      fetch(`${API}/clients?active=all`).then((r) => r.json())
        .then((d) => setClients(Array.isArray(d) ? d : [])).catch(() => {});
    }
    setNewOpen(true);
  };
  const createThread = async () => {
    if (!nt.client_id) { window.alert("Pick a client first."); return; }
    const res = await fetch(`${API}/threads`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client_id: Number(nt.client_id), subject: nt.subject, body: nt.body }),
    });
    const d = await res.json();
    setNewOpen(false); setNt({ client_id: "", subject: "", body: "" });
    loadThreads(); if (d.id) setOpenId(d.id);
  };
  const link = thread ? `${window.location.origin}/m/${thread.token}` : "";
  const copyLink = () => {
    navigator.clipboard.writeText(link);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  const linkAction = async (body) => {
    const r = await fetch(`${API}/threads/${openId}/link`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const d = await r.json().catch(() => null);
    if (d && !d.error) setThread((t) => ({ ...t, ...d }));
  };
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined,
    { month: "short", day: "numeric", year: "numeric" }) : "");
  const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString(undefined,
    { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

  if (openId && thread) {
    return (
      <>
        <header className="topbar">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button className="btn ghost small" onClick={back}>← Inbox</button>
            <div><h1 style={{ fontSize: 20 }}>{thread.client_name}</h1>
              <p className="subtitle">{thread.subject || "Message"}</p></div>
          </div>
          <div className="topbar-actions">
            <button className="btn small" onClick={copyLink} disabled={thread.link_active === false}
              title={thread.link_active === false ? "Link is off — turn it back on to share" : ""}>
              {copied ? "✓ Copied" : "🔗 Copy client link"}</button>
            <button className="btn ghost small" onClick={() => setLinkMgr(true)} title="Manage the client link">
              {thread.link_active === false
                ? <span style={{ color: "#c0392b" }}>● Link off</span>
                : thread.expires_at
                ? <span>● Expires {fmtDate(thread.expires_at)}</span>
                : <span>⚙︎ Link</span>}
            </button>
          </div>
        </header>
        <div className="msg-thread">
          {(thread.messages || []).map((m) => (
            <div key={m.id} className={`msg-row ${m.sender_type === "preparer" ? "me" : "them"}`}>
              <div className="msg-bubble">
                {m.kind === "voice"
                  ? <>{/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                      <audio controls src={`${API}${m.audio_url}`} style={{ maxWidth: "100%" }} />
                      {m.transcript && <div className="msg-tx">{m.transcript}</div>}</>
                  : m.kind === "file"
                  ? <a href={`${API}/messages/${m.id}/audio`} download={m.file_name || "attachment"}
                      style={{ color: "inherit", textDecoration: "none", display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 18 }}>📎</span>
                      <span style={{ textDecoration: "underline", wordBreak: "break-all" }}>{m.file_name || "attachment"}</span>
                      <span style={{ fontSize: 12, opacity: 0.8, whiteSpace: "nowrap" }}>↓</span>
                    </a>
                  : <span>{m.body}</span>}
                <div className="msg-time">{fmtTime(m.created_at)}
                  <button className="msg-del" title="Delete message" onClick={() => del(m.id)}
                    style={{ marginLeft: 8, background: "none", border: "none", color: "inherit",
                      opacity: 0.55, cursor: "pointer", fontSize: 12, padding: 0 }}>✕</button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="wa-composer">
          {rec ? (
            <div className="wa-recording">
              <button className="wa-rec-cancel" title="Cancel" onClick={cancelRec}>🗑</button>
              <span className="wa-rec-dot" />
              <span className="wa-rec-time">{fmtDuration(recSecs)}</span>
              <span className="wa-rec-hint">Recording… slide 🗑 to cancel</span>
              <button className="wa-send" title="Send voice note" onClick={stopRec}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M3 20.5v-6l8-2-8-2v-6l19 8z" /></svg>
              </button>
            </div>
          ) : (
            <>
              <div className="wa-attach-wrap">
                <button className="wa-icon" title="Attach" onClick={() => setAttachMenu((v) => !v)}>
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
                </button>
                {attachMenu && (
                  <>
                    <div className="wa-menu-scrim" onClick={() => setAttachMenu(false)} />
                    <div className="wa-attach-menu">
                      <label className="wa-attach-item">
                        <span className="wa-attach-ic" style={{ background: "#7c5cff" }}>📄</span> New document
                        <input type="file" multiple style={{ display: "none" }}
                          onChange={(e) => { setAttachMenu(false); attach(e); }} />
                      </label>
                      <button className="wa-attach-item" onClick={() => { setAttachMenu(false); openFilePicker(); }}>
                        <span className="wa-attach-ic" style={{ background: "#159aab" }}>🗂️</span> From Spoom files
                      </button>
                    </div>
                  </>
                )}
              </div>
              <textarea className="wa-input" value={text} placeholder="Message" rows={1}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
              {text.trim()
                ? <button className="wa-send" title="Send" onClick={send}>
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M3 20.5v-6l8-2-8-2v-6l19 8z" /></svg>
                  </button>
                : <button className="wa-send wa-mic" title="Record voice note" onClick={startRec}>
                    <svg viewBox="0 0 24 24" width="21" height="21" fill="currentColor"><path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3z" /><path d="M19 11a7 7 0 0 1-14 0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /><path d="M12 18v3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                  </button>}
            </>
          )}
        </div>
        {filePick && (
          <div className="modal-overlay" onClick={() => !attaching && setFilePick(false)}>
            <div className="exp-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520, width: "92%" }}>
              <div className="sub-head"><h3>Attach from {thread.client_name}'s files</h3>
                <button className="icon-btn" title="Close" onClick={() => setFilePick(false)}>✕</button>
              </div>
              <div style={{ maxHeight: "50vh", overflowY: "auto", margin: "4px 0 12px" }}>
                {clientFiles === null
                  ? <div className="muted small" style={{ padding: "16px 4px" }}>Loading…</div>
                  : clientFiles.length === 0
                  ? <div className="muted small" style={{ padding: "16px 4px" }}>No files on file for this client yet.</div>
                  : clientFiles.map((f) => {
                      const k = keyOf(f);
                      return (
                        <label key={k} style={{ display: "flex", alignItems: "center", gap: 10,
                          padding: "9px 6px", borderBottom: "1px solid var(--line, #e6e9ee)", cursor: "pointer" }}>
                          <input type="checkbox" checked={pickSel.includes(k)} onChange={() => togglePick(f)} />
                          <span style={{ fontSize: 18 }}>📄</span>
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ display: "block", wordBreak: "break-all" }}>{f.name}</span>
                            <span className="muted small">{f.category}{f.period ? ` · ${f.period}` : ""}</span>
                          </span>
                        </label>
                      );
                    })}
              </div>
              <div className="exp-modal-actions">
                <button className="btn ghost small" onClick={() => setFilePick(false)} disabled={attaching}>Cancel</button>
                <button className="btn primary small" onClick={attachExisting} disabled={attaching || !pickSel.length}>
                  {attaching ? "Attaching…" : `Attach ${pickSel.length || ""}`.trim()}
                </button>
              </div>
            </div>
          </div>
        )}
        {linkMgr && (
          <div className="modal-overlay" onClick={() => setLinkMgr(false)}>
            <div className="exp-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460, width: "92%" }}>
              <div className="sub-head"><h3>Client link</h3>
                <button className="icon-btn" title="Close" onClick={() => setLinkMgr(false)}>✕</button>
              </div>
              <div style={{ margin: "4px 0 14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 600,
                    color: thread.link_active === false ? "#c0392b" : "#1e824c" }}>
                    {thread.link_active === false
                      ? (thread.revoked ? "● Turned off" : "● Expired")
                      : "● Active"}</span>
                  {thread.expires_at && thread.link_active !== false &&
                    <span className="muted small">· expires {fmtDate(thread.expires_at)}</span>}
                </div>
                <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
                  <input readOnly value={link} onFocus={(e) => e.target.select()}
                    style={{ flex: 1, minWidth: 0, fontSize: 12, padding: "7px 8px",
                      border: "1px solid var(--line, #e6e9ee)", borderRadius: 6,
                      background: thread.link_active === false ? "var(--field-bg)" : "var(--panel)",
                      color: thread.link_active === false ? "var(--muted)" : "inherit" }} />
                  <button className="btn ghost small" onClick={copyLink}
                    disabled={thread.link_active === false}>{copied ? "✓" : "Copy"}</button>
                  {thread.link_active !== false && (
                    <a className="btn ghost small" href={link} target="_blank" rel="noopener noreferrer"
                      title="Opens the portal exactly as the client sees it">Open ↗</a>
                  )}
                </div>

                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Expires</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
                  {[["Never", 0], ["7 days", 7], ["30 days", 30], ["90 days", 90]].map(([lbl, d]) => {
                    const active = d === 0 ? !thread.expires_at
                      : thread.expires_at && Math.abs(
                          (new Date(thread.expires_at) - Date.now()) / 86400000 - d) < 1.5;
                    return (
                      <button key={lbl} className={`btn small ${active ? "primary" : "ghost"}`}
                        onClick={() => linkAction({ action: "expiry", days: d })}>{lbl}</button>
                    );
                  })}
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {thread.revoked
                    ? <button className="btn small" onClick={() => linkAction({ action: "restore" })}>Turn link back on</button>
                    : <button className="btn danger small" onClick={() => {
                        if (window.confirm("Turn off this link? The client will no longer be able to open it.")) linkAction({ action: "revoke" });
                      }}>Turn off link</button>}
                  <button className="btn ghost small" onClick={() => {
                    if (window.confirm("Generate a new link? The current link will stop working immediately.")) linkAction({ action: "regenerate" });
                  }}>Generate new link</button>
                </div>
                <div className="muted small" style={{ marginTop: 12 }}>
                  Turning off or regenerating takes effect immediately for the client.
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <header className="topbar">
        <div><h1>Messages</h1>
          <p className="subtitle">{threads.length} conversation{threads.length === 1 ? "" : "s"}</p></div>
        <div className="topbar-actions">
          <button className="btn primary" onClick={openNew}>+ New message</button>
        </div>
      </header>
      <div className="msg-filters">
        {[["all", "All"], ["unread", "Unread"], ["waiting", "Waiting on you"]].map(([k, l]) => (
          <button key={k} className={`btn small ${filter === k ? "primary" : "ghost"}`}
            onClick={() => setFilter(k)}>{l}</button>
        ))}
      </div>
      {threads.length === 0
        ? <div className="empty">No conversations yet. Start one with “New message”.</div>
        : <div className="msg-inbox">
            {threads.map((t) => (
              <button key={t.id} className={`msg-item${t.unread ? " unread" : ""}`}
                onClick={() => setOpenId(t.id)}>
                <div className="msg-item-top">
                  <span className="msg-item-name">{t.client_name}
                    {t.unread > 0 && <span className="msg-badge">{t.unread}</span>}</span>
                  <span className="msg-item-time">{fmtTime(t.last_message_at)}</span>
                </div>
                {t.subject && <div className="msg-item-sub">{t.subject}</div>}
                <div className="msg-item-prev">{t.preview}</div>
                {t.status === "waiting_you" && <span className="msg-status">Waiting on you</span>}
              </button>
            ))}
          </div>}

      {newOpen && (
        <div className="modal-overlay" onClick={() => setNewOpen(false)}>
          <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
            <header className="topbar"><h1>New message</h1>
              <button className="btn ghost" onClick={() => setNewOpen(false)}>Close</button></header>
            <label className="msg-field">Client
              <select value={nt.client_id} onChange={(e) => setNt({ ...nt, client_id: e.target.value })}>
                <option value="">Choose client…</option>
                {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.client_name}</option>)}
              </select></label>
            <label className="msg-field">Subject
              <input value={nt.subject} onChange={(e) => setNt({ ...nt, subject: e.target.value })}
                placeholder="e.g. Q2 sales tax docs" /></label>
            <label className="msg-field">Message
              <textarea rows={4} value={nt.body} onChange={(e) => setNt({ ...nt, body: e.target.value })}
                placeholder="Type your message…" /></label>
            <div style={{ textAlign: "right", marginTop: 10 }}>
              <button className="btn primary" onClick={createThread}>Start conversation</button></div>
          </div>
        </div>
      )}
    </>
  );
}

function FilesBrowser() {
  const [files, setFiles] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    fetch(`${API}/files`).then((r) => r.json())
      .then((x) => setFiles(Array.isArray(x) ? x : [])).catch(() => setFiles([]));
  }, []);
  if (files === null) return <div className="empty pad">Loading…</div>;

  const clearBroken = async () => {
    if (!window.confirm("Remove list entries whose file is missing from the server? "
      + "The files are already gone — this only clears the dead links.")) return;
    const r = await fetch(`${API}/admin/clear_broken`, { method: "POST" });
    const d = await r.json();
    const n = d.removed ? Object.values(d.removed).reduce((a, b) => a + b, 0) : 0;
    window.alert(n ? `Removed ${n} dead file link(s).` : "No broken entries found.");
    fetch(`${API}/files`).then((x) => x.json())
      .then((x) => setFiles(Array.isArray(x) ? x : [])).catch(() => {});
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : "");
  const ql = q.trim().toLowerCase();
  const filtered = ql
    ? files.filter((f) => (f.name || "").toLowerCase().includes(ql)
        || (f.client_name || "").toLowerCase().includes(ql))
    : files;

  const tree = {};
  filtered.forEach((f) => {
    const c = f.client_name || "—", cat = f.category || "Other", per = f.period || "—";
    tree[c] = tree[c] || {};
    tree[c][cat] = tree[c][cat] || {};
    tree[c][cat][per] = tree[c][cat][per] || [];
    tree[c][cat][per].push(f);
  });

  return (
    <>
      <header className="topbar">
        <div><h1>Files</h1>
          <p className="subtitle">{files.length} uploaded file{files.length === 1 ? "" : "s"}</p></div>
        <div className="topbar-actions">
          <button className="btn ghost" onClick={clearBroken}>Remove missing files</button>
        </div>
      </header>
      <input className="search" placeholder="Search by client or file name…"
        value={q} onChange={(e) => setQ(e.target.value)}
        style={{ maxWidth: 360, marginBottom: 14 }} />
      {Object.keys(tree).length === 0 ? <div className="empty">No files found.</div> : (
        <div className="files-tree">
          {Object.keys(tree).sort().map((client) => (
            <details key={client} className="files-client" open={!!ql}>
              <summary>📁 {client}</summary>
              {Object.keys(tree[client]).sort().map((cat) => (
                <details key={cat} className="files-cat" open={!!ql}>
                  <summary>{cat}</summary>
                  {Object.keys(tree[client][cat]).sort().map((per) => (
                    <div key={per} className="files-period">
                      <div className="files-period-label">{per}</div>
                      {tree[client][cat][per].map((f, i) => (
                        <div key={i} className="files-row">
                          <a href={`${API}${f.download}`} target="_blank" rel="noreferrer">{f.name}</a>
                          <span className="muted small">{fmt(f.at)}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </details>
              ))}
            </details>
          ))}
        </div>
      )}
    </>
  );
}

function SalesTaxReminders({ onClose }) {
  const [items, setItems] = useState(null);
  const [busyAll, setBusyAll] = useState(false);

  const fmtDate = (iso) => iso
    ? new Date(iso + "T00:00:00").toLocaleDateString(undefined,
        { month: "short", day: "numeric", year: "numeric" })
    : "";
  const fill = (t, x) => {
    const reports = (x.reports && x.reports.length)
      ? x.reports.map((r) => `• ${r}`).join("\n")
      : "• your sales report(s)";
    return (t || "")
      .replace(/{greeting}/g, x.client_name)
      .replace(/{period}/g, x.period_label)
      .replace(/{due_date}/g, fmtDate(x.due_date))
      .replace(/{reports}/g, reports);
  };

  useEffect(() => {
    Promise.all([
      fetch(`${API}/sales_tax/reminders`).then((r) => r.json()),
      fetch(`${API}/email_templates`).then((r) => r.json()),
    ]).then(([list, t]) => {
      const template = t.template_st_reminder || "";
      setItems((Array.isArray(list) ? list : []).map((x) => ({
        ...x,
        subject: `NJ sales tax — ${x.period_label} reports needed`,
        body: fill(template, x), status: "",
      })));
    }).catch(() => setItems([])); // eslint-disable-line
  }, []); // eslint-disable-line

  const setItem = (i, patch) =>
    setItems((list) => list.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  const sendOne = async (i) => {
    const it = items[i];
    if (!it.email) { setItem(i, { status: "No email on file" }); return; }
    setItem(i, { status: "Sending…" });
    const res = await fetch(`${API}/sales_tax/reminders/send`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client_id: it.client_id, to: it.email,
                             subject: it.subject, body: it.body }),
    });
    const d = await res.json().catch(() => ({}));
    setItem(i, { status: d.ok ? "✓ Sent" : (d.message || "Failed") });
  };
  const sendAll = async () => {
    setBusyAll(true);
    for (let i = 0; i < items.length; i++) {
      if (items[i].email && items[i].status !== "✓ Sent") await sendOne(i);
    }
    setBusyAll(false);
  };

  if (items === null) return <div className="empty pad">Loading…</div>;

  return (
    <>
      <header className="topbar">
        <div>
          <h1>Sales tax reminders</h1>
          <p className="subtitle">{items.length} client{items.length === 1 ? "" : "s"} with a return due soon</p>
        </div>
        <div className="topbar-actions">
          <button className="btn primary" disabled={busyAll || !items.some((x) => x.email)}
            onClick={sendAll}>Send all</button>
          <button className="btn ghost" onClick={onClose}>Close</button>
        </div>
      </header>
      {items.length === 0 ? (
        <div className="empty">No sales tax returns are due in the next 45 days.</div>
      ) : (
        <div className="st-rem-list">
          {items.map((it, i) => (
            <div key={it.client_id} className="st-rem-card">
              <div className="st-rem-head">
                <div><strong>{it.client_name}</strong>
                  <span className="muted small"> · {it.period_label} · due {fmtDate(it.due_date)}{it.overdue ? " · overdue" : ""}</span></div>
                <div className="st-rem-send">
                  {it.status && <span className="muted small">{it.status}</span>}
                  <button className="btn small" disabled={!it.email || it.status === "Sending…"}
                    onClick={() => sendOne(i)}>Send</button>
                </div>
              </div>
              {it.email
                ? <div className="muted small">To: {it.email}</div>
                : <div className="st-rem-noemail">⚠ No email on file — add one on this client's page to send.</div>}
              <input className="st-rem-subj" value={it.subject}
                onChange={(e) => setItem(i, { subject: e.target.value })} />
              <textarea className="st-rem-body" value={it.body} rows={6}
                onChange={(e) => setItem(i, { body: e.target.value })} />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function SalesTaxWorksheet({ projectId, clientName, onClose }) {
  const [d, setD] = useState(null);
  const [hist, setHist] = useState([]);
  const [customRate, setCustomRate] = useState(false);
  const [email, setEmail] = useState(null);   // filed-return email compose state

  const load = useCallback(() => {
    fetch(`${API}/projects/${projectId}/sales_tax`).then((r) => r.json())
      .then(setD).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (d && d.client_id) {
      fetch(`${API}/clients/${d.client_id}/sales_tax_history`).then((r) => r.json())
        .then((x) => setHist(Array.isArray(x) ? x : [])).catch(() => {});
    }
  }, [d && d.client_id, d && d.gross]); // eslint-disable-line

  if (!d) return <div className="empty pad">Loading…</div>;

  const patchReturn = async (body) => {
    const r = await fetch(`${API}/projects/${projectId}/sales_tax`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    setD(await r.json());
  };
  const patchLine = async (id, body) => {
    const r = await fetch(`${API}/sales_tax_lines/${id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    setD(await r.json());
  };
  const addLine = async () => {
    const r = await fetch(`${API}/projects/${projectId}/sales_tax/lines`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: "", amount: 0 }),
    });
    setD(await r.json());
  };
  const delLine = async (id) => {
    const r = await fetch(`${API}/sales_tax_lines/${id}`, { method: "DELETE" });
    setD(await r.json());
  };
  const setLineLocal = (id, field, val) =>
    setD((p) => ({ ...p, lines: p.lines.map((l) => (l.id === id ? { ...l, [field]: val } : l)) }));

  const bill = async () => {
    const input = window.prompt(
      `Bill ${clientName} for Sales Tax ${d.period_label}.\n\nAmount to charge ($)?`, "");
    if (input == null) return;
    const amount = Math.round(parseFloat(input) * 100) / 100;
    if (!(amount > 0)) { window.alert("Please enter an amount greater than 0."); return; }
    const res = await fetch(`${API}/clients/${d.client_id}/bills`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ description: `Sales Tax ${d.period_label}`, amount, bill_type: "flat" }),
    });
    window.alert(res.ok
      ? `Billed $${amount.toFixed(2)} — it's now in ${clientName}'s Billing section.`
      : "Sorry — couldn't create the bill.");
  };

  const uploadFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    const r = await fetch(`${API}/projects/${projectId}/sales_tax/files`, { method: "POST", body: fd });
    if (r.ok) setD(await r.json());
    else window.alert("Sorry — couldn't upload that file.");
    e.target.value = "";
  };
  const delFile = async (id) => {
    if (!window.confirm("Remove this file?")) return;
    const r = await fetch(`${API}/sales_tax_files/${id}`, { method: "DELETE" });
    if (r.ok) setD(await r.json());
  };
  const openEmail = async () => {
    const bam = await fetch(`${API}/projects/${projectId}/sales_tax/bill_amount`)
      .then((r) => r.json()).catch(() => ({}));
    const feeLine = bam && bam.amount != null
      ? `Our fee for preparing this return is $${Number(bam.amount).toFixed(2)}.`
      : "Our fee for preparing this return is $____.";
    setEmail({
      to: d.client_email || "",
      subject: `Your filed NJ sales tax return — ${d.period_label}`,
      body: `Dear ${clientName},\n\nAttached is your filed New Jersey sales tax return for ${d.period_label}.\n\n${feeLine}\n\nThank you,`,
      fileIds: (d.files || []).map((f) => f.id),
      status: "",
    });
  };
  const toggleEmailFile = (id) => setEmail((e) => ({
    ...e, fileIds: e.fileIds.includes(id) ? e.fileIds.filter((x) => x !== id) : [...e.fileIds, id],
  }));
  const sendFiled = async () => {
    if (!email.to) { setEmail((e) => ({ ...e, status: "Enter an email address." })); return; }
    setEmail((e) => ({ ...e, status: "Sending…" }));
    const res = await fetch(`${API}/projects/${projectId}/sales_tax/email_filed`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ to: email.to, subject: email.subject, body: email.body, file_ids: email.fileIds }),
    });
    const dd = await res.json().catch(() => ({}));
    if (dd.ok) { window.alert(`Sent to ${email.to} with ${dd.attached} file(s) attached.`); setEmail(null); }
    else setEmail((e) => ({ ...e, status: dd.message || "Couldn't send." }));
  };

  const money = (n) => "$" + Number(n || 0).toLocaleString(undefined,
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const marketplace = d.client_type !== "regular";
  const RATES = [["0.06625", "NJ standard — 6.625%"], ["0.033125", "NJ reduced — 3.3125%"]];
  const rateStr = String(d.rate);
  const knownRate = RATES.some(([v]) => v === rateStr);
  const showCustom = customRate || !knownRate;

  return (
    <>
      <header className="topbar">
        <div>
          <h1>Sales tax — {clientName}</h1>
          <p className="subtitle">{d.period_label} · NJ Form ST-50</p>
        </div>
        <div className="topbar-actions">
          <a className="btn" href={d.portal_url} target="_blank" rel="noreferrer">File on NJ portal ↗</a>
          <button className="btn ghost" onClick={onClose}>Close</button>
        </div>
      </header>

      <div className="st-toggle">
        <button className={`btn small ${marketplace ? "primary" : "ghost"}`}
          onClick={() => patchReturn({ client_type: "marketplace" })}>Marketplace seller</button>
        <button className={`btn small ${!marketplace ? "primary" : "ghost"}`}
          onClick={() => patchReturn({ client_type: "regular" })}>Regular seller</button>
        <span className="muted small">{marketplace
          ? "Marketplace collects the tax — all sales non-taxable, $0 due."
          : "You collect the tax — mark taxable lines and they're charged at the rate."}</span>
      </div>

      <h3>Sales sources</h3>
      <table className="data-table">
        <thead><tr><th>Source</th><th>Gross sales</th>{!marketplace && <th>Taxable?</th>}<th></th></tr></thead>
        <tbody>
          {d.lines.length === 0 ? (
            <tr><td colSpan={marketplace ? 3 : 4} className="muted">No sources yet. Click “Add source”.</td></tr>
          ) : d.lines.map((l) => (
            <tr key={l.id}>
              <td><input value={l.name} placeholder="e.g. Amazon"
                onChange={(e) => setLineLocal(l.id, "name", e.target.value)}
                onBlur={(e) => patchLine(l.id, { name: e.target.value })} /></td>
              <td><input type="number" value={l.amount} style={{ width: 140 }}
                onChange={(e) => setLineLocal(l.id, "amount", e.target.value)}
                onBlur={(e) => patchLine(l.id, { amount: e.target.value })} /></td>
              {!marketplace && <td><input type="checkbox" checked={!!l.taxable}
                onChange={(e) => patchLine(l.id, { taxable: e.target.checked })} /></td>}
              <td className="row-actions"><button className="icon-btn" title="Remove source"
                onClick={() => delLine(l.id)}>✕</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="st-addrow">
        <button className="btn ghost small" onClick={addLine}>+ Add source</button>
        <span className="muted small">Saved for this client — carries to next quarter</span>
      </div>

      <h3>ST-50 return values</h3>
      <div className="st-results">
        <div className="st-line"><span>Total gross receipts</span><span>{money(d.gross)}</span></div>
        <div className="st-line"><span>Non-taxable receipts</span><span>{money(d.non_taxable)}</span></div>
        <div className="st-line"><span>Taxable receipts</span><span>{money(d.taxable)}</span></div>
        <div className="st-line"><span>Sales tax rate</span>
          <span>
            <select value={showCustom ? "custom" : rateStr} onChange={(e) => {
              if (e.target.value === "custom") { setCustomRate(true); }
              else { setCustomRate(false); patchReturn({ rate: e.target.value }); }
            }}>
              {RATES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              <option value="custom">Custom…</option>
            </select>
            {showCustom && <input type="number" step="0.0001" value={d.rate} style={{ width: 100, marginLeft: 6 }}
              onChange={(e) => setD((p) => ({ ...p, rate: e.target.value }))}
              onBlur={(e) => patchReturn({ rate: e.target.value })} />}
          </span>
        </div>
        <div className="st-line total"><span>Total tax due</span><span>{money(d.tax_due)}</span></div>
      </div>

      <h3>Filing record</h3>
      <div className="st-filing">
        <label>Confirmation #
          <input value={d.confirmation_number} placeholder="from NJ portal"
            onChange={(e) => setD((p) => ({ ...p, confirmation_number: e.target.value }))}
            onBlur={(e) => patchReturn({ confirmation_number: e.target.value })} /></label>
        <label className="st-filed"><input type="checkbox" checked={!!d.filed_at}
          onChange={(e) => patchReturn({ filed: e.target.checked })} /> Filed</label>
        <button className="btn primary small" onClick={bill}>Bill this return</button>
      </div>

      <h3>Filed return &amp; attachments</h3>
      <div className="st-files">
        {(d.files || []).length === 0
          ? <div className="muted small">No files yet. Upload the filed ST-50 (and any reports).</div>
          : (d.files || []).map((f) => (
            <div key={f.id} className="st-file">
              <a href={`${API}/sales_tax_files/${f.id}/download`} target="_blank" rel="noreferrer">{f.name}</a>
              <button className="icon-btn" title="Remove file" onClick={() => delFile(f.id)}>✕</button>
            </div>
          ))}
        <div className="st-files-actions">
          <label className="btn ghost small st-upload">Upload file
            <input type="file" style={{ display: "none" }} onChange={uploadFile} /></label>
          {(d.files || []).length > 0 &&
            <button className="btn small" onClick={openEmail}>✉ Email filed return</button>}
        </div>
      </div>

      {email && (
        <div className="st-email">
          <div className="st-email-row">
            <label>To <input value={email.to}
              onChange={(e) => setEmail((x) => ({ ...x, to: e.target.value }))} /></label>
          </div>
          <input className="st-rem-subj" value={email.subject}
            onChange={(e) => setEmail((x) => ({ ...x, subject: e.target.value }))} />
          <textarea className="st-rem-body" rows={6} value={email.body}
            onChange={(e) => setEmail((x) => ({ ...x, body: e.target.value }))} />
          <div className="st-email-atts">
            {(d.files || []).map((f) => (
              <label key={f.id} className="muted small">
                <input type="checkbox" checked={email.fileIds.includes(f.id)}
                  onChange={() => toggleEmailFile(f.id)} /> {f.name}</label>
            ))}
          </div>
          <div className="st-email-send">
            {email.status && <span className="muted small">{email.status}</span>}
            <button className="btn ghost small" onClick={() => setEmail(null)}>Cancel</button>
            <button className="btn primary small" onClick={sendFiled}>Send</button>
          </div>
        </div>
      )}

      {hist.length > 0 && (
        <>
          <h3>History</h3>
          <div className="st-history">
            {hist.map((h, i) => (
              <div key={i} className={`st-hist-card${h.period_label === d.period_label ? " current" : ""}`}>
                <div className="muted small">{h.period_label}{h.filed ? " · filed" : ""}</div>
                <div className="st-hist-amt">{money(h.gross)}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

function ProjectsTab({ type, onOpenClient, refreshKey }) {
  const [rows, setRows] = useState(() => cacheGet(`proj_${type}`, []));
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("active");
  const [show, setShow] = useState("open");
  const [onlyDue, setOnlyDue] = useState(true);
  const [quarter, setQuarter] = useState(0);   // 0 = all, 1..4 = Q1..Q4
  const [loading, setLoading] = useState(() => cacheGet(`proj_${type}`, null) === null);
  const [undo, setUndo] = useState(null);
  const [flash, setFlash] = useState("");            // transient success message
  const [lastBillAmt, setLastBillAmt] = useState("");  // remembers the last fee typed
  const [worksheet, setWorksheet] = useState(null);  // open sales-tax worksheet (project row)
  const [remind, setRemind] = useState(false);       // sales-tax reminders modal

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    const p = new URLSearchParams({ type });
    if (activeFilter) p.set("active", activeFilter);
    if (show) p.set("show", show);
    fetch(`${API}/projects?${p.toString()}`)
      .then((r) => r.json())
      .then((d) => setRows(cacheSet(`proj_${type}`, Array.isArray(d) ? d : [])))
      .catch(() => setRows([]))
      .finally(() => { if (!silent) setLoading(false); });
  }, [type, activeFilter, show]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (refreshKey) load(true); }, [refreshKey]); // eslint-disable-line

  const setStatus = async (id, status) => {
    await fetch(`${API}/projects/${id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    load();
  };
  const setDue = async (id, due_date) => {
    await fetch(`${API}/projects/${id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ due_date }),
    });
    load();
  };

  const setCompletedAt = async (id, completed_at) => {
    await fetch(`${API}/projects/${id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ completed_at }),
    });
    load(true);
  };


  const complete = async (p) => {
    const res = await fetch(`${API}/projects/${p.id}/complete`, { method: "POST" });
    const result = await res.json();
    setUndo({
      id: p.id, prevStatus: p.status, label: p.period_label,
      createdNextId: result.created_next_id,
    });
    load();
  };

  const doUndo = async () => {
    if (!undo) return;
    await fetch(`${API}/projects/${undo.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: undo.prevStatus }),
    });
    if (undo.createdNextId) {
      await fetch(`${API}/projects/${undo.createdNextId}`, { method: "DELETE" });
    }
    setUndo(null);
    load();
  };

  const reopen = async (id) => {
    await fetch(`${API}/projects/${id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: "in_preparation" }),
    });
    load();
  };

  // Create a flat-fee bill for one return. The bill lands in the client's
  // Billing section and can be sent to QuickBooks from there.
  const billReturn = async (p) => {
    const label = `${PROJECT_LABELS[type]} ${p.period_label}`.trim();
    const input = window.prompt(
      `Bill ${p.client_name} for ${label}.\n\nAmount to charge ($)?`,
      lastBillAmt || "");
    if (input == null) return;                       // user hit Cancel
    const amount = Math.round(parseFloat(input) * 100) / 100;
    if (!(amount > 0)) { window.alert("Please enter an amount greater than 0."); return; }
    const res = await fetch(`${API}/clients/${p.client_id}/bills`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ description: label, amount, bill_type: "flat" }),
    });
    if (!res.ok) { window.alert("Sorry — couldn't create the bill. Please try again."); return; }
    setLastBillAmt(String(amount));
    setFlash(`Billed $${amount.toFixed(2)} to ${p.client_name} for ${label}. `
      + `It's now in their Billing section (open the client to send it to QuickBooks).`);
    setTimeout(() => setFlash(""), 6000);
  };

  const searched = rows.filter((r) =>
    r.client_name.toLowerCase().includes(search.toLowerCase()));

  // Collapse to just what needs attention: every overdue (delinquent) period
  // plus the single next-due upcoming period, per client. Periods without a
  // due date (e.g. bookkeeping) are left as-is.
  const collapseToDue = (list) => {
    const byClient = {};
    list.forEach((r) => {
      (byClient[r.client_id] = byClient[r.client_id] || []).push(r);
    });
    const out = [];
    Object.values(byClient).forEach((items) => {
      const dated = items.filter((r) => r.due_date && r.status !== "completed");
      const rest = items.filter((r) => !r.due_date || r.status === "completed");
      const overdue = dated.filter((r) => isOverdue(r.due_date, r.status));
      const upcoming = dated
        .filter((r) => !isOverdue(r.due_date, r.status))
        .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
      out.push(...overdue);
      if (upcoming.length) out.push(upcoming[0]);  // next due only
      out.push(...rest);
    });
    return out;
  };

  // Quarter buttons (sales tax) take precedence and show every matching period.
  const quartered = quarter ? searched.filter((r) => r.period_seq === quarter) : searched;
  const shown = quarter
    ? quartered
    : ((onlyDue && show !== "completed") ? collapseToDue(searched) : searched);

  return (
    <>
      <header className="topbar">
        <div>
          <h1>{PROJECT_LABELS[type]}</h1>
          <p className="subtitle">Add periods from a client's detail page (open a client on the Tax Returns tab).</p>
        </div>
        {type === "sales_tax" && (
          <div className="topbar-actions">
            <button className="btn" onClick={() => setRemind(true)}>✉ Send reminders</button>
          </div>
        )}
      </header>

      {(() => {
        // Quick status squares, computed from the open periods currently loaded.
        const open = rows.filter((r) => r.status !== "completed");
        const overdueN = open.filter((r) => isOverdue(r.due_date, r.status)).length;
        const now = new Date();
        const thisMonthN = open.filter((r) => {
          if (!r.due_date) return false;
          const d = new Date(r.due_date);
          return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
        }).length;
        return (
          <section className="metrics">
            <MetricCard label="Open periods" accent="blue" value={open.length} />
            <MetricCard label="Overdue" accent="red" value={overdueN} />
            <MetricCard label="Due this month" accent="amber" value={thisMonthN} />
          </section>
        );
      })()}

      <section className="toolbar">
        <input className="search" placeholder="Search clients…"
          value={search} onChange={(e) => setSearch(e.target.value)} />
        <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
          <option value="all">Active + inactive</option>
        </select>
        <select value={show} onChange={(e) => setShow(e.target.value)}>
          <option value="open">Open</option>
          <option value="completed">Completed</option>
          <option value="all">All</option>
        </select>
        {!quarter && (
          <label className="ext-toggle">
            <input type="checkbox" checked={onlyDue}
              onChange={(e) => setOnlyDue(e.target.checked)} />
            Next due / overdue only
          </label>
        )}
      </section>

      {type === "sales_tax" && (
        <div className="quarter-bar">
          <button className={`btn small ${quarter === 0 ? "primary" : "ghost"}`}
            onClick={() => setQuarter(0)}>All</button>
          {[1, 2, 3, 4].map((q) => (
            <button key={q} className={`btn small ${quarter === q ? "primary" : "ghost"}`}
              onClick={() => setQuarter(q)}>Q{q}</button>
          ))}
        </div>
      )}

      {undo && (
        <div className="banner undo">
          Marked “{undo.label}” complete{undo.createdNextId ? " and rolled to next period" : ""}.
          <button className="link-btn" onClick={doUndo}>Undo</button>
          <button className="link-btn dismiss" onClick={() => setUndo(null)}>Dismiss</button>
        </div>
      )}

      {flash && (
        <div className="banner">
          {flash}
          <button className="link-btn dismiss" onClick={() => setFlash("")}>Dismiss</button>
        </div>
      )}

      <section className="table-wrap">
        {loading ? (
          <div className="empty">Loading…</div>
        ) : shown.length === 0 ? (
          <div className="empty">Nothing here yet.</div>
        ) : (
          <table className="data-table mobile-cards">
            <thead>
              <tr><th>Client</th><th>Period</th><th>Status</th><th>Due date</th>
                <th>Completed</th><th></th></tr>
            </thead>
            <tbody>
              {shown.map((p) => (
                <tr key={p.id} className={p.client_active ? "" : "row-inactive"}>
                  <td className="name-cell" data-label="Client">
                    <button className="link-btn" onClick={() => onOpenClient(p.client_id)}>
                      {p.client_name}
                    </button>
                  </td>
                  <td data-label="Period">{type === "sales_tax"
                    ? <button className="link-btn" title="Open sales tax worksheet"
                        onClick={() => setWorksheet(p)}>{p.period_label}</button>
                    : p.period_label}</td>
                  <td data-label="Status">
                    <select className="status-select" value={p.status}
                      onChange={(e) => setStatus(p.id, e.target.value)}>
                      {PROJECT_STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                    </select>
                  </td>
                  <td data-label="Due date" className={isOverdue(p.due_date, p.status) ? "overdue" : ""}>
                    <input type="date" value={toDateInput(p.due_date)}
                      onChange={(e) => setDue(p.id, e.target.value)} />
                    {isOverdue(p.due_date, p.status) && <div className="tiny">overdue</div>}
                  </td>
                  <td data-label="Completed">
                    {p.status === "completed"
                      ? <input type="date" value={toDateInput(p.completed_at)}
                          onChange={(e) => setCompletedAt(p.id, e.target.value)} />
                      : <span className="muted">—</span>}
                  </td>
                  <td className="row-actions">
                    <button className="btn ghost small" title="Create a bill for this item"
                      onClick={() => billReturn(p)}>Bill</button>
                    {p.status === "completed" ? (
                      <button className="btn ghost small" onClick={() => reopen(p.id)}>Reopen</button>
                    ) : (
                      <button className="btn primary small" onClick={() => complete(p)}>Complete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {worksheet && (
        <div className="modal-overlay" onClick={() => { setWorksheet(null); load(true); }}>
          <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
            <SalesTaxWorksheet projectId={worksheet.id} clientName={worksheet.client_name}
              onClose={() => { setWorksheet(null); load(true); }} />
          </div>
        </div>
      )}

      {remind && (
        <div className="modal-overlay" onClick={() => setRemind(false)}>
          <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
            <SalesTaxReminders onClose={() => setRemind(false)} />
          </div>
        </div>
      )}
    </>
  );
}

/* ---------------- Root ---------------- */

/* ---------------- AI status chip (Phase 3.0) ---------------- */

function AiChip() {
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  useEffect(() => {
    fetch(`${API}/ai/status`).then((r) => r.json()).then(setStatus).catch(() => {});
  }, []);
  const ready = status && status.key && status.library;
  const test = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${API}/ai/test`, { method: "POST" });
      const d = await res.json();
      window.alert(d.ok ? `✅ ${d.reply}` : `Not connected:\n\n${d.error}`);
    } finally { setTesting(false); }
  };
  return (
    <button className={`ai-chip${ready ? " ready" : ""}`} onClick={test} disabled={testing}
      title="Click to test the AI connection">
      <span className="ai-dot" /> {testing ? "Testing…" : ready ? "AI ready" : "AI not set up"}
    </button>
  );
}

/* ---------------- Global search ---------------- */

function readRecentSearches() {
  try {
    const a = JSON.parse(localStorage.getItem("spoom_recent_searches") || "[]");
    // Keep only {id, name} entries (drops the old name-only format gracefully).
    return Array.isArray(a) ? a.filter((x) => x && x.id != null && x.name) : [];
  } catch (e) { return []; }
}

function GlobalSearch({ onOpenClient }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);          // highlighted index
  const [recent, setRecent] = useState(readRecentSearches());
  const inputRef = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      fetch(`${API}/search?q=${encodeURIComponent(q)}`)
        .then((r) => r.json()).then((d) => { setResults(d); setHi(0); }).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  // Ctrl+K (or "/") focuses the search from anywhere.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault(); inputRef.current && inputRef.current.focus();
      } else if (e.key === "/" && !/input|textarea|select/i.test((e.target.tagName || ""))) {
        e.preventDefault(); inputRef.current && inputRef.current.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const choose = (r) => {
    const next = [{ id: r.id, name: r.name },
                  ...recent.filter((x) => x.id !== r.id)].slice(0, 5);
    setRecent(next);
    try { localStorage.setItem("spoom_recent_searches", JSON.stringify(next)); } catch (e) {}
    onOpenClient(r.id); setQ(""); setOpen(false); inputRef.current && inputRef.current.blur();
  };

  const showRecents = open && !q.trim() && recent.length > 0;
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && results[hi]) { choose(results[hi]); }
    else if (e.key === "Escape") { setOpen(false); inputRef.current && inputRef.current.blur(); }
  };

  return (
    <div className="global-search">
      <input ref={inputRef} placeholder="Search clients…  (Ctrl+K)" value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onKeyDown={onKeyDown}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {open && results.length > 0 && (
        <div className="search-results">
          {results.map((r, i) => (
            <button key={r.id} className={i === hi ? "hi" : ""}
              onMouseEnter={() => setHi(i)} onMouseDown={() => choose(r)}>
              {r.name}
            </button>
          ))}
        </div>
      )}
      {showRecents && (
        <div className="search-results">
          <div className="search-recent-label">Recent</div>
          {recent.map((r) => (
            <button key={r.id} onMouseDown={() => choose(r)}>{r.name}</button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- This Week (agenda) ---------------- */

function AgendaTab({ onOpenClient, refreshKey }) {
  const [items, setItems] = useState(() => cacheGet("agenda_7", []));
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(() => cacheGet("agenda_7", null) === null);
  const [edit, setEdit] = useState(null); // { id, title, due_date }

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API}/agenda?days=${days}`)
      .then((r) => r.json()).then((d) => setItems(cacheSet(`agenda_${days}`, d))).catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [days]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (refreshKey) load(); }, [refreshKey]); // eslint-disable-line

  const saveEdit = async () => {
    if (!edit) return;
    await fetch(`${API}/tasks/${edit.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title: edit.title, due_date: edit.due_date || null }),
    });
    setEdit(null);
    load();
  };

  const delDeadline = async (id) => {
    await fetch(`${API}/tasks/${id}`, { method: "DELETE" });
    load();
  };

  const toggleRoll = async (it) => {
    await fetch(`${API}/tasks/${it.task_id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ roll_over: !it.roll_over }),
    });
    load();
  };

  return (
    <>
      <header className="topbar">
        <div>
          <h1>This Week</h1>
          <p className="subtitle">Everything due soon or overdue, across all work.</p>
        </div>
        <div className="topbar-actions">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>Next 7 days</option>
            <option value={14}>Next 14 days</option>
            <option value={30}>Next 30 days</option>
          </select>
        </div>
      </header>
      <section className="table-wrap">
        {loading ? (
          <div className="empty">Loading…</div>
        ) : items.length === 0 ? (
          <div className="empty">Nothing due in this window. 🎉</div>
        ) : (
          <table className="data-table mobile-cards">
            <thead>
              <tr><th>Due</th><th>Client</th><th>Type</th><th>Item</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const isTask = it.type === "task" && it.task_id;
                const editing = edit && isTask && edit.id === it.task_id;
                return (
                <tr key={i}>
                  <td data-label="Due" className={it.overdue ? "overdue" : ""}>
                    {editing ? (
                      <input type="date" value={edit.due_date || ""}
                        onChange={(e) => setEdit({ ...edit, due_date: e.target.value })} />
                    ) : (<>
                      {fmtDay(it.due_date)}
                      {it.overdue && <div className="tiny">overdue</div>}
                    </>)}
                  </td>
                  <td className="name-cell" data-label="Client">
                    <button className="link-btn" onClick={() => onOpenClient(it.client_id)}>{it.client_name}</button>
                  </td>
                  <td data-label="Type">
                    <span className="badge entity">{it.kind}</span>
                    {it.auto_generated && <span className="badge" title="Auto-generated milestone"> auto</span>}
                  </td>
                  <td data-label="Item">
                    {editing ? (
                      <input value={edit.title} style={{ width: "100%" }}
                        onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
                    ) : it.label}
                  </td>
                  <td data-label="Status">{STATUS_LABELS[it.status] || it.status}</td>
                  <td className="row-actions">
                    {isTask && (editing ? (<>
                      <button className="icon-btn" title="Save" onClick={saveEdit}>✓</button>
                      <button className="icon-btn" title="Cancel" onClick={() => setEdit(null)}>✕</button>
                    </>) : (<>
                      <button className="icon-btn" title="Edit date & title"
                        onClick={() => setEdit({ id: it.task_id, title: it.title, due_date: it.due_date })}>✎</button>
                      <button className={`na-btn${it.roll_over ? " roll-on" : ""}`}
                        title={it.roll_over ? "Rolls forward to today until done" : "Roll forward until done"}
                        onClick={() => toggleRoll(it)}>↻</button>
                      <button className="icon-btn" title="Delete" onClick={() => delDeadline(it.task_id)}>✕</button>
                    </>))}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

/* ---------------- Calendar ---------------- */

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

function CalendarTab({ onOpenClient, refreshKey }) {
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [byDate, setByDate] = useState({});
  const [holidays, setHolidays] = useState({});
  const [hebcal, setHebcal] = useState({ days: {}, title: "" });
  const [hideDone, setHideDone] = useState(true);
  const [hideProjects, setHideProjects] = useState(false);
  const [hideDeadlines, setHideDeadlines] = useState(false);
  const [hideJewish, setHideJewish] = useState(false);
  const [hideAll, setHideAll] = useState(false); // blank calendar (hide every item)
  const [typeFilter, setTypeFilter] = useState({}); // legend: when any are on, show ONLY those
  const [showMonthPick, setShowMonthPick] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false); // calendar filter menu
  const [drag, setDrag] = useState(null);   // item being dragged {type, id}
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(0);
  const [genMsg, setGenMsg] = useState("");
  // AI Planner: proposed (not-yet-applied) schedule changes the user reviews.
  const [planning, setPlanning] = useState(false);
  const [planProgress, setPlanProgress] = useState(0);
  const [planMsg, setPlanMsg] = useState("");
  const [planStatus, setPlanStatus] = useState("");
  const [planGuidance, setPlanGuidance] = useState("");
  const [planOpen, setPlanOpen] = useState(false);
  const [planPhase, setPlanPhase] = useState("intro");  // "intro" | "results"
  const [planAiUsed, setPlanAiUsed] = useState(false);
  const [planRows, setPlanRows] = useState([]);   // {task_id,title,client_name,old_date,new_date,reason,_keep}
  const [planApplying, setPlanApplying] = useState(false);

  // Visible range for the current view.
  let rangeStart, rangeEnd, gridDays;
  if (view === "month") {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    rangeStart = addDays(first, -first.getDay());
    // Only as many weeks as the month needs (5 or 6), so it fits on a page.
    const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const lastCell = addDays(last, 6 - last.getDay());
    gridDays = Math.round((lastCell - rangeStart) / 86400000) + 1;
    rangeEnd = addDays(rangeStart, gridDays - 1);
  } else if (view === "week") {
    rangeStart = addDays(cursor, -cursor.getDay());
    gridDays = 7;
    rangeEnd = addDays(rangeStart, 6);
  } else {
    rangeStart = new Date(cursor);
    gridDays = 1;
    rangeEnd = new Date(cursor);
  }

  const load = useCallback(() => {
    fetch(`${API}/calendar?from=${ymd(rangeStart)}&to=${ymd(rangeEnd)}`)
      .then((r) => r.json())
      .then((items) => {
        const map = {};
        items.forEach((it) => {
          const k = toDateInput(it.date);
          (map[k] = map[k] || []).push(it);
        });
        setByDate(map);
      })
      .catch(() => setByDate({}));
  }, [ymd(rangeStart), ymd(rangeEnd)]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (refreshKey) load(); }, [refreshKey]); // eslint-disable-line

  useEffect(() => {
    fetch(`${API}/holidays?from=${ymd(rangeStart)}&to=${ymd(rangeEnd)}`)
      .then((r) => r.json())
      .then((list) => {
        const m = {};
        (list || []).forEach((h) => {
          const k = toDateInput(h.date);
          (m[k] = m[k] || []).push(h);
        });
        setHolidays(m);
      })
      .catch(() => setHolidays({}));
  }, [ymd(rangeStart), ymd(rangeEnd)]); // eslint-disable-line

  useEffect(() => {
    const monthStart = ymd(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
    fetch(`${API}/hebcal?from=${ymd(rangeStart)}&to=${ymd(rangeEnd)}&month=${monthStart}`)
      .then((r) => r.json())
      .then((d) => setHebcal(d && d.days ? d : { days: {}, title: "" }))
      .catch(() => setHebcal({ days: {}, title: "" }));
  }, [ymd(rangeStart), ymd(rangeEnd), cursor]); // eslint-disable-line

  const step = (dir) => {
    if (view === "month") setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + dir, 1));
    else setCursor(addDays(cursor, dir * (view === "week" ? 7 : 1)));
  };

  const todayKey = ymd(new Date());
  const title = view === "month"
    ? cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })
    : view === "week"
      ? `Week of ${rangeStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
      : cursor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  const Holidays = ({ day }) => (
    <>
      {(holidays[day] || [])
        .filter((h) => !(hideJewish && (h.kind === "jewish" || h.kind === "hebrew")))
        .map((h, i) => (
        <div key={i} className={`cal-holiday ${h.kind}`} title={h.name}>{h.name}</div>
      ))}
    </>
  );

  const moveTo = async (dateKey) => {
    if (!drag) return;
    const d = drag; setDrag(null);
    try {
      await fetch(`${API}/calendar/move`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: d.type, id: d.id, date: dateKey }),
      });
    } catch (e) { /* ignore */ }
    load();
  };
  const dropProps = (key) => ({
    onDragOver: (e) => { if (drag) e.preventDefault(); },
    onDrop: () => moveTo(key),
  });

  const generate = async () => {
    setGenerating(true);
    setGenProgress(6);
    const stages = ["Clearing old deadlines…", "Scheduling tax returns…",
      "Ordering returns by group…", "Placing project deadlines…", "Spreading the workload…"];
    setGenMsg(stages[0]);
    let p = 6;
    const iv = setInterval(() => {
      p = Math.min(92, p + Math.random() * 9 + 3);
      setGenProgress(p);
      setGenMsg(stages[Math.min(stages.length - 1, Math.floor(p / 19))]);
    }, 320);
    try {
      const r = await fetch(`${API}/deadlines/generate`, { method: "POST" });
      const d = await r.json();
      clearInterval(iv);
      setGenProgress(100);
      setGenMsg(d.error ? "Couldn't generate" : `Done — created ${d.created} deadline${d.created === 1 ? "" : "s"}`);
      load();
      setTimeout(() => {
        setGenerating(false); setGenProgress(0); setGenMsg("");
        if (d.error) alert(d.error);
      }, 1000);
    } catch (e) {
      clearInterval(iv);
      setGenerating(false); setGenProgress(0); setGenMsg("");
      alert("Could not generate deadlines.");
    }
  };

  // --- AI Planner: fetch proposed schedule changes (preview only) ----------
  const runPlanner = async () => {
    setPlanning(true); setPlanProgress(6);
    const stages = ["Gathering open deadlines…", "Weighing client priority & effort…",
      "Asking the AI to rebalance…", "Spreading out crowded days…"];
    setPlanMsg(stages[0]);
    let p = 6;
    const iv = setInterval(() => {
      p = Math.min(92, p + Math.random() * 8 + 3);
      setPlanProgress(p);
      setPlanMsg(stages[Math.min(stages.length - 1, Math.floor(p / 24))]);
    }, 320);
    try {
      const r = await fetch(`${API}/deadlines/plan`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ guidance: planGuidance }),
      });
      const d = await r.json();
      clearInterval(iv); setPlanProgress(100);
      if (d.error) {
        setPlanning(false); setPlanProgress(0); setPlanMsg("");
        alert(d.error); return;
      }
      const rows = (d.proposals || []).map((row) => ({ ...row, _keep: true }));
      setPlanRows(rows);
      setPlanAiUsed(!!d.ai_used);
      setPlanStatus(d.ai_status || "");
      setTimeout(() => {
        setPlanning(false); setPlanProgress(0); setPlanMsg("");
        setPlanOpen(true);
        setPlanPhase("results");
      }, 300);
    } catch (e) {
      clearInterval(iv);
      setPlanning(false); setPlanProgress(0); setPlanMsg("");
      alert("Could not reach the planner. Is the server running?");
    }
  };

  // Apply only the rows the user kept (each via PUT /tasks/<id> due_date).
  const applyPlan = async () => {
    const keep = planRows.filter((p) => p._keep);
    if (!keep.length) { setPlanOpen(false); return; }
    setPlanApplying(true);
    for (const p of keep) {
      try {
        await fetch(`${API}/tasks/${p.task_id}`, {
          method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ due_date: p.new_date }),
        });
      } catch (e) { /* keep going; report nothing fatal */ }
    }
    setPlanApplying(false);
    setPlanOpen(false);
    setPlanRows([]);
    load();
  };

  const togglePlanRow = (taskId) =>
    setPlanRows((rows) => rows.map((p) =>
      p.task_id === taskId ? { ...p, _keep: !p._keep } : p));

  // Reschedule any calendar item (return / project / task) to a new date.
  const reschedule = async (it, dateKey) => {
    if (!dateKey) return;
    try {
      await fetch(`${API}/calendar/move`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: it.type, id: it.id, date: dateKey }),
      });
    } catch (e) { /* ignore */ }
    load();
  };

  // One calendar pill + a 📅 affordance to change its date without dragging.
  // Which legend bucket an item belongs to (for the click-to-filter legend).
  const typeKey = (it) => {
    if (it.type === "tax_year") return "return";
    if (it.type === "task") {
      return ["return", "sales_tax", "bookkeeping", "form_1099"].includes(it.category)
        ? it.category : "other";
    }
    return { "Sales Tax": "sales_tax", "Bookkeeping": "bookkeeping",
      "1099s": "form_1099", "Other": "other" }[it.kind] || "other";
  };

  // Legend acts as an "include" filter: with nothing selected everything shows;
  // select one or more types to show ONLY those.
  const activeTypeKeys = Object.keys(typeFilter).filter((k) => typeFilter[k]);
  const visibleType = (it) =>
    activeTypeKeys.length === 0 || activeTypeKeys.includes(typeKey(it));

  const kindClass = (it) => {
    if (it.type === "tax_year") return "k-return";
    if (it.type === "task") {
      // Deadlines inherit the color of whatever they're associated with.
      return { return: "k-return", sales_tax: "k-salestax",
        bookkeeping: "k-bookkeeping", form_1099: "k-1099" }[it.category] || "k-manual";
    }
    return { "Sales Tax": "k-salestax", "Bookkeeping": "k-bookkeeping",
      "1099s": "k-1099", "Other": "k-other" }[it.kind] || "k-other";
  };

  // A leading icon for each return stage, so you can tell the stage at a glance
  // even when the pill text is truncated.
  const stageIcon = (it) => {
    if (it.type !== "task" || it.category !== "return") return "";
    const l = (it.label || "").toLowerCase();
    if (l.includes("request documents")) return "📩 ";
    if (l.includes("begin preparation")) return "✏️ ";
    if (l.includes("review")) return "🔍 ";
    if (l.includes("finalize") || l.includes("file")) return "✅ ";
    return "";
  };

  const CalItem = ({ it }) => {
    const dref = useRef(null);
    return (
      <span className="cal-item-wrap">
        <button className={`cal-item ${kindClass(it)}${it.type === "task" ? " cal-deadline" : ""}${it.done ? " done" : ""}`}
          draggable={!!it.type}
          onDragStart={() => setDrag({ type: it.type, id: it.id })}
          onDragEnd={() => setDrag(null)}
          title={`${it.kind}: ${it.label}${it.roll_over ? " · rolls over" : ""}`}
          onClick={() => it.client_id && onOpenClient(it.client_id)}>
          <span className="cal-dot" /> {stageIcon(it)}{it.roll_over ? "↻ " : ""}{it.label}
        </button>
        {it.type && (
          <button className="cal-item-date" title="Change date"
            onClick={(e) => {
              e.stopPropagation();
              const el = dref.current;
              if (el) { if (el.showPicker) el.showPicker(); else el.focus(); }
            }}>📅</button>
        )}
        {it.type && (
          <input ref={dref} type="date" className="cal-date-hidden"
            defaultValue={toDateInput(it.date)}
            onChange={(e) => reschedule(it, e.target.value)} />
        )}
      </span>
    );
  };

  const DayItems = ({ items }) => (
    <>
      {(hideAll ? [] : (items || [])).filter((it) => (!hideDone || !it.done)
        && !(hideProjects && it.type === "project")
        && !(hideDeadlines && it.type === "task")
        && visibleType(it)).map((it, i) => (
        <CalItem key={i} it={it} />
      ))}
    </>
  );

  return (
    <>
      {generating && (
        <div className="gen-overlay">
          <div className="gen-box">
            <div className="gen-title">Generating deadlines</div>
            <div className="gen-bar"><div className="gen-fill" style={{ width: `${genProgress}%` }} /></div>
            <div className="gen-msg">{genMsg}</div>
          </div>
        </div>
      )}
      {(planOpen || planning) && (
        <div className="gen-overlay" onClick={() => !planApplying && !planning && setPlanOpen(false)}>
          <div className="gen-box planner-box" onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 640, width: "90%", textAlign: "left" }}>
            {planning ? (
              <div style={{ textAlign: "center" }}>
                <div className="gen-title">AI Planner</div>
                <div className="gen-bar"><div className="gen-fill" style={{ width: `${planProgress}%` }} /></div>
                <div className="gen-msg">{planMsg}</div>
              </div>
            ) : planPhase === "intro" ? (
              <>
                <div className="gen-title">AI Planner</div>
                <p className="muted small" style={{ marginTop: 4 }}>
                  I'll suggest a rebalanced schedule for your auto-generated deadlines —
                  giving higher-priority, time-heavy clients an earlier start and thinning
                  out crowded days. Nothing changes until you review and apply.
                </p>
                <p className="muted small" style={{ marginTop: 8 }}>
                  Optional — tell me what to focus on:
                </p>
                <input style={{ width: "100%", margin: "4px 0 12px" }}
                  placeholder="e.g. “push the WeiMark group earlier for next month”"
                  value={planGuidance} onChange={(e) => setPlanGuidance(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") runPlanner(); }} autoFocus />
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button className="btn ghost" onClick={() => setPlanOpen(false)}>Cancel</button>
                  <button className="btn primary" disabled={planning} onClick={runPlanner}>
                    Plan now
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="gen-title">AI Planner — proposed schedule</div>
                <p className="muted small" style={{ marginTop: 4 }}>
                  Suggestions only — nothing changes until you click “Apply selected.” {planAiUsed
                    ? "Refined by AI."
                    : planStatus === "no_key"
                      ? "AI is off (no API key set) — generated deterministically."
                      : planStatus && planStatus.startsWith("error")
                        ? `AI couldn't run (${planStatus.replace("error: ", "")}) — generated deterministically.`
                        : "Generated deterministically."}
                </p>
                <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "8px 0" }}>
                  <input style={{ flex: 1 }} placeholder="Adjust focus and re-plan…"
                    value={planGuidance} onChange={(e) => setPlanGuidance(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") runPlanner(); }} />
                  <button className="btn ghost small" disabled={planning} onClick={runPlanner}>Re-plan</button>
                </div>
                {planRows.length === 0 ? (
                  <p className="muted small pad" style={{ margin: "12px 0" }}>
                    No changes suggested — your schedule already looks balanced.
                  </p>
                ) : (
                  <div style={{ maxHeight: "50vh", overflowY: "auto", margin: "10px 0" }}>
                    <table className="planner-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ textAlign: "left", borderBottom: "1px solid #ccc" }}>
                          <th></th><th>Deadline</th><th>From</th><th>To</th><th>Why</th>
                        </tr>
                      </thead>
                      <tbody>
                        {planRows.map((p) => (
                          <tr key={p.task_id} style={{ borderBottom: "1px solid #eee",
                            opacity: p._keep ? 1 : 0.45 }}>
                            <td><input type="checkbox" checked={p._keep}
                              onChange={() => togglePlanRow(p.task_id)} /></td>
                            <td style={{ paddingRight: 8 }}>
                              <div>{p.title}</div>
                              <div className="muted small">{p.client_name}</div>
                            </td>
                            <td className="muted small">{p.old_date}</td>
                            <td className="small"><strong>{p.new_date}</strong></td>
                            <td className="muted small">{p.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button className="btn ghost" disabled={planApplying || !planRows.length}
                    onClick={() => setPlanRows((rows) => rows.map((p) => ({ ...p, _keep: true })))}>
                    Select all
                  </button>
                  <button className="btn ghost" disabled={planApplying || !planRows.length}
                    onClick={() => setPlanRows((rows) => rows.map((p) => ({ ...p, _keep: false })))}>
                    Clear all
                  </button>
                  <button className="btn ghost" disabled={planApplying}
                    onClick={() => setPlanOpen(false)}>Close</button>
                  <button className="btn primary" disabled={planApplying || !planRows.length}
                    onClick={applyPlan}>
                    {planApplying ? "Applying…"
                      : `Apply selected (${planRows.filter((p) => p._keep).length})`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <header className="topbar">
        <div>
          <h1>Calendar</h1>
          <p className="subtitle">Returns, projects, and your personal deadlines.</p>
        </div>
        <div className="topbar-actions">
          <button className="btn ghost" onClick={() => step(-1)}>‹</button>
          <div className="cal-monthpick-wrap">
            <button className="btn ghost cal-monthbtn"
              onClick={() => setShowMonthPick((s) => !s)}>
              {cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })} ▾
            </button>
            {showMonthPick && (
              <div className="cal-monthpick">
                <div className="cal-monthpick-year">
                  <button className="btn ghost"
                    onClick={() => setCursor(new Date(cursor.getFullYear() - 1, cursor.getMonth(), 1))}>‹</button>
                  <span>{cursor.getFullYear()}</span>
                  <button className="btn ghost"
                    onClick={() => setCursor(new Date(cursor.getFullYear() + 1, cursor.getMonth(), 1))}>›</button>
                </div>
                <div className="cal-monthpick-grid">
                  {["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((m, i) => (
                    <button key={m}
                      className={`btn ghost${i === cursor.getMonth() ? " active" : ""}`}
                      onClick={() => {
                        setCursor(new Date(cursor.getFullYear(), i, 1));
                        setShowMonthPick(false);
                      }}>{m}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button className="btn ghost" onClick={() => step(1)}>›</button>
          <button className="btn ghost" onClick={() => setCursor(new Date())}>Today</button>
          <select className="cal-viewsel" value={view} onChange={(e) => setView(e.target.value)}>
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </select>
          <div className="cal-filters-menu">
            {(() => {
              const activeCount = [hideDone, hideProjects, hideDeadlines,
                hideJewish, hideAll].filter(Boolean).length;
              return (
                <button className={`btn ghost cal-filters-btn${activeCount ? " has-active" : ""}`}
                  onClick={() => setFiltersOpen((o) => !o)}>
                  Filters{activeCount ? ` (${activeCount})` : ""} ▾
                </button>
              );
            })()}
            {filtersOpen && (
              <>
                <div className="cal-filters-backdrop" onClick={() => setFiltersOpen(false)} />
                <div className="cal-filters-pop">
                  <label className="cal-hidedone">
                    <input type="checkbox" checked={hideDone}
                      onChange={(e) => setHideDone(e.target.checked)} />
                    Hide completed
                  </label>
                  <label className="cal-hidedone">
                    <input type="checkbox" checked={hideProjects}
                      onChange={(e) => setHideProjects(e.target.checked)} />
                    Hide projects
                  </label>
                  <label className="cal-hidedone">
                    <input type="checkbox" checked={hideDeadlines}
                      onChange={(e) => setHideDeadlines(e.target.checked)} />
                    Hide my deadlines
                  </label>
                  <label className="cal-hidedone">
                    <input type="checkbox" checked={hideJewish}
                      onChange={(e) => setHideJewish(e.target.checked)} />
                    Hide Jewish items
                  </label>
                  <label className="cal-hidedone">
                    <input type="checkbox" checked={hideAll}
                      onChange={(e) => setHideAll(e.target.checked)} />
                    Blank calendar
                  </label>
                </div>
              </>
            )}
          </div>
          <button className="btn no-print gen-btn icon-behind" onClick={generate} disabled={generating}
            title="Create work-back milestone deadlines from each return & project due date">
            <svg className="actbtn-bg" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="3" y="4.5" width="18" height="16" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
              <path d="M3 9h18M8 2.5v4M16 2.5v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              <path d="M8.5 14.5l2.5 2.5 4.5-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="actbtn-label">
              {generating ? "Generating…" : <span className="two-line">Generate<br />deadlines</span>}
            </span>
          </button>
          <button className="btn no-print gen-btn icon-behind"
            onClick={() => { setPlanGuidance(""); setPlanRows([]); setPlanStatus("");
              setPlanAiUsed(false); setPlanPhase("intro"); setPlanOpen(true); }}
            disabled={planning || generating}
            title="Let the AI Planner suggest a rebalanced schedule for your auto-generated deadlines (you review before anything changes)">
            <svg className="actbtn-bg" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 3l2.2 4.6L19 9l-3.5 3.4.8 5L12 14.8 7.7 17.4l.8-5L5 9l4.8-1.4z"
                fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            </svg>
            <span className="actbtn-label">
              {planning ? "Planning…" : <span className="two-line">AI<br />Planner</span>}
            </span>
          </button>
          <button className="btn ghost no-print cal-actbtn icon-behind" title="Print this calendar"
            onClick={() => window.print()}>
            <svg className="actbtn-bg" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 9V3h12v6M6 18H4a1 1 0 0 1-1-1v-5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5a1 1 0 0 1-1 1h-2M6 14h12v6H6z"
                fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
            </svg>
            <span className="actbtn-label">Print</span>
          </button>
          <a className="btn ghost no-print cal-actbtn icon-behind" title="Download as Excel"
            href={`${API}/calendar/export.xlsx?from=${ymd(rangeStart)}&to=${ymd(rangeEnd)}`
              + `&month=${ymd(new Date(cursor.getFullYear(), cursor.getMonth(), 1))}`
              + `&hide_done=${hideDone ? 1 : 0}&hide_projects=${hideProjects ? 1 : 0}`
              + `&hide_jewish=${hideJewish ? 1 : 0}`}>
            <svg className="actbtn-bg" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="2.5" fill="#1D6F42" />
              <path d="M8.5 8l7 8M15.5 8l-7 8" stroke="#fff" strokeWidth="2"
                strokeLinecap="round" />
            </svg>
            <span className="actbtn-label">Excel</span>
          </a>
        </div>
      </header>

      <div className="cal-title-row">
        <h3 className="cal-title">{title}</h3>
        {!hideJewish && hebcal.title && <h3 className="cal-title-heb" dir="rtl">{hebcal.title}</h3>}
      </div>

      <div className="cal-legend">
        {[["return", "k-return", "Tax return"], ["sales_tax", "k-salestax", "Sales tax"],
          ["bookkeeping", "k-bookkeeping", "Bookkeeping"], ["form_1099", "k-1099", "1099s"],
          ["other", "k-other", "Other project"]].map(([key, cls, label]) => (
          <button key={key} type="button"
            className={`leg ${cls} leg-btn${typeFilter[key] ? " on" : ""}${activeTypeKeys.length && !typeFilter[key] ? " off" : ""}`}
            title={typeFilter[key] ? "Showing — click to clear" : "Show only this type"}
            onClick={() => setTypeFilter((h) => ({ ...h, [key]: !h[key] }))}>
            <span className="leg-dot" />{label}
          </button>
        ))}
        <span className="leg leg-deadline"><span className="leg-dot" />⇠ Dashed = my working-back deadline</span>
        <span className="leg leg-stages">Return stages: 📩 Request · ✏️ Prepare · 🔍 Review · ✅ File</span>
      </div>

      {view === "month" ? (
        <div className="cal-grid">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="cal-head">{d}</div>
          ))}
          {Array.from({ length: gridDays }).map((_, i) => {
            const d = addDays(rangeStart, i);
            const key = ymd(d);
            const inMonth = d.getMonth() === cursor.getMonth();
            const dayItems = (hideAll ? [] : (byDate[key] || [])).filter((it) =>
              (!hideDone || !it.done) && !(hideProjects && it.type === "project")
              && !(hideDeadlines && it.type === "task")
              && visibleType(it));
            const dayHols = (holidays[key] || []).filter((h) =>
              !(hideJewish && (h.kind === "jewish" || h.kind === "hebrew")));
            const hasCandle = !hideJewish && (hebcal.days[key] || {}).candle;
            const empty = !dayItems.length && !dayHols.length && !hasCandle;
            return (
              <div key={key} {...dropProps(key)}
                className={`cal-cell${inMonth ? "" : (empty ? " dim dim-empty" : " dim")}${key === todayKey ? " today" : ""}${drag ? " cal-droppable" : ""}`}>
                <div className="cal-cell-head">
                  <span className="cal-daynum">{d.getDate()}</span>
                  <span className="cal-hday" dir="rtl">{!hideJewish && (hebcal.days[key] || {}).hday}</span>
                </div>
                <div className="cal-cell-body">
                  <Holidays day={key} />
                  <DayItems items={byDate[key]} />
                  {!hideJewish && (hebcal.days[key] || {}).candle && (
                    <div className="cal-candle">🕯 {hebcal.days[key].candle}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="cal-list">
          {Array.from({ length: gridDays }).map((_, i) => {
            const d = addDays(rangeStart, i);
            const key = ymd(d);
            return (
              <div key={key} {...dropProps(key)}
                className={`cal-day${key === todayKey ? " today" : ""}${drag ? " cal-droppable" : ""}`}>
                <div className="cal-day-label">
                  {d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                  {!hideJewish && (hebcal.days[key] || {}).hday && (
                    <span className="cal-hday" dir="rtl"> · {hebcal.days[key].hday}</span>
                  )}
                  {!hideJewish && (hebcal.days[key] || {}).candle && (
                    <span className="cal-candle"> · 🕯 {hebcal.days[key].candle}</span>
                  )}
                </div>
                <div className="cal-day-items">
                  <Holidays day={key} />
                  {(byDate[key] || []).length === 0 && (holidays[key] || []).length === 0
                    ? <span className="muted small">—</span>
                    : <DayItems items={byDate[key]} />}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

/* ---------------- Activity log / History ---------------- */

function HistoryTab({ clientId, onOpenClient }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (clientId) p.set("client_id", clientId);
    if (q) p.set("q", q);
    fetch(`${API}/activity?${p.toString()}`)
      .then((r) => r.json()).then(setItems).catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [clientId, q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const table = (
    loading ? <div className="empty">Loading…</div>
      : items.length === 0 ? <div className="empty">No activity yet.</div>
        : (
          <table className="data-table">
            <thead>
              <tr><th>When</th>{!clientId && <th>Client</th>}<th>Action</th></tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id}>
                  <td className="muted small" style={{ whiteSpace: "nowrap" }}>{fmtDate(a.ts)}</td>
                  {!clientId && (
                    <td className="name-cell">
                      {a.client_id
                        ? <button className="link-btn" onClick={() => onOpenClient && onOpenClient(a.client_id)}>{a.client_name}</button>
                        : <span className="muted">{a.client_name || "—"}</span>}
                    </td>
                  )}
                  <td>{a.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
  );

  if (clientId) {
    return (
      <section className="detail-card">
        <div className="sub-head">
          <h3>History</h3>
          <input className="search" placeholder="Search…" value={q}
            onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 220 }} />
        </div>
        {table}
      </section>
    );
  }
  return (
    <>
      <header className="topbar">
        <div>
          <h1>History</h1>
          <p className="subtitle">Everything that's happened in Spoom, newest first.</p>
        </div>
        <div className="topbar-actions">
          <input className="search" placeholder="Search history…" value={q}
            onChange={(e) => setQ(e.target.value)} />
        </div>
      </header>
      <section className="table-wrap">{table}</section>
    </>
  );
}

const TABS = [
  ["agenda", "This Week"],
  ["calendar", "Calendar"],
  ["returns", "Tax Returns"],
  ["sales_tax", "Sales Tax"],
  ["bookkeeping", "Bookkeeping"],
  ["form_1099", "1099s"],
  ["other", "Other"],
  ["finances", "Finances"],
  ["messages", "Messages"],
  ["docmail", "Doc Inbox"],
  ["files", "Files"],
  ["history", "History"],
];

// The primary tabs shown in the phone bottom nav (the rest go under "More").
// Icons are inline line-SVGs (stroke:currentColor) so they take the active color.
const ic = (paths) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths}</svg>
);
const BOTTOM_NAV = [
  ["home", "Home", ic(<><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><path d="M9 22V12h6v10" /></>)],
  ["messages", "Messages", ic(<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />)],
  ["returns", "Returns", ic(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h6" /></>)],
  ["finances", "Finances", ic(<><path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>)],
];
// Tabs hidden entirely on mobile (not needed on a phone).
const MOBILE_HIDDEN_TABS = ["calendar", "history"];

// Full left/right swipe order on mobile: the 4 bottom-nav tabs first, then the
// "More" screens, so swiping past Finances just keeps going to the next screen
// (no dropdown). Excludes tabs hidden on phones.
const SWIPE_ORDER = [
  ...BOTTOM_NAV.map(([k]) => k),
  ...TABS.map(([k]) => k).filter(
    (k) => !BOTTOM_NAV.some((b) => b[0] === k) && !MOBILE_HIDDEN_TABS.includes(k)),
];

/* ---------------- Reminders sticky note (floating, bottom-left) ---------- */

function RemindersNote({ onOpenClient }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [due, setDue] = useState("");
  const [pri, setPri] = useState(false);
  const [alarm, setAlarm] = useState("");        // HH:MM alarm time (optional)
  const [popup, setPopup] = useState(false);     // raise a popup at the alarm
  const [kind, setKind] = useState("practice");  // practice | personal
  const [client, setClient] = useState(null);   // {id, name} for the new reminder
  const [sug, setSug] = useState([]);            // @client typeahead suggestions
  const [editing, setEditing] = useState(null);  // {id, text}
  const [showAuto, setShowAuto] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/tasks`).then((r) => r.json())
      .then((d) => setItems(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // Esc closes the popup (or just cancels an inline edit if one is open).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (editing) setEditing(null);
      else setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, editing]);

  const manual = items.filter((i) => !i.auto_generated && i.kind !== "personal");
  const personal = items.filter((i) => !i.auto_generated && i.kind === "personal");
  const auto = items.filter((i) => i.auto_generated && !i.done);
  const openCount = manual.filter((i) => !i.done).length
    + personal.filter((i) => !i.done).length;
  const todayStr = ymd(new Date());
  const isOverdueR = (it) => !it.done && it.due_date && toDateInput(it.due_date) < todayStr;
  const overdueCount = manual.filter(isOverdueR).length;

  // Browser notification when reminders are due/overdue (once per app load).
  const notified = useRef(false);
  useEffect(() => {
    if (notified.current || overdueCount === 0) return;
    if (typeof Notification === "undefined") return;
    const fire = () => {
      notified.current = true;
      try { new Notification(`Spoom: ${overdueCount} reminder${overdueCount === 1 ? "" : "s"} overdue`); } catch (e) { /* ignore */ }
    };
    if (Notification.permission === "granted") fire();
    else if (Notification.permission !== "denied") Notification.requestPermission().then((p) => { if (p === "granted") fire(); });
  }, [overdueCount]);

  // @client typeahead: when the text ends with "@something", search clients.
  useEffect(() => {
    const m = text.match(/@([^@]+)$/);
    const q = m ? m[1].trim() : "";
    if (!q) { setSug([]); return; }
    const t = setTimeout(() => {
      fetch(`${API}/search?q=${encodeURIComponent(q)}`).then((r) => r.json())
        .then((d) => setSug(Array.isArray(d) ? d.slice(0, 6) : [])).catch(() => setSug([]));
    }, 180);
    return () => clearTimeout(t);
  }, [text]);

  const pickClient = (c) => {
    setClient({ id: c.id, name: c.name });
    setText((t) => t.replace(/@([^@]+)$/, "").trimEnd());
    setSug([]);
  };

  const add = async () => {
    const t = text.trim();
    if (!t) return;
    if (popup && !due) { window.alert("Pick a date for a popup alarm."); return; }
    const c = client;
    const body = { title: t, due_date: due || null,
      client_id: c ? c.id : null, priority: pri ? 1 : 0,
      alarm_time: alarm || null, popup, kind };
    setText(""); setDue(""); setPri(false); setAlarm(""); setPopup(false);
    setClient(null); setSug([]);
    await fetch(`${API}/tasks`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    load();
  };

  const put = async (it, body) => {
    await fetch(`${API}/tasks/${it.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    load();
  };
  const toggle = async (it) => {
    const completing = !it.done;
    // When finishing a client-linked item, offer to log the time spent.
    if (completing && it.client_id) {
      const ans = window.prompt(
        `Completing "${it.title}".\nMinutes spent? (rounded to the nearest 15; blank to skip)`, "");
      if (ans && ans.trim() && !isNaN(Number(ans)) && Number(ans) > 0) {
        // Time is logged in 15-minute increments — round to the nearest 15.
        const mins = Math.max(15, Math.round(Number(ans) / 15) * 15);
        try {
          await fetch(`${API}/clients/${it.client_id}/time_entries`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ minutes: mins, label: it.title }),
          });
        } catch (e) { /* ignore — still mark done */ }
      }
    }
    put(it, { done: completing });
  };
  const togglePriority = (it) => put(it, { priority: it.priority ? 0 : 1 });
  const setItemDue = (it, v) => put(it, { due_date: v || null });
  const saveEdit = async () => {
    if (!editing || !editing.text.trim()) { setEditing(null); return; }
    await put({ id: editing.id }, { title: editing.text.trim() });
    setEditing(null);
  };
  const del = async (id) => { await fetch(`${API}/tasks/${id}`, { method: "DELETE" }); load(); };
  const clearDone = async () => { await fetch(`${API}/tasks/clear_done`, { method: "POST" }); load(); };
  const snooze = (it) => {
    const base = it.due_date && toDateInput(it.due_date) >= todayStr
      ? new Date(toDateInput(it.due_date)) : new Date();
    base.setDate(base.getDate() + 1);
    setItemDue(it, ymd(base));
  };
  const quick = (days) => { const d = new Date(); d.setDate(d.getDate() + days); setDue(ymd(d)); };

  const RemDate = ({ it }) => {
    const ref = useRef(null);
    return (
      <span className="rem-date">
        {it.due_date && <span className="rem-date-lbl">{fmtDay(it.due_date)}</span>}
        <button className="icon-btn" title="Set due date"
          onClick={() => { const el = ref.current; if (el) { el.showPicker ? el.showPicker() : el.focus(); } }}>📅</button>
        <input ref={ref} type="date" className="cal-date-hidden"
          value={toDateInput(it.due_date)} onChange={(e) => setItemDue(it, e.target.value)} />
      </span>
    );
  };

  const ClientChip = ({ it }) => it.client_name ? (
    <button className="rem-clientchip sm" title="Open client"
      onClick={() => onOpenClient && onOpenClient(it.client_id)}>👤 {it.client_name}</button>
  ) : null;

  const renderItem = (it) => (
    <li key={it.id} className={`${it.done ? "done" : ""}${isOverdueR(it) ? " overdue" : ""}${it.priority ? " hi" : ""}`}>
      <input type="checkbox" checked={it.done} onChange={() => toggle(it)} />
      <button className="rem-flag" title={it.priority ? "High priority — click to clear" : "Mark high priority"}
        onClick={() => togglePriority(it)}>{it.priority ? "★" : "☆"}</button>
      <div className="rem-main">
        {editing && editing.id === it.id ? (
          <input className="reminders-edit" autoFocus value={editing.text}
            onChange={(e) => setEditing({ ...editing, text: e.target.value })}
            onBlur={saveEdit}
            onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditing(null); }} />
        ) : (
          <span className="reminders-text" title="Click to edit"
            onClick={() => setEditing({ id: it.id, text: it.title })}>{it.title}</span>
        )}
        <ClientChip it={it} />
      </div>
      <div className="rem-right">
        {it.alarm_time && <span className="rem-alarm" title={it.popup ? "Popup alarm" : "Alarm"}>
          {it.popup ? "🔔" : "⏱"} {it.alarm_time}</span>}
        <RemDate it={it} />
        <button className="icon-btn" title="Snooze to tomorrow" onClick={() => snooze(it)}>⏰</button>
        <button className="icon-btn" title="Delete" onClick={() => del(it.id)}>✕</button>
      </div>
    </li>
  );

  const doneCount = manual.concat(personal).filter((i) => i.done).length;

  return (
    <div className="reminders-fab-wrap">
      {open && (
        <div className="reminders-note">
          <div className="reminders-head">
            <span>📋 Reminders</span>
            <span className="reminders-status">{openCount} open</span>
            <button className="icon-btn" title="Close" onClick={() => setOpen(false)}>✕</button>
          </div>
          <div className="reminders-add">
            <div className="rem-add-row">
              <input value={text} placeholder="Add a reminder…  (type @ to tag a client)"
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !sug.length) add(); }} />
              {client && (
                <span className="rem-clientchip">👤 {client.name}
                  <button className="icon-btn" title="Remove client"
                    onClick={() => setClient(null)}>✕</button>
                </span>
              )}
            </div>
            {sug.length > 0 && (
              <div className="rem-sug">
                {sug.map((c) => (
                  <button key={c.id} className="rem-sug-item"
                    onMouseDown={() => pickClient(c)}>{c.name}</button>
                ))}
              </div>
            )}
            <div className="rem-add-controls">
              <button className="chip" onClick={() => quick(0)}>Today</button>
              <button className="chip" onClick={() => quick(1)}>Tomorrow</button>
              <button className="chip" onClick={() => quick(7)}>+1 wk</button>
              <input type="date" className="reminders-add-date" title="Due date"
                value={due} onChange={(e) => setDue(e.target.value)} />
              <input type="time" className="reminders-add-date" title="Alarm time (optional)"
                value={alarm} onChange={(e) => setAlarm(e.target.value)} />
              <button className={`chip${popup ? " on" : ""}`}
                title="Pop up a notification at the alarm time"
                onClick={() => setPopup((p) => !p)}>🔔 Popup</button>
              <button className={`rem-flag${pri ? " on" : ""}`} title="High priority"
                onClick={() => setPri((p) => !p)}>{pri ? "★" : "☆"}</button>
              <button className="btn small" onClick={add}>Add</button>
            </div>
            <div className="rem-kind-toggle">
              {[["practice", "Practice"], ["personal", "Personal"]].map(([v, l]) => (
                <button key={v} className={kind === v ? "active" : ""}
                  onClick={() => setKind(v)}>{l}</button>
              ))}
              <span className="muted small">
                {kind === "personal" ? "Won’t affect firm metrics." : "Counts as firm work."}
              </span>
            </div>
          </div>
          <ul className="reminders-list">
            {manual.length === 0 && <li className="reminders-empty">No practice reminders — add one above.</li>}
            {manual.map(renderItem)}
          </ul>
          {personal.length > 0 && (
            <div className="rem-personal">
              <div className="rem-section-lbl">Personal <span className="muted small">(not counted in metrics)</span></div>
              <ul className="reminders-list">
                {personal.map(renderItem)}
              </ul>
            </div>
          )}
          {auto.length > 0 && (
            <div className="rem-auto">
              <button className="cl-done-toggle" onClick={() => setShowAuto((v) => !v)}>
                {showAuto ? "▾" : "▸"} Planner deadlines ({auto.length})
              </button>
              {showAuto && (
                <ul className="reminders-list">
                  {auto.map((it) => (
                    <li key={it.id} className={isOverdueR(it) ? "overdue" : ""}>
                      <input type="checkbox" checked={it.done} onChange={() => toggle(it)} />
                      <div className="rem-main">
                        <span className="reminders-text">{it.title}</span>
                        <ClientChip it={it} />
                      </div>
                      <div className="rem-right">
                        {it.due_date && <span className="rem-date-lbl">{fmtDay(it.due_date)}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {doneCount > 0 && (
            <div className="reminders-foot">
              <button className="link-btn" onClick={clearDone}>Clear {doneCount} completed</button>
            </div>
          )}
        </div>
      )}
      <button className="reminders-fab" title="Reminders"
        onClick={() => setOpen((o) => !o)}>
        {open ? "✕" : <img src="/reminders-icon.png" alt="Reminders" className="reminders-fab-img" />}
        {openCount > 0 && (
          <span className={`reminders-badge${overdueCount > 0 ? " od" : ""}`}>{openCount}</span>
        )}
      </button>
    </div>
  );
}

/* ---------------- Countdown to the next two return deadlines ------------- */

function CountdownBar() {
  const [countdown, setCountdown] = useState([]);
  useEffect(() => {
    fetch(`${API}/deadlines/countdown`).then((r) => r.json())
      .then((d) => setCountdown(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);
  if (!countdown.length) return null;
  return (
    <div className="cal-countdown top-countdown">
      {countdown.map((c, i) => (
        <div key={i} className={`cd-card${c.days <= 15 ? " urgent" : ""}`}>
          <div className="cd-days">{c.days % 1 === 0 ? c.days : `${Math.floor(c.days)}½`}</div>
          <div className="cd-meta">
            <strong>working days</strong> to {fmtDay(c.due)}
            {typeof c.cal_days === "number" &&
              <div className="cd-cal">{c.cal_days} calendar days</div>}
            <div className="cd-count">{c.count} {c.label || "returns"} left to finish</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Finances (A/R + Expenses) ---------------- */

const EXP_CATS = ["Software", "Subscriptions", "Rent", "Payroll", "Office",
  "Professional", "Taxes", "Bank/Merchant fees", "Other"];
const EXP_FREQS = [["once", "One-time"], ["monthly", "Monthly"],
  ["quarterly", "Quarterly"], ["annual", "Annual"]];
const annualize = (amt, freq) => {
  const a = Number(amt) || 0;
  if (freq === "monthly") return a * 12;
  if (freq === "quarterly") return a * 4;
  if (freq === "annual") return a;
  return 0; // one-time is not recurring
};
const finMoney = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// mm/dd/yyyy for finance displays (accepts RFC or ISO).
// Parse a date WITHOUT timezone drift: ISO "YYYY-MM-DD" keeps its literal parts;
// RFC strings ("Fri, 14 Mar 2026 00:00:00 GMT") use their UTC date.
const _finDate = (s) => {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return { y: +m[1], mo: +m[2], d: +m[3] };
  const dt = new Date(s);
  if (isNaN(dt)) return null;
  return { y: dt.getUTCFullYear(), mo: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
};
const _p2 = (n) => String(n).padStart(2, "0");
const fmtMD = (s) => { const p = _finDate(s); return p ? `${_p2(p.mo)}/${_p2(p.d)}/${p.y}` : (s || ""); };
const fmtMonthMD = (ym) => { if (!ym) return ""; const [y, m] = ym.split("-"); return `${m}/${y}`; };
const fmtLongDate = (s) => { const p = _finDate(s); return p ? `${FIN_MONTHS[p.mo - 1]} ${_p2(p.d)} ${p.y}` : (s || ""); };
const pct = (cur, prior) => { const c = Number(cur) || 0, p = Number(prior) || 0; if (!p) return c ? "100%" : "0%"; return `${Math.round(((c - p) / Math.abs(p)) * 100)}%`; };

const FIN_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtMonth = (iso) => { if (!iso) return null; const [y, m] = iso.split("-"); return `${FIN_MONTHS[+m - 1]} ${y}`; };
const thisMonthIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
const isoMonthFirst = (iso) => { const [y, m] = (iso || thisMonthIso()).split("-"); return `${y}-${m}-01`; };
const isoMonthOffset = (iso, n) => {
  let y, m;
  if (iso) { const p = iso.split("-").map(Number); y = p[0]; m = p[1]; }
  else { const d = new Date(); y = d.getFullYear(); m = d.getMonth() + 1; }
  const idx = y * 12 + (m - 1) + n;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, "0")}-01`;
};

const FIN_PERIODS = [["this_month", "This month"], ["last_month", "Last month"],
  ["this_quarter", "This quarter"], ["last_quarter", "Last quarter"],
  ["ytd", "Year to date"], ["this_year", "This year"], ["last_year", "Last year"],
  ["all_dates", "All dates"], ["custom", "Custom…"]];
const FIN_COMPARE = [["none", "No comparison"], ["prev_period", "Previous period"],
  ["prev_year", "Previous year"], ["custom", "Custom period…"]];

const PAYMENT_METHODS = ["Zelle", "ACH", "CC", "Cash", "Check", "Other"];

// Calendar-style action buttons (match the Calendar tab look).
const ExcelActBtn = ({ href, label = "Excel" }) => (
  <a className="btn ghost no-print cal-actbtn icon-behind" title="Download as Excel" href={href}>
    <svg className="actbtn-bg" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2.5" fill="#1D6F42" />
      <path d="M8.5 8l7 8M15.5 8l-7 8" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    </svg>
    <span className="actbtn-label">{label}</span>
  </a>
);
const PrintActBtn = () => {
  const wrapRef = useRef(null);
  const [menu, setMenu] = useState(false);
  const run = (orientation, fit) => {
    setMenu(false);
    const card = wrapRef.current && wrapRef.current.closest(".detail-card");
    let st = document.getElementById("pl-print-page");
    if (!st) { st = document.createElement("style"); st.id = "pl-print-page"; document.head.appendChild(st); }
    st.textContent = `@page { size: ${orientation}; margin: 0.5in; }`;
    if (card) card.classList.add("print-active");
    document.body.classList.add("printing-finance");
    if (fit) document.body.classList.add("print-fit");
    window.print();
    setTimeout(() => {
      document.body.classList.remove("printing-finance");
      document.body.classList.remove("print-fit");
      if (card) card.classList.remove("print-active");
    }, 400);
  };
  return (
    <span className="print-btn-wrap" ref={wrapRef}>
      <button className="btn ghost no-print cal-actbtn icon-behind" title="Print (portrait)"
        onClick={() => run("portrait", false)}>
        <svg className="actbtn-bg" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 9V3h12v6M6 18H4a1 1 0 0 1-1-1v-5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5a1 1 0 0 1-1 1h-2M6 14h12v6H6z"
            fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
        </svg>
        <span className="actbtn-label">Print</span>
      </button>
      <button className="btn ghost no-print print-caret" title="Print options"
        onClick={() => setMenu((m) => !m)}>▾</button>
      {menu && (
        <div className="print-menu no-print" onMouseLeave={() => setMenu(false)}>
          <button onClick={() => run("portrait", false)}>Portrait</button>
          <button onClick={() => run("landscape", false)}>Landscape</button>
          <button onClick={() => run("portrait", true)}>Fit to page · portrait</button>
          <button onClick={() => run("landscape", true)}>Fit to page · landscape</button>
        </div>
      )}
    </span>
  );
};

function FinancesTab({ onOpenClient, refreshKey }) {
  const [ar, setAr] = useState(() => cacheGet("fin_ar", null));
  const [exp, setExp] = useState(() => cacheGet("fin_exp", []));
  const [form, setForm] = useState({ name: "", category: "Software", amount: "", frequency: "monthly", incurred_date: "", end_date: "" });
  const [editId, setEditId] = useState(null);          // expense being edited
  const [editRow, setEditRow] = useState(null);        // its editable copy
  const [period, setPeriod] = useState("ytd");
  const [cFrom, setCFrom] = useState("");           // custom range
  const [cTo, setCTo] = useState("");
  const [basis, setBasis] = useState("accrual");
  const [compare, setCompare] = useState("none");
  const [period2, setPeriod2] = useState("last_year");
  const [clientFilter, setClientFilter] = useState("");   // client_id or ""
  const [clientList, setClientList] = useState([]);
  const [pnl, setPnl] = useState(() => cacheGet("fin_pnl", null));
  const [pnlState, setPnlState] = useState(() => cacheGet("fin_pnl", null) ? "ok" : "loading");
  const [colMode, setColMode] = useState("total");     // total | monthly
  const [pnlM, setPnlM] = useState(null);              // monthly P&L data
  const [rev, setRev] = useState(() => cacheGet("fin_rev", null));
  const [unbilled, setUnbilled] = useState(() => cacheGet("fin_unbilled", null));
  const [expDetail, setExpDetail] = useState(null); // monthly expense rows
  const [arBucket, setArBucket] = useState(null);   // selected aging bucket filter
  const [arAsOf, setArAsOf] = useState("");          // A/R "as of" date (blank = today)
  const [view, setView] = useState(null);          // null | 'income' | 'expenses' | 'ar'
  const [incomeFilter, setIncomeFilter] = useState(null);   // service drill-down
  const [expenseFilter, setExpenseFilter] = useState(null); // account drill-down
  const [showExpModal, setShowExpModal] = useState(false);  // add-expense popup
  const [collapse, setCollapse] = useState({ income: false, expenses: false });
  const [showManage, setShowManage] = useState(false);  // edit/delete recurring expenses
  const [accounts, setAccounts] = useState(() => cacheGet("fin_accounts", { income: [], expense: [] }));
  const [qbo, setQbo] = useState(null);                 // QuickBooks status
  const [showAccts, setShowAccts] = useState(false);    // manage-accounts modal
  const [newAcct, setNewAcct] = useState({ name: "", kind: "expense" });

  const openView = (v, filter = null) => {
    setView(v);
    if (v === "income") setIncomeFilter(filter);
    if (v === "expenses") setExpenseFilter(filter);
  };
  // Esc returns to the main summary from any report view.
  useEffect(() => {
    if (!view) return;
    const onKey = (e) => { if (e.key === "Escape") setView(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view]);

  // Shared query string for period/basis/client/compare.
  const finQS = useCallback((extra = "") => {
    let q = `basis=${basis}`;
    if (period === "custom" && cFrom && cTo) q += `&from=${cFrom}&to=${cTo}`;
    else q += `&period=${period}`;
    if (clientFilter) q += `&client_id=${clientFilter}`;
    if (compare !== "none") {
      q += `&compare=${compare}`;
      if (compare === "custom") q += `&period2=${period2}`;
    }
    return q + (extra ? `&${extra}` : "");
  }, [period, cFrom, cTo, basis, clientFilter, compare, period2]);

  const loadAr = useCallback(() => {
    const qs = arAsOf ? `?as_of=${arAsOf}` : "";
    fetch(`${API}/finance/ar${qs}`).then((r) => r.json())
      .then((d) => setAr(cacheSet("fin_ar", d))).catch(() => setAr(null));
  }, [arAsOf]);
  const loadExp = useCallback(() => {
    fetch(`${API}/expenses`).then((r) => r.json())
      .then((d) => setExp(cacheSet("fin_exp", Array.isArray(d) ? d : []))).catch(() => setExp([]));
  }, []);
  const loadPnl = useCallback(() => {
    if (period === "custom" && !(cFrom && cTo)) return; // wait for both dates
    setPnlState("loading");
    fetch(`${API}/finance/pnl?${finQS()}`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { setPnl(cacheSet("fin_pnl", d)); setPnlState("ok"); })
      .catch(() => { setPnl(null); setPnlState("error"); });
  }, [finQS, period, cFrom, cTo]);
  const loadPnlM = useCallback(() => {
    if (period === "custom" && !(cFrom && cTo)) return;
    fetch(`${API}/finance/pnl/monthly?${finQS()}`)
      .then((r) => r.json()).then(setPnlM).catch(() => setPnlM(null));
  }, [finQS, period, cFrom, cTo]);
  const loadRev = useCallback(() => {
    fetch(`${API}/finance/revenue?${finQS()}`)
      .then((r) => r.json()).then((d) => setRev(cacheSet("fin_rev", d))).catch(() => setRev(null));
  }, [finQS]);
  const loadExpDetail = useCallback(() => {
    fetch(`${API}/finance/expenses/detail?${finQS()}`)
      .then((r) => r.json()).then(setExpDetail).catch(() => setExpDetail(null));
  }, [finQS]);
  const loadUnbilled = useCallback(() => {
    fetch(`${API}/finance/unbilled`).then((r) => r.json())
      .then((d) => setUnbilled(cacheSet("fin_unbilled", d))).catch(() => setUnbilled(null));
  }, []);
  const loadAccounts = useCallback(() => {
    fetch(`${API}/finance/accounts`).then((r) => r.json())
      .then((d) => setAccounts(cacheSet("fin_accounts", { income: d.income || [], expense: d.expense || [] })))
      .catch(() => {});
  }, []);
  const loadQbo = useCallback(() => {
    fetch(`${API}/qbo/status`).then((r) => r.json()).then(setQbo).catch(() => setQbo(null));
  }, []);
  const syncQbo = async () => {
    const r = await fetch(`${API}/qbo/sync_payments`, { method: "POST" }).then((x) => x.json()).catch(() => null);
    if (r) { window.alert(`QuickBooks sync done — ${r.marked_paid || 0} bill(s) marked paid.`); loadAr(); loadPnl(); loadRev(); }
  };
  const addAccount = async () => {
    if (!newAcct.name.trim()) return;
    await fetch(`${API}/finance/accounts`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newAcct),
    });
    setNewAcct({ name: "", kind: newAcct.kind });
    loadAccounts();
  };
  const delAccount = async (id) => {
    await fetch(`${API}/finance/accounts/${id}`, { method: "DELETE" });
    loadAccounts();
  };
  const importChart = async () => {
    const r = await fetch(`${API}/finance/accounts/import_chart`, { method: "POST" })
      .then((x) => x.json()).catch(() => null);
    if (!r || r.error) { window.alert((r && r.message) || "Couldn’t read the chart-of-accounts file."); return; }
    window.alert(`Imported ${r.income_added} income and ${r.expense_added} expense accounts from QuickBooks.`);
    loadAccounts();
  };
  const importItems = async () => {
    const r = await fetch(`${API}/qbo/items/import`, { method: "POST" })
      .then((x) => x.json()).catch(() => null);
    if (!r || r.error) { window.alert((r && r.message) || "Couldn’t reach QuickBooks. Make sure it’s connected."); return; }
    window.alert(`Imported ${r.added} product/service${r.added === 1 ? "" : "s"} from QuickBooks (of ${r.total} found).`);
    loadAccounts();
  };
  const loadClients = useCallback(() => {
    fetch(`${API}/clients`).then((r) => r.json())
      .then((d) => {
        const arr = Array.isArray(d) ? d : (d.clients || []);
        // /clients returns {client_id, client_name}; normalize to {id, name}.
        setClientList(arr.map((c) => ({ id: c.client_id ?? c.id, name: c.client_name ?? c.name }))
          .filter((c) => c.id != null));
      }).catch(() => setClientList([]));
  }, []);
  useEffect(() => { loadAr(); loadExp(); loadUnbilled(); loadClients(); loadAccounts(); loadQbo(); }, [loadAr, loadExp, loadUnbilled, loadClients, loadAccounts, loadQbo]);
  useEffect(() => { loadPnl(); loadPnlM(); loadRev(); loadExpDetail(); }, [loadPnl, loadPnlM, loadRev, loadExpDetail, exp]);
  // Auto-refresh when time/billing changes elsewhere (refreshKey bumps).
  useEffect(() => {
    if (refreshKey) { loadAr(); loadExp(); loadPnl(); loadPnlM(); loadRev(); loadExpDetail(); loadUnbilled(); }
  }, [refreshKey]); // eslint-disable-line

  const addExp = async () => {
    if (!form.name.trim() || !form.amount) { window.alert("Enter a name and amount."); return; }
    await fetch(`${API}/expenses`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    setForm({ name: "", category: form.category, amount: "", frequency: form.frequency, incurred_date: "", end_date: "" });
    loadExp();
  };
  const delExp = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    await fetch(`${API}/expenses/${id}`, { method: "DELETE" }); loadExp();
  };
  const togglePaidExp = async (e) => {
    await fetch(`${API}/expenses/${e.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ paid: !e.paid }),
    });
    loadExp();
  };
  const setPaidThrough = async (e, iso) => {
    await fetch(`${API}/expenses/${e.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ paid_through: iso }),
    });
    loadExp();
  };
  // Paid status cell: one-time = simple toggle; recurring = "paid through month".
  const PaidCell = ({ e }) => {
    if (e.frequency === "once") {
      return (
        <button className={`btn small ${e.paid ? "ghost" : "primary"}`}
          onClick={() => togglePaidExp(e)}>{e.paid ? "✓ Paid" : "Unpaid"}</button>
      );
    }
    const pt = e.paid_through ? toDateInput(e.paid_through) : null;
    const next = pt ? isoMonthOffset(pt, 1) : isoMonthFirst(toDateInput(e.incurred_date));
    const behind = !pt || pt < thisMonthIso();
    return (
      <div className="exp-paid">
        <span className={`exp-paid-lbl${pt ? "" : " unpaid"}`}>
          {pt ? `Paid thru ${fmtMonth(pt)}` : "Unpaid"}
        </span>
        <button className="btn small primary" title={`Mark ${fmtMonth(next)} paid`}
          onClick={() => setPaidThrough(e, next)}>+ {fmtMonth(next)}</button>
        {pt && (
          <button className="icon-btn" title="Undo last month"
            onClick={() => setPaidThrough(e, isoMonthOffset(pt, -1))}>↶</button>
        )}
        {behind && (
          <button className="link-btn" title="Mark every month up to now as paid"
            onClick={() => setPaidThrough(e, thisMonthIso())}>Catch up to now</button>
        )}
      </div>
    );
  };
  const startEdit = (e) => {
    setEditId(e.id);
    setEditRow({
      name: e.name || "", category: e.category || "Other",
      amount: e.amount ?? "", frequency: e.frequency || "once",
      incurred_date: toDateInput(e.incurred_date),
      end_date: toDateInput(e.end_date),
    });
  };
  const cancelEdit = () => { setEditId(null); setEditRow(null); };
  const saveEdit = async () => {
    if (!editRow.name.trim() || editRow.amount === "") { window.alert("Enter a name and amount."); return; }
    await fetch(`${API}/expenses/${editId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...editRow, end_date: editRow.end_date || null }),
    });
    cancelEdit(); loadExp();
  };

  const recurAnnual = exp.filter((e) => e.frequency !== "once")
    .reduce((s, e) => s + annualize(e.amount, e.frequency), 0);
  const oneTime = exp.filter((e) => e.frequency === "once")
    .reduce((s, e) => s + (Number(e.amount) || 0), 0);

  // Expense account names for dropdowns (managed accounts, fallback to defaults).
  const expAccts = accounts.expense.length ? accounts.expense.map((a) => a.name) : EXP_CATS;

  // Drill-down filtered lists for the detail reports.
  const incItems = (rev?.items || []).filter((it) => !incomeFilter || (it.description || "Service") === incomeFilter);
  const incItemsTotal = incItems.reduce((s, it) => s + (Number(it.amount) || 0), 0);
  const expRows = (expDetail?.rows || []).filter((r) => !expenseFilter || r.account === expenseFilter);
  const expRowsTotal = expRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  return (
    <>
      <header className="topbar">
        <div>
          <h1>Finances</h1>
          <p className="subtitle">Revenue, expenses, profit, and receivables.</p>
        </div>
        <div className="fin-controls">
          <select value={period} onChange={(e) => setPeriod(e.target.value)} title="Period">
            {FIN_PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {period === "custom" && (
            <>
              <input type="date" value={cFrom} onChange={(e) => setCFrom(e.target.value)} title="From" />
              <input type="date" value={cTo} onChange={(e) => setCTo(e.target.value)} title="To" />
            </>
          )}
          <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} title="Client filter">
            <option value="">All clients</option>
            {clientList.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={compare} onChange={(e) => setCompare(e.target.value)} title="Compare to">
            {FIN_COMPARE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {compare === "custom" && (
            <select value={period2} onChange={(e) => setPeriod2(e.target.value)} title="Comparison period">
              {FIN_PERIODS.filter(([v]) => v !== "custom").map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          )}
          <div className="fin-toggle">
            {["accrual", "cash"].map((b) => (
              <button key={b} className={basis === b ? "active" : ""}
                onClick={() => setBasis(b)}>{b === "accrual" ? "Accrual" : "Cash"}</button>
            ))}
          </div>
          <button className="btn small primary"
            title="Add a new expense" onClick={() => setShowExpModal(true)}>＋ Add expense</button>
          <button className="btn small ghost" title="Add or remove income/expense accounts"
            onClick={() => setShowAccts(true)}>Accounts</button>
          {qbo && qbo.configured && (qbo.connected ? (
            <span className="qbo-chip" title={`Connected to ${qbo.company_name || "QuickBooks"}`}>
              <span className="qbo-dot" /> QB
              <button className="btn small ghost" onClick={syncQbo}>Sync payments</button>
            </span>
          ) : (
            <a className="btn small ghost" href={`${API}/qbo/connect`}>Connect QuickBooks</a>
          ))}
        </div>
      </header>

      <section className="table-wrap fin-tab">
        {view && (
          <button className="btn ghost small fin-back" onClick={() => setView(null)}>← Back to summary</button>
        )}

        {/* ============ MAIN SUMMARY ============ */}
        {!view && (
        <>
        {/* ---- Summary cards (clickable) ---- */}
        <div className="fin-cards">
          {[["Revenue", pnl?.revenue, "rev", "income"], ["Expenses", pnl?.expenses, "exp", "expenses"],
            ["Net profit", pnl?.net, (pnl?.net ?? 0) < 0 ? "neg" : "net", null],
            ["Outstanding A/R", ar?.total, "ar", "ar"]].map(([lbl, val, cls, target]) => (
            <div key={lbl} className={`fin-card ${cls}${target ? " clickable" : ""}`}
              onClick={() => target && (target === "ar" ? setView("ar") : openView(target, null))}
              title={target ? "Click for details" : undefined}>
              <div className="fin-card-val">{finMoney(val || 0)}</div>
              <div className="fin-card-lbl">{lbl}</div>
            </div>
          ))}
        </div>
        <div className="muted small" style={{ margin: "-2px 0 14px" }}>
          {pnl ? `${pnl.label} · ${basis === "cash" ? "Cash basis" : "Accrual basis"}` : ""}
        </div>

        {/* ---- Profit & Loss (QuickBooks-style) ---- */}
        <div className="detail-card">
          <div className="sub-head"><h3>Profit &amp; Loss</h3>
            <span className="sub-head-right">
              <div className="fin-toggle">
                <button className={colMode === "total" ? "active" : ""} onClick={() => setColMode("total")}>Total</button>
                <button className={colMode === "monthly" ? "active" : ""} onClick={() => setColMode("monthly")}>Monthly</button>
              </div>
              <span className="muted small">{pnl?.col1?.label}{colMode === "total" && pnl?.col2 ? ` vs ${pnl.col2.label}` : ""}</span>
              <ExcelActBtn href={`${API}/finance/pnl${colMode === "monthly" ? "/monthly" : ""}/export.xlsx?${finQS()}`} />
              <PrintActBtn />
            </span>
          </div>
          {/* Print-only QuickBooks-style header */}
          <div className="print-only print-head">
            <div className="ph-company">Sompo Consulting</div>
            <div className="ph-title">{pnl?.col2 ? "Comparative Profit and Loss" : "Profit and Loss"}</div>
            <div className="ph-dates">{fmtLongDate(pnl?.col1?.from)} to {fmtLongDate(pnl?.col1?.to)}</div>
          </div>
          {colMode === "monthly" ? (
            (!pnlM || !pnlM.months || pnlM.months.length === 0) ? (
              <div className="empty">No monthly data for this period.</div>
            ) : (
              <div className="pnl-monthly-wrap">
                <table className="data-table compact pnl-table pnl-monthly">
                  <thead><tr><th></th>
                    {pnlM.months.map((m) => <th key={m} className="num">{fmtMonthMD(m)}</th>)}
                    <th className="num">Total</th>
                  </tr></thead>
                  <tbody>
                    <tr className="pnl-section"><td colSpan={pnlM.months.length + 2}>Income</td></tr>
                    {pnlM.income.map((r) => (
                      <tr key={r.label}><td className="indent">{r.label}</td>
                        {pnlM.months.map((m) => <td key={m} className="num">{finMoney(r.months[m])}</td>)}
                        <td className="num">{finMoney(r.total)}</td></tr>
                    ))}
                    <tr className="pnl-tot"><td><strong>Total Income</strong></td>
                      {pnlM.months.map((m) => <td key={m} className="num"><strong>{finMoney(pnlM.income_total.months[m])}</strong></td>)}
                      <td className="num"><strong>{finMoney(pnlM.income_total.total)}</strong></td></tr>
                    <tr className="pnl-section"><td colSpan={pnlM.months.length + 2}>Expenses</td></tr>
                    {pnlM.expenses.map((r) => (
                      <tr key={r.label}><td className="indent">{r.label}</td>
                        {pnlM.months.map((m) => <td key={m} className="num">{finMoney(r.months[m])}</td>)}
                        <td className="num">{finMoney(r.total)}</td></tr>
                    ))}
                    <tr className="pnl-tot"><td><strong>Total Expenses</strong></td>
                      {pnlM.months.map((m) => <td key={m} className="num"><strong>{finMoney(pnlM.expenses_total.months[m])}</strong></td>)}
                      <td className="num"><strong>{finMoney(pnlM.expenses_total.total)}</strong></td></tr>
                    <tr className="pnl-net"><td><strong>Net Income</strong></td>
                      {pnlM.months.map((m) => <td key={m} className="num"><strong>{finMoney(pnlM.net.months[m])}</strong></td>)}
                      <td className="num"><strong>{finMoney(pnlM.net.total)}</strong></td></tr>
                  </tbody>
                </table>
              </div>
            )
          ) : pnlState === "loading" ? (
            <div className="empty">Loading…</div>
          ) : pnlState === "error" ? (
            <div className="empty fin-error">
              <div>Couldn’t load the P&amp;L.</div>
              <button className="btn ghost small" onClick={loadPnl}>Retry</button>
            </div>
          ) : !pnl || (pnl.revenue === 0 && pnl.expenses === 0) ? (
            <div className="empty">No revenue or expenses in {pnl ? (pnl.col1?.label || "").toLowerCase() : "this period"}. Try a wider period or add expenses below.</div>
          ) : (() => {
            const cmp = !!pnl.col2;
            const span = cmp ? 5 : 2;
            const amtCells = (cur, prior) => (<>
              <td className="num link-amt">{finMoney(cur)}</td>
              {cmp && <td className="num">{finMoney(prior)}</td>}
              {cmp && <td className="num">{finMoney((cur || 0) - (prior || 0))}</td>}
              {cmp && <td className="num">{pct(cur, prior)}</td>}
            </>);
            return (
            <table className="data-table compact pnl-table">
              {cmp && (
                <thead><tr className="pnl-colhead">
                  <th></th><th className="num">{pnl.col1.label}</th>
                  <th className="num">{pnl.col2.label}</th>
                  <th className="num">Change</th><th className="num">% Change</th>
                </tr></thead>
              )}
              <tbody>
                <tr className="pnl-section" onClick={() => setCollapse((c) => ({ ...c, income: !c.income }))}>
                  <td colSpan={span}><span className="pnl-caret">{collapse.income ? "▸" : "▾"}</span> Income</td>
                </tr>
                {!collapse.income && pnl.income.map((r) => (
                  <tr key={r.label} className="pnl-line" onClick={() => openView("income", r.label)} title={`Show ${r.label} detail`}>
                    <td className="indent">{r.label}</td>
                    {amtCells(r.cur, r.prior)}
                  </tr>
                ))}
                <tr className="pnl-tot pnl-line" onClick={() => openView("income", null)} title="Show all income detail">
                  <td><strong>Total Income</strong></td>
                  {amtCells(pnl.income_total.cur, pnl.income_total.prior)}</tr>

                <tr className="pnl-section" onClick={() => setCollapse((c) => ({ ...c, expenses: !c.expenses }))}>
                  <td colSpan={span}><span className="pnl-caret">{collapse.expenses ? "▸" : "▾"}</span> Expenses</td>
                </tr>
                {!collapse.expenses && pnl.expense_rows.map((r) => (
                  <tr key={r.label} className="pnl-line" onClick={() => openView("expenses", r.label)} title={`Show ${r.label} detail`}>
                    <td className="indent">{r.label}</td>
                    {amtCells(r.cur, r.prior)}
                  </tr>
                ))}
                <tr className="pnl-tot pnl-line" onClick={() => openView("expenses", null)} title="Show all expense detail">
                  <td><strong>Total Expenses</strong></td>
                  {amtCells(pnl.expenses_total.cur, pnl.expenses_total.prior)}</tr>

                <tr className={`pnl-net ${pnl.net < 0 ? "neg" : ""}`}>
                  <td><strong>Net Income</strong></td>
                  {amtCells(pnl.net_total.cur, pnl.net_total.prior)}</tr>
              </tbody>
            </table>
            );
          })()}
          <div className="print-only print-basis">{basis === "cash" ? "Cash basis" : "Accrual basis"}</div>
        </div>
        </>
        )}

        {/* ============ INCOME DETAIL ============ */}
        {view === "income" && (
          <div className="detail-card">
            <div className="sub-head"><h3>Income detail{incomeFilter ? ` — ${incomeFilter}` : ""}</h3>
              <span className="sub-head-right">
                {incomeFilter && <button className="btn ghost small" onClick={() => setIncomeFilter(null)}>Show all</button>}
                <span className="muted small">{rev?.label}</span>
                <ExcelActBtn href={`${API}/finance/revenue/export.xlsx?${finQS()}`} />
                <PrintActBtn />
              </span>
            </div>
            {(!rev || incItems.length === 0) ? (
              <div className="muted small pad">No income in this period{incomeFilter ? ` for ${incomeFilter}` : ""}.</div>
            ) : (
              <>
                <table className="data-table compact">
                  <thead><tr><th>Client</th><th>Description</th><th>Date</th><th>Method</th><th>Amount</th></tr></thead>
                  <tbody>
                    {incItems.map((it, i) => (
                      <tr key={i}>
                        <td>{it.client_id ? <button className="link-btn" onClick={() => onOpenClient && onOpenClient(it.client_id)}>{it.client_name}</button> : it.client_name}</td>
                        <td>{it.description}</td>
                        <td className="muted small">{fmtMD(it.date)}</td>
                        <td>{it.method || <span className="muted">—</span>}</td>
                        <td className="num">{finMoney(it.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="pnl-tot"><td colSpan={4}><strong>Total</strong></td><td className="num"><strong>{finMoney(incItemsTotal)}</strong></td></tr></tfoot>
                </table>
                {!incomeFilter && rev && (
                <div className="fin-two-col" style={{ marginTop: 14 }}>
                  <div>
                    <div className="rem-section-lbl">By month</div>
                    <table className="data-table compact"><tbody>
                      {rev.by_month.map((m) => <tr key={m.month}><td>{fmtMonthMD(m.month)}</td><td className="num">{finMoney(m.amount)}</td></tr>)}
                    </tbody></table>
                  </div>
                  <div>
                    <div className="rem-section-lbl">By payment method</div>
                    <table className="data-table compact"><tbody>
                      {rev.by_method.map((m) => <tr key={m.method}><td>{m.method}</td><td className="num">{finMoney(m.amount)}</td></tr>)}
                    </tbody></table>
                  </div>
                </div>
                )}
              </>
            )}
            {unbilled && unbilled.by_client.length > 0 && (
              <>
                <div className="rem-section-lbl" style={{ marginTop: 16 }}>Unbilled time — {finMoney(unbilled.total)} not yet charged</div>
                <table className="data-table compact">
                  <thead><tr><th>Client</th><th>Hours</th><th>Rate</th><th>Value</th></tr></thead>
                  <tbody>
                    {unbilled.by_client.map((c) => (
                      <tr key={c.client_id}>
                        <td><button className="link-btn" onClick={() => onOpenClient && onOpenClient(c.client_id)}>{c.client_name}</button></td>
                        <td>{c.hours}</td><td>{c.rate ? finMoney(c.rate) : "—"}</td><td>{finMoney(c.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}

        {/* ============ A/R DETAIL ============ */}
        {view === "ar" && (
        <div className="detail-card">
          <div className="sub-head"><h3>Accounts Receivable</h3>
            <span className="sub-head-right">
              <label className="muted small">As of
                <input type="date" value={arAsOf || (ar?.as_of || "")}
                  onChange={(e) => setArAsOf(e.target.value)} style={{ marginLeft: 6 }} />
              </label>
              {arAsOf && <button className="btn ghost small" onClick={() => setArAsOf("")}>Today</button>}
              <span className="muted small">Outstanding <strong>{finMoney(ar?.total || 0)}</strong></span>
              <ExcelActBtn href={`${API}/finance/ar/export.xlsx${arAsOf ? `?as_of=${arAsOf}` : ""}`} />
              <PrintActBtn />
            </span>
          </div>
          {/* Print-only QuickBooks-style header */}
          <div className="print-only print-head">
            <div className="ph-company">Sompo Consulting</div>
            <div className="ph-title">A/R Aging Summary</div>
            <div className="ph-dates">As of {fmtMD(ar?.as_of)}</div>
          </div>
          {!ar ? <div className="empty">Loading…</div> : ar.total === 0 ? (
            <div className="muted small pad">Nothing outstanding — all bills are paid. 🎉</div>
          ) : (
            <>
              <div className="fin-aging">
                {[["all", "All"], ["d0_30", "0–30 days"], ["d31_60", "31–60"], ["d61_90", "61–90"], ["d90", "90+"]].map(([k, lbl]) => (
                  <div key={k} className={`fin-age-card clickable${arBucket === k || (k === "all" && !arBucket) ? " sel" : ""}${k === "d90" && ar.buckets[k] > 0 ? " od" : ""}`}
                    onClick={() => setArBucket(k === "all" ? null : k)} title="Click to filter">
                    <div className="fin-age-amt">{finMoney(k === "all" ? ar.total : ar.buckets[k])}</div>
                    <div className="fin-age-lbl">{lbl}</div>
                  </div>
                ))}
              </div>
              {/* Aging summary: client × buckets */}
              <div className="rem-section-lbl">A/R Aging Summary{arBucket ? ` — filtered to ${{ d0_30: "0–30", d31_60: "31–60", d61_90: "61–90", d90: "90+" }[arBucket]} days` : ""}</div>
              <table className="data-table compact">
                <thead><tr><th>Client</th><th className="num">0–30</th><th className="num">31–60</th><th className="num">61–90</th><th className="num">90+</th><th className="num">Total</th></tr></thead>
                <tbody>
                  {ar.by_client.filter((c) => !arBucket || (c[arBucket] || 0) > 0).map((c) => (
                    <tr key={c.client_id}>
                      <td><button className="link-btn" onClick={() => onOpenClient && onOpenClient(c.client_id)}>{c.client_name}</button></td>
                      <td className="num">{c.d0_30 ? finMoney(c.d0_30) : "—"}</td>
                      <td className="num">{c.d31_60 ? finMoney(c.d31_60) : "—"}</td>
                      <td className="num">{c.d61_90 ? finMoney(c.d61_90) : "—"}</td>
                      <td className={`num ${c.d90 ? "overdue" : ""}`}>{c.d90 ? finMoney(c.d90) : "—"}</td>
                      <td className="num"><strong>{finMoney(c.amount)}</strong></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr className="pnl-tot">
                  <td><strong>Total</strong></td>
                  <td className="num"><strong>{finMoney(ar.buckets.d0_30)}</strong></td>
                  <td className="num"><strong>{finMoney(ar.buckets.d31_60)}</strong></td>
                  <td className="num"><strong>{finMoney(ar.buckets.d61_90)}</strong></td>
                  <td className="num"><strong>{finMoney(ar.buckets.d90)}</strong></td>
                  <td className="num"><strong>{finMoney(ar.total)}</strong></td>
                </tr></tfoot>
              </table>
            </>
          )}
        </div>
        )}

        {/* ============ EXPENSES DETAIL ============ */}
        {view === "expenses" && (
        <div className="detail-card">
          <div className="sub-head"><h3>Expenses{expenseFilter ? ` — ${expenseFilter}` : ""}</h3>
            <span className="sub-head-right">
              {expenseFilter && <button className="btn ghost small" onClick={() => setExpenseFilter(null)}>Show all</button>}
              <span className="muted small">{expDetail ? `${expDetail.label} · ${finMoney(expenseFilter ? expRowsTotal : expDetail.total)}` : ""}</span>
              <ExcelActBtn href={`${API}/finance/expenses/export.xlsx`} />
              <PrintActBtn />
            </span>
          </div>

          {/* Per-month detail (recurring expanded into monthly rows, one per charge date) */}
          {expRows.length > 0 ? (
            <table className="data-table compact" style={{ marginBottom: 18 }}>
              <thead><tr><th>Account</th><th>Name</th><th>Date</th><th className="num">Amount</th></tr></thead>
              <tbody>
                {expRows.map((r, i) => (
                  <tr key={i}>
                    <td>{(i === 0 || expRows[i - 1].account !== r.account) ? <strong>{r.account}</strong> : ""}</td>
                    <td>{r.name}</td>
                    <td className="muted small">{fmtMD(r.date)}</td>
                    <td className="num">{finMoney(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="pnl-tot"><td colSpan={3}><strong>Total</strong></td><td className="num"><strong>{finMoney(expRowsTotal)}</strong></td></tr></tfoot>
            </table>
          ) : (
            <div className="muted small pad">No expenses in this period{expenseFilter ? ` for ${expenseFilter}` : ""}.</div>
          )}

          <button className="exp-manage-toggle" onClick={() => setShowManage((v) => !v)}>
            {showManage ? "▾" : "▸"} Manage / edit all expenses ({exp.length})
          </button>
          {showManage && (exp.length === 0 ? (
            <div className="muted small pad">No expenses yet. Use “＋ Add expense”.</div>
          ) : (
            <table className="data-table compact">
              <thead><tr><th>Name</th><th>Category</th><th>Amount</th><th>Frequency</th><th>Runs</th><th>Paid</th><th></th></tr></thead>
              <tbody>
                {exp.map((e) => (editId === e.id ? (
                  <tr key={e.id} className="exp-editing">
                    <td><input value={editRow.name} onChange={(ev) => setEditRow({ ...editRow, name: ev.target.value })} /></td>
                    <td>
                      <select value={editRow.category} onChange={(ev) => setEditRow({ ...editRow, category: ev.target.value })}>
                        {expAccts.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td><input type="number" min="0" step="0.01" className="year-input"
                      value={editRow.amount} onChange={(ev) => setEditRow({ ...editRow, amount: ev.target.value })} /></td>
                    <td>
                      <select value={editRow.frequency} onChange={(ev) => setEditRow({ ...editRow, frequency: ev.target.value })}>
                        {EXP_FREQS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </td>
                    <td className="exp-dates">
                      <input type="date" value={editRow.incurred_date} title="Start"
                        onChange={(ev) => setEditRow({ ...editRow, incurred_date: ev.target.value })} />
                      {editRow.frequency !== "once" && (
                        <input type="date" value={editRow.end_date} title="End (blank = ongoing)"
                          onChange={(ev) => setEditRow({ ...editRow, end_date: ev.target.value })} />
                      )}
                    </td>
                    <td colSpan={2} className="exp-edit-actions">
                      <button className="btn small primary" onClick={saveEdit}>Save</button>
                      <button className="btn small ghost" onClick={cancelEdit}>Cancel</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={e.id} className={e.active ? "" : "row-inactive"}>
                    <td>{e.name}</td>
                    <td><span className="badge entity">{e.category}</span></td>
                    <td>{finMoney(e.amount)}</td>
                    <td>{(EXP_FREQS.find(([v]) => v === e.frequency) || [, e.frequency])[1]}</td>
                    <td className="muted small">
                      {e.frequency === "once" ? fmtMD(e.incurred_date) :
                        `${fmtMD(e.incurred_date)} → ${e.end_date ? fmtMD(e.end_date) : "ongoing"}`}
                    </td>
                    <td><PaidCell e={e} /></td>
                    <td className="exp-row-actions">
                      <button className="icon-btn" title="Edit" onClick={() => startEdit(e)}>✎</button>
                      <button className="icon-btn" title="Delete" onClick={() => delExp(e.id)}>✕</button>
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          ))}
          {oneTime > 0 && (
            <div className="muted small pad">One-time costs logged: <strong>{finMoney(oneTime)}</strong></div>
          )}
        </div>
        )}
      </section>

      {/* Add-expense popup */}
      {showExpModal && (
        <div className="modal-overlay" onClick={() => setShowExpModal(false)}>
          <div className="exp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sub-head"><h3>Add expense</h3>
              <button className="icon-btn" title="Close" onClick={() => setShowExpModal(false)}>✕</button>
            </div>
            <div className="exp-form-grid">
              <label>Name<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
              <label>Account
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {expAccts.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label>Amount<input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></label>
              <label>Frequency
                <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}>
                  {EXP_FREQS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
              <label>Start<input type="date" value={form.incurred_date} onChange={(e) => setForm({ ...form, incurred_date: e.target.value })} /></label>
              {form.frequency !== "once" && (
                <label>End (optional)<input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></label>
              )}
            </div>
            <div className="exp-modal-actions">
              <button className="btn ghost small" onClick={() => setShowExpModal(false)}>Cancel</button>
              <button className="btn primary small" onClick={async () => {
                if (!form.name.trim() || !form.amount) { window.alert("Enter a name and amount."); return; }
                await addExp(); setShowExpModal(false);
              }}>Add expense</button>
            </div>
          </div>
        </div>
      )}

      {/* Manage accounts popup */}
      {showAccts && (
        <div className="modal-overlay" onClick={() => setShowAccts(false)}>
          <div className="exp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sub-head"><h3>Accounts</h3>
              <span className="sub-head-right">
                <button className="btn small ghost" title="Import income + expense accounts from your QuickBooks chart-of-accounts CSV"
                  onClick={importChart}>Import accounts</button>
                {qbo && qbo.connected && (
                  <button className="btn small ghost" title="Import your products/services live from QuickBooks (for billing)"
                    onClick={importItems}>Import products/services</button>
                )}
                <button className="icon-btn" title="Close" onClick={() => setShowAccts(false)}>✕</button>
              </span>
            </div>
            <div className="manual-time" style={{ marginBottom: 12 }}>
              <input placeholder="New account name" value={newAcct.name} style={{ flex: "1 1 160px" }}
                onChange={(e) => setNewAcct({ ...newAcct, name: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") addAccount(); }} />
              <select value={newAcct.kind} onChange={(e) => setNewAcct({ ...newAcct, kind: e.target.value })}>
                <option value="expense">Expense</option>
                <option value="income">Income</option>
              </select>
              <button className="btn small primary" onClick={addAccount}>Add</button>
            </div>
            <div className="fin-two-col">
              {[["income", "Income accounts"], ["expense", "Expense accounts"]].map(([k, lbl]) => (
                <div key={k}>
                  <div className="rem-section-lbl">{lbl}</div>
                  {accounts[k].length === 0 ? <div className="muted small pad">None yet.</div> : (
                    <ul className="acct-list">
                      {accounts[k].map((a) => (
                        <li key={a.id}>{a.name}
                          <button className="icon-btn" title="Remove" onClick={() => delAccount(a.id)}>✕</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ---------------- Alarm poller (browser + in-app popups) ---------------- */

// Short audible alert via the Web Audio API (no audio file needed). Some
// browsers suppress sound on Notifications, so we always play this ourselves.
let _alarmAudioCtx = null;
function alarmBeep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!_alarmAudioCtx) _alarmAudioCtx = new Ctx();
    const ctx = _alarmAudioCtx;
    if (ctx.state === "suspended" && ctx.resume) ctx.resume();
    // Two quick rising beeps.
    [0, 0.32].forEach((offset, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = i === 0 ? 880 : 1175;
      const t0 = ctx.currentTime + offset;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.3, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.28);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.3);
    });
  } catch (e) { /* ignore */ }
}

function AlarmPoller({ onOpenClient }) {
  const [popups, setPopups] = useState([]);   // alarms currently shown in-app
  const seen = useRef(new Set());

  // Ask for desktop-notification permission up front so it's ready when an
  // alarm becomes due.
  useEffect(() => {
    if (typeof Notification !== "undefined"
        && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  const ack = useCallback((id) => {
    fetch(`${API}/tasks/${id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ alarm_fired: true }),
    }).catch(() => {});
    setPopups((p) => p.filter((x) => x.id !== id));
  }, []);

  const poll = useCallback(() => {
    fetch(`${API}/tasks/alarms`).then((r) => r.json()).then((due) => {
      if (!Array.isArray(due) || !due.length) return;
      const fresh = due.filter((d) => !seen.current.has(d.id));
      if (!fresh.length) return;
      fresh.forEach((d) => seen.current.add(d.id));
      // Audible alert on every due alarm (in-app + notification paths).
      alarmBeep();
      // Real desktop notification (best-effort).
      if (typeof Notification !== "undefined") {
        const fire = () => fresh.forEach((d) => {
          try {
            new Notification("Spoom reminder", {
              body: d.title, tag: `spoom-alarm-${d.id}`, requireInteraction: true,
            });
          } catch (e) { /* ignore */ }
        });
        if (Notification.permission === "granted") fire();
        else if (Notification.permission !== "denied") Notification.requestPermission().then((p) => { if (p === "granted") fire(); });
      }
      // In-app popup as a reliable fallback.
      setPopups((p) => [...p, ...fresh]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
  }, [poll]);

  if (!popups.length) return null;
  return (
    <div className="alarm-stack">
      {popups.map((a) => (
        <div key={a.id} className="alarm-popup">
          <div className="alarm-popup-head">🔔 Reminder</div>
          <div className="alarm-popup-body">{a.title}</div>
          {a.client_name && (
            <button className="link-btn" onClick={() => { onOpenClient && onOpenClient(a.client_id); ack(a.id); }}>
              Open {a.client_name}
            </button>
          )}
          <div className="alarm-popup-actions">
            <button className="btn small" onClick={() => ack(a.id)}>Dismiss</button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Floating work-timer panel (pinned left) --------------- */
// Top-level: persists across tabs. Polls the single open timer (running or
// paused) and ticks locally. Controls: pause/unpause, stop, delete, and
// "Finish & post" (close + save the session). Minimizes to a small pill.
function FloatingTimer({ onOpenClient, onChange }) {
  const [active, setActive] = useState(null);   // {id, client_id, client_name, elapsed, paused, label}
  const [, setTick] = useState(0);              // local 1s ticker (forces re-render)
  const [min, setMin] = useState(false);        // collapsed to a pill?
  const baseRef = useRef({ elapsed: 0, at: 0, paused: true });

  const load = useCallback(() => {
    fetch(`${API}/timer/active`).then((r) => r.json())
      .then((d) => {
        setActive(d);
        baseRef.current = {
          elapsed: d ? d.elapsed : 0,
          at: Date.now(),
          paused: d ? d.paused : true,
        };
      })
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  // Re-poll the server every 4s so external start/stop stays in sync.
  useEffect(() => {
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);
  // Local 1s ticker for a smooth elapsed display.
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  if (!active) return null;

  const b = baseRef.current;
  const shown = b.paused
    ? b.elapsed
    : b.elapsed + Math.max(0, Math.floor((Date.now() - b.at) / 1000));

  const ctl = async (action) => {
    await fetch(`${API}/timer/${active.id}/${action}`, { method: "POST" })
      .catch(() => {});
    if (action === "delete" || action === "stop" || action === "finish") {
      setActive(null);
    }
    load();
    if (onChange) onChange();
  };

  if (min) {
    return (
      <button className={`float-timer-pill${active.paused ? " paused" : ""}`}
        title={`${active.client_name} — ${fmtDuration(shown)}`}
        onClick={() => setMin(false)}>
        <span className="ft-dot" />
        {fmtDuration(shown)}
      </button>
    );
  }

  return (
    <div className={`float-timer${active.paused ? " paused" : ""}`}>
      <div className="ft-head">
        <span className="ft-dot" />
        <button className="ft-name" title="Open client"
          onClick={() => onOpenClient && onOpenClient(active.client_id)}>
          {active.client_name}
        </button>
        <button className="ft-min" title="Minimize"
          onClick={() => setMin(true)}>—</button>
      </div>
      {active.label && <div className="ft-label">{active.label}</div>}
      <div className="ft-time">{fmtDuration(shown)}{active.paused && <span className="ft-paused-tag"> paused</span>}</div>
      <div className="ft-btns">
        {active.paused ? (
          <button className="btn small" onClick={() => ctl("resume")}>▶ Resume</button>
        ) : (
          <button className="btn small ghost" onClick={() => ctl("pause")}>⏸ Pause</button>
        )}
        <button className="btn small" onClick={() => ctl("finish")}>✓ Finish &amp; post</button>
      </div>
      <div className="ft-btns">
        <button className="btn small ghost" onClick={() => ctl("stop")}>⏹ Stop</button>
        <button className="btn small danger" onClick={() => {
          if (window.confirm("Delete this running timer without saving?")) ctl("delete");
        }}>🗑 Delete</button>
      </div>
    </div>
  );
}

function Login({ onSuccess, notice }) {
  const [mode, setMode] = useState("password"); // password | mfa | forgot | sent
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [ident, setIdent] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const post = (path, body) => fetch(`${API}${path}`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const submitPassword = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const res = await post("/auth/login", { username, password });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.mfa_required) { setMode("mfa"); setCode(""); }
      else if (res.ok && data.ok) onSuccess(data.username);
      else setError(data.error || "Login failed. Please try again.");
    } catch { setError("Could not reach the server. Is the backend running?"); }
    setBusy(false);
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const res = await post("/auth/mfa_verify", { code });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) onSuccess(data.username);
      else setError(data.error || "That code didn't match.");
    } catch { setError("Could not reach the server."); }
    setBusy(false);
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await post("/auth/forgot", { identifier: ident });
      setMode("sent");
    } catch { setError("Could not reach the server."); }
    setBusy(false);
  };

  const doPasskeyLogin = async () => {
    setError(""); setBusy(true);
    try {
      const d = await loginWithPasskey();
      if (d && d.ok) onSuccess(d.username);
      else setError((d && d.error) || "Fingerprint sign-in didn't work.");
    } catch {
      setError("Fingerprint sign-in was cancelled, or isn't set up on this device yet.");
    }
    setBusy(false);
  };

  return (
    <div className="login-wrap">
      {mode === "password" && (
        <form className="login-card" onSubmit={submitPassword}>
          <img src="/spoom-logo.png" alt="Spoom" className="login-logo" />
          <h1 className="login-title">Sign in</h1>
          {notice && <div className="login-notice">{notice}</div>}
          {error && <div className="login-error">{error}</div>}
          <label className="login-label">Username
            <input className="login-input" value={username} autoFocus
              autoComplete="username" onChange={(e) => setUsername(e.target.value)} />
          </label>
          <label className="login-label">Password
            <input className="login-input" type="password" value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)} />
          </label>
          <button className="login-btn" type="submit" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
          {passkeysSupported && (
            <button type="button" className="login-btn ghost" disabled={busy}
              onClick={doPasskeyLogin}>
              🔒 Sign in with fingerprint / Face ID
            </button>
          )}
          <button type="button" className="login-link"
            onClick={() => { setMode("forgot"); setError(""); setIdent(username); }}>
            Forgot password?
          </button>
        </form>
      )}

      {mode === "mfa" && (
        <form className="login-card" onSubmit={submitCode}>
          <img src="/spoom-logo.png" alt="Spoom" className="login-logo" />
          <h1 className="login-title">Enter your code</h1>
          <p className="login-sub">Open your authenticator app and type the 6-digit code.</p>
          {error && <div className="login-error">{error}</div>}
          <input className="login-input login-code" value={code} autoFocus
            inputMode="numeric" maxLength={6} placeholder="123456"
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
          <button className="login-btn" type="submit" disabled={busy}>
            {busy ? "Checking…" : "Verify"}
          </button>
          <button type="button" className="login-link"
            onClick={() => { setMode("password"); setError(""); }}>
            Back
          </button>
        </form>
      )}

      {mode === "forgot" && (
        <form className="login-card" onSubmit={submitForgot}>
          <img src="/spoom-logo.png" alt="Spoom" className="login-logo" />
          <h1 className="login-title">Reset password</h1>
          <p className="login-sub">Enter your username or email and we'll send a reset link.</p>
          {error && <div className="login-error">{error}</div>}
          <label className="login-label">Username or email
            <input className="login-input" value={ident} autoFocus
              onChange={(e) => setIdent(e.target.value)} />
          </label>
          <button className="login-btn" type="submit" disabled={busy}>
            {busy ? "Sending…" : "Send reset link"}
          </button>
          <button type="button" className="login-link"
            onClick={() => { setMode("password"); setError(""); }}>
            Back to sign in
          </button>
        </form>
      )}

      {mode === "sent" && (
        <div className="login-card">
          <img src="/spoom-logo.png" alt="Spoom" className="login-logo" />
          <h1 className="login-title">Check your email</h1>
          <p className="login-sub">If that account exists, a reset link is on its way.
            The link works for 30 minutes.</p>
          <button type="button" className="login-btn"
            onClick={() => { setMode("password"); setError(""); }}>
            Back to sign in
          </button>
        </div>
      )}
    </div>
  );
}


function ResetPassword({ token }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Use at least 8 characters."); return; }
    if (password !== confirm) { setError("The two passwords don't match."); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/reset`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) setDone(true);
      else setError(data.error || "Could not reset the password.");
    } catch { setError("Could not reach the server."); }
    setBusy(false);
  };

  const goToLogin = () => { window.location.href = window.location.pathname; };

  return (
    <div className="login-wrap">
      {done ? (
        <div className="login-card">
          <img src="/spoom-logo.png" alt="Spoom" className="login-logo" />
          <h1 className="login-title">Password changed</h1>
          <p className="login-sub">You can now sign in with your new password.</p>
          <button className="login-btn" onClick={goToLogin}>Go to sign in</button>
        </div>
      ) : (
        <form className="login-card" onSubmit={submit}>
          <img src="/spoom-logo.png" alt="Spoom" className="login-logo" />
          <h1 className="login-title">Set a new password</h1>
          {error && <div className="login-error">{error}</div>}
          <label className="login-label">New password
            <input className="login-input" type="password" value={password} autoFocus
              autoComplete="new-password" onChange={(e) => setPassword(e.target.value)} />
          </label>
          <label className="login-label">Type it again
            <input className="login-input" type="password" value={confirm}
              autoComplete="new-password" onChange={(e) => setConfirm(e.target.value)} />
          </label>
          <button className="login-btn" type="submit" disabled={busy}>
            {busy ? "Saving…" : "Save new password"}
          </button>
        </form>
      )}
    </div>
  );
}


function SecuritySettings({ me, onClose, onChange }) {
  const [tab, setTab] = useState("profile");   // profile | firm | security | templates
  const [drilled, setDrilled] = useState(false); // mobile: a tab's sub-view is open
  const [dirty, setDirty] = useState(false);   // unsaved-changes guard
  const [openTpl, setOpenTpl] = useState(null); // which template accordion is expanded
  const csvInput = useRef(null);
  const logoInput = useRef(null);
  const [logoBust, setLogoBust] = useState(0);
  const uploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const res = await fetch(`${API}/settings/firm_logo`, { method: "POST", body: fd });
    if (res.ok) { setLogoBust(Date.now()); flash("Firm logo updated."); }
    else flash("Couldn’t upload that logo.");
    e.target.value = "";
  };
  const removeLogo = async () => {
    if (!window.confirm("Remove the firm logo?")) return;
    await fetch(`${API}/settings/firm_logo`, { method: "DELETE" });
    setLogoBust(Date.now()); flash("Firm logo removed.");
  };
  const importCsv = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const res = await fetch(`${API}/import`, { method: "POST", body: fd });
    const r = await res.json().catch(() => ({}));
    window.alert(`Imported ${r.added || 0} client(s), skipped ${r.skipped || 0}.`);
    e.target.value = "";
  };
  const [idle, setIdle] = useState(me.idle_minutes ?? 30);
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("spoom_theme") || "light"; } catch { return "light"; }
  });
  const pickTheme = (val) => {
    setTheme(val);
    try { localStorage.setItem("spoom_theme", val); } catch { /* ignore */ }
    applyTheme(val);
  };
  const [email, setEmail] = useState(me.email || "");
  const [portalUrl, setPortalUrl] = useState("");
  const [firm, setFirm] = useState(null);
  useEffect(() => {
    fetch(`${API}/settings/sales_tax`).then((r) => r.json())
      .then((d) => setPortalUrl(d.portal_url || "")).catch(() => {});
    fetch(`${API}/settings/firm`).then((r) => r.json())
      .then(setFirm).catch(() => {});
  }, []);
  const setFirmField = (k, v) => { setFirm((f) => ({ ...(f || {}), [k]: v })); setDirty(true); };
  const saveFirm = async () => {
    await fetch(`${API}/settings/firm`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(firm || {}),
    });
    setDirty(false);
    flash("Firm & invoice details saved.");
  };
  const savePortal = async () => {
    await fetch(`${API}/settings/sales_tax`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ portal_url: portalUrl }),
    });
    setDirty(false);
    flash("Sales tax filing link saved.");
  };
  const [mfaOn, setMfaOn] = useState(!!me.mfa_enabled);
  const [msg, setMsg] = useState("");
  // MFA enrollment
  const [enroll, setEnroll] = useState(null); // { qr_svg, secret }
  const [enrollCode, setEnrollCode] = useState("");
  const [enrollErr, setEnrollErr] = useState("");
  // MFA disable
  const [disabling, setDisabling] = useState(false);
  const [disablePw, setDisablePw] = useState("");
  const [disableErr, setDisableErr] = useState("");
  // Editable email templates
  const [tpl1040, setTpl1040] = useState("");
  const [tplBiz, setTplBiz] = useState("");
  const [tplReq, setTplReq] = useState("");
  useEffect(() => {
    fetch(`${API}/email_templates`).then((r) => r.json()).then((d) => {
      setTpl1040(d.template_1040 || ""); setTplBiz(d.template_business || "");
      setTplReq(d.template_request || "");
    }).catch(() => {});
  }, []);
  const saveTemplates = async () => {
    await fetch(`${API}/email_templates`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ template_1040: tpl1040, template_business: tplBiz, template_request: tplReq }),
    });
    setDirty(false);
    flash("Email templates saved.");
  };
  const resetTemplates = async () => {
    if (!window.confirm("Reset all email templates to the original wording?")) return;
    const d = await fetch(`${API}/email_templates/defaults`).then((r) => r.json());
    setTpl1040(d.template_1040 || ""); setTplBiz(d.template_business || "");
    setTplReq(d.template_request || "");
    flash("Reset — click Save to keep it.");
  };

  const put = (body) => fetch(`${API}/settings`, {
    method: "PUT", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(""), 2500); };
  // Unsaved-changes guard: confirm before leaving if edits aren't saved.
  const guardedClose = () => {
    if (dirty && !window.confirm("You have unsaved changes. Are you sure you want to leave?")) return;
    onClose();
  };
  const discard = () => {
    if (!dirty || window.confirm("Discard your unsaved changes?")) { setDirty(false); onClose(); }
  };
  const goTab = (t) => {
    if (t !== tab && dirty && !window.confirm("You have unsaved changes. Switch tabs and lose them?")) return;
    setDirty(false); setTab(t); setDrilled(true);
    // Re-sync the tab's data from the server so discarded edits don't linger.
    fetch(`${API}/settings/firm`).then((r) => r.json()).then(setFirm).catch(() => {});
    fetch(`${API}/email_templates`).then((r) => r.json()).then((d) => {
      setTpl1040(d.template_1040 || ""); setTplBiz(d.template_business || "");
      setTplReq(d.template_request || "");
    }).catch(() => {});
  };
  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // Fingerprint / Face-ID passkeys
  const [passkeys, setPasskeys] = useState(0);
  const [pkBusy, setPkBusy] = useState(false);
  useEffect(() => {
    fetch(`${API}/auth/webauthn/status`).then((r) => r.json())
      .then((d) => setPasskeys(d.count || 0)).catch(() => {});
  }, []);
  const enrollPasskey = async () => {
    setPkBusy(true);
    try {
      const d = await registerPasskey();
      if (d && d.ok) { setPasskeys((n) => n + 1); flash("Fingerprint sign-in is on."); }
      else flash((d && d.error) || "Couldn't set up fingerprint sign-in.");
    } catch { flash("Setup was cancelled, or this device doesn't support it."); }
    setPkBusy(false);
  };
  const removePasskeys = async () => {
    if (!window.confirm("Turn off fingerprint sign-in on all devices?")) return;
    await fetch(`${API}/auth/webauthn/remove`, { method: "POST" });
    setPasskeys(0); flash("Fingerprint sign-in turned off.");
  };

  const saveIdle = async (v) => {
    setIdle(v);
    await put({ idle_minutes: Number(v) });
    onChange && onChange();
    flash("Saved.");
  };
  const saveEmail = async () => { await put({ email }); onChange && onChange(); flash("Email saved."); };

  const startMfa = async () => {
    setEnrollErr("");
    const res = await fetch(`${API}/auth/mfa/setup`, { method: "POST" });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.ok) setEnroll({ qr_svg: data.qr_svg, secret: data.secret });
    else setEnrollErr(data.error || "Could not start setup.");
  };
  const confirmMfa = async () => {
    setEnrollErr("");
    const res = await fetch(`${API}/auth/mfa/enable`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code: enrollCode }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.ok) { setMfaOn(true); setEnroll(null); setEnrollCode(""); onChange && onChange(); flash("Two-step is on."); }
    else setEnrollErr(data.error || "That code didn't match.");
  };
  const disableMfa = async () => {
    setDisableErr("");
    const res = await fetch(`${API}/auth/mfa/disable`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password: disablePw }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.ok) { setMfaOn(false); setDisabling(false); setDisablePw(""); onChange && onChange(); flash("Two-step is off."); }
    else setDisableErr(data.error || "Wrong password.");
  };

  const TABS = [
    ["profile", "App Profile", "◐"],
    ["firm", "Firm Details", "🏢"],
    ["security", "Security Setup", "🔒"],
    ["templates", "Email Templates", "✉"],
  ];

  return (
    <div className="settings-canvas">
      <div className="settings-topbar">
        <h2>Settings</h2>
        <div className="settings-topbar-actions">
          {dirty && <span className="settings-dirty">Unsaved changes</span>}
          <button className="btn ghost" onClick={discard}>{dirty ? "Discard" : "Close"}</button>
          <button className="btn primary" onClick={guardedClose}>Done</button>
        </div>
      </div>
      {msg && <div className="settings-msg">{msg}</div>}

      <div className={`settings-body${drilled ? " drilled" : ""}`}>
        <nav className="settings-nav">
          {TABS.map(([k, label, ic]) => (
            <button key={k} className={`settings-nav-item${tab === k ? " active" : ""}`}
              onClick={() => goTab(k)}>
              <span className="settings-nav-ic" aria-hidden="true">{ic}</span>{label}
              <span className="settings-nav-chev" aria-hidden="true">›</span>
            </button>
          ))}
        </nav>

        <div className="settings-panel">
          <button className="settings-back" onClick={() => setDrilled(false)}>‹ All settings</button>
          {tab === "profile" && (
            <>
              <section className="settings-section">
                <h3>Appearance</h3>
                <p className="settings-hint">Choose a light or dark look, or follow your device setting.</p>
                <div className="theme-seg">
                  {[["light", "Light", "☀"], ["dark", "Dark", "☾"], ["system", "System", "◐"]].map(([val, label, ic]) => (
                    <button key={val} type="button"
                      className={`theme-seg-btn${theme === val ? " active" : ""}`}
                      onClick={() => pickTheme(val)}>
                      <span className="theme-seg-ic">{ic}</span>{label}
                    </button>
                  ))}
                </div>
              </section>
              <section className="settings-section">
                <h3>Auto sign-out</h3>
                <p className="settings-hint">Sign me out automatically after this much time with no activity.</p>
                <select className="settings-select" value={idle}
                  onChange={(e) => saveIdle(e.target.value)}>
                  <option value={5}>5 minutes</option>
                  <option value={10}>10 minutes</option>
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={120}>2 hours</option>
                  <option value={0}>Never</option>
                </select>
              </section>
              <section className="settings-section">
                <h3>Client data (CSV)</h3>
                <p className="settings-hint">Bulk-import clients from a spreadsheet, or export your full client list.</p>
                <input type="file" accept=".csv" ref={csvInput} style={{ display: "none" }} onChange={importCsv} />
                <div className="settings-row">
                  <button className="settings-btn" onClick={() => csvInput.current && csvInput.current.click()}>Import CSV</button>
                  <a className="settings-btn ghost" href={`${API}/export.csv`}>Export CSV</a>
                </div>
              </section>
            </>
          )}

          {tab === "firm" && (
            <>
              {firm && (
                <section className="settings-section">
                  <h3>Firm &amp; invoice details</h3>
                  <p className="settings-hint">Printed on the client-facing invoice.</p>
                  <div className="firm-logo-row">
                    <div className="firm-logo-preview">
                      <img src={`${API}/firm-logo?v=${logoBust}`} alt="Firm logo"
                        onError={(e) => { e.target.style.display = "none"; }} />
                    </div>
                    <div className="firm-logo-actions">
                      <input type="file" accept="image/*" ref={logoInput} style={{ display: "none" }} onChange={uploadLogo} />
                      <button className="settings-btn" onClick={() => logoInput.current && logoInput.current.click()}>Upload logo</button>
                      <button className="settings-btn ghost" onClick={removeLogo}>Remove</button>
                      <p className="settings-hint" style={{ margin: "6px 0 0" }}>Wide transparent PNG works best. Shows on the invoice &amp; startup screen.</p>
                    </div>
                  </div>
                  <div className="settings-row col">
                    <label>Firm name
                      <input className="settings-input" value={firm.firm_name || ""}
                        onChange={(e) => setFirmField("firm_name", e.target.value)} /></label>
                    <label>Tagline
                      <input className="settings-input" value={firm.firm_tagline || ""}
                        onChange={(e) => setFirmField("firm_tagline", e.target.value)} /></label>
                    <label>Address
                      <textarea className="settings-input" rows={2} value={firm.firm_address || ""}
                        onChange={(e) => setFirmField("firm_address", e.target.value)} /></label>
                    <label>EIN
                      <input className="settings-input" value={firm.firm_ein || ""}
                        onChange={(e) => setFirmField("firm_ein", e.target.value)} /></label>
                    <label>Payment note
                      <textarea className="settings-input" rows={2} value={firm.firm_pay_note || ""}
                        onChange={(e) => setFirmField("firm_pay_note", e.target.value)} /></label>
                    <div className="settings-row" style={{ gap: 12 }}>
                      <label style={{ flex: 1 }}>Due in (days)
                        <input className="settings-input" type="number" min="0" value={firm.invoice_terms_days || ""}
                          onChange={(e) => setFirmField("invoice_terms_days", e.target.value)} /></label>
                      <label style={{ flex: 1 }}>Card fee rate
                        <input className="settings-input" type="number" step="0.001" min="0" value={firm.cc_rate || ""}
                          onChange={(e) => setFirmField("cc_rate", e.target.value)} /></label>
                    </div>
                    <button className="btn primary" style={{ alignSelf: "flex-start" }} onClick={saveFirm}>Save firm details</button>
                  </div>
                </section>
              )}
              <section className="settings-section">
                <h3>Sales tax filing link</h3>
                <p className="settings-hint">The page the “File on NJ portal” button opens on a sales tax worksheet.</p>
                <div className="settings-row">
                  <input className="settings-input" type="url" value={portalUrl}
                    placeholder="https://taxportal.nj.gov"
                    onChange={(e) => { setPortalUrl(e.target.value); setDirty(true); }} />
                  <button className="settings-btn" onClick={savePortal}>Save</button>
                </div>
              </section>
            </>
          )}

          {tab === "security" && (
            <>
              <section className="settings-section">
                <h3>Recovery email</h3>
                <p className="settings-hint">Where "forgot password" reset links are sent.</p>
                <div className="settings-row">
                  <input className="settings-input" type="email" value={email}
                    placeholder="you@example.com" onChange={(e) => setEmail(e.target.value)} />
                  <button className="settings-btn" onClick={saveEmail}>Save</button>
                </div>
              </section>
              <section className="settings-section">
                <h3>Two-step login (authenticator app)</h3>
                {mfaOn && !disabling && (
                  <div className="settings-row">
                    <span className="settings-on">● On</span>
                    <button className="settings-btn ghost" onClick={() => setDisabling(true)}>Turn off</button>
                  </div>
                )}
                {mfaOn && disabling && (
                  <div className="settings-enroll">
                    <p className="settings-hint">Enter your password to turn two-step off.</p>
                    {disableErr && <div className="login-error">{disableErr}</div>}
                    <div className="settings-row">
                      <input className="settings-input" type="password" value={disablePw}
                        placeholder="Your password" onChange={(e) => setDisablePw(e.target.value)} />
                      <button className="settings-btn" onClick={disableMfa}>Confirm</button>
                      <button className="settings-btn ghost" onClick={() => { setDisabling(false); setDisablePw(""); setDisableErr(""); }}>Cancel</button>
                    </div>
                  </div>
                )}
                {!mfaOn && !enroll && (
                  <div className="settings-row">
                    <span className="settings-off">○ Off</span>
                    <button className="settings-btn" onClick={startMfa}>Turn on</button>
                  </div>
                )}
                {!mfaOn && enroll && (
                  <div className="settings-enroll">
                    <p className="settings-hint">1. Scan this with Google or Microsoft Authenticator:</p>
                    <div className="settings-qr" dangerouslySetInnerHTML={{ __html: enroll.qr_svg }} />
                    <p className="settings-hint">Can't scan? Type this key in manually:
                      <code className="settings-secret">{enroll.secret}</code></p>
                    <p className="settings-hint">2. Enter the 6-digit code it shows:</p>
                    {enrollErr && <div className="login-error">{enrollErr}</div>}
                    <div className="settings-row">
                      <input className="settings-input login-code" value={enrollCode}
                        inputMode="numeric" maxLength={6} placeholder="123456"
                        onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, ""))} />
                      <button className="settings-btn" onClick={confirmMfa}>Turn on</button>
                      <button className="settings-btn ghost" onClick={() => { setEnroll(null); setEnrollErr(""); }}>Cancel</button>
                    </div>
                  </div>
                )}
              </section>
              {passkeysSupported && (
                <section className="settings-section">
                  <h3>Fingerprint / Face ID sign-in</h3>
                  <p className="settings-hint">Sign in on this device with your fingerprint or face instead of typing your password.</p>
                  {passkeys > 0 ? (
                    <div className="settings-row">
                      <span className="settings-on">● On ({passkeys} device{passkeys === 1 ? "" : "s"})</span>
                      <button className="settings-btn" onClick={enrollPasskey} disabled={pkBusy}>
                        {pkBusy ? "Setting up…" : "Add this device"}</button>
                      <button className="settings-btn ghost" onClick={removePasskeys}>Turn off</button>
                    </div>
                  ) : (
                    <div className="settings-row">
                      <span className="settings-off">○ Off</span>
                      <button className="settings-btn" onClick={enrollPasskey} disabled={pkBusy}>
                        {pkBusy ? "Setting up…" : "Set up on this device"}</button>
                    </div>
                  )}
                </section>
              )}
            </>
          )}

          {tab === "templates" && (
            <section className="settings-section">
              <h3>Client-email templates</h3>
              <p className="settings-hint">The wording used when you email a return. Keep the
                {" "}<code className="settings-tag">{"{tags}"}</code> — Spoom fills them in per client:{" "}
                <code className="settings-tag">{"{greeting}"}</code>
                <code className="settings-tag">{"{attachments}"}</code>
                <code className="settings-tag">{"{fee}"}</code>
                <code className="settings-tag">{"{year}"}</code>.</p>
              {[
                ["1040", "1040 (individual) returns", tpl1040, setTpl1040, 10],
                ["biz", "Business / other returns", tplBiz, setTplBiz, 9],
                ["req", "Document request list (sent from the Checklist)", tplReq, setTplReq, 9],
              ].map(([key, label, val, setter, rows]) => (
                <div key={key} className={`tpl-acc${openTpl === key ? " open" : ""}`}>
                  <button className="tpl-acc-head" onClick={() => setOpenTpl(openTpl === key ? null : key)}>
                    <span>{label}</span>
                    <span className="tpl-acc-caret">{openTpl === key ? "▲" : "▼"}</span>
                  </button>
                  {openTpl === key && (
                    <div className="tpl-acc-body">
                      {key === "req" && (
                        <p className="settings-hint" style={{ margin: "0 0 4px" }}>Uses{" "}
                          <code className="settings-tag">{"{greeting}"}</code>
                          <code className="settings-tag">{"{year}"}</code>
                          <code className="settings-tag">{"{checklist}"}</code>.</p>
                      )}
                      <textarea className="settings-textarea" rows={rows} value={val}
                        onChange={(e) => { setter(e.target.value); setDirty(true); }} />
                    </div>
                  )}
                </div>
              ))}
              <div className="settings-row" style={{ marginTop: 12 }}>
                <button className="settings-btn" onClick={saveTemplates}>Save templates</button>
                <button className="settings-btn ghost" onClick={resetTemplates}>Reset to default</button>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}


function DocMailInbox({ onOpenClient }) {
  const [status, setStatus] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [atts, setAtts] = useState({});
  const [sel, setSel] = useState({});
  const [clients, setClients] = useState([]);
  const [pick, setPick] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [diag, setDiag] = useState(null);
  const [view, setView] = useState("todo");   // "todo" = new emails, "imported" = handled archive
  const [todoCount, setTodoCount] = useState(null);   // how many still need filing

  const load = () => {
    setLoading(true);
    setOpen(null);
    const url = view === "imported" ? `${API}/docmail/messages?view=imported` : `${API}/docmail/messages`;
    fetch(url).then((r) => r.json())
      .then((d) => {
        const list = Array.isArray(d) ? d : [];
        setMsgs(list);
        if (view === "todo") setTodoCount(list.length);
      })
      .catch(() => setMsgs([]))
      .finally(() => {
        setLoading(false);
        if (view === "todo") {
          fetch(`${API}/docmail/diag`).then((r) => r.json()).then(setDiag).catch(() => {});
        }
      });
  };
  useEffect(() => {
    fetch(`${API}/docmail/status`).then((r) => r.json()).then(setStatus).catch(() => {});
    fetch(`${API}/clients?active=all`).then((r) => r.json())
      .then((d) => setClients(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);
  // Reload whenever the view (To do / Imported) changes — also covers first load.
  useEffect(() => { load(); setMsg(""); }, [view]);   // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (m) => {
    if (open === m.id) { setOpen(null); return; }
    setOpen(m.id);
    if (!atts[m.id]) {
      const a = await fetch(`${API}/docmail/messages/${m.id}/attachments`).then((r) => r.json());
      setAtts((p) => ({ ...p, [m.id]: Array.isArray(a) ? a : [] }));
    }
  };
  const doImport = async (m) => {
    const p = pick[m.id] || {};
    const chosen = (atts[m.id] || []).filter((a) => sel[`${m.id}:${a.id}`]).map((a) => a.id);
    if (!p.client_id || !p.year || !chosen.length) { setMsg("Pick a client, a year, and at least one file."); return; }
    setBusy(true); setMsg("");
    const res = await fetch(`${API}/docmail/import`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message_id: m.id, client_id: Number(p.client_id), year: Number(p.year), attachment_ids: chosen }),
    });
    const d = await res.json().catch(() => ({}));
    setBusy(false);
    if (d.ok) {
      const moved = d.moved
        ? " The email moved to the Imported view."
        : "";
      setMsg(`Imported ${d.imported.length} file(s) to the client's ${p.year} documents.${moved}`);
      // The email has been filed away — drop it from the "To do" list.
      setOpen(null);
      setMsgs((list) => list.filter((x) => x.id !== m.id));
      setTodoCount((c) => (c == null ? c : Math.max(0, c - 1)));
    } else setMsg(d.message || "Import failed.");
  };

  // Manually move an email between the two views without importing any files.
  const moveMsg = async (m, to) => {
    setBusy(true); setMsg("");
    const res = await fetch(`${API}/docmail/messages/${m.id}/move`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ to }),
    });
    const d = await res.json().catch(() => ({}));
    setBusy(false);
    if (d.ok) {
      setOpen(null);
      setMsgs((list) => list.filter((x) => x.id !== m.id));
      if (to === "imported") {
        setTodoCount((c) => (c == null ? c : Math.max(0, c - 1)));
        setMsg("Moved to Imported.");
      } else {
        setTodoCount((c) => (c == null ? c : c + 1));
        setMsg("Moved back to To do.");
      }
    } else setMsg(d.message || "Couldn't move the email.");
  };

  if (status && !status.ready) {
    return (
      <>
        <header className="topbar"><h1>Document inbox</h1></header>
        <div className="empty pad">Not set up yet. Add the Microsoft Graph settings plus a <code>GRAPH_DOCMAIL</code> value (and the <strong>Mail.Read</strong> permission on the app) to read emails sent to your documents address.</div>
      </>
    );
  }
  return (
    <>
      <header className="topbar">
        <h1>Document inbox {status && status.mailbox ? <span className="subtitle">· {status.mailbox}</span> : null}</h1>
        <div className="topbar-actions"><button className="btn ghost" onClick={load}>↻ Refresh</button></div>
      </header>
      <div className="docmail-views">
        <button className={`docmail-view-btn${view === "todo" ? " active" : ""}`}
          onClick={() => setView("todo")}>📥 To do{todoCount != null ? ` (${todoCount})` : ""}</button>
        <button className={`docmail-view-btn${view === "imported" ? " active" : ""}`}
          onClick={() => setView("imported")}>✅ Imported</button>
      </div>
      {msg && <div className="banner">{msg}</div>}
      {view === "todo" && diag && (
        <div className="docmail-diag">
          <span>Looking in: <strong>{diag.folder || (diag.filter_to ? "all folders" : "Inbox")}</strong></span>
          <span>Emails found: <strong>{diag.found_total ?? "—"}</strong> (with attachments: {diag.found_with_attachments ?? "—"})</span>
          <span>Auth: {diag.token_ok === false ? "❌ failed" : diag.token_ok ? "✅ ok" : "—"}</span>
          <span>Last checked: {diag.last_check ? new Date(diag.last_check).toLocaleTimeString() : "—"}</span>
          {diag.last_error && <span className="docmail-diag-err">⚠ {diag.last_error}</span>}
        </div>
      )}
      {loading ? <div className="empty">Loading…</div>
        : msgs.length === 0 ? <div className="empty">{view === "imported"
            ? "Nothing imported yet. Emails you import will be filed here."
            : "No emails with attachments in the inbox."}</div>
          : (
            <div className="docmail-list">
              {msgs.map((m) => (
                <div key={m.id} className={`docmail-card${m.isRead ? "" : " unread"}`}>
                  <button className="docmail-head" onClick={() => toggle(m)}>
                    <div className="dm-line1">
                      <span className="dm-from">{m.fromName || m.from || "Unknown"}</span>
                      <span className="dm-date">{m.date ? m.date.slice(0, 10) : ""}</span>
                    </div>
                    <div className="dm-subject">{m.subject}</div>
                    <div className="dm-preview">{m.preview}</div>
                  </button>
                  {open === m.id && (
                    <div className="docmail-body">
                      <div className="dm-atts">
                        {(atts[m.id] || []).length === 0
                          ? <span className="muted small">No downloadable attachments.</span>
                          : (atts[m.id] || []).map((a) => (
                            <label key={a.id} className="dm-att">
                              {view === "todo" && (
                                <input type="checkbox" checked={!!sel[`${m.id}:${a.id}`]}
                                  onChange={(e) => setSel((p) => ({ ...p, [`${m.id}:${a.id}`]: e.target.checked }))} />
                              )}
                              <span className="dm-att-name">{a.name}</span>
                              <span className="muted small">{Math.round((a.size || 0) / 1024)} KB</span>
                            </label>
                          ))}
                      </div>
                      {view === "todo" ? (
                        <>
                          <div className="dm-import">
                            <select value={(pick[m.id] || {}).client_id || ""}
                              onChange={(e) => setPick((p) => ({ ...p, [m.id]: { ...(p[m.id] || {}), client_id: e.target.value } }))}>
                              <option value="">Choose client…</option>
                              {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.client_name}</option>)}
                            </select>
                            <input type="number" placeholder="Year" className="dm-year"
                              value={(pick[m.id] || {}).year || ""}
                              onChange={(e) => setPick((p) => ({ ...p, [m.id]: { ...(p[m.id] || {}), year: e.target.value } }))} />
                            <button className="btn primary small" disabled={busy} onClick={() => doImport(m)}>
                              {busy ? "Importing…" : "Import to client"}</button>
                          </div>
                          <div className="dm-move">
                            <span className="muted small">Not for a client?</span>
                            <button className="btn ghost small" disabled={busy} onClick={() => moveMsg(m, "imported")}>
                              Move to Imported →</button>
                          </div>
                        </>
                      ) : (
                        <div className="dm-move">
                          <span className="muted small">✅ Filed away — the files are on the client (if any). This is your archive copy.</span>
                          <button className="btn ghost small" disabled={busy} onClick={() => moveMsg(m, "todo")}>
                            ← Move back to To do</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
    </>
  );
}


function MobileHome({ onOpenClient, goTab, refreshKey, me }) {
  const [summary, setSummary] = useState(() => cacheGet("home_summary", null));
  const [jobs, setJobs] = useState(() => cacheGet("home_jobs", []));
  const [timer, setTimer] = useState(null);   // active work timer, or null
  const [, setNowTick] = useState(0);          // forces the running clock to re-render
  const [clients, setClients] = useState([]);
  const [sheet, setSheet] = useState(null);    // 'hours' | 'efile' | 'review'
  const [snapFile, setSnapFile] = useState(null);
  const [snapClient, setSnapClient] = useState("");
  const [snapYears, setSnapYears] = useState([]);
  const [snapYear, setSnapYear] = useState("");
  const [snapBusy, setSnapBusy] = useState(false);
  const [timeModal, setTimeModal] = useState(false);
  const [timeClient, setTimeClient] = useState("");
  const snapInput = useRef(null);

  const loadTimer = useCallback(() => {
    fetch(`${API}/timer/active`).then((r) => r.json()).then(setTimer).catch(() => {});
  }, []);
  useEffect(() => {
    fetch(`${API}/home/summary`).then((r) => r.json()).then((d) => setSummary(cacheSet("home_summary", d))).catch(() => {});
    fetch(`${API}/clients?active=active&sort=due_date&dir=asc`).then((r) => r.json())
      .then((d) => setJobs(cacheSet("home_jobs", Array.isArray(d) ? d.filter((c) => c.due_date).slice(0, 6) : [])))
      .catch(() => {});
    loadTimer();
  }, [refreshKey, loadTimer]);
  // Tick the running clock once a second (only while a timer is running).
  useEffect(() => {
    if (!timer || timer.paused) return undefined;
    const iv = setInterval(() => setNowTick((n) => n + 1), 1000);
    return () => clearInterval(iv);
  }, [timer]);
  const timerBase = useRef({ id: null, at: 0, elapsed: 0 });
  if (timer && timer.id !== timerBase.current.id) {
    timerBase.current = { id: timer.id, at: Date.now(), elapsed: timer.elapsed || 0 };
  }
  const liveSeconds = timer
    ? (timer.paused
        ? (timer.elapsed || 0)
        : timerBase.current.elapsed + Math.floor((Date.now() - timerBase.current.at) / 1000))
    : 0;
  const timerAction = async (action) => {
    if (!timer) return;
    await fetch(`${API}/timer/${timer.id}/${action}`, { method: "POST" });
    if (action === "stop" || action === "finish" || action === "delete") setTimer(null);
    loadTimer();
    fetch(`${API}/home/summary`).then((r) => r.json()).then((d) => setSummary(cacheSet("home_summary", d))).catch(() => {});
  };

  const ensureClients = useCallback(() => {
    if (clients.length) return;
    fetch(`${API}/clients?active=all`).then((r) => r.json())
      .then((d) => setClients(Array.isArray(d) ? d : [])).catch(() => {});
  }, [clients.length]);

  // --- Snap doc: capture a photo, then file it against a client + year ------
  const onSnapPicked = (e) => {
    const f = (e.target.files || [])[0];
    e.target.value = "";
    if (!f) return;
    setSnapFile(f); setSnapClient(""); setSnapYears([]); setSnapYear("");
    ensureClients();
  };
  const onSnapClient = (cid) => {
    setSnapClient(cid); setSnapYears([]); setSnapYear("");
    if (!cid) return;
    fetch(`${API}/clients/${cid}`).then((r) => r.json()).then((d) => {
      const ys = Array.isArray(d.tax_years) ? d.tax_years : [];
      setSnapYears(ys); setSnapYear(ys[0] ? String(ys[0].id) : "");
    }).catch(() => {});
  };
  const uploadSnap = async () => {
    if (!snapFile || !snapYear) return;
    setSnapBusy(true);
    const fd = new FormData(); fd.append("file", snapFile);
    await fetch(`${API}/tax_years/${snapYear}/documents`, { method: "POST", body: fd });
    setSnapBusy(false); setSnapFile(null); setSnapClient(""); setSnapYear("");
    fetch(`${API}/home/summary`).then((r) => r.json()).then((d) => setSummary(cacheSet("home_summary", d))).catch(() => {});
  };

  // --- Log time: pick a client and start the timer -------------------------
  const openTime = () => { ensureClients(); setTimeClient(""); setTimeModal(true); };
  const startTimer = async () => {
    if (!timeClient) return;
    await fetch(`${API}/clients/${timeClient}/timer`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "start" }),
    });
    setTimeModal(false); loadTimer();
  };

  const h = new Date().getHours();
  const greet = h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  const today = new Date().toLocaleDateString(undefined,
    { weekday: "long", month: "short", day: "numeric" });
  const displayName = (me && (me.display_name || me.username)) || "";
  const firstName = displayName.trim().split(/\s+/)[0] || "";
  const unread = summary ? (summary.unread_messages || 0) : 0;
  const hrsToday = summary ? (summary.hrs_today_seconds || 0) / 3600 : 0;
  const NAVY = "var(--blue)", TEAL = "var(--teal)", MUTED = "var(--muted)", LINE = "var(--card-line)";

  const sortedClients = [...clients].sort((a, b) =>
    (a.name || "").localeCompare(b.name || ""));

  return (
    <div className="m-home">
      {/* Greeting (branding + account live in the app bar above) */}
      <header style={{ padding: "0 0 2px" }}>
        <div style={{ fontSize: 13, color: MUTED }}>{today}</div>
        <div style={{ fontSize: 22, fontWeight: 700, marginTop: 1 }}>
          {greet}{firstName ? `, ${firstName}` : ""}</div>
      </header>

      {/* Quick actions */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, padding: "16px 0 2px" }}>
        <QuickAction color={NAVY} label="Snap doc" onClick={() => snapInput.current && snapInput.current.click()}
          icon={<><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></>} />
        <QuickAction color={TEAL} label="Log time" onClick={openTime}
          icon={<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>} />
        <QuickAction color={NAVY} label="Message" badge={unread} onClick={() => goTab("messages")}
          icon={<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />} />
      </div>
      <input ref={snapInput} type="file" accept="image/*" capture="environment"
        style={{ display: "none" }} onChange={onSnapPicked} />

      {/* Running timer */}
      {timer && (
        <div style={{ margin: "12px 0 0", background: "var(--card)", border: `1px solid ${LINE}`,
          borderTop: `3px solid ${TEAL}`, borderRadius: 12, padding: "13px 15px",
          boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%",
                  background: timer.paused ? "#98a2b3" : TEAL,
                  boxShadow: timer.paused ? "none" : "0 0 0 3px #d9f0ee" }} />
                <span style={{ fontSize: 11, fontWeight: 700,
                  color: timer.paused ? "#5b7186" : "#0d8577",
                  textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {timer.paused ? "Paused" : "Timer running"}</span>
              </div>
              <button onClick={() => onOpenClient(timer.client_id)}
                style={{ display: "block", background: "none", border: "none", padding: 0,
                  marginTop: 6, fontSize: 14, fontWeight: 600, color: "inherit", textAlign: "left",
                  cursor: "pointer", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis",
                  whiteSpace: "nowrap" }}>
                {timer.client_name}{timer.label ? ` — ${timer.label}` : ""}</button>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: "tabular-nums",
                color: NAVY }}>{fmtDuration(liveSeconds)}</div>
              <div style={{ display: "flex", gap: 6, marginTop: 6, justifyContent: "flex-end" }}>
                {timer.paused
                  ? <button className="btn ghost small" onClick={() => timerAction("resume")}>Resume</button>
                  : <button className="btn ghost small" onClick={() => timerAction("pause")}>Pause</button>}
                <button className="btn danger small" onClick={() => timerAction("stop")}>Stop</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Today stats — tappable */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, padding: "14px 0 0" }}>
        <StatSquare value={summary ? hrsToday.toFixed(1) : "—"} label="hrs today" color={NAVY}
          onClick={() => setSheet("hours")} />
        <StatSquare value={summary ? summary.ready_efile_count : "—"} label="ready for e-file" color={NAVY}
          onClick={() => setSheet("efile")} />
        <StatSquare value={summary ? summary.review_count : "—"} label="need review" color="#e5a53c"
          onClick={() => setSheet("review")} />
      </div>

      {/* Active jobs */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 0 8px" }}>
        <span style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase",
          letterSpacing: "0.05em", color: "#5b7186" }}>Active jobs</span>
        <button className="link-btn" onClick={() => goTab("returns")}>See all</button>
      </div>
      <div style={{ background: "var(--card)", border: `1px solid ${LINE}`, borderRadius: 12, overflow: "hidden" }}>
        {jobs.length === 0 && <div className="empty" style={{ padding: 18 }}>Nothing due soon. 🎉</div>}
        {jobs.map((c, i) => {
          const overdue = isOverdue(c.due_date, c.status);
          const chip = overdue ? CHIP_OVERDUE : (STATUS_CHIP[c.status] || STATUS_CHIP.not_started);
          const chipLabel = overdue ? "Overdue" : (STATUS_LABELS[c.status] || "—");
          const ini = (c.client_name || "?").split(/[\s,]+/).filter(Boolean)
            .slice(0, 2).map((w) => w[0]).join("").toUpperCase();
          return (
            <button key={c.tax_year_id || c.client_id} onClick={() => onOpenClient(c.client_id)}
              style={{ display: "flex", alignItems: "center", gap: 12, width: "100%",
                padding: "13px 14px", background: "none", border: "none", textAlign: "left",
                cursor: "pointer", borderBottom: i < jobs.length - 1 ? "1px solid #f0f3f7" : "none" }}>
              <span style={{ width: 36, height: 36, borderRadius: 9, background: "var(--avatar-bg)",
                color: NAVY, display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{ini}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 14, fontWeight: 600,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.client_name}</span>
                <span style={{ display: "block", fontSize: 12, color: MUTED, marginTop: 1 }}>
                  {c.entity_type}{c.latest_year ? ` · ${c.latest_year}` : ""} · due {fmtDay(c.due_date)}</span>
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, flexShrink: 0,
                background: chip.bg, border: `1px solid ${chip.bd}`, color: chip.fg,
                fontSize: 11, fontWeight: 700, padding: "4px 8px", borderRadius: 6 }}>
                <span style={{ width: 6, height: 6, background: chip.dot, borderRadius: 1 }} />{chipLabel}</span>
            </button>
          );
        })}
      </div>

      {/* Stat drill-down sheet */}
      {sheet && (
        <div className="modal-overlay" onClick={() => setSheet(null)}>
          <div className="exp-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480, width: "92%" }}>
            <div className="sub-head">
              <h3>{sheet === "hours" ? "Time logged today"
                : sheet === "efile" ? "Ready for e-file" : "Documents to review"}</h3>
              <button className="icon-btn" title="Close" onClick={() => setSheet(null)}>✕</button>
            </div>
            <div style={{ maxHeight: "55vh", overflowY: "auto", margin: "4px 0 8px" }}>
              {sheet === "hours" && (
                (summary && summary.hrs_today_entries.length)
                  ? summary.hrs_today_entries.map((r) => (
                      <button key={r.client_id} onClick={() => { setSheet(null); onOpenClient(r.client_id); }}
                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                          width: "100%", gap: 10, padding: "10px 6px", background: "none", border: "none",
                          borderBottom: `1px solid ${LINE}`, cursor: "pointer", textAlign: "left" }}>
                        <span style={{ fontSize: 14 }}>{r.client_name}</span>
                        <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: NAVY }}>
                          {(r.seconds / 3600).toFixed(1)} h</span>
                      </button>
                    ))
                  : <div className="muted small" style={{ padding: "14px 6px" }}>No time logged yet today.</div>
              )}
              {sheet === "efile" && (
                (summary && summary.ready_efile.length)
                  ? summary.ready_efile.map((r) => (
                      <button key={r.tax_year_id} onClick={() => { setSheet(null); onOpenClient(r.client_id); }}
                        style={{ display: "flex", alignItems: "center", gap: 10, width: "100%",
                          padding: "10px 6px", background: "none", border: "none",
                          borderBottom: `1px solid ${LINE}`, cursor: "pointer", textAlign: "left" }}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: "block", fontSize: 14, fontWeight: 600 }}>{r.client_name}</span>
                          <span className="muted small">{r.entity_type} · {r.year}
                            {r.due_date ? ` · due ${fmtDay(r.due_date)}` : ""}</span>
                        </span>
                      </button>
                    ))
                  : <div className="muted small" style={{ padding: "14px 6px" }}>Nothing ready to e-file.</div>
              )}
              {sheet === "review" && (
                (summary && summary.review_docs.length)
                  ? summary.review_docs.map((r) => (
                      <button key={r.doc_id} onClick={() => { setSheet(null); onOpenClient(r.client_id); }}
                        style={{ display: "flex", alignItems: "center", gap: 10, width: "100%",
                          padding: "10px 6px", background: "none", border: "none",
                          borderBottom: `1px solid ${LINE}`, cursor: "pointer", textAlign: "left" }}>
                        <span style={{ fontSize: 18 }}>📄</span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: "block", fontSize: 14, wordBreak: "break-all" }}>{r.name}</span>
                          <span className="muted small">{r.client_name}{r.year ? ` · ${r.year}` : ""}</span>
                        </span>
                      </button>
                    ))
                  : <div className="muted small" style={{ padding: "14px 6px" }}>No documents waiting on you.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Snap doc: file to client + year */}
      {snapFile && (
        <div className="modal-overlay" onClick={() => !snapBusy && setSnapFile(null)}>
          <div className="exp-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420, width: "92%" }}>
            <div className="sub-head"><h3>Save document to…</h3>
              <button className="icon-btn" title="Close" onClick={() => setSnapFile(null)}>✕</button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--field-bg)",
              border: `1px solid ${LINE}`, borderRadius: 10, padding: "9px 11px", margin: "4px 0 14px" }}>
              <span style={{ fontSize: 18 }}>📄</span>
              <span style={{ fontSize: 13, wordBreak: "break-all" }}>{snapFile.name || "Captured photo"}</span>
            </div>
            <label className="email-row col" style={{ marginBottom: 12 }}>Client
              <select value={snapClient} onChange={(e) => onSnapClient(e.target.value)}>
                <option value="">Choose a client…</option>
                {sortedClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="email-row col" style={{ marginBottom: 4 }}>Tax year
              <select value={snapYear} onChange={(e) => setSnapYear(e.target.value)} disabled={!snapClient}>
                {snapYears.length === 0 && <option value="">{snapClient ? "No tax years yet" : "Pick a client first"}</option>}
                {snapYears.map((y) => <option key={y.id} value={y.id}>{y.year}</option>)}
              </select>
            </label>
            <div className="exp-modal-actions">
              <button className="btn ghost small" onClick={() => setSnapFile(null)} disabled={snapBusy}>Cancel</button>
              <button className="btn primary small" onClick={uploadSnap} disabled={snapBusy || !snapYear}>
                {snapBusy ? "Saving…" : "Save document"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Log time: pick client and start */}
      {timeModal && (
        <div className="modal-overlay" onClick={() => setTimeModal(false)}>
          <div className="exp-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420, width: "92%" }}>
            <div className="sub-head"><h3>Start a timer</h3>
              <button className="icon-btn" title="Close" onClick={() => setTimeModal(false)}>✕</button>
            </div>
            <label className="email-row col" style={{ margin: "4px 0" }}>Client
              <select value={timeClient} onChange={(e) => setTimeClient(e.target.value)}>
                <option value="">Choose a client…</option>
                {sortedClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <div className="exp-modal-actions">
              <button className="btn ghost small" onClick={() => setTimeModal(false)}>Cancel</button>
              <button className="btn primary small" onClick={startTimer} disabled={!timeClient}>Start timer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function QuickAction({ color, label, icon, badge, onClick }) {
  return (
    <button onClick={onClick} style={{ background: "var(--card)", border: "1px solid #e6eaf0",
      borderRadius: 12, padding: "14px 8px", textAlign: "center", cursor: "pointer",
      boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
      <div style={{ width: 40, height: 40, margin: "0 auto 8px", borderRadius: 11, background: color,
        display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--card)" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round">{icon}</svg>
        {badge > 0 && (
          <span style={{ position: "absolute", top: -4, right: -4, minWidth: 16, height: 16,
            borderRadius: 8, background: "#e5484d", color: "var(--card)", fontSize: 10, fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px" }}>{badge}</span>
        )}
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 600 }}>{label}</div>
    </button>
  );
}

function StatSquare({ value, label, color, onClick }) {
  return (
    <button onClick={onClick} style={{ background: "var(--card)", border: "1px solid #e6eaf0",
      borderRadius: 12, padding: 12, textAlign: "left", cursor: "pointer" }}>
      <div style={{ fontSize: 22, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 2 }}>{label}</div>
    </button>
  );
}

// Firm branding for the startup screen. The active logo is served by the
// backend (/firm-logo) — uploaded per-firm in Settings, so a second firm can
// brand their own Spoom with no code change. Falls back to the wordmark.
const FIRM = { logo: `${API}/firm-logo`, name: "Sompo Consulting", tagline: "Quality Accounting Services" };

function Splash({ onDone, ready }) {
  const [logoOk, setLogoOk] = useState(true);
  const [minMet, setMinMet] = useState(false);
  // Show the graphics for at least this long so it never flashes by...
  useEffect(() => {
    const t = setTimeout(() => setMinMet(true), 3200);
    return () => clearTimeout(t);
  }, []);
  // ...then leave as soon as the app is loaded. A hard cap prevents a hang
  // from trapping the user on the splash forever.
  useEffect(() => {
    if (minMet && ready) onDone();
  }, [minMet, ready, onDone]);
  useEffect(() => {
    const cap = setTimeout(onDone, 12000);
    return () => clearTimeout(cap);
  }, [onDone]);
  return (
    <div className="splash" onClick={onDone}>
      <div className="splash-grid" />
      <div className="splash-arc" />
      <span className="splash-ring r1" />
      <span className="splash-ring r2" />
      <span className="splash-ring r3" />
      <div className="splash-core">
        <div style={{ position: "relative" }}>
          {logoOk
            ? <img src="/spoom-logo-white.png" alt="Spoom" className="splash-logo" onError={() => setLogoOk(false)} />
            : <div className="splash-word">Spoom</div>}
          <div className="splash-scan" />
        </div>
        <div className="splash-bar" />
        <div className="splash-status">Initializing workspace…</div>
      </div>
      <div className="splash-firm">
        <span className="splash-firm-lbl">Prepared for</span>
        <div className="splash-firm-chip">
          <img src={FIRM.logo} alt={FIRM.name}
            onError={(e) => { e.target.parentNode.style.display = "none"; }} />
        </div>
      </div>
      <div className="splash-powered">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round">
          <rect x="5" y="4" width="14" height="17" rx="2" />
          <path d="M9 4V3h6v1" />
          <path d="M8.5 12l2 2 4-4" />
        </svg>
        powered by Spoom · a product of Zuntar
      </div>
    </div>
  );
}

function App() {
  const [tab, setTab] = useState(
    () => (typeof window !== "undefined" && window.innerWidth < 768) ? "home" : "returns");
  const [selected, setSelected] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // --- Live swipe pager between the bottom-nav screens (mobile) ----------
  const pagerRef = useRef(null);
  const gestureRef = useRef({ x: 0, y: 0, active: false, locked: false, w: 0, dir: 0, to: null });
  const pendingTab = useRef(null);
  const swipeStateRef = useRef({ tab: "home", selected: null });
  // drag = live visual state: dx (px the track is shifted), to (incoming tab),
  // dir (+1 next / -1 prev), anim (true while snapping/committing).
  const [drag, setDrag] = useState({ dx: 0, to: null, dir: 0, anim: false });

  useEffect(() => {
    const el = pagerRef.current;
    const g = gestureRef.current;
    const width = () => (el && el.clientWidth) || window.innerWidth;
    // Walk up from the touch target: bail only if we're inside something the
    // user genuinely scrolls sideways (a wide table, a slider, an opted-out
    // region) — NOT just any styled wrapper.
    const inHorizontalScroller = (node) => {
      for (let n = node; n && n !== document.body; n = n.parentElement) {
        if (n.matches && n.matches("input[type='range'], .no-swipe")) return true;
        if (n.scrollWidth - n.clientWidth > 12) {
          const ov = getComputedStyle(n).overflowX;
          if (ov === "auto" || ov === "scroll") return true;
        }
      }
      return false;
    };

    const start = (e) => {
      if (window.innerWidth >= 768 || swipeStateRef.current.selected !== null
          || e.touches.length > 1 || inHorizontalScroller(e.target)) {
        g.active = false; return;
      }
      g.x = e.touches[0].clientX; g.y = e.touches[0].clientY;
      g.active = true; g.locked = false; g.w = width();
      g.dir = 0; g.to = null;
    };
    const move = (e) => {
      if (!g.active) return;
      const dx = e.touches[0].clientX - g.x;
      const dy = e.touches[0].clientY - g.y;
      if (!g.locked) {
        const ax = Math.abs(dx), ay = Math.abs(dy);
        // Engage as soon as the finger commits to a direction.
        if (ax < 6 && ay < 6) return;
        // Clearly vertical → let the page scroll; give up this gesture.
        if (ay > ax) { g.active = false; return; }
        // Horizontal wins → we own it from here on.
        g.locked = true;
        const i = SWIPE_ORDER.indexOf(swipeStateRef.current.tab);
        g.dir = dx < 0 ? 1 : -1;
        const ni = i + g.dir;
        g.to = (i !== -1 && ni >= 0 && ni < SWIPE_ORDER.length) ? SWIPE_ORDER[ni] : null;
      }
      if (e.cancelable) e.preventDefault();     // we own this gesture now
      // No live neighbor (true edge, or the More sheet) → rubber-band.
      const shift = g.to == null ? dx / 3.2 : dx;
      g.dx = shift;
      setDrag({ dx: shift, to: g.to, dir: g.dir, anim: false });
    };
    const end = () => {
      if (!g.active || !g.locked) { g.active = false; return; }
      g.active = false; g.locked = false;
      const dx = g.dx || 0;
      const threshold = g.w * 0.22;
      if (g.to != null && Math.abs(dx) > threshold) {
        pendingTab.current = g.to;
        setDrag({ dx: g.dir < 0 ? g.w : -g.w, to: g.to, dir: g.dir, anim: true });
      } else {
        setDrag((d) => ({ ...d, dx: 0, anim: true }));   // snap back
      }
    };
    window.addEventListener("touchstart", start, { passive: true, capture: true });
    window.addEventListener("touchmove", move, { passive: false, capture: true });
    window.addEventListener("touchend", end, { passive: true, capture: true });
    window.addEventListener("touchcancel", end, { passive: true, capture: true });
    return () => {
      window.removeEventListener("touchstart", start, { capture: true });
      window.removeEventListener("touchmove", move, { capture: true });
      window.removeEventListener("touchend", end, { capture: true });
      window.removeEventListener("touchcancel", end, { capture: true });
    };
  }, []);   // bind once; live dx lives in the gesture ref

  const onPagerTransitionEnd = () => {
    if (!drag.anim) return;
    if (pendingTab.current) {
      const t = pendingTab.current; pendingTab.current = null;
      setTab(t); setMoreNav(false);
    }
    setDrag({ dx: 0, to: null, dir: 0, anim: false });
  };

  const renderTab = (t) => (
    t === "home" ? (
      <MobileHome onOpenClient={setSelected} goTab={setTab} refreshKey={refreshKey} me={me} />
    ) : t === "agenda" ? (
      <AgendaTab onOpenClient={setSelected} refreshKey={refreshKey} />
    ) : t === "history" ? (
      <HistoryTab onOpenClient={setSelected} />
    ) : t === "calendar" ? (
      <CalendarTab onOpenClient={setSelected} refreshKey={refreshKey} />
    ) : t === "returns" ? (
      <Dashboard onOpenClient={setSelected} refreshKey={refreshKey} />
    ) : t === "finances" ? (
      <FinancesTab onOpenClient={setSelected} refreshKey={refreshKey} />
    ) : t === "messages" ? (
      <MessagesTab />
    ) : t === "docmail" ? (
      <DocMailInbox onOpenClient={setSelected} />
    ) : t === "files" ? (
      <FilesBrowser onOpenClient={setSelected} />
    ) : (
      <ProjectsTab type={t} onOpenClient={setSelected} refreshKey={refreshKey} />
    )
  );

  // Login gate. "checking" until we ask the server who we are. If the wall is
  // off (REQUIRE_LOGIN not set) the app is open exactly as before.
  const [auth, setAuth] = useState({ status: "checking", user: null });
  const [me, setMe] = useState(null);        // full account info when logged in
  const [notice, setNotice] = useState("");  // message shown on the login screen
  const [showSettings, setShowSettings] = useState(false);
  const [accountMenu, setAccountMenu] = useState(false);
  const [hideHeader, setHideHeader] = useState(false);
  const [splash, setSplash] = useState(() => {
    try { return typeof window !== "undefined" && !sessionStorage.getItem("spoom_splash_shown"); }
    catch { return false; }
  });
  const [idleWarn, setIdleWarn] = useState(false);
  const [moreNav, setMoreNav] = useState(false);   // phone "More" tab sheet
  const lastActivity = useRef(Date.now());

  // Android back button: close an open client / More sheet, or return to Home,
  // instead of exiting the app. Keep one trap history entry so a single back
  // never quits mid-app.
  const navState = useRef({});
  navState.current = { selected, tab, moreNav };
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.history.pushState({ spoom: true }, "");
    const onPop = () => {
      const s = navState.current;
      if (s.selected !== null) setSelected(null);
      else if (s.moreNav) setMoreNav(false);
      else if (s.tab !== "home" && window.innerWidth < 768) setTab("home");
      window.history.pushState({ spoom: true }, "");   // re-arm the trap
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Password-reset links arrive as ?reset_token=... — detected once at startup.
  const resetToken = useRef(
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("reset_token") : null
  ).current;

  const refreshMe = useCallback(() => fetch(`${API}/auth/me`)
    .then((r) => r.json())
    .then((d) => {
      if (d.logged_in) { setMe(d); setAuth({ status: "in", user: d.username }); }
      else if (d.wall_on) setAuth({ status: "out", user: null });
      else setAuth({ status: "in", user: null }); // wall off -> open access
      return d;
    })
    .catch(() => setAuth({ status: "in", user: null })), []); // never lock out on error

  useEffect(() => { if (!resetToken) refreshMe(); }, [resetToken, refreshMe]);

  // Warm the cache for every main screen once we're logged in, so the first
  // switch to any tab is instant instead of triggering a cold fetch.
  useEffect(() => {
    if (auth.status !== "in") return;
    const j = (u) => fetch(`${API}${u}`).then((r) => r.ok ? r.json() : null).catch(() => null);
    let cancelled = false;
    (async () => {
      const [summary, metrics, ar, pnl, rev, unbilled, exp, agenda, jobs, threads] = await Promise.all([
        j("/home/summary"), j("/metrics"), j("/finance/ar"),
        j(`/finance/pnl?period=ytd&basis=accrual`), j("/finance/revenue?period=ytd&basis=accrual"),
        j("/finance/unbilled"), j("/expenses"), j("/agenda?days=7"),
        j("/clients?active=active&sort=due_date&dir=asc"), j("/threads?filter=all"),
      ]);
      if (cancelled) return;
      if (summary) cacheSet("home_summary", summary);
      if (metrics) cacheSet("dash_metrics", metrics);
      if (ar) cacheSet("fin_ar", ar);
      if (pnl) cacheSet("fin_pnl", pnl);
      if (rev) cacheSet("fin_rev", rev);
      if (unbilled) cacheSet("fin_unbilled", unbilled);
      if (Array.isArray(exp)) cacheSet("fin_exp", exp);
      if (agenda) cacheSet("agenda_7", agenda);
      if (Array.isArray(threads)) cacheSet("msg_threads_all", threads);
      if (Array.isArray(jobs)) {
        cacheSet("home_jobs", jobs.filter((c) => c.due_date).slice(0, 6));
        cacheSet("dash_clients", jobs);
      }
    })();
    return () => { cancelled = true; };
  }, [auth.status]);

  const logout = useCallback(async (reason) => {
    try { await fetch(`${API}/auth/logout`, { method: "POST" }); } catch { /* ignore */ }
    setMe(null);
    setIdleWarn(false);
    setNotice(reason === "idle" ? "You were signed out after a period of inactivity." : "");
    setAuth({ status: "out", user: null });
  }, []);

  // If a normal request comes back unauthorized, our session ended — show login.
  useEffect(() => {
    const onUnauth = () => { setAuth((a) => (a.status === "in" ? { status: "out", user: null } : a)); };
    window.addEventListener("spoom-unauthorized", onUnauth);
    return () => window.removeEventListener("spoom-unauthorized", onUnauth);
  }, []);

  // Idle auto-logout: watch for activity; warn near the limit, then sign out.
  useEffect(() => {
    if (auth.status !== "in" || !me || !me.wall_on) return undefined;
    const mins = Number(me.idle_minutes || 0);
    if (!mins) return undefined; // "Never"
    const limit = mins * 60000;
    const bump = () => { lastActivity.current = Date.now(); setIdleWarn(false); };
    const evs = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const iv = setInterval(() => {
      const idleMs = Date.now() - lastActivity.current;
      if (idleMs >= limit) logout("idle");
      else if (idleMs >= limit - 60000) setIdleWarn(true);
    }, 10000);
    return () => {
      clearInterval(iv);
      evs.forEach((e) => window.removeEventListener(e, bump));
    };
  }, [auth.status, me, logout]);

  // Auto-hide the pinned header after a spell of no activity (phones only). It
  // slides back the moment you scroll, tap, or type.
  useEffect(() => {
    if (typeof window === "undefined" || window.innerWidth >= 768) return undefined;
    let t;
    const reset = () => {
      setHideHeader(false);
      clearTimeout(t);
      t = setTimeout(() => setHideHeader(true), 4500);
    };
    const evs = ["scroll", "pointerdown", "touchstart", "keydown"];
    evs.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(t);
      evs.forEach((e) => window.removeEventListener(e, reset));
    };
  }, []);

  const close = () => {
    setSelected(null);
    setRefreshKey((k) => k + 1); // refresh views so edits show
  };
  // Bump without closing — lets the underlying tab (e.g. Finances) refresh live
  // when time is logged or billed from inside the open client modal.
  const bumpRefresh = () => setRefreshKey((k) => k + 1);

  // Press Esc to close the open client popup.
  useEffect(() => {
    if (selected === null) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]); // eslint-disable-line

  if (resetToken) {
    return <ResetPassword token={resetToken} />;
  }
  if (splash) {
    return <Splash ready={auth.status !== "checking"} onDone={() => {
      try { sessionStorage.setItem("spoom_splash_shown", "1"); } catch { /* ignore */ }
      setSplash(false);
    }} />;
  }
  if (auth.status === "checking") {
    return <div className="login-wrap"><div className="login-card">Loading…</div></div>;
  }
  if (auth.status === "out") {
    return <Login notice={notice} onSuccess={() => { setNotice(""); refreshMe(); }} />;
  }

  return (
    <div className="app">
      <div className={`topbar-sticky${selected !== null ? " topbar-hidden" : ""}${hideHeader ? " topbar-autohide" : ""}`}>
        <div className="appbar">
          <div className="appbar-brand">
            <img src="/spoom-logo.png" alt="Spoom" className="brand-img" />
          </div>
          <CountdownBar />
          <div className="appbar-brand appbar-firm">
            <img src="/sompo-logo.png" alt="Sompo Consulting" className="brand-img" />
          </div>
          {auth.user && (() => {
            const acctName = (me && (me.display_name || me.username)) || auth.user || "";
            const acctInitials = acctName
              ? acctName.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase()
              : "S";
            const item = {
              display: "flex", alignItems: "center", gap: 10, width: "100%",
              padding: "12px 14px", background: "none", border: "none", cursor: "pointer",
              fontSize: 14, color: "var(--text)", textAlign: "left",
            };
            return (
              <div className="appbar-account no-print" style={{ position: "relative" }}>
                <button onClick={() => setAccountMenu((v) => !v)}
                  title={`Signed in as ${acctName}`} aria-label="Account menu"
                  style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--avatar-bg)",
                    color: "var(--avatar-fg)", fontWeight: 700, fontSize: 13, border: "none",
                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {acctInitials}
                </button>
                {accountMenu && (
                  <>
                    <div onClick={() => setAccountMenu(false)}
                      style={{ position: "fixed", inset: 0, zIndex: 40 }} />
                    <div style={{ position: "absolute", top: 46, right: 0, zIndex: 41, minWidth: 224,
                      background: "var(--panel)", border: "1px solid var(--border)", borderRadius: 12,
                      boxShadow: "0 14px 30px -10px rgba(0,0,0,0.35)", overflow: "hidden" }}>
                      <div style={{ padding: "14px 14px 12px", borderBottom: "1px solid var(--hairline)" }}>
                        <img src="/sompo-logo.png" alt="Sompo Consulting"
                          style={{ height: 30, width: "auto", display: "block", marginBottom: 8 }} />
                        <div style={{ fontSize: 12.5, color: "var(--muted)" }}>Signed in as {acctName}</div>
                      </div>
                      <button style={item} onClick={() => { setAccountMenu(false); setShowSettings(true); }}>
                        <span style={{ fontSize: 16 }}>⚙</span> Settings</button>
                      <button style={{ ...item, borderTop: "1px solid var(--hairline)", color: "var(--red)" }}
                        onClick={() => { setAccountMenu(false); logout(); }}>
                        <span style={{ fontSize: 16 }}>⏻</span> Sign out</button>
                    </div>
                  </>
                )}
              </div>
            );
          })()}
        </div>
        <div className="tabs-row">
          <nav className="tabs">
            {TABS.map(([k, label]) => (
              <button key={k} className={`tab${tab === k ? " active" : ""}`}
                onClick={() => setTab(k)}>{label}</button>
            ))}
          </nav>
          <GlobalSearch onOpenClient={setSelected} />
        </div>
      </div>

      <div className="pager-vp" ref={pagerRef}>
        <div className="pager-track"
          style={{ transform: (drag.dx || drag.to != null) ? `translateX(${drag.dx}px)` : "none",
                   transition: drag.anim ? "transform 0.26s ease" : "none" }}
          onTransitionEnd={onPagerTransitionEnd}>
          <div className="pager-pane">{renderTab(tab)}</div>
          {drag.to != null && (
            <div className="pager-pane pager-side"
              style={{ left: drag.dir < 0 ? "100%" : "-100%" }}>
              {renderTab(drag.to)}
            </div>
          )}
        </div>
      </div>
      {(() => { swipeStateRef.current = { tab, selected }; return null; })()}

      {selected !== null && (
        <div className="modal-overlay" onClick={close}>
          <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
            <ClientDetail clientId={selected} onBack={close} onDataChange={bumpRefresh}
              onSwitchClient={(id) => setSelected(id)}
              onMessageClient={(cid) => { close(); setTab("messages"); }} />
          </div>
        </div>
      )}

      <RemindersNote onOpenClient={setSelected} />
      <FloatingTimer onOpenClient={setSelected} onChange={bumpRefresh} />
      <AlarmPoller onOpenClient={setSelected} />

      <div className="ai-chip-float no-print">
        <AiChip />
      </div>

      {showSettings && me && (
        <SecuritySettings me={me} onClose={() => setShowSettings(false)}
          onChange={refreshMe} />
      )}

      {idleWarn && (
        <div className="idle-warn no-print">
          <span>You'll be signed out soon due to inactivity.</span>
          <button className="settings-btn" onClick={() => {
            lastActivity.current = Date.now(); setIdleWarn(false); refreshMe();
          }}>Stay signed in</button>
        </div>
      )}

      {moreNav && (
        <div className="mbn-sheet-overlay no-print" onClick={() => setMoreNav(false)}>
          <div className="mbn-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="mbn-sheet-title">More</div>
            {TABS.filter(([k]) => !BOTTOM_NAV.some((b) => b[0] === k) && !MOBILE_HIDDEN_TABS.includes(k)).map(([k, label]) => (
              <button key={k} className={`mbn-sheet-item${tab === k ? " active" : ""}`}
                onClick={() => { setTab(k); setSelected(null); setMoreNav(false); }}>{label}</button>
            ))}
          </div>
        </div>
      )}

      <nav className="mobile-bottom-nav no-print">
        {BOTTOM_NAV.map(([k, label, icon]) => (
          <button key={k} className={`mbn-item${tab === k && !moreNav ? " active" : ""}`}
            onClick={() => { setTab(k); setSelected(null); setMoreNav(false); }}>
            <span className="mbn-icon" aria-hidden="true">{icon}</span>
            <span className="mbn-label">{label}</span>
          </button>
        ))}
        <button className={`mbn-item${moreNav ? " active" : ""}`}
          onClick={() => setMoreNav((v) => !v)}>
          <span className="mbn-icon" aria-hidden="true"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M3 12h18M3 18h18" /></svg></span>
          <span className="mbn-label">More</span>
        </button>
      </nav>
    </div>
  );
}

export default App;
